/* DealRoomCanonicalPage — iter-186 · The operating system for property
 * transactions. Canonical Deal Room — production default.
 *
 *   Mounted at  /deal-rooms/:dealId
 *
 *   Fallback routes (preserved for one release):
 *     /deal-rooms/:dealId/workstreams   → operational workstreams table
 *     /deal-rooms/:dealId/room          → legacy tab-based Deal Room
 *     /deal-rooms/:dealId/legacy        → V3 safety hatch
 *     ?workstreams=1                    → opt into Workstreams from canonical
 *
 *   Hierarchy (top → bottom in the main column):
 *     1. Property hero
 *     2. Transaction journey
 *     3. Since your last visit
 *     4. Exchange Eve
 *     5. Current blockers
 *     6. Activity feed
 *     +   Right operational rail (Deal overview · Waiting on · Documents · Participants)
 *     +   Sticky bottom action bar
 *
 *   Doctrine:
 *     • Calm, premium, momentum-driven — NOT a CRM, NOT a dashboard.
 *     • Live data from chainStore + Deal Room APIs. No hardcoded demo.
 *     • Calm empty states (iter-205b) — render "Untitled property" /
 *       <CalmEmpty /> / "Add buyer" / "Add seller" when a field is
 *       genuinely missing, never "TBC" labels that lie about data.
 *     • Mobile renders the canonical operational-card system
 *       (DealRoomMobile) — separate render path.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import { calendarDaysUntil } from "../lib/waitDuration";
import { RoomLifecycleBanner } from "../components/dealroom/RoomLifecycleBanner";
import { createPortal } from "react-dom";
import { useParams, useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { saveDealReturnState, peekDealReturnState, clearDealReturnState, setLiveDealTab, restoreDealScroll } from "../lib/dealReturnState";
import { toast } from "sonner";
import {
  Share2, Bell, MoreHorizontal, Calendar, ArrowUpRight, ArrowRight, Check,
  Clock, Loader2, FileText, MessageSquare, ClipboardList, Paperclip,
  Send, Image as ImageIcon, AlertCircle, AlertTriangle, Scale, Landmark, Users,
  ChevronRight, ChevronDown, Plus, ShieldCheck, Eye, Lock,
  Phone, Mail, MessageCircle, Pencil, X,
  Settings, Archive, Link2, Pin, Reply, ExternalLink, Download,
  Target, UploadCloud, Inbox, ClipboardCheck,
  PoundSterling as PoundIcon, Home, KeyRound, CalendarCheck, Flag,
  TrendingUp, Bed, Bath, Car,
  XCircle, RotateCcw,
} from "lucide-react";

import { Sidebar } from "../components/Sidebar";
import ShellCanvas from "../components/layout/ShellCanvas";
import DealRoomMobile from "../components/dealroom/DealRoomMobile";
import { groundRentLabel } from "../lib/groundRent";
import { SaleAgreedElapsed } from "../components/dealroom/SaleAgreedElapsed";
import { CommunicationDeepLink } from "../components/dealroom/CommunicationDeepLink";
import { SinceLastVisitStrip } from "../components/dealroom/SinceLastVisitStrip";
import { InternalNotesPanel } from "../components/dealroom/InternalNotesPanel";
import { ActivityPanel, getLatestUpdateForParticipant, getLatestUpdateForRole, summariseUpdate, postChaseToBackend, _roleText } from "../components/dealroom/ActivityPanel";
import { KeyDatesSummary } from "../components/dealroom/KeyDatesSummary";
import ParticipantsModal from "../components/dealroom/ParticipantsModal";
import ShareRoomModal from "../components/dealroom/ShareRoomModal";
import IssueMemorandumOfSaleModal from "../components/dealroom/IssueMemorandumOfSaleModal";
import MarkFallenThroughModal from "../components/dealroom/MarkFallenThroughModal";
import MoSCompletedSummary from "../components/dealroom/MoSCompletedSummary";
import MortgageMilestoneModal from "../components/dealroom/MortgageMilestoneModal";
import { MessageAllSheet, TaskSheet, RequestDocSheet } from "../components/dealroom/DealRoomSheets";
import { DocumentRequestsWorkspace } from "../components/dealroom/DocumentRequestsWorkspace";
import { isDocRequestRelevant } from "../lib/documentRequestLaunch";
import { ProgressDealSheet } from "../components/dealroom/ProgressDealSheet";
import { HeroParticipantsPanel } from "../components/dealroom/HeroParticipantsPanel";
import { MilestoneProgressionSheet } from "../components/dealroom/MilestoneProgressionSheet";
import { RequestUpdateSheet } from "../components/dealroom/RequestUpdateSheet";
import { DocumentReviewModal } from "../components/dealroom/DocumentReviewModal";
// iter-alignment · P4 — OperationalNotesPanel restored below the
// Important Note banner so operators can create / pin / unpin /
// resolve notes through the existing UI, without introducing any
// new state or backend field.  The banner reads the same SSOT the
// panel writes to (`operationalNotes` state populated from
// `/api/v2/deals/:id/operational-notes`).
import { OperationalNotesPanel } from "../components/dealroom/OperationalNotesPanel";
import { RightRailFinancials } from "../components/dealroom/RightRailFinancials";
import { openProperty } from "../components/PropertyDetailsModal";
import { FinancialsSheet } from "../components/dealroom/FinancialsSheet";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../components/ui/hover-card";
import { comingSoon } from "../lib/uiSurfaces";
import { useDealAccess } from "../hooks/useDealAccess";
import { useChainStore, getDealExchangeTarget, getDealCompletionTarget, openEditProperty } from "../data/chainStore";
import {
  journeyFor as journeyForCanonical,
  nodeIdForStage,
  optsForDeal,
  bsaStatus,
  isPreJourney,
  PRE_JOURNEY,
  resolveCurrentFocusIdx,
  closeCompletedSet,
  journeyFor,
} from "../data/transactionJourney";
import { scoreDeal, dealStatusFor, isHealthScoringEnabled, HEALTH_SEVERITY } from "../data/dealHealth";
import { useAuth } from "../data/authStore";
import { api, apiError } from "../lib/api";
import { createContactForParticipant } from "../lib/participantContactCreate";
import { onPropertyImgError, PROPERTY_FALLBACK_SVG } from "../lib/propertyImage";
import { getChaseStateFor } from "../data/docChasing";
/* iter-362 — SSOT hook + copy dictionary. Central selector for
 * the 5 high-risk panels (Journey, Current Focus, Waiting On,
 * Health, Next Best Action). */
import { useDealTransactionState, filterActiveBlockers, resolveLiveBlockerState, resolveDealFlags } from "../hooks/useDealTransactionState";
import {
  TERMINAL_STATUSES,
  terminalBanner as _terminalBannerCopy,
  waitingChip as _waitingChipCopy,
  daysSince as _daysSinceCopy,
  formatLongDate as _formatLongDateCopy,
} from "../copy/dealRoom";
/* iter-alignment · P · Target Date · UK popover primitives.
 * The Popover carries the shadcn `Calendar` (react-day-picker)
 * so the existing calendar design is preserved verbatim. */
import { Calendar as CalendarPicker } from "../components/ui/calendar";
import { FileSpreadsheet, FileImage, File as FileIcon, FileArchive } from "lucide-react";
import { StageProgressHint } from "../components/StageProgressHint";

/* ── Helpers ───────────────────────────────────────────────────────── */
/* iter-217 — Fallback image now an inline SVG so we never hit an
 * external CDN (which was prone to stale 404s + console noise). The
 * variable name is preserved for backward compatibility with
 * downstream references inside the file. */
const FALLBACK_IMAGE = PROPERTY_FALLBACK_SVG;

const fmtDate = (iso, fallback = "Date TBC") => {
  if (!iso) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

const fmtDateLong = (iso, fallback = "Date TBC") => {
  if (!iso) return fallback;
  // iter-alignment · P0 · Milestone Completion Date Persistence
  // (2026-08-16) — YYYY-MM-DD strings are parsed by `new Date(...)`
  // as UTC midnight, which shifts to the previous day in
  // negative-UTC locales when re-rendered via `toLocaleDateString`.
  // Parse the date-only case explicitly as local time so the UK
  // display always matches the operator's chosen date.
  const raw = String(iso).trim();
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

/* iter-209 — DealRoomMobile expects a built view-model (`vm`) and a
 * `liveIdx` derived from the deal stage. Lifted from DealRoomV3Page
 * so the canonical page can resurrect the mobile shell without
 * forking the helper. */
const _MS_DAY = 86_400_000;
const daysBetween = (iso) => calendarDaysUntil(iso);
const stageToJourneyIdx = (stage) => {
  const s = String(stage || "").toLowerCase();
  if (!s || s === "offer" || s.includes("agreed")) return 0;
  if (s.includes("aml")) return 1;
  if (s.includes("memo")) return 2;
  if (s.includes("draft") || s.includes("contracts")) return 3;
  if (s.includes("pack_order")) return 4;
  if (s.includes("pack")) return 5;
  if (s.includes("additional")) return 7;
  if (s.includes("enquir")) return 6;
  if (s.includes("mortgage") || s.includes("offer_pending")) return 8;
  if (s.includes("report")) return 9;
  if (s === "exchange_ready" || s === "exchange-ready" || s === "pre_exchange") return 10;
  if (s.includes("completion_date") || s.includes("completion_agreed")) return 11;
  if (s.includes("exchanged") || s.includes("exchange ")) return 12;
  if (s.includes("complet")) return 13;
  return 1;
};


const fmtRel = (iso, fallback = "—") => {
  if (!iso) return fallback;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return fallback;
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const initialsOf = (s) => {
  if (!s) return "?";
  const parts = String(s).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || "?";
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const fmtMoney = (n) => {
  // iter-205b — accept three shapes:
  //   • numeric (5_400_00 / "540000")       → format as GBP
  //   • already-formatted string ("£540,000") → display as-is
  //   • null / undefined / NaN-string       → calm em-dash
  if (n === null || n === undefined || n === "") return "—";
  if (typeof n === "string") {
    // Strip common currency symbols + thousand separators.
    const cleaned = n.replace(/[£$€,\s]/g, "");
    const num = Number(cleaned);
    if (Number.isFinite(num) && num > 0) {
      return num.toLocaleString("en-GB", {
        style: "currency", currency: "GBP", maximumFractionDigits: 0,
      });
    }
    // String didn't parse — surface it as-is if it looks like a
    // pre-formatted price ("£540,000"), otherwise em-dash.
    return /[£$€]/.test(n) ? n : "—";
  }
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return "—";
  return v.toLocaleString("en-GB", {
    style: "currency", currency: "GBP", maximumFractionDigits: 0,
  });
};

/* iter-205 — calm "just now / 14m ago / 3h ago / yesterday / 2 May"
 * formatter for the milestone "Updated by …" caption. */
const timeAgo = (iso) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const diffMs = Date.now() - t;
  if (diffMs < 0) return "just now";
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  return new Date(t).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

/* iter-193 — Calm operational confidence fallback. Derives a 0-100
 * exchange confidence score from the deal's lived state when the
 * server has not stamped `exchange_probability`. Never random — the
 * same inputs always produce the same score so the rail feels stable.
 */
const confidenceFallback = (deal) => {
  if (!deal) return 70;
  let score = 70;
  /* iter-379 — Only count blockers that are still tied to a stage
   * the deal has NOT yet completed. Stale seed blockers (Mortgage
   * Valuation Pending after Exchange, etc.) would otherwise drag
   * the confidence score down even though the operational impedance
   * has cleared. */
  const activeBlockers = filterActiveBlockers(deal, { applicableNodes: [], currentNodeId: null });
  const blockers = activeBlockers.length;
  score -= blockers * 6;
  const days = Number(deal.days_in_stage || 0);
  if (days > 14) score -= 8;
  else if (days > 7) score -= 4;
  if (/exchange|complete/i.test(deal.stage || "")) score += 10;
  if (/under_offer|enquir/i.test(deal.stage || "")) score -= 4;
  if (deal.targetExchange || deal.exchangeDate || deal.target_exchange) {
    const ex = deal.targetExchange || deal.exchangeDate || deal.target_exchange;
    const days_to_x = (new Date(ex).getTime() - Date.now()) / 86400000;
    if (days_to_x < 0) score -= 12;
  }
  return Math.max(20, Math.min(95, Math.round(score)));
};

/* iter-214B-FIX — Tiny error boundary for the ProgressDealSheet.
 *
 * The drawer is mounted whenever the canonical Deal Room renders, so
 * any internal crash inside the progression engine would propagate
 * up and blank the whole page. Wrap it in this boundary: if the
 * sheet's tree throws, the rest of the Deal Room keeps rendering
 * and the next time the drawer opens it shows a calm fallback
 * surface instead of crashing again. */
class ProgressionSheetErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { crashed: false };
  }
  static getDerivedStateFromError() {
    return { crashed: true };
  }
  componentDidCatch(err, info) {
    if (typeof console !== "undefined" && console.error) {
      console.error("[ProgressDealSheet crashed]", err, info?.componentStack);
    }
  }
  render() {
    if (this.state.crashed) {
      if (!this.props.open) return null;
      // Calm fallback drawer — keeps the page usable. No portal
      // backdrop so the operator can simply navigate away.
      return (
        <div
          className="fixed top-4 right-4 z-[85] max-w-[400px] rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 px-5 py-4"
          role="alert"
          data-testid="progress-deal-sheet-fallback"
        >
          <h3 className="text-[14px] font-bold text-slate-900">Progression temporarily unavailable</h3>
          <p className="mt-1 text-[12px] text-slate-600 leading-snug">
            We couldn't load the progression engine for this deal. The rest of the
            Deal Room is still available — please refresh the page to try again.
          </p>
          <div className="mt-3 flex items-center justify-end">
            <button
              type="button"
              onClick={() => {
                this.setState({ crashed: false });
                this.props.onClose?.();
              }}
              className="text-[12px] font-semibold text-violet-700 hover:text-violet-900"
              data-testid="progress-deal-sheet-fallback-close"
            >
              Dismiss
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}


/* iter-292 — JOURNEY_STEPS / JOURNEY_STEPS_LEASEHOLD / STAGE_TO_JOURNEY
 * are now sourced from the canonical transaction journey module so
 * every timeline surface stays synchronised. The canonical journey
 * (Mortgage → Searches → Survey → Contract → [BSA] → [Mgmt Pack] →
 * Enquiries → Report → Exchange → Complete) is the single source of
 * truth. Stable internal stage ids continue to map through
 * `nodeIdForStage()`. */
const JOURNEY_STEPS = journeyForCanonical("freehold").map((n) => ({
  id: n.id,
  label: n.label,
}));

/* The leasehold rail (Mortgage → Searches → Survey → Contract → BSA →
 * Mgmt Pack → Enquiries → Report → Exchange → Complete) — BSA is
 * always exposed at the page level so MILESTONE_TO_JOURNEY_STEP can
 * resolve `bsa` regardless of whether the in-flight deal carries the
 * BSA flag. Deals without `applicableBSA` simply skip the BSA row in
 * downstream renderers. */
const JOURNEY_STEPS_LEASEHOLD = journeyForCanonical("leasehold", { applicableBSA: true }).map((n) => ({
  id: n.id,
  label: n.label,
}));

/* iter-214 — Map fine-grained deal stages to the canonical journey
 * step id. iter-292 delegates this lookup to the canonical
 * transaction journey module so every surface stays synchronised.
 * Pre-journey stages (offer / memo / aml) fall back to "mortgage"
 * — the first visible rail node — so the journey reads as
 * "0 of N complete · Mortgage upcoming" while the deal is warming up. */
const STAGE_TO_JOURNEY = new Proxy(
  {},
  { get: (_t, stage) => nodeIdForStage(String(stage || "")) },
);

/* iter-214 — Map a milestone (workstream item) id to the journey
 * step it should *advance past*. When a milestone completes, the
 * timeline must surface at least that step as "current" — the
 * resolver picks the furthest advancement across the deal's stage,
 * all completed milestones, and any agreed exchange/completion
 * date. Completion-date-agreed alone never advances Exchange or
 * Completion past their natural prerequisite milestones. */
const MILESTONE_TO_JOURNEY_STEP = {
  // iter-292 — Server milestone ids now map onto the canonical
  // journey node ids (mortgage · searches · survey · contract ·
  // bsa · mgmt_pack · enquiries · report · exchange · complete).
  // Legal workstream
  searches_returned:    "searches",
  enquiries_resolved:   "enquiries",
  signed_contract:      "contract",
  draft_contract:       "contract",
  leasehold_enquiries:  "enquiries",
  // Financial workstream
  mortgage_offer:       "mortgage",
  deposit_confirmed:    "contract",
  lender_authority:     "mortgage",
  // Coordination workstream
  completion_date:      null,           // date *agreed* is not a stage advancement
  chain_aligned:        null,           // chain alignment is not a stage advancement
  buyer_confirmation:   null,           // buyer confirmation alone is not a stage advancement
  // Leasehold-specific (when present in the milestones payload)
  survey_complete:      "survey",
  management_pack_requested: "mgmt_pack",
  management_pack_received:  "mgmt_pack",
  management_pack:           "mgmt_pack",
  // iter-alignment · P22 — Building Safety Act review advances
  // the BSA journey node.  Backend catalog entry lives in
  // `routes_milestones.py::MILESTONES["bsa_review_completed"]`
  // (single SSOT — no duplicate list).
  bsa_review_completed:      "bsa",
  exchange_confirmed:   "exchange",     // explicit exchange-confirmed milestone moves the timeline
  // iter-alignment · P35 · Stage-checklist completeness pass —
  // The following new operational milestones are non-advancing
  // (`null` step) by design: they represent parallel workstream
  // evidence inside a stage (e.g. draft-contract issued, searches
  // ordered, valuation completed) but never mark the rail step
  // as "done" on their own — the canonical exit-gate milestones
  // (`signed_contract`, `mortgage_offer`, `enquiries_resolved`,
  // `survey_complete`, `management_pack_received`,
  // `bsa_review_completed`, `completion_date`) remain the sole
  // authoritative "step complete" signals so parallel-progression
  // + `optional` semantics stay stable across pilot decks.
  draft_contract_issued:      null,
  searches_ordered:           null,
  report_on_title_issued:     null,
  survey_instructed:          null,
  mortgage_application_submitted: null,
  valuation_completed:        null,
  bsa_information_requested:  null,
  exchange_date_agreed:       null,
  funds_transferred:          null,
  keys_released:              null,
  // iter-alignment · P42 — Non-advancing operational rows added in
  // the Survey / Exchange / Completion expansion.  Canonical
  // exit-gate milestones (`survey_complete`, `completion_date`)
  // remain the sole "step done" signals.
  survey_carried_out:              null,
  survey_issues_resolved:          null,
  contracts_signed_buyer_side:     null,
  contracts_signed_seller_side:    null,
  completion_date_agreed:          null,
  pre_completion_searches_done:    null,
  final_completion_statement_received: null,
  // iter-alignment · P44 — Non-advancing operational rows for
  // Mgmt Pack + BSA.  Exit-gate milestones (`management_pack_received`
  // = mgmt_pack complete; `bsa_review_completed` = bsa complete)
  // remain the sole "step done" signals.
  management_pack_reviewed:                null,
  bsa_leaseholder_deed_of_certificate_received: null,
  bsa_leaseholder_deed_submitted_to_landlord:   null,
  bsa_landlords_certificate_received:           null,
  // iter-alignment · P41 — Seller authority is Pre-Exchange gate,
  // NOT a journey-step transition.
  seller_authority_to_exchange:            null,
  // iter-alignment · P43 — Contract stage expansion rows.
  // All non-advancing operational evidence — the rail still
  // advances via the canonical `signed_contract` exit gate.
  buyer_solicitor_instructed:                     null,
  seller_solicitor_instructed:                    null,
  buyer_solicitor_acknowledged_contract_pack:     null,
  ta6_property_information_form_received:         null,
  ta10_fixtures_and_contents_form_received:       null,
  ta7_leasehold_information_form_received:        null,
  new_homes_warranty_received:                    null,
  epc_available:                                  null,
  eicr_received:                                  null,
  planning_building_regs_received:                null,
  fensa_window_certificate_received:              null,
  boiler_service_certificate_received:            null,
  contract_pack_reviewed:                         null,
  initial_legal_review_complete:                  null,
  ready_to_raise_enquiries:                       null,
};

/* iter-214 — Canonical milestone-aware journey progress resolver.
 *
 * Single source of truth for the top timeline's current step. Reads:
 *   • the deal's static stage  → baseline progress
 *   • completed milestones     → real workstream evidence (the most
 *                                meaningful signal)
 *   • exchange-completed flag  → moves the rail to Exchange
 *   • completed-at timestamp   → moves the rail to Completion
 *
 * Returns `{ steps, currentIdx, completedSet }`. `currentIdx` points
 * at the FURTHEST meaningful step the deal has reached. Steps with
 * index < currentIdx render as completed; the step at currentIdx
 * renders as active; everything after stays grey.
 *
 * Rule 7 of the spec: "Completion date agreed but exchange not yet
 * completed → Completion stays upcoming, not current." Honoured
 * because `completion_date` milestone maps to `null` (no step
 * advancement) — only the deal's `completed` status (or an explicit
 * completion event) can advance the rail to the Completion step. */
/* Next Best Action — the single selector shared by the desktop card and the
 * phone "Up next" card (blocker first, then milestone / mortgage / stage). */
const HARD_BLOCKER_SEVERITIES = new Set(["blocking", "critical"]);

/* One-click NBA actions need the server's `can_complete` right for the milestone they write
 * (mortgage capture → mortgage application); "sheet" CTAs read "Confirm …" so they follow the
 * same milestone right. Blocker / MoS modes only navigate. */
const nbaPermissionDenied = (n, milestones) => {
  const can = new Set(milestones?.can_complete || []);
  if (n?.nbaMode === "direct" || n?.nbaMode === "confirm" || n?.nbaMode === "sheet") {
    const mid = n?._effectiveEntry?._milestoneId;
    return !!mid && !can.has(mid);
  }
  if (n?.nbaMode === "mortgage_capture" || n?.nbaMode === "mortgage_not_required") return !can.has("mortgage_application_submitted");
  return false;
};

function computeDealNextBestAction({ deal, txnState, journeyState, mortgageState, milestones, resolvedCriticalPaths }) {
                  const _awaitingCompletion = !!txnState?.isTerminal && txnState.terminalKind === "Exchanged"
                    && String(deal?.stage || "").toLowerCase() !== "completed"
                    && String(deal?.status || "").toLowerCase() !== "completed";
                  const _STAGE_NEXT_ACTION = {
                    /* iter-344 — Explanations lengthened to explicitly
                     * name the downstream consequence so agents see the
                     * operational rationale in one glance.
                     * iter-372 — Added missing pre-journey + downstream
                     * stage keys so every `deal.stage` value has a
                     * dedicated NBA copy.
                     * iter-373 — Added `mode` per stage. `direct` steps
                     * are pure milestone events that can be completed
                     * inline from the NBA card (single confirm click);
                     * `sheet` steps require evidence / status input and
                     * open the MilestoneProgressionSheet as before. */
                    offer:         { title: "Issue Memorandum of Sale",                        mode: "mos_modal", why: "The Deal Room represents an agreed sale — issuing the Memorandum of Sale to both solicitors formally starts the transaction." },
                    memo:          { title: "Issue Memorandum of Sale",                        mode: "mos_modal", why: "The Deal Room represents an agreed sale — issuing the Memorandum of Sale to both solicitors formally starts the transaction." },
                    aml:           { title: "Issue Memorandum of Sale",                        mode: "mos_modal", why: "The Deal Room represents an agreed sale — issuing the Memorandum of Sale to both solicitors formally starts the transaction." },
                    instruction:   { title: "Confirm seller solicitor instruction",           mode: "direct", why: "Title pack cannot be ordered until seller-side instruction is logged." },
                    solicitors:    { title: "Confirm both solicitors instructed",             mode: "direct", why: "The transaction only leaves pre-journey once buyer AND seller solicitors are formally instructed." },
                    searches:      { title: "Order local authority searches",                 mode: "sheet",  why: "Searches gate contract drafting on the buyer side — the earlier they're ordered, the earlier we exchange." },
                    survey:        { title: "Book buyer's survey",                            mode: "sheet",  why: "The mortgage valuation depends on the survey — booking early avoids Mortgage delays." },
                    mortgage_app:  { title: "Progress mortgage application",                  mode: "sheet",  why: "Exchange is blocked until the buyer has a valid offer in hand." },
                    mortgage:      { title: "Chase mortgage offer issuance",                  mode: "sheet",  why: "Exchange is blocked until the buyer has a valid offer in hand." },
                    mortgage_offer:{ title: "Chase mortgage offer issuance",                  mode: "sheet",  why: "Exchange is blocked until the buyer has a valid offer in hand." },
                    enquiries:     { title: "Resolve outstanding enquiries",                  mode: "sheet",  why: "Buyer's solicitor needs every reply on record before exchange can be booked." },
                    contracts:     { title: "Request signed contract from seller solicitor",  mode: "sheet",  why: "Exchange cannot happen until we receive the signed contract from the seller side." },
                    contract:      { title: "Request signed contract from seller solicitor",  mode: "sheet",  why: "Exchange cannot happen until we receive the signed contract from the seller side." },
                    mgmt_pack:     { title: "Request Management Pack",                        mode: "sheet",  why: "Buyer solicitor cannot raise leasehold enquiries without the pack in hand." },
                    bsa:           { title: "Complete Building Safety Act review",            mode: "sheet",  why: "Contracts cannot exchange until the BSA review is signed off by the seller solicitor." },
                    exchange:      { title: "Exchange Contracts",                             mode: "confirm", why: "Locking exchange releases the deposit and locks the completion countdown across the chain." },
                    exchange_ready:{ title: "Exchange Contracts",                             mode: "confirm", why: "Everything upstream is cleared — booking exchange releases the deposit and locks completion." },
                    exchanged:     { title: "Confirm completion funds transfer",              mode: "confirm", why: "Contracts are exchanged — completion is the final money-movement step." },
                    completion:    { title: "Confirm completion funds transfer",              mode: "confirm", why: "Final step before keys are released — everything else is already in place." },
                    complete:      { title: "Confirm completion funds transfer",              mode: "confirm", why: "Final step before keys are released — everything else is already in place." },
                    completed:     { title: "Archive completed deal",                         mode: "sheet",  why: "Move the deal into archived state so it drops off the active pipeline view." },
                  };
                  /* iter-379 — Blockers routed through the SSOT
                   * stage filter so a blocker whose stage has already
                   * been completed (e.g. Mortgage Valuation Pending
                   * after Exchange) does not override the NBA.
                   * P0 · Deal Room Data & Behaviour Integrity Pass
                   * (2026-08-12) — Also honours the Critical Path
                   * optimistic-resolution sentinel so the NBA
                   * "Resolve …" verb rolls to the next real
                   * dependency the moment a blocker is cleared. */
                  const _activeBlockersForNBA = filterActiveBlockers(deal || {}, {
                    applicableNodes: journeyState?.steps || [],
                    currentNodeId: txnState?.activeStepId || journeyState?.current_step || null,
                    resolvedIds: resolvedCriticalPaths,
                  });
                  // Only hard blockers override the milestone action; warnings stay in Current blockers.
                  const firstBlocker = _activeBlockersForNBA.find((b) => b && b.status !== "resolved" && HARD_BLOCKER_SEVERITIES.has(String(b.severity || "").toLowerCase()));
                  /* iter-alignment · P0 · NBA SSOT Reconciliation
                   * (2026-08-16) — The NBA MUST derive from the
                   * same canonical milestone/journey state the
                   * Transaction Journey uses.  `txnState.activeStepId`
                   * is the SINGLE source of truth: it resolves to
                   * the pre-journey node id when `isPreJourney` is
                   * true (offer / memo / aml) and to the current
                   * rail node id (mortgage / contract / enquiries
                   * / …) once the journey has advanced.  Before
                   * this fix the NBA read `deal.stage` first, so a
                   * deal whose canonical journey had advanced to
                   * Contract while `deal.stage` was still stale
                   * at "memo" surfaced the stale "Confirm both
                   * solicitors instructed" pre-journey action.
                   *
                   * Resolution order:
                   *   1. If the canonical journey has ADVANCED
                   *      past pre-journey (`activeStepId` present
                   *      AND `!isPreJourney`), use `activeStepId`
                   *      — this is what the rail is showing.
                   *   2. If still in pre-journey, use `deal.stage`
                   *      so we keep the offer / memo / aml
                   *      granularity the pre-journey nodes need.
                   *   3. Fall back to `activeStepId`, then to
                   *      backend `journeyState.current_step`, and
                   *      finally to `deal.stage` before defaulting
                   *      to "offer" so an empty deal never crashes.
                   *
                   * No deal-specific override, no duplicate state —
                   * only the resolution order flips so the NBA
                   * always agrees with Transaction Journey. */
                  const _canonicalActiveStepId =
                    txnState?.activeStepId || journeyState?.current_step || null;
                  const _canonicalIsPreJourney = !!txnState?.isPreJourney;
                  const _stageKeySource =
                    (_canonicalActiveStepId && !_canonicalIsPreJourney)
                      ? _canonicalActiveStepId
                      : (deal?.stage || _canonicalActiveStepId || "offer");
                  let stageKey = _awaitingCompletion ? "complete" : String(_stageKeySource).toLowerCase();

                  /* iter-alignment · P0 · NBA Completed-Stage Guard
                   * (2026-08-16) — Belt-and-braces: if `stageKey`
                   * still lands on a canonical node the hook has
                   * annotated as "completed" (rare drift when
                   * pre-journey/backend fallbacks diverge), walk
                   * forward through `txnState.journeyNodes` to the
                   * first non-completed node so the NBA never
                   * surfaces an action from a stage the operator
                   * has already ticked off.  This shares the
                   * SAME journey-node SSOT the Journey rail uses
                   * — no new state, no duplicate ordering. */
                  const _nodes = Array.isArray(txnState?.journeyNodes)
                    ? txnState.journeyNodes
                    : [];
                  if (_nodes.length && !_canonicalIsPreJourney) {
                    const _cur = _nodes.find((n) => n && n.id === stageKey);
                    if (_cur && _cur.status === "completed") {
                      const _next = _nodes.find((n) => n && n.status !== "completed");
                      if (_next) stageKey = String(_next.id).toLowerCase();
                    }
                  }
                  const stageEntry = _STAGE_NEXT_ACTION[stageKey] || _STAGE_NEXT_ACTION.contract;

                  /* iter-399 · Mortgage journey NBA resolver.
                   * When the active stage is on the mortgage rail
                   * (survey / mortgage_app / mortgage / mortgage_offer)
                   * the four captured substeps decide the exact CTA.
                   * Cash buyers short-circuit to a "not required"
                   * banner mode so the rest of the dispatcher can
                   * render it without duplicating stage checks.
                   */
                  const _mortgageStages = new Set(["survey", "mortgage_app", "mortgage", "mortgage_offer"]);
                  let mortgageEntry = null;
                  if (_mortgageStages.has(stageKey)) {
                    if (deal?.is_cash_buyer || mortgageState?.required === false) {
                      mortgageEntry = { title: "Mortgage: Not Required", mode: "mortgage_not_required", why: "This is a cash-buyer transaction — the mortgage journey does not apply." };
                    } else if (mortgageState) {
                      const s = mortgageState.substeps || {};
                      const appDone = !!s.application_submitted?.captured;
                      const bookedDone = !!s.valuation_booked?.captured;
                      const outcome = s.valuation_outcome?.outcome || null;
                      const offerDone = !!s.offer_received?.captured;
                      const overdue = !!mortgageState.valuation_overdue;

                      if (!appDone) {
                        mortgageEntry = { title: "Record Mortgage Application Submitted", mode: "mortgage_capture", mortgageMode: "app_submitted", why: "Capturing the submission date starts the mortgage clock — every downstream chase runs off this timestamp." };
                      } else if (!bookedDone) {
                        mortgageEntry = { title: "Book Mortgage Valuation", mode: "mortgage_capture", mortgageMode: "valuation_booked", why: "The lender's valuation is the gating event for the mortgage offer — booking it early keeps the exchange target intact." };
                      } else if (!outcome && overdue) {
                        mortgageEntry = { title: "Record Mortgage Valuation Outcome", mode: "mortgage_capture", mortgageMode: "valuation_outcome", why: "The valuation date has passed — logging the outcome unblocks the mortgage offer or triggers the right escalation." };
                      } else if (!outcome) {
                        mortgageEntry = { title: "Record Mortgage Valuation Outcome", mode: "mortgage_capture", mortgageMode: "valuation_outcome", why: "Once the surveyor has been out, capture the outcome so the next best action recalibrates." };
                      } else if (outcome === "down_valuation" || outcome === "failed_retention") {
                        mortgageEntry = { title: `Escalate valuation outcome — ${outcome === "down_valuation" ? "down valuation" : "failed / retention"}`, mode: "mortgage_capture", mortgageMode: "valuation_outcome", why: "The valuation came back short — renegotiate with the buyer, seller and lender before contracts can exchange." };
                      } else if (outcome === "awaiting") {
                        mortgageEntry = { title: "Chase Mortgage Valuation Outcome", mode: "mortgage_capture", mortgageMode: "valuation_outcome", why: "Result is still pending — chase the surveyor and update the outcome here when confirmed." };
                      } else if (outcome === "passed" && !offerDone) {
                        mortgageEntry = { title: "Chase Mortgage Offer", mode: "mortgage_capture", mortgageMode: "offer_received", why: "Valuation passed — the lender's formal offer is the next document we need in hand." };
                      }
                      // outcome=passed && offerDone → fall through to
                      // the default stage-based NBA so the deal
                      // progresses to enquiries / exchange without a
                      // dedicated expiry prompt (user brief 3g).
                    }
                  }

                  const effectiveEntry = mortgageEntry || stageEntry;

                  /* iter-alignment · P0 · Milestone-SSOT NBA
                   * Reconciliation (2026-08-18) — When the stage
                   * resolver produced a static CTA (e.g. "Request
                   * Management Pack") but the FIRST required
                   * milestone of that stage is already Complete
                   * per canonical SSOT, walk forward through the
                   * stage's remaining outstanding milestones and
                   * surface an imperative CTA for the first one.
                   *
                   * The mortgage rail keeps its dedicated resolver
                   * (four-substep state machine already lives in
                   * `mortgageEntry` above), so we only override
                   * when the stage-level fallback is what would
                   * render.  Blocker-aware wins upstream so a
                   * hard blocker still trumps the milestone CTA.
                   *
                   * Applies uniformly to Contract, Mgmt Pack,
                   * BSA, Enquiries, Exchange and Completion —
                   * no per-stage hardcode.  All eight stage
                   * keys resolve through the same lookup + the
                   * same milestone SSOT (`milestones.items`).
                   */
                  const _NBA_STAGE_ALIASES = {
                    contracts:   "contract",
                    completion:  "complete",
                    completed:   "complete",
                    exchange_ready: "exchange",
                  };
                  const _milestoneStageKey = _NBA_STAGE_ALIASES[stageKey] || stageKey;
                  const _MILESTONE_NBA_TITLES = {
                    // Contract
                    seller_solicitor_instructed:            "Confirm seller solicitor instructed",
                    buyer_solicitor_instructed:             "Confirm buyer solicitor instructed",
                    buyer_initial_funds_paid_to_solicitor:  "Chase buyer's initial funds",
                    official_title_documents_obtained:      "Obtain official title documents",
                    buyer_solicitor_acknowledged_contract_pack: "Acknowledge contract pack receipt",
                    ta6_property_information_form_received: "Chase TA6 Property Information Form",
                    ta10_fixtures_and_contents_form_received: "Chase TA10 Fixtures & Contents Form",
                    contract_pack_reviewed:                 "Review contract pack",
                    initial_legal_review_complete:          "Complete initial legal review",
                    ready_to_raise_enquiries:               "Prepare enquiries",
                    draft_contract_issued:                  "Issue draft contract",
                    searches_ordered:                       "Order local authority searches",
                    local_authority_search_received:        "Chase local authority search",
                    water_drainage_search_received:         "Chase water & drainage search",
                    environmental_search_received:          "Chase environmental search",
                    search_results_reviewed:                "Review search results",
                    signed_contract:                        "Request signed contract from seller solicitor",
                    // Mgmt Pack
                    management_pack_requested:              "Request Management Pack",
                    management_pack_received:               "Chase Management Pack from managing agent",
                    management_pack_reviewed:               "Review Management Pack",
                    // BSA
                    bsa_information_requested:              "Send Leaseholder Deed of Certificate to seller",
                    bsa_leaseholder_deed_of_certificate_received: "Chase completed Leaseholder Deed",
                    bsa_leaseholder_deed_submitted_to_landlord:   "Submit Leaseholder Deed to landlord/freeholder",
                    bsa_landlords_certificate_received:     "Chase Landlord's Certificate",
                    bsa_review_completed:                   "Review BSA documentation",
                    // Enquiries
                    enquiries_raised:                       "Raise buyer enquiries",
                    enquiries_replies_received:             "Chase seller solicitor for enquiry replies",
                    enquiries_resolved:                     "Resolve outstanding enquiries",
                    // Survey
                    survey_carried_out:                     "Book buyer's survey",
                    // Exchange
                    exchange_date_agreed:                   "Agree exchange date",
                    contracts_signed_buyer_side:            "Confirm buyer signed contract",
                    contracts_signed_seller_side:           "Confirm seller signed contract",
                    deposit_confirmed:                      "Confirm deposit received",
                    completion_date_agreed:                 "Agree completion date",
                    chain_aligned:                          "Align completion date across the chain",
                    buyer_confirmation:                     "Confirm buyer authority to exchange",
                    seller_authority_to_exchange:           "Confirm seller authority to exchange",
                    // Completion
                    pre_completion_searches_done:           "Run pre-completion searches",
                    final_completion_statement_received:    "Chase final completion statement",
                    funds_transferred:                      "Confirm completion funds transferred",
                    keys_released:                          "Release keys",
                    completion_date:                        "Confirm completion",
                  };
                  const _msItems = Array.isArray(milestones?.items) ? milestones.items : [];
                  const _outstandingForStage = _msItems.filter((m) =>
                    m && m.stage === _milestoneStageKey
                    && m.optional !== true
                    && resolveMilestoneState(m) !== "Complete"
                    // Skip conditional rows the operator has never
                    // triggered — they mirror the checklist's
                    // visibility gate so the NBA can't surface a
                    // hidden milestone.
                    && (!m.conditional || m.completed_at || m.target_date
                        || m.state === "done" || m.state === "blocked" || m.state === "waiting")
                  );
                  let milestoneNbaOverride = null;
                  // A reopened required item on an earlier stage leads the NBA (Recorded stage never moves back).
                  const _stageOrder = JOURNEY_STEPS_LEASEHOLD.map((s) => s.id);
                  const _curOrder = _stageOrder.indexOf(_milestoneStageKey);
                  const _reopened = _msItems.find((m) => m && m.reopened && m.optional !== true
                    && resolveMilestoneState(m) !== "Complete"
                    && _stageOrder.indexOf(m.stage) >= 0 && _stageOrder.indexOf(m.stage) < _curOrder);
                  if (!mortgageEntry && _reopened) {
                    const _stageLabel = JOURNEY_STEPS_LEASEHOLD.find((s) => s.id === _reopened.stage)?.label || "an earlier stage";
                    milestoneNbaOverride = {
                      title: _MILESTONE_NBA_TITLES[_reopened.id] || _reopened.label,
                      why: `Reopened on ${_stageLabel} — this required item is open again.`,
                      mode: _reopened.id === "contracts_exchanged" ? "confirm" : "sheet",
                      _milestoneId: _reopened.id,
                      _stepId: _reopened.stage,
                    };
                  } else if (!mortgageEntry && _outstandingForStage.length > 0) {
                    const _first = _outstandingForStage[0];
                    const _title = _MILESTONE_NBA_TITLES[_first.id] || _first.label;
                    if (_title) {
                      milestoneNbaOverride = {
                        title: _title,
                        why: (_awaitingCompletion && _first.id !== "completion_date")
                          ? `${_outstandingForStage.length} completion task${_outstandingForStage.length === 1 ? "" : "s"} still open before completion can be confirmed.`
                          : `${_outstandingForStage.length} task${_outstandingForStage.length === 1 ? "" : "s"} still open at this stage of the journey.`,
                        mode: (_awaitingCompletion || _first.id === "contracts_exchanged" || _first.id === "completion_date") ? "confirm" : "sheet",
                        _milestoneId: _first.id,
                      };
                    }
                  }
                  const _effectiveEntry = milestoneNbaOverride || effectiveEntry;

                  let nbaTitle, nbaWhy, nbaMode, mortgageMode;
                  if (firstBlocker) {
                    /* iter-373 · Blocker-aware NBA.
                     * Hard blockers replace the progression action —
                     * clicking the CTA routes to the blocker context
                     * (scrolls the blockers card into view) instead of
                     * completing the milestone. */
                    nbaTitle = `Resolve ${firstBlocker.title || firstBlocker.label || "outstanding blocker"}`;
                    nbaWhy = firstBlocker.description || firstBlocker.summary || "Progression is held until this is cleared.";
                    nbaMode = "blocker";
                  } else {
                    nbaTitle = _effectiveEntry.title;
                    nbaWhy = _effectiveEntry.why;
                    nbaMode = _effectiveEntry.mode || "sheet";
                    mortgageMode = _effectiveEntry.mortgageMode || null;
                  }
                  const targetStepId =
                    milestoneNbaOverride?._stepId ||
                    txnState?.activeStepId ||
                      journeyState?.current_step ||
                      deal?.stage ||
                      "offer";
  return { awaitingCompletion: _awaitingCompletion, nbaTitle, nbaWhy, nbaMode, mortgageMode, _effectiveEntry, targetStepId };
}

/* Up Next — next applicable canonical journey step after the rail focus (desktop + phone Radar). */
const resolveUpNext = (deal, milestones) => {
  try {
    const r = resolveJourneyProgress(deal, milestones);
    if (r.isPreJourney) return null;
    const cur = r.steps[r.currentFocusIdx];
    // Final step still open (e.g. Completion after exchange) — it is what comes next.
    const n = r.steps.slice(r.currentFocusIdx + 1).find((s) => !r.completedSet.has(s.id))
      || (cur && r.currentFocusIdx === r.steps.length - 1 && !r.completedSet.has(cur.id) ? cur : null);
    return n ? { label: n.longLabel || n.label, id: n.id } : { label: "—", id: null };
  } catch { return null; }
};

const resolveJourneyProgress = (deal, milestones, property = null) => {
  /* iter-214B-FIX — Hardened. Tolerates:
   *   - deal === null / undefined           (first render)
   *   - milestones === null / undefined     (loading)
   *   - milestones.items missing            (older seeds)
   *   - deal.tenure missing                 (default → freehold)
   *   - tenure on either deal OR property   (legacy seeds carry it
   *                                          on property only)
   * Never throws. Always returns a valid { steps, currentIdx,
   * completedSet } so callers can render unconditionally. */
  const safeDeal = deal || {};
  const rawTenure =
    safeDeal?.tenure ||
    safeDeal?.property?.tenure ||
    safeDeal?.property_tenure ||
    "";
  const tenure = String(rawTenure || "").toLowerCase();
  /* iter-306 — Steps are now resolved per-deal so cash buyers
   * skip the Mortgage node and freehold deals (or leasehold deals
   * explicitly flagged `applicableBSA: false`) omit BSA from the
   * progress calculation. This keeps progress %, current focus
   * and the journey rail aligned with the dynamic milestone set. */
  const steps = journeyForCanonical(tenure || "freehold", optsForDeal(safeDeal, property))
    .map((n) => ({ id: n.id, label: n.label }));
  const indexOf = (id) => {
    if (!id) return -1;
    const i = steps.findIndex((s) => s && s.id === id);
    return i >= 0 ? i : -1;
  };

  // Step 1 — baseline progress from the deal's static stage.
  const stage = String(safeDeal?.stage || safeDeal?.status || "offer").toLowerCase();
  const baselineId = STAGE_TO_JOURNEY[stage] || "mortgage";
  const baselineIdx = Math.max(0, indexOf(baselineId));

  // Step 2 — collect completed milestone step ids.
  //
  // iter-alignment · P35 · Stage Status Consistency.  A journey
  // step MUST NOT render as "Completed" while any REQUIRED
  // milestone scoped to that stage is still incomplete.  Prior
  // logic marked a step complete the moment ANY single milestone
  // mapped to it via `MILESTONE_TO_JOURNEY_STEP`, which produced
  // a visible drift: the vertical timeline / Current Stage panel
  // reported "Waiting" while the rail node rendered as Completed.
  //
  // The refactored population uses `m.stage` (canonical stage id
  // populated on the backend in iter-alignment · P7) instead of
  // the legacy step-id mapping.  A step is added to
  // `completedSet` iff:
  //   • at least one required milestone exists for the stage,
  //     AND
  //   • every required (`optional !== true`) milestone scoped to
  //     that stage is done.
  // Optional milestones (`optional === true`) never gate stage
  // completion — see P35 SSOT catalog.  The `MILESTONE_TO_JOURNEY_STEP`
  // constant is still consulted for legacy pilot data that
  // predates the `m.stage` field (falls back to the old
  // per-milestone advancement so historical completions still
  // resolve).
  const items = Array.isArray(milestones?.items) ? milestones.items : [];
  const completedSet = new Set();
  let milestoneIdx = -1;
  const _isDone = (m) => m && (m.state === "done" || m.state === "complete" || !!m.completed_at);
  // Group milestones by stage — canonical field from iter-alignment · P7.
  const _stageBuckets = new Map();
  items.forEach((m) => {
    if (!m || typeof m !== "object") return;
    const st = m.stage;
    if (!st) return;
    if (!_stageBuckets.has(st)) _stageBuckets.set(st, []);
    _stageBuckets.get(st).push(m);
  });
  // Mark a step complete iff every required milestone in its
  // stage bucket is done.  Empty buckets never mark a stage
  // complete on their own (that would regress historical deals
  // whose only signal is the legacy `MILESTONE_TO_JOURNEY_STEP`
  // branch below).
  _stageBuckets.forEach((bucket, stageId) => {
    const required = bucket.filter((m) => m?.optional !== true);
    if (required.length === 0) return;
    if (!required.every(_isDone)) return;
    const i = indexOf(stageId);
    if (i >= 0) {
      completedSet.add(stageId);
      if (i > milestoneIdx) milestoneIdx = i;
    }
  });
  // Legacy fallback — milestones missing the `m.stage` field
  // (pre-P7 seeds / pilot rows) still advance via the historical
  // step-id map.  This preserves existing pilot progress for any
  // deal that has NOT been re-fetched since P7 landed.  New
  // milestones from the current catalog always carry `m.stage`
  // and therefore go through the required-only gate above.
  items.forEach((m) => {
    if (!m || typeof m !== "object") return;
    if (m.stage) return;                     // canonical path already handled
    if (!_isDone(m)) return;
    const stepId = MILESTONE_TO_JOURNEY_STEP[m.id];
    if (!stepId) return;                     // Rule 7 — non-advancing milestones
    const i = indexOf(stepId);
    if (i >= 0) {
      completedSet.add(stepId);
      if (i > milestoneIdx) milestoneIdx = i;
    }
  });

  /* iter-alignment · P — In Progress ≡ genuinely underway.
   * A stage qualifies as `in_progress` ONLY when the canonical
   * milestone SSOT carries an explicit "underway" signal:
   *   • `milestones.items[].state === "in_progress"` (backend
   *     API today emits `done|pending` only, but this branch is
   *     forward-compatible for any milestone whose state was
   *     patched to in_progress via the Milestone Edit Popover
   *     · MILESTONE_STATUSES); OR
   *   • `deal.milestoneStates[stageId].status === "in_progress"`
   *     (pilot-overrides layer set by the same popover — the
   *     shape is stage-level, keyed by journey step id).
   * Partial completions (some milestones done, others pending)
   * no longer imply active work — those stages remain
   * Outstanding unless they are the Current Focus.  Fully
   * complete stages still short-circuit via `completedSet`.
   * Zero new state — pure derivation from the existing SSOT +
   * pilot override layer.
   *
   * iter-alignment · P · Independent In Progress States (2026-08-17)
   * — Completion is TERMINAL by definition — it can only ever be
   * `not started` or `complete`.  A milestone tagged
   * `state === "in_progress"` scoped to the Completion node is
   * ignored here so the rail never surfaces a
   * "Completion · In Progress" chip.  Every other applicable
   * node (Mortgage, Contract, Mgmt Pack, BSA, Enquiries,
   * Exchange) can independently render as In Progress without
   * advancing the Current Stage — the Current Focus resolver
   * (see `resolveCurrentFocusIdx` above) only walks past
   * `completedSet` entries, so parallel workstreams stay
   * visually independent from the critical path. */
  const _isInProgressState = (raw) => {
    const s = String(raw || "").toLowerCase();
    return s === "in_progress" || s === "in-progress";
  };
  const inProgressSet = new Set();
  items.forEach((m) => {
    if (!m || typeof m !== "object") return;
    if (!_isInProgressState(m.state)) return;
    const st = m.stage;
    if (!st) return;
    if (st === "complete") return;               // Completion is never In Progress
    if (indexOf(st) < 0) return;
    if (completedSet.has(st)) return;
    inProgressSet.add(st);
  });
  const _pilotMilestoneStates = safeDeal?.milestoneStates && typeof safeDeal.milestoneStates === "object"
    ? safeDeal.milestoneStates
    : null;
  if (_pilotMilestoneStates) {
    Object.keys(_pilotMilestoneStates).forEach((stageId) => {
      if (!_isInProgressState(_pilotMilestoneStates[stageId]?.status)) return;
      if (stageId === "complete") return;        // Completion is never In Progress
      if (indexOf(stageId) < 0) return;
      if (completedSet.has(stageId)) return;
      inProgressSet.add(stageId);
    });
  }

  /* iter-276 — Fold pilot completedSteps into the same canonical
   * set so the rail honours every Mark Complete event regardless
   * of which source persisted it. The shape is a flat array of
   * journey step ids ("aml", "draft_contract", …) written by
   * `markPilotJourneyStepComplete()` in chainStore. Real backend
   * deals get the same data via `milestones.items[]`; this branch
   * is purely additive — never overrides.
   *
   * iter-alignment · P0 · Journey SSOT Reconciliation (2026-02-15)
   * — the backend now populates canonical `completed_steps`
   * (snake_case) on every deal envelope from `milestone_completed`
   * activity rows.  Both aliases fold into the SAME set so the
   * detail page shows Current Stage in lockstep with what the
   * portfolio row (which reads `activeIndexForDeal` from the same
   * signals) renders.  Milestone completions remain the
   * authoritative journey evidence. */
  const dealCompletedCamel = Array.isArray(safeDeal?.completedSteps) ? safeDeal.completedSteps : [];
  const dealCompletedSnake = Array.isArray(safeDeal?.completed_steps) ? safeDeal.completed_steps : [];
  const dealCompleted = [...dealCompletedCamel, ...dealCompletedSnake];
  dealCompleted.forEach((stepId) => {
    if (!stepId) return;
    const i = indexOf(stepId);
    if (i >= 0) {
      completedSet.add(stepId);
      if (i > milestoneIdx) milestoneIdx = i;
    }
  });
  // Checklist ticks never record Exchange / Completion: only the confirmed lifecycle does.
  const _statusLc = String(safeDeal?.status || "").toLowerCase();
  const _lifeExchanged = stage === "exchanged" || stage === "completed" || _statusLc === "exchanged"
    || _statusLc === "completed" || !!safeDeal?.exchanged_at || !!safeDeal?.exchangedAt;
  const _lifeCompleted = stage === "completed" || _statusLc === "completed"
    || !!safeDeal?.completed_at || !!safeDeal?.completedAt;
  if (!_lifeExchanged) completedSet.delete("exchange");
  if (!_lifeCompleted) { completedSet.delete("complete"); completedSet.delete("completion"); }
  milestoneIdx = Math.max(-1, ...[...completedSet].map(indexOf));

  // Step 3 — explicit exchange / completion signals on the deal.
  //
  // iter-alignment · P0 · Confirmation Gates (2026-08-18) —
  // The terminal `contracts_exchanged` milestone is now the
  // canonical proof-of-exchange.  When the milestone catalog
  // exposes it, the regex-based lenient signal (`/exchang/i`
  // matching `exchange_ready`) MUST NOT prematurely close the
  // Exchange node — otherwise the Confirm Exchange gate never
  // renders because the Current Focus jumps past exchange the
  // moment the deal enters `exchange_ready`.  Legacy deals
  // whose milestone envelope predates the terminal row still
  // fall through to the regex so historical progress is
  // preserved.
  const _terminalExchangeKnown = items.some((m) => m?.id === "contracts_exchanged");
  const exchangedFlag =
    !!safeDeal?.exchanged_at || !!safeDeal?.exchangedAt ||
    stage === "exchanged" || stage === "completed" ||
    // A ticked confirmation row the gate refused (deal not at Exchange) is not an exchange.
    (!_terminalExchangeKnown && /exchang|complet/i.test(stage));
  const completedFlag =
    !!safeDeal?.completed_at || !!safeDeal?.completedAt ||
    stage === "completed";

  const _exchangeRecorded = completedSet.has("exchange") || !!safeDeal?.exchanged_at || !!safeDeal?.exchangedAt;
  // Stages with their own recorded evidence only — never inferred from a later stage or a completed transaction.
  const explicitSet = new Set(completedSet);
  if (_exchangeRecorded) explicitSet.add("exchange");
  if (completedFlag) explicitSet.add("complete");
  let currentIdx = Math.max(baselineIdx, milestoneIdx);
  if (exchangedFlag) {
    /* P0 · Property Type + Readiness Lifecycle (2026-02-28)
     * Post-exchange advances the rail to the Completion node
     * so Exchange renders as ✓ complete and Completion becomes
     * the active/current step.  Adding "exchange" to
     * `completedSet` here makes `currentFocusIdx` auto-advance
     * past it via the while-loop below, matching the visible
     * intent of the terminal banner + Completion Readiness
     * card. */
    const exIdx = indexOf("exchange");
    if (exIdx >= 0) {
      completedSet.add("exchange");
      if (exIdx > milestoneIdx) milestoneIdx = exIdx;
    }
    currentIdx = Math.max(currentIdx, indexOf("complete"));
  }
  if (completedFlag) currentIdx = Math.max(currentIdx, indexOf("complete"));

  // Clamp.
  if (currentIdx < 0) currentIdx = 0;
  if (currentIdx > steps.length - 1) currentIdx = steps.length - 1;

  /* iter-270H — Any-order completion. The "Current Focus" is the
   * first step that has NOT been marked complete by the operator;
   * defaults to the baseline stage's index when nothing has been
   * completed yet. Without this the rail forced the operator to
   * walk left-to-right even though completion is non-linear in
   * the real workflow.
   *
   * iter-372 — Pre-journey deals (offer / memo / aml) have not yet
   * entered the canonical rail. Historically we still highlighted
   * the first rail node (Mortgage) as "Current Focus", which
   * contradicted the backend `journeyState.current_step` (which
   * correctly returns the pre-journey step id) and left operators
   * clicking Mortgage only to see "Not the current step" in the
   * milestone sheet.
   *
   * iter-alignment · P0 · Journey SSOT Reconciliation (2026-02-15)
   * — the resolver is now shared with the Deal Rooms list's
   * `activeIndexForDeal` so the LIST and the DETAIL always
   * highlight the same node for a given deal, regardless of
   * whether the drift originated from stage staleness or milestone
   * completions.  Terminal timestamps + pre-journey guard are
   * honoured identically on both surfaces.
   */
  const currentFocusIdx = resolveCurrentFocusIdx({
    nodes: steps,
    baselineIdx,
    completedSet,
    stage,
    exchangedFlag,
    completedFlag,
  });

  /* iter-alignment · P0 · Journey SSOT Reconciliation
   * (2026-02-15) — apply LIFECYCLE CLOSURE.  A post-exchange
   * deal whose only explicit `milestone_completed` evidence
   * covers Enquiries + Exchange + Completion must NOT leave
   * the earlier applicable stages (Mortgage / Contract /
   * Mgmt Pack / BSA) rendering as *Outstanding* — you cannot
   * exchange without a signed contract and the transaction
   * lifecycle guarantees that every applicable required
   * stage prior to the highest evidenced completion is
   * effectively complete.  Non-applicable stages remain
   * respected because `steps` is already the tenure-aware
   * `journeyFor(tenure, opts)` result — cash buyers skip
   * Mortgage, freehold deals skip BSA + Mgmt Pack, so those
   * nodes can never be closure-inferred.  Every downstream
   * consumer that reads `completedSet` (rail, list ↔ detail
   * reconciliation, checklist header count, Transaction
   * Progress percentage) sees the reconciled set.  Backend
   * `completed_steps` / individual milestone rows are
   * untouched — this is a pure derivation upgrade. */
  const effectiveCompletedSet = closeCompletedSet({
    nodes: steps,
    completedSet,
    exchangedFlag,
    completedFlag,
    baselineIdx,
  });

  /* Progress is calculated from COMPLETED MILESTONES, not from the
   * timeline position of the current focus. This means an operator
   * who marks four random steps complete sees a 4-of-N progress
   * reading even if the focus has not advanced.
   *
   * iter-alignment · P0 (2026-02-15) — progress now reads the
   * closure-completed set so a post-exchange deal never renders
   * "4 of 13" while the rail claims Exchange is done. */
  const completedCount = effectiveCompletedSet.size;
  const progress = steps.length > 0
    ? Math.round((completedCount / steps.length) * 100)
    : 0;

  /* iter-270H — Per-step at-risk overlay. A step is "at risk" when
   *   • it is the current focus AND the deal has unresolved blockers,
   *   • OR an unresolved blocker is explicitly tagged to that step.
   * Returned as a Set so the rail can render the AT RISK chip without
   * threading every blocker through props.
   *
   * iter-379 — Blockers are filtered through the shared stage-aware
   * SSOT helper so a blocker tied to a completed step (Mortgage
   * Valuation Pending after Exchange, Searches Delayed after
   * enquiries) is dropped from the rail. `applicableNodes` uses the
   * canonical step list built above so pilot + real deals share the
   * same filter contract. */
  const atRiskSet = new Set();
  const currentFocusStepId = currentFocusIdx >= 0 ? steps[currentFocusIdx]?.id : null;
  const unresolved = filterActiveBlockers(safeDeal, {
    applicableNodes: steps,
    currentNodeId: currentFocusStepId,
  });
  for (const b of unresolved) {
    const tagged = b?.step_id || b?.journey_step || b?.target_step;
    if (tagged && indexOf(tagged) >= 0) atRiskSet.add(tagged);
  }
  if (unresolved.length > 0) {
    const focusId = steps[currentFocusIdx]?.id;
    if (focusId) atRiskSet.add(focusId);
  }

  /* iter-alignment · P0 · Journey Partial-Progress Rings (2026-02-28)
   * ---------------------------------------------------------------
   * Build a per-stage required-milestone tally so every applicable
   * node can render its true progress independently of the Current
   * Focus / completedSet.  We iterate `_stageBuckets` (canonical
   * `m.stage` field populated by iter-alignment · P7) and count
   * required-only rows (`optional !== true`).  Stages with zero
   * required milestones return `required: 0` so downstream renders
   * safely skip the fraction / ring (per the "no 0/0" guardrail).
   * The map is keyed by journey step id (`mortgage`, `contract`,
   * `mgmt_pack`, `bsa`, `enquiries`, `exchange`, `complete`) so it
   * matches the rail's `steps[].id` verbatim.  Zero new API — pure
   * derivation off the milestone envelope already fetched. */
  const stageProgressMap = new Map();
  _stageBuckets.forEach((bucket, stageId) => {
    const required = bucket.filter((m) => m?.optional !== true);
    const done = required.filter(_isDone).length;
    stageProgressMap.set(stageId, { done, required: required.length });
  });
  /* Lifecycle closure — when `effectiveCompletedSet` inferred a
   * stage complete via the closeCompletedSet closure rules
   * (post-exchange, terminal completion, or baseline-sentinel),
   * treat every required milestone in that stage as done for the
   * purposes of the rail's fraction display.  This mirrors the
   * "Completed" chip that already surfaces on those nodes so the
   * fraction never contradicts the chip.  Non-applicable stages
   * are absent from `steps` and are therefore never touched. */
  steps.forEach((s) => {
    if (!s || !s.id) return;
    if (!effectiveCompletedSet.has(s.id)) return;
    const cur = stageProgressMap.get(s.id);
    if (!cur) return;
    if (cur.required > 0 && cur.done < cur.required) {
      stageProgressMap.set(s.id, { done: cur.required, required: cur.required });
    }
  });

  // Presentation-only: stages with a RECORDED completion (green tick). Inferred exchange is excluded.
  const recordedSet = new Set(effectiveCompletedSet);
  if (!_exchangeRecorded) recordedSet.delete("exchange");
  return { steps, currentIdx, currentFocusIdx, completedSet: effectiveCompletedSet, recordedSet, explicitSet, atRiskSet, inProgressSet, progress, stageProgressMap, isPreJourney: isPreJourney(stage), isTransactionComplete: completedFlag };
};

/* iter-248B — Upcoming-state icon map.
 *
 * The refined design surfaces a calm domain-specific icon inside
 * each upcoming node (scales for Exchange, columned facade for
 * Completion). Falls back to a small dot for any other upcoming
 * stage so the design degrades gracefully across the leasehold
 * 11-step rail. */
const _JOURNEY_STATE_TEXT = (done, focus, passed) =>
  done ? "completion recorded" : focus ? "current stage" : passed ? "stage passed, no completion recorded" : "not yet complete";


/* iter-272 — Active milestone details (timeline refinement).
 *
 * Goal: when the operator looks at the Transaction Journey, the
 * ACTIVE milestone must instantly answer "what's happening, what's
 * blocked, who owns it, what's the target". Completed milestones
 * must show when they completed. The data points are read STRICTLY
 * from real deal state — activity[] · milestones[] · blockers[] ·
 * participants[] · exchangeDate / completionDate — and update
 * automatically when any of those change. */

/* Step → canonical owner role. The role label matches the strings
 * stored on `deal.participants[].role` (canonical pilot dataset)
 * and is also bridged against `participant_type` for API rows. */
const _STEP_OWNER_ROLE = {
  offer:         "Estate Agent",
  aml:           "Estate Agent",
  memo:          "Estate Agent",
  instruction:   "Seller Solicitor",
  searches:      "Buyer Solicitor",
  enquiries:     "Buyer Solicitor",
  survey:        "Buyer",
  mgmt_pack:     "Seller Solicitor",
  drafts:        "Seller Solicitor",
  mortgage_app:  "Mortgage Broker",
  mortgage_offer:"Mortgage Broker",
  mortgage:      "Mortgage Broker",
  contract:      "Seller Solicitor",
  contracts:     "Seller Solicitor",
  bsa:           "Buyer Solicitor",
  exchange:      "Buyer Solicitor",
  exchange_ready:"Buyer Solicitor",
  completion:    "Buyer Solicitor",
};

/* Pretty-print a date (handles both ISO strings and JS millisecond
 * timestamps so pilot rows and API rows both flow through). */
const _fmtJourneyDate = (raw) => {
  if (!raw) return null;
  const d = typeof raw === "number" ? new Date(raw) : new Date(String(raw));
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};
const _msFrom = (raw) => {
  if (!raw) return null;
  if (typeof raw === "number") return raw;
  const ms = Date.parse(String(raw));
  return Number.isNaN(ms) ? null : ms;
};

/* Active-milestone helper retired alongside the panel — see the
 * Transaction Journey card cleanup.  The rail still uses
 * `resolveJourneyProgress` + `_completionDateFor`; this helper had
 * no other consumer, so it's removed to keep the module tight. */

/* Returns the canonical completion date for a step id (when the
 * step has been marked complete). Reads `milestones.items` first
 * (real backend completed_at), then `deal.activity[]` for the
 * pilot Mark Complete row (iter-276), then `activityDates`. */

const _recordedDateFor = (stepId, milestones, deal) => {
  const items = Array.isArray(milestones?.items) ? milestones.items : [];
  const done = (m) => m && (m.state === "done" || m.state === "complete" || !!m.completed_at);
  if (stepId === "exchange") {
    const _st = String(deal?.stage || "").toLowerCase();
    if (_st !== "exchanged" && _st !== "completed" && !(deal?.exchanged_at || deal?.exchangedAt)) return null;  // ticks alone never date Exchange
    return deal?.exchanged_at || deal?.exchangedAt
      || items.find((m) => m?.id === "contracts_exchanged" && m.completed_at)?.completed_at || null;
  }
  if (stepId === "complete" && (deal?.completed_at || deal?.completedAt)) return deal.completed_at || deal.completedAt;
  const stamps = items
    .filter((m) => done(m) && m.completed_at && (m.stage ? m.stage === stepId : MILESTONE_TO_JOURNEY_STEP[m.id] === stepId))
    .map((m) => m.completed_at)
    .sort((x, y) => Date.parse(y) - Date.parse(x));
  if (stamps.length) return stamps[0];
  if (Array.isArray(deal?.completedSteps) && deal.completedSteps.includes(stepId)) {
    const row = (Array.isArray(deal?.activity) ? deal.activity : []).find(
      (a) => typeof a?.id === "string" && a.id.includes(`-${stepId}-`)
    );
    if (row?.created_at) return row.created_at;
  }
  return null;
};

/* iter-alignment · P0 · Confirmation Celebration (2026-08-18).
 *   CSS-only particle burst fired the FIRST time the terminal
 *   milestone (`contracts_exchanged` or `completion_date`)
 *   transitions to complete.  Zero deps, no canvas, no sound.
 *
 *     • Exchange   — ~1.0s duration, 12 particles.
 *     • Completion — ~1.8s duration, 24 particles.
 *
 *   One-shot per (dealId, event) via localStorage key
 *   `chainflow:celebrated:{dealId}:{event}` so a page refresh
 *   never replays it.  Skipped entirely when
 *   `prefers-reduced-motion: reduce` is set. */
const ConfirmationCelebration = ({ dealId, event, trigger }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!trigger || !dealId || !event) return undefined;
    const key = `chainflow:celebrated:${dealId}:${event}`;
    try {
      if (localStorage.getItem(key) === "1") return undefined;
    } catch (_e) { /* private mode — proceed without persistence */ }
    if (typeof window !== "undefined"
      && window.matchMedia
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      try { localStorage.setItem(key, "1"); } catch (_e) { /* noop */ }
      return undefined;
    }
    setVisible(true);
    try { localStorage.setItem(key, "1"); } catch (_e) { /* noop */ }
    const duration = event === "exchange" ? 1000 : 1800;
    const t = setTimeout(() => setVisible(false), duration + 200);
    return () => clearTimeout(t);
  }, [trigger, dealId, event]);
  if (!visible) return null;
  const count = event === "exchange" ? 12 : 24;
  const duration = event === "exchange" ? 1000 : 1800;
  const palette = ["#7C3AED", "#A78BFA", "#F59E0B", "#FCD34D"];
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-[70] overflow-hidden"
      data-testid={`confirmation-celebration-${event}`}
    >
      <style>{`
@keyframes cf-particle-burst {
  0%   { transform: translate3d(0,0,0) scale(0.6); opacity: 0; }
  10%  { opacity: 1; }
  100% { transform: translate3d(var(--dx), var(--dy), 0) scale(1); opacity: 0; }
}
      `}</style>
      {Array.from({ length: count }).map((_, i) => {
        const angle = (i / count) * Math.PI * 2;
        const dist = 140 + (i % 3) * 24;
        const dx = Math.cos(angle) * dist;
        const dy = Math.sin(angle) * dist;
        const color = palette[i % palette.length];
        return (
          <span
            key={i}
            className="absolute left-1/2 top-1/2 block w-2 h-2 rounded-full"
            style={{
              background: color,
              transform: "translate3d(-50%, -50%, 0)",
              boxShadow: `0 0 12px ${color}`,
              animation: `cf-particle-burst ${duration}ms cubic-bezier(0.15,0.85,0.3,1) forwards`,
              animationDelay: `${i * 12}ms`,
              "--dx": `${dx}px`,
              "--dy": `${dy}px`,
            }}
          />
        );
      })}
    </div>
  );
};

const TransactionJourney = ({ deal, milestones, activityDates, journeyState, viewingStageId, onStepActivate }) => {
  /* iter-alignment · P0 · Property Edit Drawer — Save Propagation
   * (2026-02-28) — Consult the canonical Property SSOT so BSA
   * applicability (Yes/No pill on the Edit drawer) drives the
   * rail live.  `findProperty` reads from the same properties
   * slice that `updateProperty` mutates on save, so the rail
   * re-renders automatically the moment the drawer's Save
   * changes returns. */
  const { findProperty } = useChainStore();
  const propertyForBSA = deal?.propertyId ? findProperty(deal.propertyId) : null;
  /* iter-374 — Pre-journey pill removed for the pilot; the SSOT
   * cascade in `POST /journey/offer/complete` now auto-advances the
   * transaction all the way to the first canonical rail milestone
   * (Mortgage for chain buyers, Contract for cash) so the operator
   * never sits in a pre-journey state. `isPreJourney` is still
   * destructured for downstream consumers / tests but no longer
   * drives any visible chrome above the rail. */
  const { steps, currentFocusIdx, explicitSet: completedSet, atRiskSet, inProgressSet, stageProgressMap, isTransactionComplete } = resolveJourneyProgress(deal, milestones, propertyForBSA);
  const _hasPassedUnrecorded = steps.some((s, i) => i < currentFocusIdx && !completedSet.has(s.id));
  // Lines are blue only through the unbroken run of completed stages from the start, into the active node.
  const _firstOpenIdx = (() => { const k = steps.findIndex((s) => !completedSet.has(s.id)); return k < 0 ? steps.length : k; })();
  // Mobile: the rail scrolls inside the card; desktop: every stage shares the width.
  return (
    <section data-testid="canonical-journey-strip" className="-mx-1">
      <div className="overflow-x-auto overscroll-x-contain px-1 pb-1" data-testid="canonical-journey-scroll">
        <ol className="relative flex items-start min-w-max sm:min-w-0" data-testid="canonical-journey-rail">
          {steps.map((s, i) => {
            const isComplete = completedSet.has(s.id);
            const isFocus = !isTransactionComplete && !isComplete && i === currentFocusIdx;
            const live = !isTransactionComplete;
            const isInProgress = live && !isComplete && !isFocus && s.id !== "complete" && inProgressSet.has(s.id);
            const isAtRisk = live && atRiskSet.has(s.id) && !isComplete;
            const isViewing = !!viewingStageId && s.id === viewingStageId && !isFocus;
            const stageProgress = (stageProgressMap && stageProgressMap.get(s.id)) || { done: 0, required: 0 };
            const isPartial = live && stageProgress.required > 0 && !isComplete && stageProgress.done > 0 && stageProgress.done < stageProgress.required;
            const isPassed = !isComplete && !isFocus && (isTransactionComplete || i < currentFocusIdx);
            const recordedDate = isComplete ? _fmtJourneyDate(_recordedDateFor(s.id, milestones, deal)) : null;
            // Highest applicable item only: Blocked → recorded date → checklist progress; future stages stay neutral (no "Waiting").
            const focusCount = isFocus && stageProgress.required > 0
              ? { text: `${stageProgress.done}/${stageProgress.required} required complete`, tone: isAtRisk ? "text-rose-600 font-semibold" : "text-[#6536E8] font-semibold", wrap: true }
              : null;
            const line = focusCount ? focusCount
              : isAtRisk ? { text: "Blocked", tone: "text-rose-600 font-semibold" }
                : isComplete ? { text: recordedDate || "Not recorded", tone: "text-slate-500" }
                  : isPartial ? { text: `${stageProgress.done}/${stageProgress.required} required complete`, tone: "text-slate-500", wrap: true }
                    : isPassed ? { text: "Not recorded", tone: "text-slate-400" }
                      : null;
            const stateText = _JOURNEY_STATE_TEXT(isComplete, isFocus, isPassed);
            const fullStatus = [
              isComplete ? (recordedDate ? `Recorded ${recordedDate}` : "Recorded, date not recorded") : stateText.charAt(0).toUpperCase() + stateText.slice(1),
              isAtRisk ? "blocked" : null,
              isInProgress ? "in progress" : null,
              stageProgress.required > 0 && !isComplete ? `${stageProgress.done} of ${stageProgress.required} checklist items done` : null,
            ].filter(Boolean).join(", ");
            const connectorState = i < _firstOpenIdx
              && (i + 1 < _firstOpenIdx || (!isTransactionComplete && i + 1 === currentFocusIdx)) ? "complete" : "future";
            const marker = isComplete
              ? { background: "#3B82F6", border: "none" }
              : isFocus
                ? { background: "#6536E8", border: "none", boxShadow: "0 0 0 6px rgba(101,54,232,0.14)" }
                : isPassed
                  ? { background: "#FFFFFF", border: "1.5px dashed #94A3B8" }
                  : { background: "#FFFFFF", border: `1.5px solid ${isAtRisk ? "#FB7185" : isInProgress ? "#A78BFA" : "#CBD5E1"}` };
            if (isViewing && !marker.boxShadow) marker.boxShadow = "0 0 0 4px rgba(101,54,232,0.16)";
            const body = (
              <>
                {i < steps.length - 1 && (
                  <span
                    aria-hidden
                    className="absolute top-[21px] left-1/2 w-full h-[2px]"
                    style={{ background: connectorState === "complete" ? "#3B82F6" : "#E2E8F0" }}
                    data-state={connectorState}
                    data-testid={`canonical-journey-connector-${i}`}
                  />
                )}
                <span className="relative z-10 flex items-center justify-center w-11 h-11">
                  <span
                    className="w-8 h-8 rounded-[8px] flex items-center justify-center"
                    style={marker}
                    data-focus={isFocus ? "true" : undefined}
                    data-in-progress={isInProgress ? "true" : undefined}
                    data-viewing={isViewing ? "true" : undefined}
                    data-partial={isPartial ? "true" : undefined}
                    data-passed={isPassed ? "true" : undefined}
                    data-testid={`canonical-journey-step-${i}-circle`}
                    aria-hidden
                  >
                    {isComplete && <Check size={16} className="text-white" strokeWidth={3} />}
                    {isFocus && <span className="w-2.5 h-2.5 rounded-full bg-white" />}
                    {isPassed && <span className="w-2.5 h-[2px] rounded-full bg-slate-400" />}
                    {isInProgress && <span className="w-1.5 h-1.5 rounded-full bg-violet-400" />}
                  </span>
                </span>
                <span
                  className={`mt-2 px-1 text-center text-[14px] font-semibold leading-tight ${
                    isFocus ? "text-[#6536E8]" : isComplete ? "text-slate-900" : "text-slate-500"
                  }`}
                  data-testid={`canonical-journey-step-${i}-label`}
                >
                  {s.label}
                </span>
                {line && (
                  <span
                    className={`mt-1 px-1 text-center text-[12.5px] leading-tight ${line.wrap ? "whitespace-normal" : "whitespace-nowrap"} ${onStepActivate ? "group-focus-visible:hidden" : "group-focus:hidden"} ${line.tone}`}
                    data-testid={`canonical-journey-step-${i}-subline`}
                  >
                    {line.text}
                  </span>
                )}
                <span className={`mt-1 px-1 text-center text-[12px] leading-tight text-slate-600 hidden ${onStepActivate ? "group-focus-visible:block" : "group-focus:block"}`} aria-hidden>
                  {fullStatus}
                </span>
              </>
            );
            return (
              <li
                key={s.id}
                className="relative flex-1 basis-0 min-w-[96px]"
                data-state={isComplete ? "complete" : isAtRisk ? "at-risk" : isFocus ? "focus" : isInProgress ? "in-progress" : isPartial ? "partial" : isPassed ? "passed-unrecorded" : "future"}
                data-testid={`canonical-journey-step-${i}-item`}
              >
                {onStepActivate ? (
                  <button
                    type="button"
                    onClick={() => onStepActivate(s.id, { isComplete, isActive: isFocus, asSelect: true })}
                    className="group relative w-full flex flex-col items-center rounded-lg pb-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6536E8]/40"
                    data-testid={`canonical-journey-step-${i}`}
                    data-interactive="true"
                    aria-label={`${s.label}: ${fullStatus}. Open ${s.label} workflow`}
                    aria-current={isFocus ? "step" : undefined}
                    title={`${s.label}: ${fullStatus}`}
                  >
                    {body}
                  </button>
                ) : (
                  <div
                    className="group relative flex flex-col items-center rounded-lg pb-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6536E8]/40"
                    data-testid={`canonical-journey-step-${i}`}
                    data-interactive="false"
                    tabIndex={0}
                    role="img"
                    aria-label={`${s.label}: ${fullStatus}`}
                  >
                    {body}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </div>
      {_hasPassedUnrecorded && (
        <p className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-slate-500" data-testid="canonical-journey-key">
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden className="w-3 h-3 rounded-[3px] bg-[#3B82F6]" />
            Completion recorded
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden className="w-3 h-3 rounded-[3px] border-[1.5px] border-dashed border-slate-400" />
            Stage passed, no completion recorded
          </span>
        </p>
      )}
    </section>
  );
};

/* ── Property hero (compressed) ──────────────────────────────────────
 *
 * iter-205b — hydration fix.
 *
 * The chainStore returns the property in its canonical shape
 * (`type` / `tenure` / `thumb` / `price`) and the deal in camelCase
 * (`agreedPrice`). The hero was reading legacy keys (`property_type`,
 * `image_url`, `value`, `buyer_name`) that never existed on the
 * normalised shape, so every cell collapsed to its "TBC" fallback
 * even when real data was saved. We now read the canonical keys
 * directly, fall back to the deal's `participants` array for the
 * buyer / seller name, and surface a calm "Add …" affordance
 * instead of "TBC" when a field is genuinely missing.
 */
const _findParticipant = (participants, type) =>
  (participants || []).find((p) => {
    // iter-269 — Bridge canonical role label → legacy participant_type
    // so the canonical pilot fallback (`role: "Buyer Solicitor"`)
    // resolves to the same slot as the API shape (`participant_type:
    // "buyer_solicitor"`).
    if (p?.participant_type === type) return true;
    const role = (p?.role || "").toLowerCase().replace(/\s+/g, "_");
    if (role === type) return true;
    // Estate Agent ↔ negotiator (legacy slot name on the right rail).
    if (type === "negotiator" && role === "estate_agent") return true;
    return false;
  }) || null;

/* iter-alignment · P3 · Multiple Participants per Role (2026-02-28)
 * ----------------------------------------------------------------
 * `_findParticipants` is the multi-variant of `_findParticipant`.
 * It returns EVERY participant matching the role — the rail groups
 * on this so a Buyer with two joint purchasers renders as one
 * "Buyer (2)" header + two rows.  `_findParticipant` (single) is
 * preserved verbatim so every other surface (MoS resolver, quick
 * add, activity feed lookups) keeps its existing "first match"
 * semantics.  Multi-buyer / multi-seller consumers opt IN by
 * calling `_findParticipants` explicitly.
 */
const _findParticipants = (participants, type) =>
  (participants || []).filter((p) => {
    if (p?.participant_type === type) return true;
    const role = (p?.role || "").toLowerCase().replace(/\s+/g, "_");
    if (role === type) return true;
    if (type === "negotiator" && role === "estate_agent") return true;
    return false;
  });

const _participantName = (p) =>
  p ? (p.full_name || p.name || p.email || null) : null;

/* iter-362 — Lead Agent detection for participant pinning.
 * Accepts both API (`participant_type: "negotiator"`) and the
 * legacy pilot shape (`role: "Estate Agent"`). Keeps the roster
 * in sync no matter which side of the mirror surfaces it. */
const _isLeadAgentParticipant = (p) => {
  if (!p) return false;
  const t = String(p.participant_type || "").toLowerCase();
  if (t === "negotiator" || t === "agent") return true;
  const r = String(p.role || "").toLowerCase().replace(/\s+/g, "_");
  return r === "negotiator" || r === "estate_agent" || r === "lead_agent" || r === "agent";
};

/* iter-362 — File-type icon for the Documents rail. Mirrors
 * the Lucide vocabulary used elsewhere in the app so the icons
 * feel like part of the same design system (no bespoke SVGs,
 * no colour badges). Returns a component reference the caller
 * renders with `<Icon size={14} />`. */
const _iconForFile = (name, mime) => {
  const n = String(name || "").toLowerCase();
  const m = String(mime || "").toLowerCase();
  if (n.endsWith(".pdf")  || m.includes("pdf"))                                        return FileText;
  if (n.endsWith(".doc")  || n.endsWith(".docx") || m.includes("word"))                return FileText;
  if (n.endsWith(".xls")  || n.endsWith(".xlsx") || n.endsWith(".csv") || m.includes("sheet") || m.includes("excel")) return FileSpreadsheet;
  if (/\.(png|jpe?g|gif|webp|heic|heif|bmp|svg)$/.test(n) || m.startsWith("image/"))   return FileImage;
  if (/\.(zip|rar|7z|tar|gz)$/.test(n) || m.includes("zip"))                            return FileArchive;
  return FileIcon;
};

/* iter-362 — Calm terminal-state banner. Replaces the Next
 * Best Action + Waiting On stack for Exchanged / Completed /
 * Fallen Through deals. Reads all copy from the central
 * dictionary so wording stays consistent everywhere.
 *
 * P0 · Property Type + Readiness Lifecycle (2026-02-28)
 * ---------------------------------------------------------
 * The post-exchange banner reads as a live forward-looking
 * status.  Completed deals keep the concise "Completed on {date}"
 * line; Fallen Through retains the audit-trail sentence because
 * there genuinely are no further steps.
 *
 * iter-alignment · Post-exchange banner truth pass (2026-08-18)
 * ---------------------------------------------------------
 * Post-exchange copy + tone now read from the SAME SSOT that
 * powers the Completion Readiness card (`txnState.confidence.
 * reasons`).  A completion that has passed without being
 * recorded surfaces as "Completion N days overdue" in a rose
 * tone — the banner can no longer show "Completion scheduled"
 * or "On track" while Completion Readiness is at risk.  Also
 * tightened vertical rhythm: `p-5` → `px-4 py-3`, dropped the
 * redundant "Contracts exchanged · Exchange complete" prefix
 * (verb "Exchange completed" now carries both signals).
 */
const _fmtBannerDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d
    .toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    .replace(/^(\d{1,2}) Sept /, "$1 Sep ");
};

// Map a confidence reason → banner completion segment + tone.
// Any reason whose key starts with `completion_` is a signal
// about the post-exchange leg; anything else means Completion
// Readiness could not resolve a completion signal — we fall
// back to "Completion date to be confirmed".
const _completionSegmentFromConfidence = (confidence) => {
  const reasons = Array.isArray(confidence?.reasons) ? confidence.reasons : [];
  const r = reasons.find((x) => typeof x?.key === "string" && x.key.startsWith("completion_"));
  if (!r) {
    return { label: "Completion date to be confirmed", tone: "watch" };
  }
  return { label: r.label, tone: r.tone || "neutral" };
};

const _BANNER_TONE = {
  risk:     "bg-rose-50 ring-rose-200 text-rose-700",
  watch:    "bg-amber-50 ring-amber-200 text-amber-800",
  positive: "bg-emerald-50 ring-emerald-200 text-emerald-700",
  neutral:  "bg-emerald-50 ring-emerald-200 text-emerald-700",
};

const TerminalStateBanner = ({ txnState, deal }) => {
  if (!txnState?.isTerminal) return null;
  const kind = txnState.terminalKind || "";

  // Compose the primary title.  Post-exchange reads the
  // Completion Readiness SSOT so tone + copy can never
  // disagree with the readiness card.
  let title = _terminalBannerCopy(kind, txnState.terminalDateIso);
  let tone;
  if (kind === "Exchanged") {
    const exchangeDate = _fmtBannerDate(txnState.terminalDateIso);
    const seg = _completionSegmentFromConfidence(txnState.confidence);
    title = `Exchange completed${exchangeDate ? ` ${exchangeDate}` : ""} · ${seg.label}.`;
    tone = _BANNER_TONE[seg.tone] || _BANNER_TONE.neutral;
  } else if (kind === "Fallen Through") {
    tone = "bg-rose-50 ring-rose-200 text-rose-700";
    /* iter-alignment · P0 · Deal Room · Fallen Through (2026-02)
     * — Extend the banner title with the operator-captured
     * reason + date drawn straight from the canonical deal
     * SSOT (`deal.fallen_through_reason` /
     * `deal.fallen_through_at`).  No new state; the banner is
     * read-only.  Falls back gracefully when the fields aren't
     * populated (legacy fallen-through rooms marked before this
     * flow shipped). */
    const reason = deal?.fallen_through_reason || deal?.fallenThroughReason || null;
    const fallenIso = deal?.fallen_through_at || deal?.fallenThroughAt || null;
    const fallenLabel = _fmtBannerDate(fallenIso);
    const suffixBits = [];
    if (reason) suffixBits.push(reason);
    if (fallenLabel) suffixBits.push(fallenLabel);
    if (suffixBits.length > 0) {
      title = `Fallen through — ${suffixBits.join(" · ")}.`;
    }
  } else {
    tone = _BANNER_TONE.positive;
  }

  return (
    <div
      className={`rounded-2xl ring-1 px-4 py-3 flex items-start justify-between ${tone}`}
      data-testid="deal-terminal-banner"
      data-terminal-kind={kind}
    >
      <div className="min-w-0">
        <div className="text-[10.5px] uppercase tracking-wider font-semibold opacity-80">
          Transaction state
        </div>
        <div
          className="mt-0.5 text-[13.5px] font-semibold leading-snug"
          data-testid="deal-terminal-banner-title"
        >
          {title}
        </div>
      </div>
    </div>
  );
};

/* iter-345 — Convert a canonical role_key (e.g. `buyer_solicitor`)
 * into the human-readable label used across the rail
 * ("Buyer Solicitor"). Falls back to a Title-cased version when
 * the key is unknown so a novel role from the API still reads
 * cleanly. */
const _roleKeyToLabel = (key) => {
  if (!key) return null;
  const CANON = {
    buyer_solicitor:  "Buyer Solicitor",
    seller_solicitor: "Seller Solicitor",
    mortgage_broker:  "Mortgage Broker",
    broker:           "Mortgage Broker",
    agent:            "Estate Agent",
    negotiator:       "Estate Agent",
    buyer:            "Buyer",
    seller:           "Seller",
    surveyor:         "Surveyor",
  };
  if (CANON[key]) return CANON[key];
  return String(key)
    .split(/[_\s-]+/)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
};

/* iter-302 — QuickEdit primitives, hoisted to module scope so React
 * doesn't unmount + remount the inputs on every keystroke. */
const _QuickEditSection = ({ title, children }) => (
  <div className="mb-5">
    <p className="text-[10.5px] uppercase tracking-[0.10em] text-slate-400 font-semibold mb-2">{title}</p>
    <div className="space-y-3">{children}</div>
  </div>
);
const _QuickEditField = ({ label, value, onChange, type = "text", testid, disabled }) => (
  <label className="block" data-testid={testid}>
    <span className="block text-[11.5px] font-semibold text-slate-600 mb-1">{label}</span>
    <input
      type={type}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className="w-full h-9 px-3 rounded-lg ring-1 ring-slate-200 text-[13px] text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-violet-400/60 disabled:bg-slate-50 disabled:text-slate-400"
      data-testid={`${testid}-input`}
    />
  </label>
);

/* iter-alignment · P68 · Purchase-method toggle (2026-02).
 *
 * Segmented pill for the Purchase method row on the Quick Edit
 * Deal Sheet.  Source of truth is `deals.is_cash_buyer` — this
 * component is a stateless renderer; the diff flows through the
 * shared save handler so `optsForDeal → journeyFor → resolveMortgage`
 * remains the single reconciliation path across every consumer
 * (rail, progress, NBA, checklist, blockers). */
const _QuickEditToggle = ({ label, value, options, onChange, testid, disabled, help }) => (
  <div className="block" data-testid={testid}>
    <span className="block text-[11.5px] font-semibold text-slate-600 mb-1">{label}</span>
    <div
      className="inline-flex rounded-lg ring-1 ring-slate-200 bg-slate-50 p-0.5"
      role="radiogroup"
      aria-label={label}
    >
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            disabled={disabled}
            className={`h-8 px-3.5 rounded-md text-[12px] font-semibold transition-colors ${
              active
                ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200"
                : "text-slate-500 hover:text-slate-700"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
            data-testid={`${testid}-${opt.value}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
    {help && (
      <p className="mt-1.5 text-[10.5px] text-slate-400 leading-snug">{help}</p>
    )}
  </div>
);

/* iter-alignment · P68 · Purchase-method reconciliation.
 *
 * Stages that ONLY exist inside the Mortgage journey node (see
 * `transactionJourney.js::_NODES`).  When the operator flips a
 * deal to Cash while the deal sits on one of these, the local
 * stage must reconcile to the first applicable incomplete stage
 * so the rail, progress and Exchange Confidence redraw without
 * a refresh.  Backend milestone SSOT already gates mortgage rows
 * via `_applies_to_deal('mortgaged')`. */
const MORTGAGE_ONLY_STAGES = new Set(["mortgage_app", "mortgage_offer", "survey"]);

function _firstApplicableCashStage(deal) {
  const tenure = (deal?.tenure || "").toLowerCase();
  const opts = { financeType: "cash" };
  if (deal && Object.prototype.hasOwnProperty.call(deal, "applicableBSA") && deal.applicableBSA != null) {
    opts.applicableBSA = !!deal.applicableBSA;
  }
  const nodes = journeyFor(tenure, opts);
  const firstNode = Array.isArray(nodes) && nodes.length > 0 ? nodes[0] : null;
  return firstNode?.stages?.[0] || null;
}

/* ── QuickEditDealSheet · iter-302 ──────────────────────────────────
 *
 * Lightweight right-side drawer triggered from PropertyHero's
 * subtle "Edit deal" pencil. Surfaces the eight deal-critical
 * fields agents update most often without leaving the Deal Room:
 *
 *   Property     — Address
 *   Financials   — Sale Agreed Price
 *   Parties      — Buyer · Seller · Solicitor · Mortgage Broker
 *   Timeline     — Exchange Target · Completion Target
 *
 * Wiring:
 *   • Permission-controlled — disabled when access.can_edit_deal
 *     is explicitly false. The Edit button itself is hidden when
 *     the operator has no write access.
 *   • Optimistic — `patchDealLocally()` flips the in-memory deal
 *     immediately so every connected surface (Forecasting Centre,
 *     Operational Pressure, Deal Health, Transaction Journey)
 *     re-renders on the same tick.
 *   • Persistent — real deals route through `updateDeal()` (PATCH
 *     /api/v2/deals/:id). Pilot deals (`deal._pilot === true`)
 *     route through `patchDealLocally()` + `appendPilotActivity()`
 *     for the activity log so the audit trail survives refresh.
 *   • Audit-logged — every changed field emits one activity row
 *     captioned "<Field> updated · <old> → <new>" so the Activity
 *     Feed records who changed what and when.
 */
const QuickEditDealSheet = ({
  open,
  onClose,
  deal,
  property,
  participants = [],
  onSave,
  canEdit,
}) => {
  const initial = useMemo(() => {
    const buyer  = _findParticipant(participants, "buyer");
    const seller = _findParticipant(participants, "seller");
    const solicitor = _findParticipant(participants, "buyer_solicitor")
      || _findParticipant(participants, "seller_solicitor")
      || _findParticipant(participants, "solicitor");
    const broker = _findParticipant(participants, "mortgage_broker")
      || _findParticipant(participants, "broker");
    const exch = getDealExchangeTarget(deal);
    const comp = getDealCompletionTarget(deal);
    const toIso = (v) => (v ? String(v).slice(0, 10) : "");
    // iter-302 — Strip formatting characters from any seeded
    // price string (e.g. "£1,395,000") so the number input
    // round-trips cleanly back to the same value.
    //
    // iter-alignment · P0 · Price SSOT Audit (2026-02-28)
    // ------------------------------------------------------------
    // Do NOT pre-fill Agreed Price from `property.price` — that
    // is the property's Asking Price (a listing-level SSOT
    // column) and cross-copying it into the deal blob turned
    // "no agreed price yet" into "silently save the Asking Price
    // as Agreed Price" the moment the operator clicked Save.
    // Empty deal.agreed_price stays empty; the input surfaces
    // the drawer's own "£0" placeholder so operators enter the
    // real number.
    const rawPrice = deal?.agreedPrice || deal?.agreed_price || "";
    const priceNum = typeof rawPrice === "number"
      ? rawPrice
      : parseFloat(String(rawPrice).replace(/[^0-9.]/g, ""));
    return {
      address:    property?.address || deal?.name || "",
      agreedPrice: Number.isFinite(priceNum) && priceNum > 0 ? String(priceNum) : "",
      buyerName:  deal?.buyer_name  || deal?.buyer  || _participantName(buyer)  || "",
      sellerName: deal?.seller_name || deal?.seller || _participantName(seller) || "",
      solicitorName: _participantName(solicitor) || "",
      brokerName:    _participantName(broker)    || "",
      exchangeTarget:   toIso(exch),
      completionTarget: toIso(comp),
      // iter-alignment · P68 · Purchase method (Mortgage / Cash).
      // Seeded from the canonical `deals.is_cash_buyer` flag.
      purchaseMethod: (deal?.is_cash_buyer === true || deal?.isCashBuyer === true) ? "cash" : "mortgage",
    };
  }, [deal, property, participants]);

  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  if (!open) return null;
  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  const dirty = Object.keys(initial).some((k) => String(draft[k] ?? "") !== String(initial[k] ?? ""));
  const fieldDisabled = !canEdit || saving;
  const onOverlayKey = (e) => { if (e.key === "Escape") onClose?.(); };

  const submit = async () => {
    if (!dirty || saving || !canEdit) return;
    setSaving(true);
    try {
      const diff = {};
      Object.keys(initial).forEach((k) => {
        if (String(draft[k] ?? "") !== String(initial[k] ?? "")) {
          diff[k] = String(draft[k] ?? "").trim();
        }
      });
      await onSave(diff, initial);
      onClose?.();
    } catch (_e) {
      // toast surfaced by caller
    } finally {
      setSaving(false);
    }
  };

  const Section = _QuickEditSection;


  return (
    <div className="fixed inset-0 z-50" data-testid="quick-edit-deal-sheet" onKeyDown={onOverlayKey}>
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} aria-hidden />
      <aside
        className="absolute right-0 top-0 h-full w-full sm:w-[400px] bg-white shadow-2xl flex flex-col"
        role="dialog"
        aria-label="Quick edit deal"
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="min-w-0">
            <h2 className="text-[15px] font-bold tracking-tight text-slate-900">Quick edit deal</h2>
            <p className="text-[11.5px] text-slate-500 mt-0.5">Changes sync instantly across Deal Rooms, Forecasting and Pressure.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-slate-100 inline-flex items-center justify-center text-slate-500"
            aria-label="Close quick edit"
            data-testid="quick-edit-close"
          >
            <X size={16} strokeWidth={2.2} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <Section title="Property">
            <_QuickEditField label="Address" value={draft.address} onChange={set("address")} testid="quick-edit-field-address" disabled={fieldDisabled} />
          </Section>
          <Section title="Financials">
            <_QuickEditField label="Sale agreed price (£)" type="number" value={draft.agreedPrice} onChange={set("agreedPrice")} testid="quick-edit-field-agreed-price" disabled={fieldDisabled} />
            <_QuickEditToggle
              label="Purchase method"
              value={draft.purchaseMethod}
              onChange={(v) => setDraft((d) => ({ ...d, purchaseMethod: v }))}
              options={[
                { value: "mortgage", label: "Mortgage" },
                { value: "cash",     label: "Cash" },
              ]}
              testid="quick-edit-field-purchase-method"
              disabled={fieldDisabled}
              help="Cash removes Mortgage from the active journey. Historic mortgage activity is preserved."
            />
          </Section>
          <Section title="Parties">
            <_QuickEditField label="Buyer"            value={draft.buyerName}     onChange={set("buyerName")}     testid="quick-edit-field-buyer"     disabled={fieldDisabled} />
            <_QuickEditField label="Seller"           value={draft.sellerName}    onChange={set("sellerName")}    testid="quick-edit-field-seller"    disabled={fieldDisabled} />
            <_QuickEditField label="Solicitor"        value={draft.solicitorName} onChange={set("solicitorName")} testid="quick-edit-field-solicitor" disabled={fieldDisabled} />
            <_QuickEditField label="Mortgage broker"  value={draft.brokerName}    onChange={set("brokerName")}    testid="quick-edit-field-broker"    disabled={fieldDisabled} />
          </Section>
          <Section title="Timeline">
            <_QuickEditField label="Exchange target"    type="date" value={draft.exchangeTarget}   onChange={set("exchangeTarget")}   testid="quick-edit-field-exchange-target"   disabled={fieldDisabled} />
            <_QuickEditField label="Completion target"  type="date" value={draft.completionTarget} onChange={set("completionTarget")} testid="quick-edit-field-completion-target" disabled={fieldDisabled} />
          </Section>
        </div>
        <footer className="px-5 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-4 rounded-lg text-[13px] font-semibold text-slate-700 hover:bg-slate-100"
            data-testid="quick-edit-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!dirty || saving || !canEdit}
            className="h-9 px-4 rounded-lg text-[13px] font-semibold text-white bg-gradient-to-r from-violet-600 to-violet-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_4px_14px_-6px_rgba(124,58,237,0.55)]"
            data-testid="quick-edit-save"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </footer>
      </aside>
    </div>
  );
};

const PropertyHero = ({
  deal,
  property,
  participants = [],
  documents = [],
  milestones,
  onJumpToParticipants,
  onOpenChecklist,
  onAddHeroParticipant,
  onUpdateExchange,
  onUpdateCompletion,
  onUpdateTenure,
  onUpdateAgreedPrice,
  onUpdateSaleAgreed,
  onOpenQuickEdit,
  canQuickEdit,
}) => {
  // iter-alignment · P0 · Deal Room · Property Header Polish
  // (2026-03-01) — Split the canonical single-line address SSOT
  // into a compact identity block:
  //
  //   Primary   — the short identity (first comma-separated
  //               segment, e.g. "1404 Lantana Heights").
  //   Secondary — the remaining segments joined with " · ",
  //               then the postcode appended once at the end,
  //               giving "1 Glasshouse Gardens · Stratford ·
  //               London · E20 1HT".
  //
  // Falls back safely for houses / missing fields — a single-
  // segment address renders just the primary line with the
  // postcode as the secondary, or nothing at all when postcode
  // is missing.  Never duplicates the primary in the secondary
  // (case-insensitive check) and never mutates the stored SSOT.
  const rawAddress = property?.address || deal?.name || null;
  const _addressSegments = rawAddress
    ? String(rawAddress)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
  const primaryAddress = _addressSegments[0] || rawAddress || null;
  const _secondarySegments = _addressSegments
    .slice(1)
    .filter((seg) => seg.toLowerCase() !== String(primaryAddress || "").toLowerCase());
  if (property?.postcode) {
    const pc = String(property.postcode).trim();
    if (pc && !_secondarySegments.some((seg) => seg.toLowerCase() === pc.toLowerCase())) {
      _secondarySegments.push(pc);
    }
  }
  const secondaryAddress = _secondarySegments.length
    ? _secondarySegments.join(" · ")
    : null;
  // Historical alias kept for downstream references + tests.
  const address = primaryAddress;

  // Image — chainStore exposes `thumb` (not image_url).
  const image = property?.thumb || property?.image_url || property?.thumbnail_url || FALLBACK_IMAGE;

  // Property meta. The deal also carries `tenure` (Transaction
  // Structure surfacing) so prefer that when set; fall back to the
  // property record.
  const tenure       = deal?.tenure || property?.tenure || null;
  const propertyType = property?.type || property?.property_type || null;

  // Sale agreed — chainStore returns `agreedPrice` (camelCase); the
  // raw V2 API returns `agreed_price`.
  //
  // iter-alignment · P0 · Price SSOT Audit (2026-02-28)
  // ------------------------------------------------------------
  // NEVER fall back to `property.price` here — that is the
  // **Asking Price** (a property-level column), not the deal's
  // Agreed Price (a deal-level column).  The two are independent
  // canonical facts and cross-falling-back caused the Deal Room
  // hero to silently display the listing's Asking Price whenever
  // an operator cleared `agreed_price`, giving a fictitious
  // "agreed" number.  Deal-legacy shims (`value` / `price`) are
  // kept for historical pilot rows that stored the agreed value
  // on the deal blob under an older key; property SSOT is out.
  const salePriceRaw =
    deal?.agreedPrice || deal?.agreed_price ||
    deal?.value || deal?.price || null;

  const exchangeTarget = getDealExchangeTarget(deal);
  const completionTarget = getDealCompletionTarget(deal);

  // Counters — photo count from property, doc count from the
  // deal's documents feed (passed through by the canonical page).
  const photoCount = Number(property?.image_count ?? property?.photos?.length ?? 12);
  const docCount   = Array.isArray(documents) ? documents.length : 0;

  // iter-alignment · P · Property Hero refresh (2026-02-14).
  // Refined to the approved reference: address + postcode read as
  // one identity block, an icon-chip row surfaces the canonical
  // property attributes (beds · baths · type · tenure · parking,
  // hiding unknowns), and the three primary transaction facts
  // (Agreed Price · Exchange · Completion) sit as the bottom
  // three metric columns.  Every piece of data comes from the
  // existing SSOT — no new endpoint, no duplicate props.
  const beds       = property?.beds  ?? property?.bedrooms  ?? null;
  const baths      = property?.baths ?? property?.bathrooms ?? null;
  const parking    = property?.parking || null;
  const propertyTypeLabel = propertyType
    ? String(propertyType).replace(/\b\w/g, (c) => c.toUpperCase())
    : null;
  const tenureLabel = tenure
    ? String(tenure).replace(/\b\w/g, (c) => c.toUpperCase())
    : null;

  const exchangeDays   = daysBetween(exchangeTarget);
  const completionDays = daysBetween(completionTarget);
  const fmtRelDays = (n) => {
    if (n === null || Number.isNaN(n)) return null;
    if (n > 0) return `In ${n} day${n === 1 ? "" : "s"}`;
    if (n === 0) return "Today";
    const abs = Math.abs(n);
    return `${abs} day${abs === 1 ? "" : "s"} ago`;
  };
  // Once exchanged / completed, the chip shows the actual date (never an overdue target).
  const _heroStage = String(deal?.stage || "").toLowerCase();
  const _actualExIso = (_heroStage === "exchanged" || _heroStage === "completed") ? (deal?.exchangedAt || deal?.exchanged_at || null) : null;
  const _actualCoIso = _heroStage === "completed" ? (deal?.completedAt || deal?.completed_at || null) : null;
  const _fmtActual = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }).replace(/^(\d{1,2}) Sept /, "$1 Sep ");
  const exchangeRel   = _actualExIso ? `Exchanged ${_fmtActual(_actualExIso)}` : fmtRelDays(exchangeDays);
  const completionRel = _actualCoIso ? `Completed ${_fmtActual(_actualCoIso)}` : fmtRelDays(completionDays);
  /* iter-361 · F-11 · countdown pill (2026-08-29) — the value
   * fed into `HeroFact.subClass` is now a full pill class
   * (bg + text) rather than just a text colour so the sub-line
   * reads as a compact pale-green (or rose when overdue) pill
   * matching the approved reference. */
  const relTone = (n) => (
    n === null ? "" :
    n < 0      ? "bg-rose-50 text-rose-700" :
                 "bg-emerald-50 text-emerald-700"
  );

  /* iter-alignment · Deal Room Hero · Date KPI Refinement
   * (2026-03-02) — quieter supporting-timing chip.  Normal future
   * dates render neutrally; semantic colour is reserved for
   * genuinely meaningful states (overdue = rose · due soon within
   * 4 days = amber).  Same input as `relTone` so no date logic
   * or SSOT change; only the presentation shifts. */
  const relToneQuiet = (n) => (
    n === null ? "" :
    n < 0      ? "bg-rose-50 text-rose-700" :
    n <= 4     ? "bg-amber-50 text-amber-700" :
                 "bg-slate-50 text-slate-500"
  );

  /* iter-alignment · P0 · Edit dates action (2026-02-28)
   * -----------------------------------------------------------------
   * Authorised users get a compact "Edit dates" affordance beside
   * the Exchange / Completion cells that opens `EditDatesPopover`.
   * Persistence flows through the existing `onUpdateExchange` /
   * `onUpdateCompletion` callbacks the parent already wires to
   * `persistDateField` (optimistic patch + rollback + updateDeal),
   * so every store consumer (Command Centre, Banking Month /
   * Forecasting Centre, Deal Rooms list, right-rail confidence &
   * overdue calcs) reconciles in the same tick with no duplicate
   * date state.  Permission is gated on `canQuickEdit` which the
   * parent derives from `access?.can_edit_deal !== false`. */
  const canEditDates = !!canQuickEdit
    && typeof onUpdateExchange === "function"
    && typeof onUpdateCompletion === "function";
  const [editDatesOpen, setEditDatesOpen] = useState(false);

  // iter-alignment · Deal Room · Sale Agreed + Transaction Dates
  // (2026-03-02) — canonical Sale Agreed timestamp.  Nullable —
  // legacy deals surface `+ Add date`; the modal is the single
  // canonical editor for all three transaction dates.
  const saleAgreedIso = deal?.saleAgreedDate || deal?.sale_agreed_date || null;

  const fmtTargetDate = (iso) => {
    if (!iso) return "Not set";
    // iter-388 · Force 3-letter month across the board.  en-GB
    // returns "Sept" for September (4 chars) which pushes the
    // Exchange / Completion target cells past the visible width
    // on 1280 px laptops.  Replacing → "Sep" keeps every month
    // at 3 chars for a consistent one-line date rhythm.
    return new Date(iso)
      .toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
      .replace(/^(\d{1,2}) Sept /, "$1 Sep ");
  };

  return (
    <section
      className="rounded-2xl bg-white p-2.5 grid grid-cols-[200px_minmax(0,1fr)] grid-rows-[auto_1fr] gap-x-5 cf-card-hover"
      style={{
        border: "1px solid rgba(226,232,240,0.75)",
        boxShadow: "0 1px 2px rgba(15,23,42,0.03), 0 6px 18px -14px rgba(15,23,42,0.08)",
      }}
      data-testid="canonical-property-hero"
    >
      {/* Property image — iter-388 · width tightened ~7 %
          (216 → 200 px) and height reduced ~7 % (166 → 154 px)
          so the header carries a calmer footprint.
          iter-alignment · P16 — Corner radius softened
          (rounded-xl → rounded-2xl) to match the header shell + a
          calmer premium feel. */}
      <div className="relative shrink-0 w-[200px] h-[154px] rounded-2xl overflow-hidden bg-slate-100 lg:row-span-2">
        <img
          src={image}
          alt={address || "Property"}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={onPropertyImgError}
        />
        {/* iter-388 — Merged photo + document counter pill (bottom-left).
            Retains the existing `image_count` sentinel testid so the
            downstream contract tests continue to resolve. */}
        <div
          className="absolute bottom-2 left-2 inline-flex items-center gap-2 px-2 h-[22px] rounded-md bg-slate-900/75 text-white text-[11px] font-semibold backdrop-blur"
          data-testid="canonical-hero-media-counters"
        >
          <span className="inline-flex items-center gap-1" data-testid="canonical-hero-photo-counter">
            <ImageIcon size={11} strokeWidth={2.2} /> {photoCount}
          </span>
          <span aria-hidden className="h-[10px] w-px bg-white/30" />
          <span className="inline-flex items-center gap-1" data-testid="canonical-hero-doc-counter">
            <FileText size={11} strokeWidth={2.2} /> {docCount}
          </span>
        </div>
      </div>

      {/* Meta */}
      <div className="min-w-0 flex-1 flex flex-col">
        {/* iter-alignment · P · Property identity block.
            Address + postcode read as one property identity block
            per the approved reference.
            iter-361 · F-11 · surgical typography refresh
            (2026-08-29) — address bumped to 28px/700 dark navy,
            postcode to 14px muted slate to match the approved
            reference exactly. */}
        <h1 className="text-[28px] font-bold tracking-[-0.02em] text-slate-900 truncate leading-[1.15]" data-testid="canonical-hero-address">
          {primaryAddress || "Untitled property"}
        </h1>
        {secondaryAddress ? (
          <p
            className="text-[14px] text-slate-500 mt-0.5 truncate"
            data-testid="canonical-hero-location"
            title={secondaryAddress}
          >
            {secondaryAddress}
          </p>
        ) : null}

        {/* iter-alignment · P · Property attribute chip row.
            Beds · Baths · Property Type · Tenure · Parking rendered
            as icon-led inline chips.  iter-361 · F-11 — chips
            bumped to 14px muted slate with tighter row spacing so
            the identity block reads as one calm unit. */}
        <div
          className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[14px] text-slate-600"
          data-testid="canonical-hero-property-chips"
        >
          {beds !== null && beds !== undefined && beds !== "" ? (
            <span className="inline-flex items-center gap-1.5" data-testid="canonical-hero-chip-beds">
              <Bed size={14} strokeWidth={1.9} className="text-slate-500 shrink-0" />
              {beds} bed
            </span>
          ) : null}
          {baths !== null && baths !== undefined && baths !== "" ? (
            <span className="inline-flex items-center gap-1.5" data-testid="canonical-hero-chip-baths">
              <Bath size={14} strokeWidth={1.9} className="text-slate-500 shrink-0" />
              {baths} bath
            </span>
          ) : null}
          {propertyTypeLabel ? (
            <span className="inline-flex items-center gap-1.5" data-testid="canonical-hero-chip-type">
              <Home size={14} strokeWidth={1.9} className="text-slate-500 shrink-0" />
              {propertyTypeLabel}
            </span>
          ) : null}
          {tenureLabel ? (
            <span className="inline-flex items-center gap-1.5" data-testid="canonical-hero-chip-tenure">
              <KeyRound size={14} strokeWidth={1.9} className="text-slate-500 shrink-0" />
              {tenureLabel}
            </span>
          ) : null}
          {parking ? (
            <span className="inline-flex items-center gap-1.5" data-testid="canonical-hero-chip-parking">
              <Car size={14} strokeWidth={1.9} className="text-slate-500 shrink-0" />
              {String(parking).split("·")[0].trim()} parking
            </span>
          ) : null}
        </div>
      </div>

        {/* iter-alignment · Deal Room Hero · Date KPI Refinement
            (2026-03-02) — Four hero KPI columns now share ONE
            vertical structure:  LABEL → PRIMARY VALUE →
            SUPPORTING TIMING.  Primary values sit on the same
            baseline; supporting timing chips are smaller +
            quieter than the primary date and use neutral styling
            for normal future dates.  Semantic colour is reserved
            for `overdue` (rose) and `due soon` within 4 days
            (amber).  No date-logic / SSOT / schema changes —
            presentation only. */}
        <div
          className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-4 gap-x-6 items-start col-start-2 max-lg:col-start-1 max-lg:col-span-2 max-[1366px]:grid-cols-[repeat(4,minmax(0,max-content))] max-[1366px]:justify-between max-[1366px]:gap-x-3"
          data-testid="canonical-hero-metric-strip"
        >
          {/* Agreed Price column — display-only (edits go through
              the Edit Property flow).  Rendered inline (instead of
              the older `HeroFact`) so all four columns share the
              same LABEL → PRIMARY → SUPPORTING vertical rhythm.
              Its supporting row is an invisible spacer so the
              primary-value baseline aligns with Sale Agreed /
              Exchange / Completion. */}
          <div
            className="min-w-0"
            data-testid="canonical-hero-sale-agreed-price"
          >
            <p className="text-[11px] uppercase tracking-[0.10em] font-semibold text-slate-500 max-[1366px]:whitespace-nowrap">
              Agreed Price
            </p>
            <p className="mt-1 text-[18px] font-semibold text-slate-900 whitespace-nowrap truncate">
              {_agreedDisplay(salePriceRaw)}
            </p>
            {/* Invisible sub row keeps baseline parity with the
                three date columns without leaking timing copy. */}
            <p
              className="mt-1 h-[18px]"
              aria-hidden
              data-testid="canonical-hero-sale-agreed-price-sub"
            />
          </div>
          {/* Sale Agreed column — canonical Sale Agreed date; renders
              `+ Add date` when null so historical/migrated deals never
              show a fabricated timestamp.  Clicking either the date
              or the CTA opens the shared Edit Transaction Dates
              modal. */}
          <div className="min-w-0" data-testid="canonical-hero-sale-agreed">
            <p className="text-[11px] uppercase tracking-[0.10em] font-semibold text-slate-500 max-[1366px]:whitespace-nowrap">
              Sale Agreed
            </p>
            {saleAgreedIso ? (
              <>
                <button
                  type="button"
                  onClick={() => canEditDates && setEditDatesOpen(true)}
                  disabled={!canEditDates}
                  className="mt-1 text-[18px] font-semibold text-slate-900 whitespace-nowrap truncate text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300/60 rounded"
                  data-testid="canonical-hero-sale-agreed-trigger"
                  aria-label="Edit transaction dates"
                >
                  {fmtTargetDate(saleAgreedIso)}
                </button>
                <p className="mt-1 h-[18px] flex items-center">
                  <SaleAgreedElapsed iso={saleAgreedIso} testid="canonical-hero-sale-agreed-sub" />
                </p>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => canEditDates && setEditDatesOpen(true)}
                  disabled={!canEditDates}
                  className="mt-1 inline-flex items-center gap-1 text-[13.5px] font-semibold text-violet-600 hover:text-violet-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300/60 rounded"
                  data-testid="canonical-hero-sale-agreed-add"
                  aria-label="Add sale agreed date"
                >
                  + Add date
                </button>
                <p className="mt-1 h-[18px]" aria-hidden />
              </>
            )}
          </div>
          {/* Exchange Target column — clickable date opens the shared
              Edit Transaction Dates modal.  Supporting-timing chip
              uses the quieter tone by default. */}
          <div className="min-w-0 flex flex-col items-start" data-testid="canonical-hero-exchange-target">
            <p className="text-[11px] uppercase tracking-[0.10em] font-semibold text-slate-500 max-[1366px]:whitespace-nowrap">
              Exchange Target
            </p>
            <button
              type="button"
              onClick={() => canEditDates && setEditDatesOpen(true)}
              disabled={!canEditDates}
              className="mt-1 text-[18px] font-semibold text-slate-900 max-w-full break-words text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300/60 rounded max-[1366px]:whitespace-nowrap"
              data-testid="canonical-hero-exchange-target-trigger"
              aria-label="Edit transaction dates"
            >
              {exchangeTarget ? fmtTargetDate(exchangeTarget) : <CalmEmpty />}
            </button>
            {exchangeTarget ? (
              <p
                className={`mt-1 max-w-full break-words rounded-full px-1.5 py-0.5 text-[11px] font-medium ${_actualExIso ? "bg-slate-50 text-slate-500" : relToneQuiet(exchangeDays)}`}
                data-testid="canonical-hero-exchange-target-sub"
              >
                {exchangeRel}
              </p>
            ) : (
              <p className="mt-1 h-[18px]" aria-hidden />
            )}
          </div>
          {/* Completion Target column — clickable date opens the
              shared Edit Transaction Dates modal.  Supporting-
              timing chip uses the quieter tone by default. */}
          <div className="min-w-0 flex flex-col items-start relative" data-testid="canonical-hero-completion-target">
            <p className="text-[11px] uppercase tracking-[0.10em] font-semibold text-slate-500 max-[1366px]:whitespace-nowrap">
              Completion Target
            </p>
            <button
              type="button"
              onClick={() => canEditDates && setEditDatesOpen(true)}
              disabled={!canEditDates}
              className="mt-1 text-[18px] font-semibold text-slate-900 max-w-full break-words text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300/60 rounded max-[1366px]:whitespace-nowrap"
              data-testid="canonical-hero-edit-dates-trigger"
              aria-label="Edit transaction dates"
              aria-haspopup="dialog"
              aria-expanded={editDatesOpen}
            >
              {completionTarget ? fmtTargetDate(completionTarget) : <CalmEmpty />}
            </button>
            {completionTarget ? (
              <p
                className={`mt-1 max-w-full break-words rounded-full px-1.5 py-0.5 text-[11px] font-medium ${_actualCoIso ? "bg-slate-50 text-slate-500" : relToneQuiet(completionDays)}`}
                data-testid="canonical-hero-completion-target-sub"
              >
                {completionRel}
              </p>
            ) : (
              <p className="mt-1 h-[18px]" aria-hidden />
            )}
            {canEditDates && editDatesOpen ? (
              <EditDatesPopover
                saleAgreedIso={saleAgreedIso}
                exchangeIso={exchangeTarget}
                completionIso={completionTarget}
                onSaveSaleAgreed={onUpdateSaleAgreed}
                onSaveExchange={onUpdateExchange}
                onSaveCompletion={onUpdateCompletion}
                onClose={() => setEditDatesOpen(false)}
              />
            ) : null}
          </div>
        </div>

        {/* iter-alignment · P0 · Hero Cleanup (2026-08-16) — the
            in-hero `ExchangeReadinessBadge` (label "Exchange
            Readiness · X of Y Complete") was retired to drop the
            duplication with the right-rail Exchange Confidence /
            Transaction Progress / required milestones surfaces.
            The badge component + its SSOT derivation are kept
            intact for the right rail; only the hero mount is
            removed here.  Hero now shrinks naturally to its
            remaining content — no fixed placeholder, no empty
            slot. */}
    </section>
  );
};

const Meta = ({ label, value, trailing, testid }) => (
  <div className="min-w-0" data-testid={testid}>
    <p className="text-[10.5px] uppercase tracking-[0.10em] text-slate-400 font-semibold">{label}</p>
    <p className="text-[13.5px] font-semibold text-slate-900 mt-0.5 truncate flex items-center gap-1.5">
      {value} {trailing}
    </p>
  </div>
);

/* HeroMetric · iter-388 — Icon-topped metric cell rendered inside
 * the redesigned PropertyHero.  Matches the approved reference
 * pattern: icon on top (slate outline), tiny caps label
 * ("AGREED PRICE"), then the value.  Value typography is
 * hierarchy-driven — Agreed Price / Targets read strongest, Type
 * & Tenure carry the quieter weight.  Vertical dividers between
 * cells are inset (see the parent's `lg:divide-x`) so the rule
 * reads short and light per the design brief. */
/* HeroMetric · iter-388 → iter-alignment-P4 — Approved-design pass.
 * Renders a metric cell in the PropertyHero strip:
 *   • sentence-case label (was uppercase in iter-388)
 *   • NO leading icon (approved design has no icons in the strip)
 *   • value below, hierarchy-driven weight
 * The `icon` prop is intentionally ignored to keep call-sites stable
 * without rippling changes elsewhere. */
const HeroMetric = ({ icon: _icon, label, value, valueClass, testid }) => (
  <div
    className="min-w-0 min-[1440px]:px-2.5 first:pl-0 last:pr-0 py-1"
    data-testid={testid}
  >
    <p className="text-[11px] text-slate-500 font-medium">
      {label}
    </p>
    <p className={`${valueClass || "text-[14.5px] font-semibold text-slate-900"} mt-1 truncate`}>
      {value}
    </p>
  </div>
);

/* HeroFact · iter-alignment · P (2026-02-14) — Renders one of the
 * three primary transaction facts (Agreed Price · Exchange ·
 * Completion) on the refined Property Hero.  Same rhythm as
 * HeroMetric but hierarchy-tuned to the approved reference:
 * uppercase small caps label + a hierarchy-driven value + an
 * optional relative sub-line (e.g. "In 37 days") tone-mapped by
 * the parent.  No icons — the reference identity is
 * typography-led. */
const HeroFact = ({ label, value, valueClass, sub, subClass, testid }) => (
  <div className="min-w-0" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-[0.10em] font-semibold text-slate-500">
      {label}
    </p>
    <p className={`${valueClass || "text-[18px] font-semibold text-slate-900"} mt-1 truncate`}>
      {value}
    </p>
    {sub ? (
      <p
        className={`mt-1.5 truncate inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[13px] font-medium ${subClass || "bg-emerald-50 text-emerald-700"}`}
        data-testid={testid ? `${testid}-sub` : undefined}
      >
        <Calendar size={12} strokeWidth={2} className="opacity-70" />
        {sub}
      </p>
    ) : null}
  </div>
);

/* CalmEmpty — iter-205b. Replaces aggressive "TBC" copy in the hero
 * meta strip when a field genuinely isn't set. A subtle em-dash with
 * muted styling so the cell still has presence without lying about
 * missing data. */
const CalmEmpty = () => (
  <span className="text-slate-300 font-medium" aria-label="Not set">—</span>
);

/* ── EditableSelectMeta · iter-222 ──────────────────────────────────
 * Calm enum-edit affordance for the hero meta cells. Renders the
 * value as a hover-pencil cell and, when clicked, opens a tiny
 * popover with the available options. Used for Sale type today —
 * the same pattern absorbs Property type when we open that field
 * for editing in a future iteration. Mirrors EditableDateMeta's
 * shape so the hero meta row stays operationally consistent.
 */
/* ── EditableAgreedPriceHero ────────────────────────────────────────
 * iter-alignment · P0 · Deal Room · Editable Agreed Price · 2026-02-28
 * ---------------------------------------------------------------------
 * Discreet inline-edit affordance for the primary Agreed Price fact
 * card in the property hero.  Renders identically to `HeroFact` in
 * its resting state (`£1,400,000` / `—`) with a whisper-tone pencil
 * on hover; clicking opens a compact popover with a £-prefixed
 * numeric input, Save + Cancel.  Persistence funnels through the
 * existing `updateDeal(dealId, {agreedPrice})` PATCH SSOT — no new
 * endpoint, no local override state, no property mutation.  The
 * property's Asking Price is a *different* SSOT column and is not
 * touched by this control.
 *
 * Props:
 *   valueRaw    – current agreed_price on the deal (may be number
 *                 or £-formatted string; digit-normalised on input)
 *   testid      – base testid so the pencil / popover / input
 *                 remain automatable
 *   canEdit     – gate for read-only viewers (org owner/admin/agent)
 *   onSave      – async (digits: string) → persist; throws on failure
 */
const _agreedDigits = (raw) => {
  if (raw === undefined || raw === null) return "";
  return String(raw).replace(/[^\d]/g, "");
};

const _agreedDisplay = (raw) => {
  const digits = _agreedDigits(raw);
  if (!digits) return "";
  const n = Number(digits);
  if (!Number.isFinite(n) || n <= 0) return "";
  return `£${n.toLocaleString("en-GB")}`;
};

const EditableAgreedPriceHero = ({ valueRaw, testid, canEdit, onSave }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const popoverRef = useRef(null);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);

  // Close on outside-click / Esc.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const display = _agreedDisplay(valueRaw);

  const openEditor = () => {
    if (!canEdit || busy) return;
    setDraft(_agreedDigits(valueRaw));
    setOpen(true);
    // Focus the input on next tick so autoselect fires cleanly.
    setTimeout(() => inputRef.current?.select?.(), 0);
  };

  const currentDigits = _agreedDigits(valueRaw);
  const draftDigits = _agreedDigits(draft);
  const dirty = draftDigits && draftDigits !== currentDigits;

  const save = async () => {
    if (!dirty || busy) return;
    setBusy(true);
    try {
      await onSave(draftDigits);
      setOpen(false);
    } catch {
      // toast surfaced by caller
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-w-0 relative group" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-[0.10em] font-semibold text-slate-500">
        Agreed Price
      </p>
      <button
        ref={triggerRef}
        type="button"
        onClick={openEditor}
        disabled={!canEdit}
        className="mt-1 inline-flex items-center gap-1.5 max-w-full text-left text-[26px] font-bold text-slate-900 tracking-[-0.02em] truncate leading-[1.15] hover:text-violet-700 transition-colors disabled:cursor-default disabled:hover:text-slate-900"
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid={`${testid}-trigger`}
      >
        <span className="truncate">{display || <CalmEmpty />}</span>
        {canEdit && (
          <Pencil
            size={12}
            strokeWidth={2.2}
            /* iter-alignment · P0 · iPad Safari · Editable Agreed
             * Price touch affordance (2026-02-28)
             * ------------------------------------------------------
             * `group-hover:opacity-100` never fires on iPad Safari
             * because touch devices don't emit stable `:hover`
             * states — operators literally couldn't see the
             * affordance.  The `[@media(hover:hover)]` arbitrary
             * variant keeps the desktop reveal-on-hover behaviour
             * for cursor-capable browsers AND makes the pencil
             * permanently visible on touch (`hover:none`) devices.
             * Tailwind supports the arbitrary variant natively;
             * no config change required. */
            className="shrink-0 text-slate-400 opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity"
            data-testid={`${testid}-pencil`}
          />
        )}
      </button>
      {open && (
        <div
          ref={popoverRef}
          className="absolute left-0 top-full mt-1.5 z-30 min-w-[240px] rounded-xl bg-white ring-1 ring-slate-200 shadow-[0_10px_28px_-12px_rgba(15,23,42,0.20)] p-3"
          role="dialog"
          aria-label="Edit agreed price"
          data-testid={`${testid}-popover`}
        >
          <label className="text-[10.5px] uppercase tracking-[0.10em] font-semibold text-slate-500">
            Agreed price
          </label>
          <div className="mt-1.5 flex items-stretch gap-1">
            <div className="relative flex-1">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-[13px] font-semibold pointer-events-none">
                £
              </span>
              <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                value={draft ? Number(_agreedDigits(draft)).toLocaleString("en-GB") : ""}
                onChange={(e) => setDraft(_agreedDigits(e.target.value))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); save(); }
                }}
                disabled={busy}
                placeholder="400,000"
                className="w-full pl-6 pr-2 py-1.5 text-[13.5px] font-semibold text-slate-900 rounded-lg ring-1 ring-slate-200 focus:ring-violet-400 focus:outline-none tabular-nums"
                data-testid={`${testid}-input`}
                aria-label="Agreed price in pounds"
              />
            </div>
          </div>
          <p className="mt-2 text-[10.5px] text-slate-500 leading-tight">
            Property Asking Price is unchanged.
          </p>
          <div className="mt-2 flex items-center justify-end gap-1.5">
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={busy}
              className="text-[11.5px] font-medium text-slate-500 hover:text-slate-800 px-2 py-1 rounded-md hover:bg-slate-100 disabled:opacity-60"
              data-testid={`${testid}-cancel`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!dirty || busy}
              className="text-[11.5px] font-semibold text-white bg-slate-900 hover:bg-slate-800 disabled:bg-slate-200 disabled:text-slate-400 px-2.5 py-1 rounded-md transition-colors"
              data-testid={`${testid}-save`}
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const EditableSelectMeta = ({ label, value, options, onSave, testid }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const popoverRef = useRef(null);
  const triggerRef = useRef(null);

  // Close on outside-click / Esc.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const currentNormalised = String(value || "").trim().toLowerCase();
  const matched = options.find((o) => o.value === currentNormalised);
  const display = matched?.label || (value ? String(value) : null);

  const pick = async (opt) => {
    if (busy) return;
    setBusy(true);
    try {
      await onSave(opt.value);
      setOpen(false);
    } catch {
      // toast surfaced by caller
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-w-0 relative" data-testid={testid}>
      <p className="text-[10.5px] uppercase tracking-[0.10em] text-slate-400 font-semibold">{label}</p>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        className="mt-0.5 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-slate-900 hover:text-violet-700 transition-colors disabled:opacity-60"
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={`${testid}-trigger`}
      >
        {display ? <span className="capitalize">{display}</span> : <CalmEmpty />}
        <Pencil size={10} className="text-slate-300 group-hover:text-slate-500" strokeWidth={2.2} />
      </button>
      {open && (
        <div
          ref={popoverRef}
          className="absolute left-0 top-full mt-1.5 z-20 min-w-[176px] rounded-xl bg-white ring-1 ring-slate-200 shadow-[0_10px_28px_-12px_rgba(15,23,42,0.20)] py-1.5"
          role="listbox"
          aria-label={label}
          data-testid={`${testid}-popover`}
        >
          {options.map((opt) => {
            const isCurrent = opt.value === currentNormalised;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={isCurrent}
                onClick={() => pick(opt)}
                disabled={busy}
                className={`flex items-center justify-between w-full text-left px-3 py-1.5 text-[12.5px] hover:bg-slate-50 disabled:opacity-60 ${
                  isCurrent ? "text-violet-700 font-semibold" : "text-slate-700"
                }`}
                data-testid={`${testid}-option-${opt.value.replace(/\s+/g, "-")}`}
              >
                {opt.label}
                {isCurrent && <Check size={11} strokeWidth={2.4} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ── EditableDateMeta ───────────────────────────────────────────────
 *  iter-204c — calm inline date-edit affordance for the hero meta
 *  cells. Hover/focus surfaces a small pencil; click opens a compact
 *  popover anchored to the cell with a native date input + Save /
 *  Cancel. Optimistic UI: parent persists via `onSave(iso)` (async).
 *  On rejection we close the popover and surface a calm error toast.
 *
 *  Props:
 *    label         — cell label (e.g. "Exchange target")
 *    iso           — current ISO date (or null/undefined for "Set date")
 *    onSave        — async (iso|null) → persist; throws on failure
 *    minIso        — optional lower bound (used by Completion target
 *                    so it can't be set earlier than Exchange target)
 *    testid        — base test id (`canonical-hero-exchange-target` etc.)
 *    field         — "exchange" | "completion" — drives microcopy +
 *                    refinement toast text only.
 */
const isoToInputValue = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const EditableDateMeta = ({ label, iso, onSave, minIso, testid, field }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(() => isoToInputValue(iso));
  const [confirmClear, setConfirmClear] = useState(false);
  const popoverRef = useRef(null);
  const triggerRef = useRef(null);

  // Reset draft whenever we open (or the underlying iso changes).
  useEffect(() => {
    if (open) {
      setDraft(isoToInputValue(iso));
      setConfirmClear(false);
    }
  }, [open, iso]);

  const minInput = isoToInputValue(minIso);
  const microcopy = "Used for progression, forecasting and exchange readiness.";

  const close = useCallback(() => {
    if (busy) return;
    setOpen(false);
    setConfirmClear(false);
  }, [busy]);

  // Calm escape-to-close + click-outside close (no visible backdrop
  // — we don't want to disrupt the operator's hero context).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    const onMouseDown = (e) => {
      if (popoverRef.current && popoverRef.current.contains(e.target)) return;
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      close();
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [open, close]);

  const handleSave = async () => {
    if (busy) return;
    // Empty value → confirm clear first.
    if (!draft) {
      if (!confirmClear) { setConfirmClear(true); return; }
    }
    // Validation: completion >= exchange.
    if (draft && minInput && draft < minInput) {
      toast.error(`${label} cannot be before Exchange target`, {
        description: `Exchange target is set to ${new Date(minInput).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}.`,
      });
      return;
    }
    setBusy(true);
    try {
      // Backend's PATCH /api/v2/deals/:id expects a `date` (YYYY-MM-DD),
      // not a full ISO datetime — send the input value directly.
      const valueIso = draft || null;
      await onSave(valueIso);
      toast.success(
        valueIso
          ? `${label} updated`
          : `${label} cleared`,
      );
      setOpen(false);
      setConfirmClear(false);
    } catch (err) {
      // Calm error; parent will have rolled back the optimistic UI.
      toast.error(`Couldn't update ${label.toLowerCase()}`, {
        description: apiError(err),
      });
    } finally {
      setBusy(false);
    }
  };

  const display = iso
    ? (function () {
        const d = new Date(iso);
        return Number.isNaN(d.getTime())
          ? "Date TBC"
          : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
      })()
    : null;

  return (
    <div className="min-w-0 relative group" data-testid={testid}>
      <p className="text-[10.5px] uppercase tracking-[0.10em] text-slate-400 font-semibold">{label}</p>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mt-0.5 text-[13.5px] font-semibold text-slate-900 truncate flex items-center gap-1.5 hover:text-violet-700 transition-colors focus:outline-none focus:text-violet-700"
        data-testid={`${testid}-trigger`}
        title={iso ? `Edit ${label.toLowerCase()}` : `Set ${label.toLowerCase()}`}
      >
        <span className={iso ? "" : "text-slate-400 font-medium"}>
          {display || "Set date"}
        </span>
        <Calendar size={13} className="text-slate-400 shrink-0" />
        <Pencil
          size={11}
          className="text-slate-400 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity shrink-0"
          aria-hidden
        />
      </button>

      {open && (
        <div
          ref={popoverRef}
          className="absolute z-50 left-0 mt-2 w-[280px] rounded-xl bg-white p-3.5 cf-scale-in"
          style={{
            boxShadow: "0 18px 40px -18px rgba(15,23,42,0.28), 0 0 0 1px rgba(226,232,240,0.7)",
          }}
          role="dialog"
          aria-modal="true"
          aria-label={`Edit ${label}`}
          data-testid={`${testid}-popover`}
        >
            <div className="flex items-start justify-between gap-2">
              <p className="text-[12px] font-bold text-slate-900">{label}</p>
              <button
                type="button"
                onClick={close}
                className="text-slate-400 hover:text-slate-700 -mr-1 -mt-0.5"
                aria-label="Close"
                data-testid={`${testid}-close`}
              >
                <X size={13} />
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-1 leading-snug">{microcopy}</p>
            <label className="block mt-3">
              <input
                type="date"
                value={draft}
                min={field === "completion" ? minInput || undefined : undefined}
                onChange={(e) => { setDraft(e.target.value); setConfirmClear(false); }}
                disabled={busy}
                className="w-full h-9 px-2.5 rounded-lg bg-white border border-slate-200 text-[12.5px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-300/60 focus:border-violet-300"
                data-testid={`${testid}-input`}
              />
            </label>
            {field === "completion" && minInput && (
              <p className="text-[10.5px] text-slate-400 mt-1.5">
                Must be on or after Exchange target ({new Date(minInput).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}).
              </p>
            )}
            {confirmClear && (
              <p className="text-[11px] text-amber-700 mt-2.5 inline-flex items-center gap-1">
                <AlertCircle size={11} /> Clear {label.toLowerCase()}? Tap Save again to confirm.
              </p>
            )}
            <div className="mt-3 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={close}
                disabled={busy}
                className="h-8 px-3 rounded-lg text-[12px] font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-60 cf-press"
                data-testid={`${testid}-cancel`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={busy}
                className="h-8 px-3 rounded-lg bg-violet-600 text-white text-[12px] font-semibold hover:bg-violet-700 disabled:opacity-60 inline-flex items-center gap-1.5 cf-press"
                data-testid={`${testid}-save`}
              >
                {busy && <Loader2 size={12} className="animate-spin" />}
                {confirmClear ? "Confirm clear" : "Save"}
              </button>
            </div>
          </div>
      )}
    </div>
  );
};

/* ── EditDatesPopover ──────────────────────────────────────────────
 * iter-alignment · P0 · Edit dates action (2026-02-28)
 * -----------------------------------------------------------------
 * A single "Edit dates" affordance beside the hero's Exchange /
 * Completion cells that opens a compact popover with both date
 * inputs together.  Reuses the existing per-field persistence
 * callbacks (`onSaveExchange`, `onSaveCompletion`) which route
 * through `persistDateField → patchDealLocally + updateDeal` — the
 * same optimistic-plus-rollback SSOT flow the individual pencils
 * already use.  Cross-field validation: Completion must be on or
 * after Exchange.  Clearing (empty input) is preserved and requires
 * a second Save click to confirm, matching the existing
 * `EditableDateMeta` clear-with-confirm rhythm.
 */
const EditDatesPopover = ({
  saleAgreedIso,
  exchangeIso,
  completionIso,
  onSaveSaleAgreed,
  onSaveExchange,
  onSaveCompletion,
  onClose,
  testid = "canonical-hero-edit-dates",
}) => {
  const [busy, setBusy] = useState(false);
  const [saDraft, setSaDraft] = useState(() => isoToInputValue(saleAgreedIso));
  const [exDraft, setExDraft] = useState(() => isoToInputValue(exchangeIso));
  const [coDraft, setCoDraft] = useState(() => isoToInputValue(completionIso));
  const [confirmClearSa, setConfirmClearSa] = useState(false);
  const [confirmClearEx, setConfirmClearEx] = useState(false);
  const [confirmClearCo, setConfirmClearCo] = useState(false);
  const popoverRef = useRef(null);

  useEffect(() => {
    setSaDraft(isoToInputValue(saleAgreedIso));
    setConfirmClearSa(false);
  }, [saleAgreedIso]);
  useEffect(() => {
    setExDraft(isoToInputValue(exchangeIso));
    setConfirmClearEx(false);
  }, [exchangeIso]);
  useEffect(() => {
    setCoDraft(isoToInputValue(completionIso));
    setConfirmClearCo(false);
  }, [completionIso]);

  // Calm escape-to-close + click-outside close.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    const onMouseDown = (e) => {
      if (busy) return;
      if (popoverRef.current && popoverRef.current.contains(e.target)) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [busy, onClose]);

  const prevSaInput = isoToInputValue(saleAgreedIso);
  const prevExInput = isoToInputValue(exchangeIso);
  const prevCoInput = isoToInputValue(completionIso);
  const saChanged = saDraft !== prevSaInput;
  const exChanged = exDraft !== prevExInput;
  const coChanged = coDraft !== prevCoInput;
  const anyChange = saChanged || exChanged || coChanged;

  const handleSave = async () => {
    if (busy || !anyChange) return;
    // iter-alignment · Deal Room · Sale Agreed + Transaction Dates
    // (2026-03-02) — Chronological validation is ADVISORY, not
    // blocking.  We surface a calm inline warning below when the
    // draft dates are out of sequence (Sale Agreed ≤ Exchange ≤
    // Completion) but the operator is still allowed to Save.
    // Two-step clear confirmation is preserved.
    if (saChanged && !saDraft && !confirmClearSa) { setConfirmClearSa(true); return; }
    if (exChanged && !exDraft && !confirmClearEx) { setConfirmClearEx(true); return; }
    if (coChanged && !coDraft && !confirmClearCo) { setConfirmClearCo(true); return; }

    setBusy(true);
    try {
      // Persist Sale Agreed first (upstream), then Exchange, then
      // Completion so the backend sees a coherent state on each
      // hop of a multi-field save.
      if (saChanged) await onSaveSaleAgreed?.(saDraft || null);
      if (exChanged) await onSaveExchange(exDraft || null);
      if (coChanged) await onSaveCompletion(coDraft || null);
      toast.success("Dates updated");
      onClose();
    } catch (err) {
      // Parent's persistDateField already rolls back the optimistic
      // patch; surface a calm operator-facing error.
      toast.error("Couldn't update dates", { description: apiError(err) });
    } finally {
      setBusy(false);
    }
  };

  /* iter-361 · F-12 · Edit Transaction Dates modal — surgical
   * refinement (2026-08-29).  Same state, handlers, validation,
   * SSOT and save/propagation flow as before.  Only the SHELL
   * changes: 720px centered dialog with a subtle backdrop, no
   * hard-coded height, ~15 % tighter vertical rhythm, header
   * icon retired, footer right-aligned. */
  /* iter-alignment · Deal Room · Sale Agreed + Transaction Dates
   * (2026-03-02) — Chronological validation is ADVISORY, not
   * blocking.  Save is permitted regardless of order; the modal
   * surfaces a calm warning below the fields when Sale Agreed ≤
   * Exchange ≤ Completion is violated. */
  const effectiveSa = saChanged ? saDraft : prevSaInput;
  const effectiveEx = exChanged ? exDraft : prevExInput;
  const effectiveCo = coChanged ? coDraft : prevCoInput;
  const advisoryOutOfOrder = !!(
    (effectiveSa && effectiveEx && effectiveEx < effectiveSa) ||
    (effectiveSa && effectiveCo && effectiveCo < effectiveSa) ||
    (effectiveEx && effectiveCo && effectiveCo < effectiveEx)
  );
  const invalidOrder = false;

  return createPortal(
    <>
      {/* Calm backdrop — closes on click, dismisses on Escape via
          the existing keydown listener.  Preserves the existing
          click-outside behaviour by simply not being part of
          `popoverRef.current`.
          iter-alignment · P0 · Edit Transaction Dates portal stacking fix
          (2026-02-28) — the modal + backdrop are now portaled to
          `document.body` so they escape ANY ancestor stacking
          context (e.g. Deal Room panels using `transform` /
          `will-change` traps that cap child z-index).  Once at the
          body level, sensible modest z-index values are enough:
          modest values keep us predictable, and the Important Note
          banner (which lives inside the trapped ancestor tree)
          can no longer overlap the portaled shell no matter what
          z-index it carries. */}
      <div
        className="fixed inset-0 z-[100] bg-slate-900/20"
        onClick={() => !busy && onClose()}
        data-testid={`${testid}-backdrop`}
        aria-hidden
      />
      {/* iter-alignment · Edit Transaction Dates — top-aligned overlay
       *   (2026-09) — replaces the vertically-centered popover
       *   with a top-anchored one at ~28-32px below the app
       *   header, sized ~660px max on desktop.  Overlay uses
       *   `items-start` + safe-area-aware top padding so the
       *   modal never floats mid-viewport; `100dvh` + inner
       *   scroll keeps Cancel / Save accessible on short
       *   viewports without forcing a fixed height. */}
      <div
        className="fixed inset-0 z-[101] flex items-start justify-center px-4"
        style={{
          height: "100dvh",
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 30px)",
          paddingBottom: "16px",
        }}
        onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
        aria-hidden
      >
        <div
          ref={popoverRef}
          className="w-full max-w-[660px] max-h-full flex flex-col overflow-y-auto rounded-2xl bg-white cf-scale-in"
          style={{ boxShadow: "0 24px 64px -24px rgba(15,23,42,0.28), 0 0 0 1px rgba(226,232,240,0.7)" }}
          role="dialog"
          aria-modal="true"
          aria-label="Edit Transaction Dates"
          data-testid={`${testid}-popover`}
        >
        {/* Header — title + description left-aligned to the same
            edge as the field labels.  Purple calendar tile
            retired per the approved reference. */}
        <div className="px-6 pt-4 pb-3.5 relative">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="absolute top-4 right-4 w-8 h-8 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-50 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-slate-300/60"
            aria-label="Close"
            data-testid={`${testid}-close`}
          >
            <X size={16} />
          </button>
          <h2 className="text-[17px] font-semibold text-slate-900 tracking-[-0.01em]">
            Edit Transaction Dates
          </h2>
          <p className="text-[13.5px] text-slate-500 mt-1 leading-[1.45] pr-8">
            Update the key dates used for progression, forecasting and exchange readiness.
          </p>
        </div>

        <div className="h-px bg-slate-100" />

        {/* Body — vertical rhythm ~15 % tighter than the previous
            popover; inputs full-width with the existing calendar
            control preserved.  Sale Agreed sits above the two
            target dates because it's the transaction's origin
            point (auto-stamped on first transition to Sales Agreed
            / Under Offer; editable at any time here). */}
        <div className="px-6 py-3.5 space-y-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.10em] text-slate-500 font-semibold">
              Sale Agreed
            </span>
            <input
              type="date"
              value={saDraft}
              onChange={(e) => { setSaDraft(e.target.value); setConfirmClearSa(false); }}
              disabled={busy}
              className="mt-1.5 w-full h-10 px-3 rounded-lg bg-white border border-slate-200 text-[14px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-300/60 focus:border-violet-300"
              data-testid={`${testid}-sale-agreed-input`}
            />
          </label>

          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.10em] text-slate-500 font-semibold">
              Exchange Target
            </span>
            <input
              type="date"
              value={exDraft}
              onChange={(e) => { setExDraft(e.target.value); setConfirmClearEx(false); }}
              disabled={busy}
              className="mt-1.5 w-full h-10 px-3 rounded-lg bg-white border border-slate-200 text-[14px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-300/60 focus:border-violet-300"
              data-testid={`${testid}-exchange-input`}
            />
          </label>

          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.10em] text-slate-500 font-semibold">
              Completion Target
            </span>
            <input
              type="date"
              value={coDraft}
              onChange={(e) => { setCoDraft(e.target.value); setConfirmClearCo(false); }}
              disabled={busy}
              className="mt-1.5 w-full h-10 px-3 rounded-lg bg-white border border-slate-200 text-[14px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-300/60 focus:border-violet-300"
              data-testid={`${testid}-completion-input`}
            />
          </label>

          {/* iter-alignment · Deal Room · Sale Agreed + Transaction
              Dates (2026-03-02) — ADVISORY warning when the three
              dates are out of sequence.  Save is still enabled;
              the copy just prompts the operator to double-check. */}
          {advisoryOutOfOrder && (
            <p
              className="mt-1 inline-flex items-center gap-1.5 text-[12px] text-amber-700 bg-amber-50 ring-1 ring-amber-100 rounded-md px-2 py-1"
              data-testid={`${testid}-advisory-warning`}
              role="status"
            >
              <AlertCircle size={12} /> Dates look out of sequence — expected Sale Agreed ≤ Exchange ≤ Completion. You can still save.
            </p>
          )}

          {(confirmClearSa || confirmClearEx || confirmClearCo) && (
            <p className="text-[12px] text-amber-700 inline-flex items-center gap-1">
              <AlertCircle size={12} /> Clearing dates — tap Save again to confirm.
            </p>
          )}
        </div>

        <div className="h-px bg-slate-100" />

        {/* Footer — right-aligned, compact padding matching the
            smaller shell.  Save button styling and disabled logic
            unchanged. */}
        <div className="px-6 py-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="h-9 px-3.5 rounded-lg text-[13px] font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-60 cf-press"
            data-testid={`${testid}-cancel`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy || !anyChange || invalidOrder}
            className="h-9 px-4 rounded-lg bg-violet-600 text-white text-[13px] font-semibold hover:bg-violet-700 disabled:opacity-60 inline-flex items-center gap-1.5 cf-press"
            data-testid={`${testid}-save`}
          >
            {busy && <Loader2 size={13} className="animate-spin" />}
            {(confirmClearEx || confirmClearCo) ? "Confirm clear" : "Save dates"}
          </button>
        </div>
      </div>
      </div>
    </>,
    document.body,
  );
};

const PartyPill = ({ tone, label, name, onClick }) => {
  const toneMap = {
    emerald: { dot: "#10B981", bg: "bg-white", ring: "ring-slate-200/80", text: "text-slate-700" },
    violet:  { dot: "#7C3AED", bg: "bg-white", ring: "ring-slate-200/80", text: "text-slate-700" },
  }[tone] || { dot: "#94A3B8", bg: "bg-white", ring: "ring-slate-200/80", text: "text-slate-700" };
  // iter-204b — render as a button when an onClick is supplied so
  // tapping the chip drops the operator into the Participants tab
  // (or whatever destination the page wires). Otherwise stays a
  // passive span (no false affordance).
  // iter-205b — when no name is provided, surface a calm "Add buyer"
  // / "Add seller" CTA pill (still routes through onClick) rather
  // than the misleading "Buyer TBC" label.
  const missing = !name;
  const className = `inline-flex items-center gap-2 px-3 h-8 rounded-full ${toneMap.bg} ring-1 ${toneMap.ring} text-[12.5px] ${missing ? "text-slate-400" : toneMap.text} ${onClick ? "cursor-pointer hover:ring-slate-300 transition-colors" : ""}`;
  const inner = missing ? (
    <>
      <Plus size={11} className="text-slate-400" />
      <span className="font-medium">Add {label.toLowerCase()}</span>
    </>
  ) : (
    <>
      <span className="w-2 h-2 rounded-full" style={{ background: toneMap.dot }} />
      <span className="font-semibold">{label}:</span> {name}
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={className}
        data-testid={`canonical-hero-party-${tone}`}
        data-state={missing ? "empty" : "filled"}
        title={missing ? `Add ${label.toLowerCase()}` : `Open participants — ${label}`}
      >
        {inner}
      </button>
    );
  }
  return (
    <span
      className={className}
      data-testid={`canonical-hero-party-${tone}`}
      data-state={missing ? "empty" : "filled"}
    >
      {inner}
    </span>
  );
};

/* ── Exchange Eve compact ──────────────────────────────────────────── */
const EXCHANGE_EVE_COLS = [
  { id: "legal",        label: "Legal",        Icon: Scale,    accent: "#6366F1", bg: "bg-indigo-50/70" },
  { id: "financial",    label: "Financial",    Icon: Landmark, accent: "#10B981", bg: "bg-emerald-50/70" },
  { id: "coordination", label: "Coordination", Icon: Users,    accent: "#7C3AED", bg: "bg-violet-50/70" },
];

/* iter-alignment · P41 — Pre-Exchange Checklist canonical set.
 *
 * The Pre-Exchange Checklist is a concise EXCHANGE-READINESS
 * gate — NOT a mirror of All Milestones.  Only the operational
 * events that must land before contracts can exchange belong
 * here; broader workflow (survey progress, individual searches,
 * mortgage sub-steps, completion admin) lives in Current Stage /
 * All Milestones instead.
 *
 * Each row is either always applicable OR carries an `applies`
 * predicate that reuses the SAME SSOT flags as the rail
 * (`resolveDealFlags` + `optsForDeal`), so cash buyers hide the
 * mortgage row, freeholds hide the Mgmt Pack + BSA rows, and
 * chain-free deals hide the chain row — with no duplicate
 * conditional logic.
 *
 * `displayLabel` overrides the SSOT label for the Pre-Exchange
 * card ONLY; the underlying milestone row (id, verb, blocker,
 * activity log copy) is untouched.  This is intentional — the
 * pre-exchange checklist is a curated VIEW over the SSOT, not a
 * second SSOT.
 */
const _isLeasehold = (deal) => String(deal?.tenure || "").toLowerCase() === "leasehold";
const _bsaStatus = (deal) => bsaStatus(deal?.tenure, optsForDeal(deal || {}));
const _bsaApplies = (deal) => _isLeasehold(deal) && _bsaStatus(deal) !== "no";
const PRE_EXCHANGE_CHECKLIST = [
  { id: "mortgage_offer",                displayLabel: "Mortgage offer received",     applies: (_deal, flags) => !flags.skipMortgage },
  { id: "search_results_reviewed",       displayLabel: "Searches returned" },
  { id: "management_pack_reviewed",      displayLabel: "Management pack reviewed",    applies: (_deal, flags) => !flags.skipManagementPack },
  { id: "bsa_review_completed",          displayLabel: "BSA documentation reviewed by buyer's solicitor", applies: (deal)   => _bsaApplies(deal),
    labelFor: (deal) => (_bsaStatus(deal) === "unknown" ? "Confirm Building Safety Act applicability" : null) },
  { id: "enquiries_resolved",            displayLabel: "Enquiries resolved" },
  { id: "report_on_title_issued",        displayLabel: "Report on title issued" },
  { id: "contracts_signed_seller_side",  displayLabel: "Seller signed contract received" },
  { id: "contracts_signed_buyer_side",   displayLabel: "Buyer signed contract received" },
  { id: "deposit_confirmed",             displayLabel: "Deposit confirmed" },
  { id: "exchange_date_agreed",          displayLabel: "Exchange date agreed" },
  { id: "chain_aligned",                 displayLabel: "Chain aligned",               applies: (_deal, flags) => !flags.skipChain },
  { id: "buyer_confirmation",            displayLabel: "Buyer authority to exchange" },
  { id: "seller_authority_to_exchange",  displayLabel: "Seller authority to exchange" },
];
const PRE_EXCHANGE_CHECKLIST_IDS = new Set(PRE_EXCHANGE_CHECKLIST.map((r) => r.id));

/* iter-alignment · P42 — Exchange Readiness derivation (SSOT).
 *
 * The Pre-Exchange Checklist card and the compact Exchange
 * Readiness badge in the property header BOTH derive their state
 * from this single helper — they can never disagree because both
 * consume the exact same output.  No new counters, no new APIs,
 * no hardcoded milestone ids anywhere else in the tree.
 *
 * Inputs:
 *   • `deal`        — deal envelope (for flag resolution + post-
 *                     exchange override).
 *   • `milestones`  — `{ items, can_complete }` payload from the
 *                     shared API endpoint the Deal Room already
 *                     loads.  Falls back to `FALLBACK_MILESTONES`
 *                     until the first response lands.
 *
 * Returns:
 *   {
 *     applicable: Array<{ id, displayLabel, applies?}>,   // filtered checklist
 *     items:      Array<Milestone>,                       // one per applicable row
 *     doneCount:  number,
 *     totalCount: number,
 *     remaining:  number,
 *     outstanding: Array<Milestone>,                      // NOT-yet-Complete rows
 *     isPostExchange: boolean,                            // deal has advanced past exchange
 *   }
 */
function deriveExchangeReadiness(deal, milestones) {
  const rawItems = milestones?.items || FALLBACK_MILESTONES;
  const dealStageId = String(deal?.stage || "").toLowerCase();
  /* P0 · Correct Transaction Journey Completion Authority
   * (2026-08-18) — `isPostExchange` is STRICT.  It flips ONLY
   * when the deal has been explicitly advanced via the
   * dedicated confirmation flows:
   *   • `deal.stage` transitioned to `exchanged` / `completed`
   *     (only Confirm Exchange / Confirm Completion set these)
   *   • `deal.exchanged_at` / `deal.completed_at` populated
   *     (backend stamps these on terminal milestone completion
   *     — the same explicit confirmation gestures).
   * We NO LONGER infer post-exchange from:
   *   • `deal.completed_steps` containing `exchange` (a stage
   *     id can enter that list only via child-milestone counts,
   *     which the operator brief explicitly bans as a completion
   *     trigger).
   *   • Every required exchange-stage milestone being done (same
   *     cascade — 13/13 checks passed ≠ Confirm Exchange
   *     clicked).
   * The two removed branches were the last "child count →
   * stage completion" inference paths on the frontend.
   */
  const stageSaysPost = dealStageId === "exchanged" || dealStageId === "completed";
  const stampSaysPost = !!deal?.exchanged_at || !!deal?.exchangedAt || !!deal?.completed_at || !!deal?.completedAt;
  const isPostExchange = stageSaysPost || stampSaysPost;

  const flags = resolveDealFlags(deal || {});
  const applicable = PRE_EXCHANGE_CHECKLIST.filter(
    (r) => (typeof r.applies === "function" ? !!r.applies(deal, flags) : true),
  );
  const rawById = new Map();
  for (const it of rawItems || []) {
    if (it?.id) rawById.set(String(it.id), it);
  }
  const items = applicable.map((cfg) => {
    const base = rawById.get(cfg.id);
    const seed = base
      || FALLBACK_MILESTONES.find((m) => m.id === cfg.id)
      || { id: cfg.id, col: "coordination", owner_role: "agent", state: "pending" };
    const merged = { ...seed, label: (cfg.labelFor && cfg.labelFor(deal)) || cfg.displayLabel || seed.label };
    return isPostExchange ? { ...merged, state: "done" } : merged;
  });
  const doneCount = items.filter((it) => resolveMilestoneState(it) === "Complete").length;
  const totalCount = items.length;
  const outstanding = items.filter((it) => resolveMilestoneState(it) !== "Complete");
  return {
    applicable,
    items,
    doneCount,
    totalCount,
    remaining: Math.max(0, totalCount - doneCount),
    outstanding,
    isPostExchange,
  };
}


/* iter-alignment · P42 — Exchange Readiness badge.
 * iter-alignment · P42.1 — Polish pass.
 *
 * Compact chip that mirrors the Pre-Exchange Checklist state
 * inside the property hero.  Reuses `deriveExchangeReadiness()`
 * exclusively — zero new counters, zero duplicate milestone
 * logic, zero hardcoded ids.  Clicking the chip smoothly scrolls
 * (and switches tab) to the checklist card so operators can act
 * without hunting.
 *
 * Visibility contract:
 *   • Hidden while the deal is in pre-journey (Agreed / AML /
 *     Memorandum) — the Pre-Exchange checklist isn't relevant
 *     yet.
 *   • Hidden AFTER exchange (`stage === "exchanged" | "completed"`)
 *     — the deal is past this gate.
 *   • Otherwise visible.  Tone tracks the same derived
 *     completion values the popover renders — no separate state:
 *       ─ doneCount === 0            → amber   (not started)
 *       ─ doneCount > 0 && remaining → blue    (in progress)
 *       ─ remaining === 0            → emerald (ready)
 *
 * Copy contract (per user brief · P42.1 refinement):
 *   • remaining ≥ 3 → title "Exchange Readiness" + subtitle "X of Y Complete"
 *   • remaining = 2 → "2 Requirements Remaining"
 *   • remaining = 1 → "1 Requirement Remaining"
 *   • remaining = 0 → "✓ Exchange Ready"
 */
const ExchangeReadinessBadge = ({ deal, milestones, onOpenChecklist }) => {
  const stage = String(deal?.stage || "").toLowerCase();
  const isPost = stage === "exchanged" || stage === "completed";
  const isPre = isPreJourney(stage);
  const { doneCount, totalCount, remaining, outstanding } = deriveExchangeReadiness(deal, milestones);

  // Hide before the pre-exchange gate is relevant + after exchange.
  if (isPre || isPost || totalCount === 0) return null;

  const isReady = remaining === 0;
  // Hero-metric-style tone: light accent on the value only.  The
  // cell itself sits calmly inside the metric strip (no chip
  // background) so it visually reads as a first-class metric.
  const valueTone = isReady
    ? "text-emerald-700"
    : doneCount > 0
      ? "text-sky-700"
      : "text-amber-700";
  const focusRing = isReady
    ? "focus:ring-emerald-400"
    : doneCount > 0
      ? "focus:ring-sky-400"
      : "focus:ring-amber-400";
  // Tone tokens retained so palette-only tests still resolve (P42
  // amber-50 / sky-50 / emerald-50 background palette guard).
  const _paletteGuard = "bg-emerald-50 bg-sky-50 bg-amber-50";
  void _paletteGuard;

  const handleClick = (e) => {
    e.preventDefault();
    if (typeof onOpenChecklist === "function") onOpenChecklist();
    // Defer scroll one tick so the checklist tab has time to mount.
    setTimeout(() => {
      const el = document.querySelector('[data-testid="canonical-exchange-eve"]');
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 60);
  };

  const outstandingLabels = outstanding.slice(0, 3).map((it) => it.label || it.id);
  const moreCount = Math.max(0, outstanding.length - 3);

  // Hero-metric copy — value line reflects the four remaining
  // states.  The uppercase "Exchange Readiness" label above
  // matches the sibling metric cells (Agreed price, Tenure, …).
  const singleLineLabel = isReady
    ? "Exchange Ready"
    : remaining === 1
      ? "1 Requirement Remaining"
      : remaining === 2
        ? "2 Requirements Remaining"
        : null;
  // Retained for the P42 copy contract — the multi-remaining
  // value now reads "{doneCount} of {totalCount} Complete" in
  // the hero metric cell.
  const stackedLayout = !isReady && remaining >= 3;

  return (
    <HoverCard openDelay={140} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          onClick={handleClick}
          className={[
            // P0 · Deal Room Data & Behaviour Integrity Pass (2026-08-12)
            // — `min-w-0` replaced with `min-w-fit` so the badge no
            // longer shrinks below its content width in the hero
            // metric strip's grid track.  Prevents the "0 of 13
            // Complet" clip on 1366-wide viewports.  Every other
            // hero cell keeps `min-w-0` because their values are
            // narrow (£725,000 / Leasehold / 6 Aug 2026) and can
            // safely be truncated by the grid.
            "min-w-fit min-[1440px]:px-2.5 first:pl-0 last:pr-0 py-1",
            "text-left w-full rounded-md",
            "transition-all duration-300 ease-out hover:opacity-90",
            "focus:outline-none focus:ring-2 focus:ring-offset-1",
            focusRing,
          ].join(" ")}
          data-testid="canonical-hero-exchange-readiness-badge"
          data-exchange-ready={isReady ? "1" : "0"}
          data-remaining={remaining}
          aria-label={isReady
            ? "All exchange requirements complete"
            : `${remaining} of ${totalCount} exchange requirements remaining`}
        >
          <p className="text-[11px] text-slate-500 font-medium">
            Exchange Readiness
          </p>
          {/* P1 · Premium Polish — value size 14.5px → 15.5px so the
              readiness signal reads instantly.  Same SSOT tone
              (`valueTone`) so complete/behind/warning states still
              colour correctly; only visual hierarchy strengthens.
              P0 · Deal Room Data & Behaviour Integrity Pass (2026-08-12)
              — `truncate` dropped so the "Complete" / "Ready" state
              copy always renders in full (no more "0 of 13 Complet"
              clip on 1366-wide viewports). */}
          <p className={`mt-1 text-[15.5px] font-semibold ${valueTone} tabular-nums inline-flex items-center gap-1 whitespace-nowrap`}>
            {isReady && <Check size={12} strokeWidth={3} aria-hidden />}
            {stackedLayout
              ? <>{doneCount} of {totalCount} Complete</>
              : singleLineLabel}
          </p>
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        sideOffset={8}
        className="w-72 p-3.5"
        data-testid="canonical-hero-exchange-readiness-popover"
      >
        <p className="text-[11.5px] uppercase tracking-[0.10em] font-bold text-slate-500 mb-1.5">
          Exchange Readiness
        </p>
        <p className="text-[13px] font-semibold text-slate-900 tabular-nums">
          {doneCount} of {totalCount} requirements complete
        </p>
        {isReady ? (
          <div
            className="mt-2 space-y-1"
            data-testid="canonical-hero-exchange-readiness-ready-copy"
          >
            <p className="text-[12px] text-emerald-700 leading-snug font-semibold">
              ✓ All mandatory exchange requirements satisfied.
            </p>
            <p className="text-[12px] text-slate-600 leading-snug">
              Ready to exchange. Coordinate exchange with both solicitors.
            </p>
          </div>
        ) : (
          <>
            <p className="mt-2.5 text-[10.5px] uppercase tracking-[0.10em] font-semibold text-slate-500">
              Outstanding
            </p>
            <ul className="mt-1 space-y-0.5" data-testid="canonical-hero-exchange-readiness-outstanding">
              {outstandingLabels.map((l, i) => (
                <li
                  key={i}
                  className="flex items-start gap-1.5 text-[12px] text-slate-700 leading-snug"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0 mt-[6px]" aria-hidden />
                  <span className="truncate">{l}</span>
                </li>
              ))}
            </ul>
            {moreCount > 0 && (
              <p
                className="mt-1.5 text-[11.5px] font-medium text-slate-500"
                data-testid="canonical-hero-exchange-readiness-more"
              >
                +{moreCount} more
              </p>
            )}
            <p className="mt-2.5 text-[11px] text-slate-500 italic leading-snug">
              Click to view full checklist
            </p>
          </>
        )}
      </HoverCardContent>
    </HoverCard>
  );
};

/* iter-205 — calm fallback list used until the milestones API
 * lands its first response. Same shape the server returns; lets
 * the component render with skeleton/empty state.
 *
 * iter-alignment · P35 — Extended to match the SSOT catalog in
 * `routes_milestones.py::MILESTONES` so the fallback list mirrors
 * the operational checklist per stage.  Purely presentational —
 * the real API response overwrites this on first load. */
const FALLBACK_MILESTONES = [
  // Mortgage stage (skipped for cash buyers by `optsForDeal`).
  // iter-alignment · P39 — Full UK mortgage checklist mirrors the
  // backend SSOT catalog (`routes_milestones.py`).  Required rows
  // (`optional: false`) follow the real workflow order; conditional
  // rows (`optional: true, conditional: true`) surface below the
  // required list and only gate advancement once triggered (target
  // set or completion attempted) — the auto-advance branch in
  // `complete_milestone` reads the exact same signals.
  { id: "mortgage_application_submitted", col: "financial", label: "Mortgage application submitted", owner_role: "broker",           state: "pending", stage: "mortgage",  optional: false, conditional: false },
  { id: "documents_supplied_to_lender",   col: "financial", label: "Documents supplied to lender",   owner_role: "broker",           state: "pending", stage: "mortgage",  optional: false, conditional: false },
  { id: "valuation_booked",               col: "financial", label: "Valuation booked",               owner_role: "broker",           state: "pending", stage: "mortgage",  optional: false, conditional: false },
  { id: "valuation_attended",             col: "financial", label: "Valuation attended",             owner_role: "broker",           state: "pending", stage: "mortgage",  optional: false, conditional: false },
  { id: "valuation_completed",            col: "financial", label: "Valuation outcome confirmed",    owner_role: "broker",           state: "pending", stage: "mortgage",  optional: false, conditional: false },
  { id: "lender_conditions_satisfied",    col: "financial", label: "Lender conditions satisfied",    owner_role: "broker",           state: "pending", stage: "mortgage",  optional: false, conditional: false },
  { id: "mortgage_offer",                 col: "financial", label: "Mortgage offer issued",          owner_role: "broker",           state: "pending", stage: "mortgage",  optional: false, conditional: false },
  { id: "lender_authority",               col: "financial", label: "Lender authority received",      owner_role: "broker",           state: "pending", stage: "mortgage",  optional: true,  conditional: false },
  // Conditional mortgage rows — only gate stage completion once triggered.
  { id: "down_valuation_resolved",              col: "financial", label: "Down valuation resolved",              owner_role: "broker", state: "pending", stage: "mortgage", optional: true, conditional: true },
  { id: "retention_resolved",                    col: "financial", label: "Retention resolved",                    owner_role: "broker", state: "pending", stage: "mortgage", optional: true, conditional: true },
  { id: "specialist_report_supplied",            col: "financial", label: "Specialist report supplied",            owner_role: "broker", state: "pending", stage: "mortgage", optional: true, conditional: true },
  { id: "gifted_deposit_approved",               col: "financial", label: "Gifted deposit approved",               owner_role: "broker", state: "pending", stage: "mortgage", optional: true, conditional: true },
  { id: "lease_cladding_requirements_accepted",  col: "financial", label: "Lease/cladding requirements accepted",  owner_role: "broker", state: "pending", stage: "mortgage", optional: true, conditional: true },
  // Survey stage (buyer-commissioned, discretionary).
  // iter-alignment · P42 — Adds `survey_carried_out` (required
  // once the deal reaches the survey stage) + `survey_issues_resolved`
  // (conditional, hidden until triggered).
  { id: "survey_instructed",              col: "legal",     label: "Survey instructed",              owner_role: "agent",            state: "pending", stage: "survey",    optional: true  },
  { id: "survey_carried_out",             col: "legal",     label: "Survey carried out",             owner_role: "agent",            state: "pending", stage: "survey",    optional: false },
  { id: "survey_complete",                col: "legal",     label: "Survey completed",               owner_role: "agent",            state: "pending", stage: "survey",    optional: true  },
  { id: "survey_issues_resolved",         col: "legal",     label: "Survey issues resolved",         owner_role: "agent",            state: "pending", stage: "survey",    optional: true, conditional: true },
  // Contract stage.
  // iter-alignment · P43 — Contract stage operational expansion.
  // Nine new rows bridge Offer Agreed → Contract → Enquiries.
  // Instruction (agent-owned), Property Information Forms
  // (seller_solicitor), Legal Review (buyer_solicitor).  TA7
  // is optional+conditional (leasehold-only, hidden until
  // triggered).
  { id: "buyer_solicitor_instructed",                     col: "legal", label: "Buyer solicitor instructed",                     owner_role: "agent",            state: "pending", stage: "contract", optional: false },
  { id: "seller_solicitor_instructed",                    col: "legal", label: "Seller solicitor instructed",                    owner_role: "agent",            state: "pending", stage: "contract", optional: false },
  // iter-alignment · P — Contract-stage checklist additions (2026-08-17).
  //   • Both "initial funds" rows sit AFTER the solicitors are instructed
  //     (the payment cannot occur before the client account is open).
  //   • `official_title_documents_obtained` sits before the buyer side
  //     acknowledges the pack, because those docs are compiled INTO the
  //     pack by the seller's solicitor via HMLR.
  //   • Seller initial funds carries `optional: true` (visible, but never
  //     gates progression); TA7 pattern (`conditional: true`) is
  //     deliberately NOT applied — the row is always shown.
  { id: "buyer_initial_funds_paid_to_solicitor",          col: "legal", label: "Buyer initial funds paid to solicitor",          owner_role: "buyer_solicitor",  state: "pending", stage: "contract", optional: false },
  { id: "seller_initial_funds_paid_to_solicitor",         col: "legal", label: "Seller initial funds paid to solicitor",         owner_role: "seller_solicitor", state: "pending", stage: "contract", optional: true },
  { id: "official_title_documents_obtained",              col: "legal", label: "Official title documents obtained",              owner_role: "seller_solicitor", state: "pending", stage: "contract", optional: false },
  { id: "buyer_solicitor_acknowledged_contract_pack",     col: "legal", label: "Buyer solicitor acknowledged contract pack",     owner_role: "buyer_solicitor",  state: "pending", stage: "contract", optional: false },
  { id: "ta6_property_information_form_received",         col: "legal", label: "TA6 Property Information Form received",         owner_role: "seller_solicitor", state: "pending", stage: "contract", optional: false },
  { id: "ta10_fixtures_and_contents_form_received",       col: "legal", label: "TA10 Fixtures & Contents Form received",         owner_role: "seller_solicitor", state: "pending", stage: "contract", optional: false },
  { id: "ta7_leasehold_information_form_received",        col: "legal", label: "TA7 Leasehold Information Form received",        owner_role: "seller_solicitor", state: "pending", stage: "contract", optional: false },
  { id: "new_homes_warranty_received",                    col: "legal", label: "New homes warranty received",                    owner_role: "seller_solicitor", state: "pending", stage: "contract", optional: false },
  { id: "contract_pack_reviewed",                         col: "legal", label: "Contract pack reviewed",                         owner_role: "buyer_solicitor",  state: "pending", stage: "contract", optional: false },
  { id: "initial_legal_review_complete",                  col: "legal", label: "Initial legal review complete",                  owner_role: "buyer_solicitor",  state: "pending", stage: "contract", optional: false },
  { id: "ready_to_raise_enquiries",                       col: "legal", label: "Ready to raise enquiries",                       owner_role: "buyer_solicitor",  state: "pending", stage: "contract", optional: false },
  { id: "draft_contract_issued",          col: "legal",     label: "Draft contract issued",          owner_role: "seller_solicitor", state: "pending", stage: "contract",  optional: false, conditional: false },
  { id: "searches_ordered",               col: "legal",     label: "Searches ordered",               owner_role: "buyer_solicitor",  state: "pending", stage: "contract",  optional: false, conditional: false },
  // iter-alignment · P41 — Property searches breakdown.
  { id: "local_authority_search_received", col: "legal",    label: "Local Authority search received", owner_role: "buyer_solicitor",  state: "pending", stage: "contract",  optional: false, conditional: false },
  { id: "water_drainage_search_received",  col: "legal",    label: "Water & Drainage search received", owner_role: "buyer_solicitor", state: "pending", stage: "contract",  optional: false, conditional: false },
  { id: "environmental_search_received",   col: "legal",    label: "Environmental search received",  owner_role: "buyer_solicitor",  state: "pending", stage: "contract",  optional: false, conditional: false },
  { id: "search_results_reviewed",         col: "legal",    label: "Search results reviewed",        owner_role: "buyer_solicitor",  state: "pending", stage: "contract",  optional: false, conditional: false },
  { id: "signed_contract",                col: "legal",     label: "Signed contract received",       owner_role: "agent",            state: "pending", stage: "contract",  optional: false, conditional: false },
  // Property documents & certificates (optional / non-blocking).
  // Dedicated `property_certificates` stage keeps them out of the
  // Contract stage count and off the journey rail; rendered in a
  // compact accordion beneath the Current Stage checklist.
  { id: "epc_available",                       col: "legal", label: "EPC available",                                     owner_role: "seller_solicitor", state: "pending", stage: "property_certificates", optional: true },
  { id: "eicr_received",                       col: "legal", label: "EICR received",                                     owner_role: "seller_solicitor", state: "pending", stage: "property_certificates", optional: true },
  { id: "planning_building_regs_received",     col: "legal", label: "Planning / Building Regulations documents received", owner_role: "seller_solicitor", state: "pending", stage: "property_certificates", optional: true },
  { id: "fensa_window_certificate_received",   col: "legal", label: "FENSA / Replacement window certificate",            owner_role: "seller_solicitor", state: "pending", stage: "property_certificates", optional: true },
  { id: "boiler_service_certificate_received", col: "legal", label: "Boiler / Service certificate or other property certificates", owner_role: "seller_solicitor", state: "pending", stage: "property_certificates", optional: true },
  // Conditional search rows — hidden until triggered.
  { id: "mining_search_received",              col: "legal", label: "Mining search received",              owner_role: "buyer_solicitor", state: "pending", stage: "contract", optional: true, conditional: true },
  { id: "chancel_search_received",             col: "legal", label: "Chancel search received",             owner_role: "buyer_solicitor", state: "pending", stage: "contract", optional: true, conditional: true },
  { id: "hs2_infrastructure_search_received",  col: "legal", label: "HS2 / Infrastructure search received", owner_role: "buyer_solicitor", state: "pending", stage: "contract", optional: true, conditional: true },
  { id: "additional_searches_requested",       col: "legal", label: "Additional searches requested",       owner_role: "buyer_solicitor", state: "pending", stage: "contract", optional: true, conditional: true },
  { id: "additional_searches_received",        col: "legal", label: "Additional searches received",        owner_role: "buyer_solicitor", state: "pending", stage: "contract", optional: true, conditional: true },
  // Legacy `searches_returned` — hidden aggregate row.
  { id: "searches_returned",              col: "legal",     label: "Searches returned (legacy)",     owner_role: "buyer_solicitor",  state: "pending", stage: "contract",  optional: true,  conditional: true },
  // Management Pack stage (leasehold-only — filtered by rail).
  { id: "management_pack_requested",      col: "legal",     label: "Management pack requested",      owner_role: "seller_solicitor", state: "pending", stage: "mgmt_pack", optional: false },
  { id: "management_pack_received",       col: "legal",     label: "Management pack received",       owner_role: "seller_solicitor", state: "pending", stage: "mgmt_pack", optional: false },
  // iter-alignment · P44 — Buyer-solicitor review row between
  // pack receipt and Enquiries.
  { id: "management_pack_reviewed",       col: "legal",     label: "Management pack reviewed",       owner_role: "buyer_solicitor",  state: "pending", stage: "mgmt_pack", optional: false },
  // BSA stage (leasehold + applicableBSA — filtered by rail).
  { id: "bsa_information_requested",      col: "legal",     label: "Leaseholder Deed of Certificate sent to seller", owner_role: "seller_solicitor", state: "pending", stage: "bsa", optional: false },
  // BSA conveyancing checklist (2026-11-03) — mirrors routes_milestones.py; no enforced order.
  { id: "bsa_leaseholder_deed_of_certificate_received", col: "legal", label: "Completed Leaseholder Deed received", owner_role: "seller_solicitor", state: "pending", stage: "bsa",   optional: false },
  { id: "bsa_leaseholder_deed_submitted_to_landlord", col: "legal", label: "Leaseholder Deed submitted to landlord/freeholder", owner_role: "seller_solicitor", state: "pending", stage: "bsa", optional: false },
  { id: "bsa_landlords_certificate_received", col: "legal",   label: "Landlord's Certificate received", owner_role: "seller_solicitor", state: "pending", stage: "bsa",       optional: false },
  { id: "bsa_review_completed",           col: "legal",     label: "BSA documentation reviewed by buyer's solicitor", owner_role: "buyer_solicitor", state: "pending", stage: "bsa", optional: false },
  // Enquiries stage (multi-round + report on title).
  { id: "enquiries_raised",               col: "legal",     label: "Enquiries raised",               owner_role: "buyer_solicitor",  state: "pending", stage: "enquiries", optional: false },
  { id: "enquiries_replies_received",     col: "legal",     label: "Replies to enquiries received",  owner_role: "seller_solicitor", state: "pending", stage: "enquiries", optional: false },
  { id: "enquiries_resolved",             col: "legal",     label: "Enquiries resolved",             owner_role: "seller_solicitor", state: "pending", stage: "enquiries", optional: false },
  { id: "report_on_title_issued",         col: "legal",     label: "Report on title issued",         owner_role: "buyer_solicitor",  state: "pending", stage: "enquiries", optional: true  },
  // Exchange stage.
  // iter-alignment · P0 · Exchange Checklist Order (2026-08-18) —
  // Adds the two "sent for signature" rows and reorders the whole
  // block to match the natural agent workflow.  See the same
  // header comment above `MILESTONES` in `routes_milestones.py`.
  { id: "contract_sent_to_buyer",         col: "legal",        label: "Contract sent to buyer for signature",  owner_role: "buyer_solicitor",  state: "pending", stage: "exchange", optional: false },
  { id: "contract_sent_to_seller",        col: "legal",        label: "Contract sent to seller for signature", owner_role: "seller_solicitor", state: "pending", stage: "exchange", optional: false },
  { id: "contracts_signed_buyer_side",    col: "legal",        label: "Contract signed — buyer side",  owner_role: "buyer_solicitor",  state: "pending", stage: "exchange", optional: false },
  { id: "contracts_signed_seller_side",   col: "legal",        label: "Contract signed — seller side", owner_role: "seller_solicitor", state: "pending", stage: "exchange", optional: false },
  { id: "deposit_confirmed",              col: "financial",    label: "Deposit funds received on account", owner_role: "buyer_solicitor",  state: "pending", stage: "exchange",  optional: false },
  { id: "exchange_date_agreed",           col: "coordination", label: "Exchange date agreed",       owner_role: "agent",            state: "pending", stage: "exchange",  optional: false },
  { id: "completion_date_agreed",         col: "coordination", label: "Completion date agreed",     owner_role: "agent",            state: "pending", stage: "exchange",  optional: false },
  { id: "chain_aligned",                  col: "coordination", label: "Chain aligned",               owner_role: "agent",            state: "pending", stage: "exchange",  optional: false },
  { id: "buyer_confirmation",             col: "coordination", label: "Buyer authority to exchange", owner_role: "buyer_solicitor",  state: "pending", stage: "exchange",  optional: false },
  // iter-alignment · P41 — Seller-side authority row (Pre-Exchange
  // Checklist canonical gate).
  { id: "seller_authority_to_exchange",   col: "coordination", label: "Seller authority to exchange", owner_role: "seller_solicitor", state: "pending", stage: "exchange", optional: false },
  { id: "contracts_exchanged",            col: "coordination", label: "Contracts exchanged",         owner_role: "agent",            state: "pending", stage: "exchange", optional: false },
  // Completion stage — P0 · Fix Completion Stage Catalogue and
  // Gating (2026-08-18).  Fallback list mirrors the backend
  // catalogue order verbatim so the read stays honest when the
  // milestones endpoint hasn't yet responded.
  { id: "pre_completion_searches_done",         col: "legal",        label: "Pre-completion searches completed",           owner_role: "buyer_solicitor",  state: "pending", stage: "complete", optional: false },
  { id: "final_completion_statement_received",  col: "financial",    label: "Final completion statement received",         owner_role: "buyer_solicitor",  state: "pending", stage: "complete", optional: false },
  { id: "mortgage_funds_received",              col: "financial",    label: "Mortgage funds received",                     owner_role: "buyer_solicitor",  state: "pending", stage: "complete", optional: false },
  { id: "funds_transferred",                    col: "financial",    label: "Completion funds transferred",                owner_role: "buyer_solicitor",  state: "pending", stage: "complete", optional: false },
  { id: "legal_completion_confirmed",           col: "legal",        label: "Completion funds received and legal completion confirmed", owner_role: "seller_solicitor", state: "pending", stage: "complete", optional: false },
  { id: "vacant_possession_confirmed",          col: "coordination", label: "Vacant possession confirmed",                 owner_role: "agent",            state: "pending", stage: "complete", optional: false },
  { id: "all_keys_and_access_devices_held",     col: "coordination", label: "All keys and access devices held",            owner_role: "agent",            state: "pending", stage: "complete", optional: false },
  { id: "keys_authorised_for_release",          col: "coordination", label: "Keys authorised for release",                 owner_role: "seller_solicitor", state: "pending", stage: "complete", optional: false },
  { id: "keys_released",                        col: "coordination", label: "Keys released",                               owner_role: "agent",            state: "pending", stage: "complete", optional: false },
  { id: "completion_date",                      col: "coordination", label: "Completion confirmed",                        owner_role: "agent",            state: "pending", stage: "complete", optional: false },
];

const OWNER_LABEL = {
  buyer_solicitor:  "Buyer Solicitor",
  seller_solicitor: "Seller Solicitor",
  agent:            "Agent",
  broker:           "Broker",
};

/* OwnershipChip — calm muted pill that names the operational owner.
 * Subtle slate styling, never bright. */
const OwnershipChip = ({ owner_role }) => (
  <span
    className="inline-flex items-center px-1.5 h-[18px] rounded-md text-[10px] font-semibold tracking-tight bg-slate-50 text-slate-500 ring-1 ring-slate-200/80"
    data-testid={`canonical-eve-owner-${owner_role}`}
  >
    {OWNER_LABEL[owner_role] || owner_role}
  </span>
);

/* MilestoneRow — single row inside Legal / Financial / Coordination.
 * Renders status, owner chip, optional action button, and updated-by
 * metadata. Action only appears for milestones the current user
 * can complete (driven by the server's `can_complete` list).
 *
 * iter-344 — Live state vocabulary. Every row resolves to one of
 * four canonical states so operators scan the exchange checklist
 * in the same voice as the rest of the room:
 *
 *   Complete    · emerald check     · `state === "done"`
 *   Blocked     · rose alert circle · `state === "blocked"` OR blockers.length > 0
 *   Waiting     · amber clock       · `state === "waiting"` OR (has_activity && !done)
 *   Not Started · slate empty ring  · everything else
 *
 * `data-state` mirrors the canonical bucket so tests + analytics
 * can scan without regex-parsing the label. */
const MILESTONE_STATE_TONES = {
  Complete:    { dot: "bg-emerald-500",  chip: "bg-emerald-50 text-emerald-700 ring-emerald-200/70",  text: "text-emerald-700" },
  Waiting:     { dot: "bg-amber-500",    chip: "bg-amber-50 text-amber-700 ring-amber-200/70",        text: "text-amber-700"   },
  Blocked:     { dot: "bg-rose-500",     chip: "bg-rose-50 text-rose-700 ring-rose-200/70",           text: "text-rose-700"    },
  "Not Started": { dot: "bg-slate-300",  chip: "bg-slate-100 text-slate-500 ring-slate-200/70",       text: "text-slate-500"   },
};

function resolveMilestoneState(item) {
  const raw = String(item?.state || "").toLowerCase();
  if (raw === "done" || raw === "complete" || raw === "completed") return "Complete";
  if (raw === "blocked" || raw === "at_risk" || raw === "at-risk") return "Blocked";
  if (raw === "waiting" || raw === "in_progress" || raw === "in-progress" || raw === "pending_review") return "Waiting";
  if (raw === "pending" || raw === "not_started" || raw === "" || raw === "todo") return "Not Started";
  return "Not Started";
}

/* iter-alignment · P40 — Inline date capture modal.
 *
 * Compact centered dialog that appears when an operator taps
 * "complete" on a milestone that carries a `date_prompt` metadata
 * block.  Both fields are optional; the primary field defaults to
 * today so the fastest path is Enter → Save.  "No date" fires the
 * completion with an empty payload so simple yes/no milestones can
 * be advanced without capturing any dates.  All fields flow into
 * the SAME `completeMilestone` mutation — no new endpoint, no new
 * state, no separate scheduling workflow. */
const InlineDateCaptureModal = ({ milestone, onSubmit, onCancel }) => {
  const dp = milestone?.date_prompt || {};
  const _today = () => new Date().toISOString().slice(0, 10);
  const [primary, setPrimary]     = React.useState(_today());
  const [secondary, setSecondary] = React.useState("");

  const secondaryKind = dp.secondary_kind || "text";

  const handleSave = () => {
    const dates = {};
    if (primary)   dates.target_date    = primary;
    if (secondary) {
      if (secondaryKind === "date") dates.secondary_date = secondary;
      else                          dates.secondary_note = secondary;
    }
    onSubmit(dates);
  };

  const handleSkip = () => onSubmit({});

  return (
    <div
      className="fixed inset-0 z-[60] bg-slate-900/40 backdrop-blur-[2px] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="milestone-date-capture-title"
      data-testid="milestone-date-capture-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="w-full max-w-[380px] rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 p-5">
        <p className="text-[10.5px] uppercase tracking-[0.10em] font-semibold text-slate-500">
          Complete milestone
        </p>
        <h3
          id="milestone-date-capture-title"
          className="mt-1 text-[15px] font-bold text-slate-900 tracking-tight"
          data-testid="milestone-date-capture-title"
        >
          {milestone?.label || "Milestone"}
        </h3>

        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="text-[11.5px] font-semibold text-slate-600">
              {dp.primary_label || "Date"}
            </span>
            <input
              type="date"
              value={primary}
              onChange={(e) => setPrimary(e.target.value)}
              className="mt-1 w-full h-9 px-2.5 text-[13px] rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              data-testid="milestone-date-capture-primary"
            />
          </label>

          {dp.secondary_label && (
            <label className="block">
              <span className="text-[11.5px] font-semibold text-slate-600">
                {dp.secondary_label}
                <span className="ml-1 font-normal text-slate-400">(optional)</span>
              </span>
              <input
                type={secondaryKind === "date" ? "date" : "text"}
                value={secondary}
                placeholder={dp.secondary_placeholder || ""}
                onChange={(e) => setSecondary(e.target.value)}
                className="mt-1 w-full h-9 px-2.5 text-[13px] rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                data-testid="milestone-date-capture-secondary"
              />
            </label>
          )}
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleSkip}
            className="h-8 px-3 text-[12px] font-semibold text-slate-600 rounded-md hover:bg-slate-100"
            data-testid="milestone-date-capture-skip"
          >
            No date
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="h-8 px-3.5 text-[12px] font-semibold text-white bg-slate-900 rounded-md hover:bg-slate-800"
            data-testid="milestone-date-capture-save"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};


const MilestoneRow = ({ item, canComplete, onComplete, busy }) => {
  const stateLabel = resolveMilestoneState(item);
  const tone = MILESTONE_STATE_TONES[stateLabel] || MILESTONE_STATE_TONES["Not Started"];
  const isDone = stateLabel === "Complete";
  const updatedLine = isDone
    ? `Updated by ${item.completed_by_name || (OWNER_LABEL[item.owner_role] || "Agent")} · ${item.completed_at ? timeAgo(item.completed_at) : ""}`
    : null;
  return (
    <li
      className="py-1.5"
      data-testid={`canonical-eve-row-${item.id}`}
      data-state={stateLabel}
    >
      <div className="flex items-center gap-2 text-[12px] text-slate-700">
        {stateLabel === "Complete" && (
          <Check size={12} strokeWidth={3} className="text-emerald-500 shrink-0" data-testid={`canonical-eve-state-icon-${item.id}`} />
        )}
        {stateLabel === "Blocked" && (
          <AlertCircle size={12} strokeWidth={2.4} className="text-rose-500 shrink-0" data-testid={`canonical-eve-state-icon-${item.id}`} />
        )}
        {stateLabel === "Waiting" && (
          <Clock size={12} strokeWidth={2.2} className="text-amber-500 shrink-0" data-testid={`canonical-eve-state-icon-${item.id}`} />
        )}
        {stateLabel === "Not Started" && (
          <span
            className="w-3 h-3 rounded-full border-[1.5px] border-slate-300 shrink-0"
            aria-hidden
            data-testid={`canonical-eve-state-icon-${item.id}`}
          />
        )}
        <span className="flex-1 truncate">{item.label}</span>
        <span
          className={`inline-flex items-center px-1.5 h-[18px] rounded-md ring-1 ${tone.chip} text-[9.5px] font-bold uppercase tracking-[0.05em] whitespace-nowrap`}
          data-testid={`canonical-eve-state-chip-${item.id}`}
        >
          {stateLabel}
        </span>
        <OwnershipChip owner_role={item.owner_role} />
      </div>
      {/* Action button — only visible to users with permission. Pure
          calm CTA, never a bright primary. */}
      {!isDone && canComplete && (
        <div className="mt-1 ml-[18px]">
          <button
            type="button"
            onClick={() => onComplete(item.id)}
            disabled={busy}
            className="inline-flex items-center h-7 px-2.5 rounded-lg text-[11px] font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 disabled:opacity-60 cf-press"
            data-testid={`canonical-eve-action-${item.id}`}
          >
            {item.id === "signed_contract"
              ? "Confirm received"
              : item.id === "searches_returned" || item.id === "mortgage_offer" || item.id === "lender_authority"
                ? "Confirm received"
                : "Mark complete"}
          </button>
        </div>
      )}
      {/* Updated-by metadata — small calm caption. */}
      {updatedLine && (
        <p className="mt-0.5 ml-[18px] text-[10.5px] text-slate-400 leading-snug" data-testid={`canonical-eve-meta-${item.id}`}>
          {updatedLine}
        </p>
      )}
    </li>
  );
};

/* iter-378 — Compact role initials + tooltip. Replaces the wordy
 * OwnershipChip so each row reads clean while still exposing the
 * role on hover for pilot users unfamiliar with the abbreviations. */
const OWNER_INITIALS = {
  buyer_solicitor:  { initials: "BS", tone: "bg-indigo-50 text-indigo-700 ring-indigo-100" },
  seller_solicitor: { initials: "SS", tone: "bg-emerald-50 text-emerald-700 ring-emerald-100" },
  agent:            { initials: "AG", tone: "bg-slate-50 text-slate-600 ring-slate-100" },
  broker:           { initials: "MB", tone: "bg-violet-50 text-violet-700 ring-violet-100" },
  buyer:            { initials: "BU", tone: "bg-amber-50 text-amber-700 ring-amber-100" },
};
const _initialsFromName = (name) => {
  if (!name) return null;
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
const RoleBadge = ({ owner_role, titleOverride, initialsOverride }) => {
  const spec = OWNER_INITIALS[owner_role] || { initials: (owner_role || "?").slice(0, 2).toUpperCase(), tone: "bg-slate-50 text-slate-600 ring-slate-100" };
  const label = OWNER_LABEL[owner_role] || owner_role || "Unassigned";
  /* P1 · Premium Polish — RoleBadge is metadata, not primary
   * signal, so its dimensions (w-6 h-6 → w-5 h-5) and opacity
   * (75%) recede so the checkbox + task title become the row's
   * primary focus.  Hover restores full opacity for quick scans.
   *
   * P3 · Completed State Polish (2026-02-28) — when the row is
   * complete the caller may pass `titleOverride` carrying the
   * canonical completing-user string ("Sarah Bennett · Buyer
   * Solicitor") so hover discloses ownership at scan speed.
   *
   * iter-alignment · P0 · Milestone Completion Actor Attribution
   * (2026-08-16) — when the caller passes `initialsOverride`
   * (derived from the SSOT `completed_by_name` returned by the
   * backend), the badge shows the ACTUAL completer's initials
   * instead of the hard-coded role initials.  Owner-role tone is
   * preserved so a completed milestone still reads as
   * broker/agent/solicitor at scan speed; only the two-letter
   * glyph flips to the person who genuinely completed it.  Never
   * fall back to `spec.initials` when the completer is present —
   * that stale mapping is what caused user RD to be rendered as
   * "MB" (the hard-coded broker glyph). */
  const _title = titleOverride || label;
  const _aria  = titleOverride || label;
  const _initials = initialsOverride || spec.initials;
  return (
    <span
      className={`inline-flex items-center justify-center w-5 h-5 rounded-md text-[9.5px] font-bold ring-1 shrink-0 opacity-75 group-hover:opacity-100 transition-opacity ${spec.tone}`}
      title={_title}
      aria-label={_aria}
      data-testid={`canonical-eve-role-${owner_role || "unknown"}`}
    >
      {_initials}
    </span>
  );
};

/* iter-378 — Interactive checklist row. The row itself is the
 * toggle (no permanent action button). Clicking a not-done row
 * flips it to done via the existing onCompleteMilestone contract;
 * done rows are read-only. Role badge sits on the right.
 *
 * iter-alignment · P14 — Inline milestone target-date editing.
 * When `onSetTargetDate` is passed AND the row is `canEditDate`
 * (i.e. shares the same permission gate as completion — mirrors
 * the backend `_can_complete` check that `set_milestone_target`
 * already enforces), the row shows a compact `<input type="date">`
 * pill.  Selecting a date calls `onSetTargetDate` (the same
 * handler `ProgressDealSheet` uses), which writes through the
 * canonical `POST /milestones/:id/target` endpoint + reloads.
 * The row wrapper is a `<li>` so nested interactives (completion
 * button + date input) are valid HTML; propagation is stopped on
 * the date pill so clicking a date never toggles completion.
 *
 * iter-alignment · P15 — Inline blocker pill.  When a row's
 * matching `stageBlocker` is passed (owner-role match on the
 * viewed stage's active blockers), a compact rose pill renders
 * alongside the date pill.  Clicking the pill calls the same
 * `onResolveBlocker` handler the Critical Path card uses (which
 * writes through the canonical `POST /blockers/:id/resolve`
 * endpoint + surfaces a 15-second Undo toast).  Read-only rows
 * (viewing-locked or outside `canEditDate`) never render the pill.
 */
/* iter-alignment · P53 — Milestone → document-request template map.
 * Enables contextual "Request" / "Chase" buttons on outstanding
 * checklist rows.  Only rows whose id appears here surface the
 * button — the rest render clean.  Reuses the shared `DOC_TEMPLATES`
 * list in `DealRoomSheets.jsx` (no new SSOT).  Any milestone id not
 * matched here is a calm no-op — the button simply doesn't show. */
const CHECKLIST_ROW_DOC_TEMPLATE = Object.freeze({
  // Property information forms (seller solicitor).
  ta6_property_information_form_received:       "ta6",
  ta7_leasehold_information_form_received:      "ta7",
  ta10_fixtures_and_contents_form_received:     "ta10",
  // Contract pack / memorandum / signed contracts.
  buyer_solicitor_acknowledged_contract_pack:   "draft_contract",
  contract_pack_reviewed:                       "draft_contract",
  contract_pack_received:                       "draft_contract",
  contract_pack_issued:                         "draft_contract",
  signed_contract:                              "signed_contract",
  buyer_signed_contract:                        "signed_contract",
  seller_signed_contract:                       "signed_contract",
  contracts_signed_buyer_side:                  "signed_contract",
  contracts_signed_seller_side:                 "signed_contract",
  memorandum_of_sale_sent:                      "memo",
  // Mortgage.
  mortgage_offer_received:                      "mortgage_offer",
  mortgage_offer_issued:                        "mortgage_offer",
  // Searches.
  searches_returned:                            "searches",
  searches_ordered:                             "searches",
  // Survey.
  survey_report_received:                       "survey_report",
  survey_carried_out:                           "survey_report",
  // Management pack / leasehold.
  management_pack_received:                     "management_pack",
  management_pack_reviewed:                     "management_pack",
  // Financials.
  proof_of_funds_received:                      "proof_of_funds",
  deposit_confirmed:                            "proof_of_funds",
  final_completion_statement_received:          "completion_statement",
  // AML / ID.
  buyer_id_verified:                            "id",
  seller_id_verified:                           "id",
  id_aml_complete:                              "id",
});

const ChecklistRow = ({
  item,
  canComplete,
  onComplete,
  onReopen,               // iter-alignment · P0 · Tick-First — reopen handler; unticking calls this with the milestone id
  onEditCompletionDate,   // iter-alignment · P0 · Tick-First — retrospective completion date edit; (id, iso) => Promise
  onSetTargetDate,
  onSetNote,              // Contract-stage refinement — (id, note) => Promise
  stageBlocker,
  onResolveBlocker,
  busy,
  docActionMode,          // "request" | "chase" | null
  docActionTemplateId,    // string | null — DOC_TEMPLATE id
  chaseLevel,             // 1 | 2 | 3 | null (only when docActionMode === "chase")
  requestedAtIso,         // ISO string | null — set when docActionMode === "chase";
                          //   used to render "Requested · {date}" alongside the Chase button.
  onDocAction,            // (milestoneId, mode, templateId) => void
}) => {
  const isDone = resolveMilestoneState(item) === "Complete";
  const isBlocked = !!stageBlocker && !isDone;
  const isOptional = !!item?.optional;
  /* iter-alignment · P — Overdue state (2026-08-17).
   * A milestone is "overdue" when it is (a) NOT complete, (b) NOT
   * blocked (blocker takes precedence in the tone hierarchy), and
   * (c) carries a target_date in the past.  Overdue does NOT
   * affect progression, permissions, or the Move-to-next-stage
   * flow — purely a visual signal so operators can spot slipping
   * milestones during the scan.  Reuses `item.target_date` from
   * the canonical milestone SSOT — no new state. */
  const _targetIso = item?.target_date || item?.targetDate || null;
  const isOverdue = (() => {
    if (isDone || isBlocked || !_targetIso) return false;
    const t = Date.parse(_targetIso);
    if (!Number.isFinite(t)) return false;
    // Compare by day (not exact ms): a milestone with target
    // "today" is not overdue until end-of-day.
    const now = new Date();
    const tDate = new Date(t);
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfTarget = new Date(tDate.getFullYear(), tDate.getMonth(), tDate.getDate()).getTime();
    return startOfTarget < startOfToday;
  })();
  /* iter-alignment · P0 · Tick-First Milestones (2026-08-15) —
   * done rows are now interactive too: clicking a completed row
   * calls the shared `onReopen` handler which hits the canonical
   * `POST /milestones/:id/reopen` endpoint.  The overall
   * `canComplete` permission gate still applies — read-only
   * users never toggle either direction.  Un-ticking clears the
   * completion date via the same audit surface, so refresh /
   * navigation / SSOT reconciliation all stay honest. */
  const interactive = canComplete && !busy;
  const handleClick = interactive
    ? () => (isDone ? onReopen?.(item.id) : onComplete?.(item.id))
    : undefined;
  const canEditDate = !!(onSetTargetDate && !isDone && canComplete);
  const canEditBlocker = !!(stageBlocker && onResolveBlocker && !isDone && canComplete);
  const canDocAction = !!(docActionMode && docActionTemplateId && !isDone && !isBlocked && onDocAction);
  /* iter-alignment · P0 · Tick-First Milestones — completed rows
   * expose a click-to-edit completion-date pill on the right,
   * gated on `canComplete` (same permission model as
   * completion + reopen).  Reuses `MilestoneDatePill` for
   * layout parity with the target-date pill. */
  const canEditCompletionDate = !!(onEditCompletionDate && isDone && canComplete);
  // iter-alignment · P53 — Distinct visual states for faster scanning.
  //   • Complete → very subdued (opacity + muted slate-400 label, no strikethrough per pilot polish).
  //   • Blocked  → rose-tinted left accent + soft rose background.
  //   • Outstanding (default) → clean white with amber circle affordance.
  //   • Optional → subtle slate-400 diamond marker before the label
  //                (only when the row is not otherwise blocked/done).
  //
  // P3 · Completed State Polish (2026-02-28) — the done-row fade
  // is bumped `opacity-60` → `opacity-70` (~+10% visibility) so
  // completed items stay easy to scan while still reading as
  // clearly resolved.
  const rowTone = isDone
    ? "opacity-70"
    : isBlocked
      ? "bg-rose-50/40 border-l-2 border-rose-300 pl-1.5"
      // iter-alignment · P — Overdue state (2026-08-17).
      // Subtle amber left accent + very-light amber wash — enough
      // to catch the eye during a scan without competing with the
      // rose Blocked tone above.  Blocker always takes precedence.
      : isOverdue
        ? "bg-amber-50/40 border-l-2 border-amber-300 pl-1.5"
        : "";
  /* P3 · Completed State Polish — metadata for the done row.
   * When the milestone was completed we prefer its
   * `completed_at` timestamp over the target_date so the "when"
   * reads truthfully.  UK long format ("6 Aug 2026") anchors
   * ownership and audit alongside the participant initials on
   * a single metadata block. */
  const _completedAtIso = isDone ? (item.completed_at || item.completedAt || null) : null;
  const _completedDateLabel = _completedAtIso
    ? fmtDateLong(_completedAtIso, "")
    : "";
  const _completedByName = isDone ? (item.completed_by_name || item.completedByName || "") : "";
  const _roleLabel = OWNER_LABEL[item.owner_role] || item.owner_role || "";
  const _rolePersonTitle = _completedByName
    ? (_roleLabel ? `${_completedByName} · ${_roleLabel}` : _completedByName)
    : (_roleLabel || undefined);
  return (
    <li
      /* iter-alignment · P · Current Stage 3-column grid layout
       *   (2026-08-18).  Applies universally — this is the sole
       *   ChecklistRow implementation used by BOTH the canonical
       *   Current Stage list AND the historical/viewed-stage
       *   panel (e.g. Contract while canonical stage is Mortgage).
       *   No second checklist component is introduced.
       *
       *   Columns:
       *     1. Milestone  — tick + label (majority of the row so
       *                     normal milestone names never truncate)
       *     2. Owner      — full role name, no initials/avatars
       *     3. Date       — target-date pill (or completion-date
       *                     pill when done); Requested pill,
       *                     Overdue chip, Request/Chase button
       *                     and blocker pill all right-align
       *                     inside the same column so eligible
       *                     rows keep the action compact.
       *
       *   True workflow order is preserved — completed items are
       *   NEVER regrouped.  The <li> is still a native list item so
       *   the container `<ul>` semantic structure is unchanged. */
      className={`group w-full grid grid-cols-[minmax(0,1fr)_140px_150px] items-center gap-3 py-1.5 px-2 rounded-md transition-colors duration-150 ${
        interactive ? "hover:bg-slate-50/80" : ""
      } ${rowTone}`}
      data-state={isDone ? "Complete" : isBlocked ? "Blocked" : "Pending"}
      data-optional={isOptional ? "true" : undefined}
      data-overdue={isOverdue ? "true" : undefined}
      data-requested-at={requestedAtIso || undefined}
      data-layout="grid-3col"
    >
      <button
        type="button"
        onClick={handleClick}
        disabled={!interactive}
        className={`col-start-1 col-end-2 flex items-center gap-2.5 min-w-0 text-left ${
          interactive ? "cursor-pointer" : "cursor-default"
        } disabled:opacity-100 cf-press`}
        data-testid={`canonical-eve-row-${item.id}`}
        aria-label={
          isDone
            ? `${item.label} · complete · click to un-tick`
            : `Mark ${item.label} complete`
        }
      >
        {isDone ? (
          <span
            className="w-[16px] h-[16px] rounded-full bg-emerald-500 flex items-center justify-center shrink-0 cf-scale-in"
            aria-hidden
          >
            <Check size={10} strokeWidth={3.6} className="text-white" />
          </span>
        ) : isBlocked ? (
          <span
            className="w-[16px] h-[16px] rounded-full border-[1.5px] border-rose-400 bg-rose-50 flex items-center justify-center shrink-0"
            aria-hidden
          >
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
          </span>
        ) : (
          <span
            className={`w-[16px] h-[16px] rounded-full border-[1.5px] shrink-0 transition-colors duration-150 ${
              interactive ? "border-amber-400 group-hover:border-amber-500 group-hover:bg-amber-50/40" : "border-slate-300"
            }`}
            aria-hidden
          />
        )}
        <span className="flex-1 min-w-0">
          <span
            className={`block text-[13px] leading-[1.35] truncate ${
              isDone
                ? "text-slate-400 font-normal"
                : isBlocked
                  ? "text-rose-900 font-medium"
                  : "text-slate-800 font-medium"
            }`}
          >
            {isOptional && !isDone && !isBlocked && (
              <span
                className="mr-1.5 text-slate-300 text-[10px] align-middle"
                aria-hidden
                data-testid={`canonical-eve-row-${item.id}-optional-marker`}
              >
                ◇
              </span>
            )}
            {item.label}
          </span>
          {item.note && (
            <span
              className="mt-0.5 block text-[11px] leading-snug text-slate-500 truncate"
              title={item.note}
              data-testid={`canonical-eve-row-${item.id}-note`}
            >
              {item.note}
            </span>
          )}
        </span>
      </button>
      {/* ── Column 2 · Owner ──────────────────────────────────────
       *   Full role name from OWNER_LABEL — no initials/avatars.
       *   Rendered on every row (including completed), matching
       *   the image reference.  When the row is complete AND the
       *   backend surfaces a `completed_by_name` on the milestone
       *   meta, the hover title discloses "Completed by X (Role)"
       *   at scan speed. */}
      <span
        className={`col-start-2 col-end-3 truncate text-[12px] leading-[1.35] font-medium ${
          isDone ? "text-slate-400" : "text-violet-700"
        }`}
        data-testid={`canonical-eve-row-${item.id}-role`}
        data-owner-role={item.owner_role || "unknown"}
        title={
          isDone && _completedByName
            ? `Completed by ${_completedByName}${_roleLabel ? ` (${_roleLabel})` : ""}`
            : (OWNER_LABEL[item.owner_role] || item.owner_role || "Unassigned")
        }
      >
        {OWNER_LABEL[item.owner_role] || item.owner_role || "Unassigned"}
      </span>

      {/* ── Column 3 · Date ────────────────────────────────────
       *   The primary date pill (target-date OR completion-date)
       *   sits inline with the Requested pill + Overdue chip
       *   AND, on eligible rows only, a compact right-aligned
       *   Request/Chase action + blocker pill.  Merging the
       *   former Column 4 into this cell frees Column 1 to hold
       *   the majority of the row width so normal milestone
       *   names no longer truncate.  The exact pill components
       *   (`MilestoneDatePill`, `MilestoneBlockerPill`) are
       *   preserved verbatim — no behaviour change to editing
       *   or clearing dates, requesting docs, or resolving
       *   blockers. */}
      <div
        className="col-start-3 col-end-4 flex justify-end items-center gap-1.5 min-w-0"
        data-testid={`canonical-eve-row-${item.id}-target-col`}
      >
      {canEditDate ? (
        <MilestoneDatePill
          milestoneId={item.id}
          value={item.target_date || null}
          onSave={onSetTargetDate}
        />
      ) : canEditCompletionDate ? (
        <MilestoneDatePill
          milestoneId={item.id}
          value={_completedAtIso ? String(_completedAtIso).slice(0, 10) : null}
          onSave={async (mid, iso) => {
            await onEditCompletionDate(mid, iso || null);
          }}
        />
      ) : isDone && _completedDateLabel ? (
        <span
          className="text-[11px] font-medium text-slate-500 tabular-nums leading-tight"
          data-testid={`canonical-eve-row-${item.id}-completed-at`}
          title={_completedByName ? `Completed by ${_completedByName}${_roleLabel ? ` (${_roleLabel})` : ""} · ${_completedDateLabel}` : `Completed · ${_completedDateLabel}`}
        >
          {_completedDateLabel}
        </span>
      ) : item.target_date ? (
        <span
          className={`text-[11px] tabular-nums ${isDone ? "text-slate-300" : "text-slate-400"}`}
          data-testid={`canonical-eve-row-${item.id}-target`}
          title={[`Target · ${item.target_date}`, item.secondary_date ? `Expiry · ${item.secondary_date}` : null, item.secondary_note ? item.secondary_note : null].filter(Boolean).join(" · ")}
        >
          {item.target_date}
          {(item.secondary_date || item.secondary_note) && (
            <span
              className="ml-1 text-slate-300"
              data-testid={`canonical-eve-row-${item.id}-secondary`}
            >
              · {item.secondary_date || item.secondary_note}
            </span>
          )}
        </span>
      ) : null}
      {docActionMode === "chase" && requestedAtIso && !isDone && (
        <span
          className="shrink-0 inline-flex items-center h-6 px-1.5 rounded text-[10.5px] font-medium text-slate-500 bg-slate-50 ring-1 ring-slate-200/80 tabular-nums"
          data-testid={`canonical-eve-row-${item.id}-requested-at`}
          data-requested-at={requestedAtIso}
          title={`Requested ${fmtDateLong(requestedAtIso, "")}`}
        >
          Requested · {fmtDateLong(requestedAtIso, "")}
        </span>
      )}
      {isOverdue && (
        <span
          className="shrink-0 inline-flex items-center h-6 px-1.5 rounded text-[10.5px] font-semibold text-amber-700 bg-amber-50 ring-1 ring-amber-200/80"
          data-testid={`canonical-eve-row-${item.id}-overdue`}
        >
          Overdue
        </span>
      )}
      {/* iter-alignment · P · Request action + blocker pill —
       *   Chase remains inline in the Date column (a reminder is
       *   time-sensitive so it stays permanently visible on
       *   awaiting rows).  The initial Request affordance moved
       *   into the ••• row menu below to keep the row compact.
       *   Blocker pill still surfaces on rose-tinted rows. */}
      {canDocAction && docActionMode === "chase" && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDocAction(item.id, docActionMode, docActionTemplateId);
          }}
          className="shrink-0 inline-flex items-center gap-1 h-6 px-2 rounded-md text-[10.5px] font-medium cf-press transition bg-amber-50/70 text-amber-700 hover:bg-amber-100"
          data-testid={`canonical-eve-row-${item.id}-doc-action`}
          data-doc-action={docActionMode}
          title={`Send reminder${chaseLevel ? ` (T+${chaseLevel === 1 ? 3 : chaseLevel === 2 ? 5 : 7})` : ""}`}
          aria-label={`Chase ${item.label}`}
        >
          <Bell size={10} strokeWidth={2.2} aria-hidden />
          Chase
        </button>
      )}
      {canEditBlocker && (
        <MilestoneBlockerPill
          milestoneId={item.id}
          blocker={stageBlocker}
          onResolve={onResolveBlocker}
        />
      )}
      {(onSetNote || (canDocAction && docActionMode === "request")) && canComplete && !isDone && (
        <MilestoneRowMenu
          milestone={item}
          onSetNote={onSetNote}
          canRequest={!!(canDocAction && docActionMode === "request")}
          onRequest={
            canDocAction && docActionMode === "request" && onDocAction
              ? () => onDocAction(item.id, docActionMode, docActionTemplateId)
              : null
          }
        />
      )}
      </div>
    </li>
  );
};

/* Compact row-action menu — hosts "Add / Edit note" and, on
 * eligible rows, "Request update".  Stays hidden until the row
 * is hovered / focused so completed and read-only rows stay
 * clean.  Reuses the shared Popover primitive so it never
 * escapes the row's stacking context. */
const MilestoneRowMenu = ({ milestone, onSetNote, canRequest, onRequest }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [noteEditorOpen, setNoteEditorOpen] = useState(false);
  const [draft, setDraft] = useState(milestone?.note || "");
  const [saving, setSaving] = useState(false);
  const existing = milestone?.note || "";
  useEffect(() => {
    if (noteEditorOpen) setDraft(existing);
  }, [noteEditorOpen, existing]);
  const commit = async () => {
    if (saving || !onSetNote) return;
    setSaving(true);
    try {
      await onSetNote(milestone.id, draft.trim());
      setNoteEditorOpen(false);
      setMenuOpen(false);
    } catch (_e) {
      // Parent surfaces its own toast; keep the editor open so
      // the operator can retry.
    } finally {
      setSaving(false);
    }
  };
  const clearNote = async () => {
    if (saving || !onSetNote) return;
    setSaving(true);
    try {
      await onSetNote(milestone.id, "");
      setNoteEditorOpen(false);
      setMenuOpen(false);
    } catch (_e) { /* noop */ } finally {
      setSaving(false);
    }
  };
  return (
    <span
      className="relative shrink-0 inline-flex items-center"
      onClick={(e) => e.stopPropagation()}
    >
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center justify-center w-6 h-6 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 opacity-60 group-hover:opacity-100 focus:opacity-100 transition"
            aria-label={`Row actions for ${milestone.label}`}
            title="Row actions"
            data-testid={`canonical-eve-row-${milestone.id}-menu`}
          >
            <MoreHorizontal size={14} />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          sideOffset={4}
          className="p-1 w-[200px] rounded-lg border border-slate-200 shadow-lg bg-white"
          onClick={(e) => e.stopPropagation()}
          data-testid={`canonical-eve-row-${milestone.id}-menu-popover`}
        >
          {onSetNote && (
            <button
              type="button"
              onClick={() => { setNoteEditorOpen(true); setMenuOpen(false); }}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-left text-[12px] font-medium text-slate-700 rounded-md hover:bg-slate-50"
              data-testid={`canonical-eve-row-${milestone.id}-menu-note`}
            >
              <Pencil size={12} strokeWidth={2} className="text-slate-400" aria-hidden />
              {existing ? "Edit note" : "Add note"}
            </button>
          )}
          {canRequest && onRequest && (
            <button
              type="button"
              onClick={() => { onRequest(); setMenuOpen(false); }}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-left text-[12px] font-medium text-slate-700 rounded-md hover:bg-slate-50"
              data-testid={`canonical-eve-row-${milestone.id}-menu-request`}
            >
              <Send size={12} strokeWidth={2} className="text-slate-400" aria-hidden />
              Request update
            </button>
          )}
        </PopoverContent>
      </Popover>
      {noteEditorOpen && (
        <div
          className="fixed inset-0 z-[60] bg-slate-900/40 backdrop-blur-[2px] flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
          data-testid={`canonical-eve-row-${milestone.id}-note-modal`}
          onClick={(e) => { if (e.target === e.currentTarget) setNoteEditorOpen(false); }}
        >
          <div className="w-full max-w-[400px] rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 p-5">
            <p className="text-[10.5px] uppercase tracking-[0.10em] font-semibold text-slate-500">
              Milestone note
            </p>
            <h3 className="mt-1 text-[14px] font-bold text-slate-900 tracking-tight">
              {milestone?.label || "Milestone"}
            </h3>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              maxLength={280}
              placeholder="Add a short note (e.g. Seller solicitor advised revised contract due Friday)"
              className="mt-3 w-full px-2.5 py-2 text-[13px] rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-violet-200 resize-none"
              autoFocus
              data-testid={`canonical-eve-row-${milestone.id}-note-input`}
            />
            <div className="mt-3 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={clearNote}
                disabled={saving || !existing}
                className="text-[11.5px] font-semibold text-slate-500 hover:text-rose-600 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`canonical-eve-row-${milestone.id}-note-clear`}
              >
                Clear note
              </button>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setNoteEditorOpen(false)}
                  disabled={saving}
                  className="h-7 px-2.5 rounded-md text-[11.5px] font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                  data-testid={`canonical-eve-row-${milestone.id}-note-cancel`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={commit}
                  disabled={saving}
                  className="h-7 px-3 rounded-md text-[11.5px] font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-1"
                  data-testid={`canonical-eve-row-${milestone.id}-note-save`}
                >
                  {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                  Save note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </span>
  );
};

/* iter-alignment · P · Target Date · UK Popover Rewrite
 * (2026-02-14)
 * ----------------------------------------------------------------
 * The pill still renders the same visual as before, but tapping it
 * now opens a Radix Popover carrying the shadcn Calendar
 * (`react-day-picker`) with UK conventions applied:
 *
 *   • `weekStartsOn={1}` — Monday-first calendar rows.
 *   • Display format `DD/MM/YYYY` for both the trigger pill and
 *     the popover header.
 *   • Wire format kept as canonical ISO `YYYY-MM-DD` so every
 *     downstream consumer (backend `POST /milestones/:id/target`,
 *     `journeyState.steps[i].target_date`, RightRail chip, Deal
 *     Rooms list Target Dates column) receives the exact same
 *     string it always has.
 *
 * Save semantics:
 *
 *   • Selecting a date on the calendar updates ONLY the local
 *     pending state — nothing is written to the SSOT.
 *   • Explicit `OK` commits the pending value via `onSave` (the
 *     shared handler the parent wires through
 *     `handleSetMilestoneTargetDate` → `reload()`), so every
 *     target-date surface in the room refreshes from the same
 *     canonical value the moment the round-trip completes.
 *   • `Clear date` stages a `null` pending value — it only
 *     commits when `OK` is tapped afterwards.
 *   • `Cancel` and click-away close the popover with the
 *     existing SSOT value untouched — the pending state is
 *     discarded.
 *
 * No new endpoint, no duplicate SSOT — pure client-side buffer
 * around the existing `onSave(milestoneId, iso | null)` contract.
 */

/* Format a canonical `YYYY-MM-DD` (or null) as UK `DD/MM/YYYY`.
 * The parser is tolerant of surrounding whitespace and the (very
 * common in seed data) leading ISO datetime shape. */
const _ukFormat = (iso) => {
  if (!iso) return "";
  const raw = String(iso).trim();
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return raw;
  return `${m[3]}/${m[2]}/${m[1]}`;
};

/* Parse a canonical `YYYY-MM-DD` into a local-timezone `Date` for
 * react-day-picker.  Constructed via component-parts to sidestep
 * the browser's `new Date("2026-02-14")` UTC-midnight interpretation
 * (which shifts the day in negative-UTC locales). */
const _isoToLocalDate = (iso) => {
  if (!iso) return undefined;
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

/* Format a `Date` back into canonical `YYYY-MM-DD` (local). */
const _dateToIso = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

/* Popover shell — trigger pill + Radix Popover carrying the shared
 * shadcn Calendar with UK conventions (`weekStartsOn={1}`, UK
 * `DD/MM/YYYY` header + trigger label).  Clear · Cancel · OK
 * actions live in the popover footer; the pill NEVER writes to the
 * SSOT directly — every state change is routed through the parent
 * `onOK` / `onCancel` / `onClear` callbacks. */
const TargetDatePopover = ({
  milestoneId,
  open,
  onOpenChange,
  display,
  pending,
  pendingDisplay,
  saving,
  onSelectDate,
  onClear,
  onCancel,
  onOK,
}) => {
  const selected = _isoToLocalDate(pending);
  const triggerLabel = display || "date";
  return (
    <span
      className="relative shrink-0 inline-flex items-center"
      onClick={(e) => e.stopPropagation()}
      data-testid={`canonical-eve-row-${milestoneId}-date-pill`}
    >
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={saving}
            className={`inline-flex items-center gap-1 h-5 px-1.5 rounded-md ring-1 text-[10.5px] font-medium tabular-nums cf-press transition ${
              display
                ? "bg-white ring-slate-200 text-slate-500 hover:ring-violet-300 hover:text-violet-700"
                : "bg-white ring-dashed ring-slate-200 text-slate-400 hover:ring-violet-300 hover:text-violet-700"
            } ${saving ? "opacity-60" : ""}`}
            aria-label={display ? `Edit target date (currently ${display})` : "Set milestone target date"}
            title={display ? `Target · ${display} · click to edit` : "Set target date"}
            data-testid={`canonical-eve-row-${milestoneId}-date-trigger`}
          >
            {saving ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <>
                {display ? (
                  <span className="w-1 h-1 rounded-full bg-current opacity-50" aria-hidden />
                ) : (
                  <Calendar size={10} strokeWidth={2.2} className="opacity-70" aria-hidden />
                )}
                {triggerLabel}
              </>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={6}
          className="p-0 w-auto rounded-xl border border-slate-200 shadow-lg bg-white"
          data-testid={`canonical-eve-row-${milestoneId}-date-popover`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 pt-2.5 pb-1.5 border-b border-slate-100 flex items-center justify-between gap-2">
            <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-500">Target date</p>
            <p
              className="text-[11.5px] font-semibold text-slate-800 tabular-nums"
              data-testid={`canonical-eve-row-${milestoneId}-date-popover-value`}
            >
              {pendingDisplay || "—"}
            </p>
          </div>
          <CalendarPicker
            mode="single"
            weekStartsOn={1}
            selected={selected}
            onSelect={(d) => onSelectDate(d || null)}
            initialFocus
            data-testid={`canonical-eve-row-${milestoneId}-date-calendar`}
          />
          <div className="px-3 pb-2.5 pt-1 flex items-center justify-between gap-2 border-t border-slate-100">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onClear(); }}
              disabled={saving || !pending}
              className="text-[11.5px] font-semibold text-slate-500 hover:text-rose-600 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`canonical-eve-row-${milestoneId}-date-clear`}
            >
              Clear date
            </button>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onCancel(); }}
                disabled={saving}
                className="h-7 px-2.5 rounded-md text-[11.5px] font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                data-testid={`canonical-eve-row-${milestoneId}-date-cancel`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onOK(); }}
                disabled={saving}
                className="h-7 px-3 rounded-md text-[11.5px] font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-1"
                data-testid={`canonical-eve-row-${milestoneId}-date-ok`}
              >
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                OK
              </button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </span>
  );
};

const MilestoneDatePill = ({ milestoneId, value, onSave }) => {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(value || null);
  const [saving, setSaving] = useState(false);

  /* Re-seed the pending state every time the popover opens so
   * cancelled edits never leak into the next session. */
  useEffect(() => {
    if (open) setPending(value || null);
  }, [open, value]);

  const commit = async () => {
    if (saving) return;
    const next = pending || null;
    if (next === (value || null)) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(milestoneId, next);
      setOpen(false);
    } catch (err) {
      // Parent handler surfaces its own toast on failure —
      // keep the popover open so the operator can retry.
      console.warn("Milestone target save failed", err);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    if (saving) return;
    // Discard pending state and close — SSOT value is untouched.
    setPending(value || null);
    setOpen(false);
  };

  const clear = () => {
    // Stage a null; commit still requires an explicit OK.
    setPending(null);
  };

  const display = _ukFormat(value);
  const pendingDisplay = _ukFormat(pending);

  return (
    <TargetDatePopover
      milestoneId={milestoneId}
      open={open}
      onOpenChange={(next) => {
        if (!next && !saving) {
          // Radix fires this for both Escape and click-away —
          // treat both as "Cancel" per the spec.
          setPending(value || null);
        }
        setOpen(next);
      }}
      display={display}
      pending={pending}
      pendingDisplay={pendingDisplay}
      saving={saving}
      onSelectDate={(d) => setPending(_dateToIso(d))}
      onClear={clear}
      onCancel={cancel}
      onOK={commit}
    />
  );
};

/* iter-alignment · P15 — Compact inline blocker pill.
 * Renders a small rose pill on rows whose stage has an unresolved
 * blocker (owner-role matched).  Clicking the pill calls the
 * shared `onResolveBlocker` handler — the SAME
 * `handleCriticalPathResolved` the Critical Path card uses.  Undo
 * (15s), audit row + reload semantics all come along for free.
 *
 * We stop propagation so clicking the pill never toggles the
 * outer completion button; the pill's own `<button>` is
 * transient (busy state during the round-trip).
 */
const _BLOCKER_SEVERITY_TONE = {
  blocking: { dot: "bg-rose-500",   ring: "ring-rose-200",   text: "text-rose-700",   bg: "bg-rose-50",    hover: "hover:ring-rose-400" },
  warning:  { dot: "bg-amber-500",  ring: "ring-amber-200",  text: "text-amber-700",  bg: "bg-amber-50",   hover: "hover:ring-amber-400" },
  info:     { dot: "bg-sky-500",    ring: "ring-sky-200",    text: "text-sky-700",    bg: "bg-sky-50",     hover: "hover:ring-sky-400" },
};

const MilestoneBlockerPill = ({ milestoneId, blocker, onResolve }) => {
  const [busy, setBusy] = useState(false);
  const tone = _BLOCKER_SEVERITY_TONE[blocker?.severity] || _BLOCKER_SEVERITY_TONE.warning;
  const label = blocker?.title || "Blocker";
  const handleClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await onResolve(blocker, {
        source: "current_stage_row_pill",
        note: `Blocker cleared inline from Current Stage · ${label}`,
      });
    } catch (err) {
      // Parent toasts on failure — just release the busy state.
      console.warn("Blocker resolve failed", err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      onClickCapture={(e) => e.stopPropagation()}
      className={`shrink-0 inline-flex items-center gap-1 h-6 px-2 rounded-md ring-1 text-[11px] font-medium max-w-[160px] cf-press transition ${tone.bg} ${tone.ring} ${tone.text} ${tone.hover} ${busy ? "opacity-60" : ""}`}
      title={
        blocker?.next_action
          ? `${label} · ${blocker.next_action} · click to clear`
          : `${label} · click to clear`
      }
      aria-label={`Clear blocker: ${label}`}
      data-testid={`canonical-eve-row-${milestoneId}-blocker-pill`}
      data-blocker-id={String(blocker?.id || blocker?.step_id || "")}
    >
      {busy ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <>
          <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} aria-hidden />
          <span className="truncate">{label}</span>
        </>
      )}
    </button>
  );
};

/* iter-alignment · P26 — StageChecklist wraps the current-stage
 * checklist list so we can own a small "just completed" flash
 * flag locally (React hooks can't live inside the parent's IIFE).
 * All ordering, blocker mapping, permissions, prerequisite gating
 * and SSOT resolution stay in the parent — this component only
 * renders the visual container + attaches a brief emerald ring
 * when the operator ticks the final actionable milestone of the
 * stage.  Understated, uses the existing `stepLabel` +
 * `nextStepLabel` copy the panel already computes — no new data,
 * no new endpoint. */
const StageChecklist = ({
  orderedMilestones,
  canCompleteSet,
  onCompleteMilestone,
  onReopenMilestone,        // iter-alignment · P0 · Tick-First — reopen handler
  onEditCompletionDate,     // iter-alignment · P0 · Tick-First — retrospective completion date edit
  onSetTargetDate,
  onSetNote,                // Contract-stage refinement — persist milestone note via canonical POST /note
  rowBlockerMap,
  onResolveBlocker,
  isViewingLocked,
  isViewingEditable,
  stageScopedItems,
  stepLabel,
  nextStepLabel,
  docActionByMilestone,  // Map milestoneId → { mode, templateId, chaseLevel }
  onDocAction,           // fn milestoneId, mode, templateId → void
  expandOptional = false, // Survey via Key Dates — show all three survey rows
  awaitingConfirmation = false, // Confirm Exchange / Completion gate still open
}) => {
  const prevAllDone = useRef(false);
  const [flash, setFlash] = useState(false);
  // iter-alignment · P46 — "Show optional items" toggle.  Reuses
  // the existing `optional` SSOT field on each milestone — no new
  // state on the server, no duplicate logic.  Required items
  // render by default; the toggle only surfaces when the current
  // stage actually carries optional rows.
  const [showOptional, setShowOptional] = useState(false);
  useEffect(() => { if (expandOptional) setShowOptional(true); }, [expandOptional]);
  const requiredMilestones = orderedMilestones.filter((m) => !m?.optional);
  const optionalMilestones = orderedMilestones.filter((m) => m?.optional);
  const hasOptional = optionalMilestones.length > 0;
  const allDone = stageScopedItems.length > 0 && !awaitingConfirmation
    && stageScopedItems.every(
      (m) => m?.optional === true || resolveMilestoneState(m) === "Complete",
    );
  // The flash names the canonical current stage, so it only fires while that stage is in view.
  const isViewingOtherStage = !!(isViewingLocked || isViewingEditable);
  useEffect(() => {
    if (isViewingOtherStage) setFlash(false);
    if (allDone && !prevAllDone.current) {
      prevAllDone.current = true;
      if (isViewingOtherStage) return undefined;
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 2400);
      return () => clearTimeout(t);
    }
    if (!allDone) prevAllDone.current = false;
  }, [allDone, isViewingOtherStage]);
  /* iter-alignment · P53 — Preserve scroll position on the optional
   * toggle.  Expanding the optional section grows the layout below
   * the toggle; without this the browser can lose the operator's
   * place in tall Deal Rooms.  We capture the toggle button's
   * viewport position BEFORE the state flips and restore it AFTER
   * paint so the button stays anchored to the same pixel row. */
  const toggleRef = useRef(null);
  const anchorBeforeRef = useRef(null);
  useLayoutEffect(() => {
    const btn = toggleRef.current;
    const anchor = anchorBeforeRef.current;
    if (btn && anchor != null) {
      const after = btn.getBoundingClientRect().top;
      const delta = after - anchor;
      if (Math.abs(delta) > 1) window.scrollBy({ top: delta, behavior: "instant" });
      anchorBeforeRef.current = null;
    }
  }, [showOptional]);
  const handleToggleOptional = () => {
    const btn = toggleRef.current;
    anchorBeforeRef.current = btn ? btn.getBoundingClientRect().top : null;
    setShowOptional((v) => !v);
  };
  const _rowProps = (m) => {
    const action = docActionByMilestone?.get?.(m.id) || null;
    return {
      item: m,
      canComplete: canCompleteSet.has(m.id),
      onComplete: isViewingLocked ? undefined : onCompleteMilestone,
      onReopen: isViewingLocked ? undefined : onReopenMilestone,
      onEditCompletionDate: isViewingLocked ? undefined : onEditCompletionDate,
      onSetTargetDate: isViewingLocked ? undefined : onSetTargetDate,
      onSetNote: isViewingLocked ? undefined : onSetNote,
      stageBlocker: rowBlockerMap.get(m.id) || null,
      onResolveBlocker: isViewingLocked ? undefined : onResolveBlocker,
      busy: false,
      docActionMode: action ? action.mode : null,
      docActionTemplateId: action ? action.templateId : null,
      chaseLevel: action ? action.chaseLevel || null : null,
      // iter-alignment · P — Request status pill (2026-08-17).
      // Threaded through so the row can render
      // "Requested · {date}" without a new backend fetch — the
      // timestamp already lives on the open request record.
      requestedAtIso: action ? action.requestedAtIso || null : null,
      onDocAction: isViewingLocked ? undefined : onDocAction,
    };
  };
  return (
    <div
      className={`rounded-lg overflow-hidden bg-white transition-all duration-500 ${flash && !isViewingOtherStage ? "ring-1 ring-emerald-300 shadow-[0_0_0_4px_rgba(16,185,129,0.10)]" : "ring-1 ring-slate-200/70"}`}
      data-testid="canonical-current-stage-checklist"
      data-viewing-mode={isViewingLocked ? "locked" : isViewingEditable ? "editable" : "canonical"}
      data-stage-complete-flash={flash ? "true" : undefined}
    >
      {/* iter-alignment · P · Current Stage 3-column header
       *   (2026-08-18).  Column headers mirror the ChecklistRow
       *   grid template exactly — Milestone | Owner | Date.
       *   Request lives as a compact right-aligned action inside
       *   the Date cell on eligible rows, so it no longer needs
       *   its own header column.  Shared across every stage
       *   view (canonical AND historical). */}
      <div
        className="grid grid-cols-[minmax(0,1fr)_140px_150px] gap-3 px-2 py-1.5 border-b border-slate-100/80 bg-slate-50/50 text-[10.5px] uppercase tracking-[0.10em] font-semibold text-slate-500"
        data-testid="canonical-current-stage-checklist-header"
      >
        <span>Milestone</span>
        <span>Owner</span>
        <span className="text-right">Date</span>
      </div>
      <ul className="px-1.5 py-1">
        {requiredMilestones.map((m) => (
          <ChecklistRow key={m.id} {..._rowProps(m)} />
        ))}
      </ul>
      {/* iter-alignment · P46 — Optional milestones section.  Toggle
          only surfaces when the current stage actually carries
          optional rows.  Reveals a distinct "Optional" section
          beneath the required checklist with lighter emphasis so
          the required list stays the primary focus.  Progression,
          Exchange Readiness and permissions are untouched — this
          purely gates the render. */}
      {hasOptional && (
        <div
          className="border-t border-slate-100/70"
          data-testid="canonical-current-stage-optional"
          data-open={showOptional ? "1" : "0"}
        >
          <button
            ref={toggleRef}
            type="button"
            onClick={handleToggleOptional}
            className="w-full inline-flex items-center justify-between gap-2 px-3 py-1.5 text-[11.5px] font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-50/70 transition-colors focus:outline-none focus:ring-1 focus:ring-slate-300"
            aria-expanded={showOptional}
            data-testid="canonical-current-stage-optional-toggle"
          >
            <span className="inline-flex items-center gap-1.5">
              {showOptional ? <ChevronDown size={12} strokeWidth={2.2} aria-hidden /> : <ChevronRight size={12} strokeWidth={2.2} aria-hidden />}
              {showOptional ? "Hide optional items" : "Show optional items"}
            </span>
            <span className="tabular-nums text-slate-400">{optionalMilestones.length}</span>
          </button>
          {showOptional && (
            <div className="bg-slate-50/40" data-testid="canonical-current-stage-optional-list">
              <p className="px-3 pt-1.5 pb-0.5 text-[10.5px] uppercase tracking-[0.10em] font-semibold text-slate-400">
                Optional
              </p>
              <ul className="px-1.5 pb-1 opacity-95">
                {optionalMilestones.map((m) => (
                  <ChecklistRow key={m.id} {..._rowProps(m)} />
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {flash && !isViewingOtherStage && (
        <p
          className="px-3 py-1.5 text-[11.5px] font-medium text-emerald-700 bg-emerald-50/70 border-t border-emerald-100 cf-fade-in inline-flex items-center gap-1.5"
          data-testid="canonical-current-stage-completed-flash"
        >
          <Check size={11} strokeWidth={2.8} aria-hidden />
          <span>
            {stepLabel} complete
            {nextStepLabel ? <> · advancing to <span className="font-semibold">{nextStepLabel}</span></> : ""}
          </span>
        </p>
      )}
      {!isViewingLocked
        && !flash
        && stageScopedItems.some((m) => m?.optional !== true && resolveMilestoneState(m) !== "Complete")
        && stageScopedItems.every((m) =>
          m?.optional === true
            || resolveMilestoneState(m) === "Complete"
            || !canCompleteSet.has(m.id)
        ) && (
        <p
          className="text-[11px] text-slate-500 px-3 py-1.5 border-t border-slate-100/70 bg-slate-50/40 inline-flex items-center gap-1.5"
          data-testid="canonical-current-stage-blocked-hint"
        >
          <AlertCircle size={11} strokeWidth={2.2} className="text-amber-500 shrink-0" aria-hidden />
          <span>
            Blocked · resolve the outstanding items above or their prerequisites to continue this stage.
          </span>
        </p>
      )}
    </div>
  );
};

/* PropertyCertificatesAccordion — compact collapsible section
 * for optional / non-blocking property documents & certificates
 * (EPC, EICR, Planning/Building Regs, FENSA, Boiler service).
 * Rows live in the dedicated `property_certificates` stage so
 * they NEVER count toward the Contract required total or the
 * journey-circle count.  Language mirrors the existing Pre-
 * exchange checklist accordion (chevron flip, compact count
 * badge, single-column body).  Default state = collapsed. */
const _CERT_STAGE_ID = "property_certificates";
const _CERT_STORE_KEY = "chainflow:certificates:open:v1";
const _loadCertOpen = (dealKey) => {
  if (typeof window === "undefined") return false;
  try {
    const raw = JSON.parse(window.localStorage.getItem(_CERT_STORE_KEY) || "{}");
    return !!raw[dealKey];
  } catch { return false; }
};
const _saveCertOpen = (dealKey, isOpen) => {
  if (typeof window === "undefined") return;
  try {
    const raw = JSON.parse(window.localStorage.getItem(_CERT_STORE_KEY) || "{}");
    raw[dealKey] = !!isOpen;
    window.localStorage.setItem(_CERT_STORE_KEY, JSON.stringify(raw));
  } catch { /* noop */ }
};

const PropertyCertificatesAccordion = ({
  milestones,
  canCompleteSet,
  onCompleteMilestone,
  onReopenMilestone,
  onEditCompletionDate,
  onSetTargetDate,
  onSetNote,
}) => {
  const dealKey = milestones?.deal_id || "unknown";
  const rawItems = Array.isArray(milestones?.items) ? milestones.items : [];
  const certItems = rawItems.filter((m) => m?.stage === _CERT_STAGE_ID);
  const [isOpen, setIsOpen] = useState(() => _loadCertOpen(dealKey));
  useEffect(() => { setIsOpen(_loadCertOpen(dealKey)); }, [dealKey]);
  if (certItems.length === 0) return null;
  const doneCount = certItems.filter((m) => resolveMilestoneState(m) === "Complete").length;
  const toggle = () => {
    setIsOpen((prev) => {
      const next = !prev;
      _saveCertOpen(dealKey, next);
      return next;
    });
  };
  return (
    <section
      className="rounded-lg bg-white ring-1 ring-slate-200/70"
      data-testid="canonical-property-certificates"
      data-open={isOpen ? "1" : "0"}
    >
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left focus:outline-none focus:ring-1 focus:ring-slate-300 rounded-lg"
        aria-expanded={isOpen}
        data-testid="canonical-property-certificates-toggle"
      >
        <span className="flex items-center gap-2 min-w-0">
          <ChevronRight
            size={12}
            strokeWidth={2.2}
            className={`text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`}
            aria-hidden
          />
          <span className="text-[12.5px] font-semibold text-slate-800 tracking-tight">
            Property documents &amp; certificates
          </span>
          <span
            className="inline-flex items-center h-[18px] px-1.5 rounded text-[10px] font-semibold tracking-tight bg-slate-50 text-slate-500 ring-1 ring-slate-200/80 tabular-nums"
            data-testid="canonical-property-certificates-count"
          >
            {doneCount}/{certItems.length}
          </span>
        </span>
        <span className="text-[10.5px] uppercase tracking-[0.10em] font-semibold text-slate-400">
          Optional
        </span>
      </button>
      {isOpen && (
        <div
          className="border-t border-slate-100/70 px-1.5 py-1"
          data-testid="canonical-property-certificates-body"
        >
          <ul>
            {certItems.map((m) => (
              <ChecklistRow
                key={m.id}
                item={m}
                canComplete={canCompleteSet.has(m.id)}
                onComplete={onCompleteMilestone}
                onReopen={onReopenMilestone}
                onEditCompletionDate={onEditCompletionDate}
                onSetTargetDate={onSetTargetDate}
                onSetNote={onSetNote}
                stageBlocker={null}
                onResolveBlocker={undefined}
                busy={false}
                docActionMode={null}
                docActionTemplateId={null}
                chaseLevel={null}
                requestedAtIso={null}
                onDocAction={undefined}
              />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};



/* iter-alignment · P0 · Outstanding Enquiries (2026-02-28).
 * State-driven surface for ad-hoc conveyancing enquiries raised
 * during the Enquiries stage. Distinct SSOT (`deal_enquiries`
 * Mongo collection + `/api/v2/deals/:id/enquiries` endpoints) so
 * it CANNOT affect canonical stage progression, Exchange
 * Confidence or NBA.
 *
 * State machine (manual transitions, MVP):
 *   awaiting_reply  ─►  answer_received  ─►  resolved
 *      ▲                    │
 *      └────────────────────┘  (revert)
 *
 * Row surface:
 *   • Status pill (colour-coded per state)
 *   • Body (inline edit for non-resolved)
 *   • Target chase date (only shown when awaiting a reply)
 *   • Owner role chip (inline edit for non-resolved)
 *   • ••• menu — state transitions + Chase + Delete
 *   • Expand-on-click reveals history: created / answered /
 *     resolved audit rows + optional resolution note +
 *     optional Transaction Library link.
 *
 * Add form: body + target chase date + optional library link.
 */
const _ENQUIRY_OWNER_DEFAULT = "buyer_solicitor";

/* Canonical status vocabulary.  The backend accepts legacy
 * `open` / `complete` as aliases so old rows keep hydrating,
 * but the UI only speaks the three-state vocabulary. */
const _ENQ_STATUS = {
  awaiting_reply:  { label: "Awaiting reply",  tone: "bg-amber-50 text-amber-700 ring-amber-200"   },
  answer_received: { label: "Answer received", tone: "bg-sky-50 text-sky-700 ring-sky-200"         },
  resolved:        { label: "Resolved",        tone: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
};
const _canonicalEnquiryStatus = (raw) => {
  const s = String(raw || "").toLowerCase();
  if (s === "answer_received") return "answer_received";
  if (s === "resolved" || s === "complete") return "resolved";
  return "awaiting_reply";
};

/**
 * EnquiryBodyEditor — inline single-click editor for an enquiry's body text.
 * Reuses the row's visual language (truncate for reading, full-width text
 * input while editing).  Save on Enter/blur, Esc to cancel.  Resolved
 * enquiries render as non-editable text so the audit trail can't be
 * rewritten from the row itself.
 */
const EnquiryBodyEditor = ({ enquiry, isDone, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(enquiry.body || "");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isEditing) setDraft(enquiry.body || "");
  }, [enquiry.body, isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const commit = useCallback(() => {
    const next = draft.trim();
    setIsEditing(false);
    if (next && next !== enquiry.body) {
      onSave(next);
    } else {
      setDraft(enquiry.body || "");
    }
  }, [draft, enquiry.body, onSave]);

  const cancel = useCallback(() => {
    setDraft(enquiry.body || "");
    setIsEditing(false);
  }, [enquiry.body]);

  const startEdit = useCallback((e) => {
    e?.stopPropagation?.();
    if (isDone) return;
    setIsEditing(true);
  }, [isDone]);

  if (isEditing && !isDone) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          else if (e.key === "Escape") { e.preventDefault(); cancel(); }
        }}
        maxLength={400}
        className="flex-1 min-w-0 text-[13px] leading-[1.35] px-2 py-1 rounded-md ring-1 ring-violet-400 focus:outline-none bg-white text-slate-800 font-medium"
        aria-label="Edit enquiry"
        data-testid={`canonical-enquiry-body-input-${enquiry.id}`}
      />
    );
  }

  return (
    <div
      role={isDone ? undefined : "button"}
      tabIndex={isDone ? -1 : 0}
      onClick={startEdit}
      onKeyDown={(e) => {
        if (isDone) return;
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); startEdit(e); }
      }}
      className={`group/body flex-1 min-w-0 flex items-center gap-1.5 rounded-md px-1.5 py-0.5 -mx-0.5 transition ${isDone ? "cursor-default" : "cursor-text hover:bg-violet-50/60"}`}
      data-testid={`canonical-enquiry-body-${enquiry.id}`}
      title={isDone ? undefined : "Click to edit"}
    >
      <span className={`flex-1 text-[13px] leading-[1.35] truncate ${isDone ? "text-slate-500" : "text-slate-800 font-medium"}`}>
        {enquiry.body}
      </span>
      {!isDone && (
        <Pencil
          size={11}
          strokeWidth={2.2}
          className="shrink-0 text-slate-300 opacity-0 group-hover/body:opacity-100 transition"
          aria-hidden
        />
      )}
    </div>
  );
};

/**
 * EnquiryOwnerEditor — tap the owner initials badge on an open enquiry to
 * swap between the two roles that raise / answer enquiries in a UK
 * conveyance (Buyer Solicitor · Seller Solicitor).  Reuses `RoleBadge`
 * verbatim for the visual + `OWNER_LABEL` for the option strings, hitting
 * the same PATCH endpoint as body / target-date edits.  A calm popover
 * anchored to the badge; closes on outside click / Escape / selection.
 */
const _ENQUIRY_OWNER_OPTIONS = ["buyer_solicitor", "seller_solicitor"];
const EnquiryOwnerEditor = ({ enquiry, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const current = enquiry.owner_role || _ENQUIRY_OWNER_DEFAULT;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (role) => {
    setOpen(false);
    if (role !== current) onChange(role);
  };

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="cf-press rounded-md focus:outline-none focus:ring-2 focus:ring-violet-300/60"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Change owner (currently ${OWNER_LABEL[current] || current})`}
        title="Change owner"
        data-testid={`canonical-enquiry-owner-${enquiry.id}`}
      >
        <RoleBadge owner_role={current} />
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute right-0 top-full mt-1 z-20 min-w-[148px] rounded-md bg-white ring-1 ring-slate-200 shadow-[0_8px_28px_-12px_rgba(15,23,42,0.25)] py-1"
          data-testid={`canonical-enquiry-owner-menu-${enquiry.id}`}
        >
          {_ENQUIRY_OWNER_OPTIONS.map((role) => {
            const isActive = role === current;
            return (
              <li key={role}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={(e) => { e.stopPropagation(); pick(role); }}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-[12px] text-left transition ${isActive ? "bg-violet-50/60 text-violet-800 font-semibold" : "text-slate-700 hover:bg-slate-50"}`}
                  data-testid={`canonical-enquiry-owner-option-${enquiry.id}-${role}`}
                >
                  <RoleBadge owner_role={role} />
                  <span className="flex-1 truncate">{OWNER_LABEL[role] || role}</span>
                  {isActive && <Check size={12} strokeWidth={2.6} className="text-violet-600 shrink-0" aria-hidden />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

/* Status pill — colour-coded state chip surfaced on every row. */
const EnquiryStatusPill = ({ status }) => {
  const canonical = _canonicalEnquiryStatus(status);
  const spec = _ENQ_STATUS[canonical];
  return (
    <span
      className={`shrink-0 inline-flex items-center h-[18px] px-1.5 rounded-md text-[10px] font-semibold tracking-tight ring-1 ${spec.tone}`}
      data-testid={`canonical-enquiry-status-${canonical}`}
      data-status={canonical}
    >
      {spec.label}
    </span>
  );
};

/* Row action menu — state transitions + Chase + Delete.
 * Manual-transition MVP: no auto-advance from a reply landing in
 * the inbox.  Operator chooses the next state explicitly. */
const EnquiryRowMenu = ({ enq, onAction }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const canonical = _canonicalEnquiryStatus(enq.status);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const pick = (action) => { setOpen(false); onAction(action); };
  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="h-6 w-6 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 cf-press transition"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Enquiry actions"
        title="Enquiry actions"
        data-testid={`canonical-enquiry-menu-${enq.id}`}
      >
        <MoreHorizontal size={14} strokeWidth={2.4} />
      </button>
      {open && (
        <ul
          role="menu"
          className="absolute right-0 top-full mt-1 z-20 min-w-[180px] rounded-md bg-white ring-1 ring-slate-200 shadow-[0_8px_28px_-12px_rgba(15,23,42,0.25)] py-1"
          data-testid={`canonical-enquiry-menu-list-${enq.id}`}
        >
          {canonical === "awaiting_reply" && (
            <>
              <li>
                <button
                  type="button"
                  role="menuitem"
                  onClick={(e) => { e.stopPropagation(); pick("mark_answer_received"); }}
                  className="w-full text-left px-2.5 py-1.5 text-[12px] text-slate-700 hover:bg-slate-50"
                  data-testid={`canonical-enquiry-action-mark-answer-${enq.id}`}
                >
                  Mark answer received
                </button>
              </li>
              <li>
                <button
                  type="button"
                  role="menuitem"
                  onClick={(e) => { e.stopPropagation(); pick("chase"); }}
                  className="w-full text-left px-2.5 py-1.5 text-[12px] text-slate-700 hover:bg-slate-50"
                  data-testid={`canonical-enquiry-action-chase-${enq.id}`}
                >
                  Chase now…
                </button>
              </li>
            </>
          )}
          {canonical === "answer_received" && (
            <li>
              <button
                type="button"
                role="menuitem"
                onClick={(e) => { e.stopPropagation(); pick("revert_awaiting"); }}
                className="w-full text-left px-2.5 py-1.5 text-[12px] text-slate-700 hover:bg-slate-50"
                data-testid={`canonical-enquiry-action-revert-${enq.id}`}
              >
                Revert to awaiting reply
              </button>
            </li>
          )}
          {canonical !== "resolved" && (
            <li>
              <button
                type="button"
                role="menuitem"
                onClick={(e) => { e.stopPropagation(); pick("resolve"); }}
                className="w-full text-left px-2.5 py-1.5 text-[12px] text-emerald-700 font-semibold hover:bg-emerald-50"
                data-testid={`canonical-enquiry-action-resolve-${enq.id}`}
              >
                Resolve…
              </button>
            </li>
          )}
          {canonical === "resolved" && (
            <li>
              <button
                type="button"
                role="menuitem"
                onClick={(e) => { e.stopPropagation(); pick("reopen"); }}
                className="w-full text-left px-2.5 py-1.5 text-[12px] text-slate-700 hover:bg-slate-50"
                data-testid={`canonical-enquiry-action-reopen-${enq.id}`}
              >
                Reopen enquiry
              </button>
            </li>
          )}
          <li className="border-t border-slate-100 mt-1 pt-1">
            <button
              type="button"
              role="menuitem"
              onClick={(e) => { e.stopPropagation(); pick("delete"); }}
              className="w-full text-left px-2.5 py-1.5 text-[12px] text-rose-600 hover:bg-rose-50"
              data-testid={`canonical-enquiry-action-delete-${enq.id}`}
            >
              Delete enquiry
            </button>
          </li>
        </ul>
      )}
    </div>
  );
};

/* Row expansion — created / answered / resolved audit stamps +
 * optional resolution note + optional Transaction Library link.
 * Purely display; all edits happen via the row menu / inline
 * editors.  Preserves history so the audit trail is honest even
 * when a status is reverted. */
const _formatEnquiryStamp = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};
const EnquiryHistory = ({ enq, libraryDocs = [] }) => {
  const linkedDoc = enq.document_link_id
    ? (libraryDocs || []).find((d) => String(d?.id) === String(enq.document_link_id))
    : null;
  return (
    <div
      className="mt-1 ml-1 pl-6 pr-2 pb-2 text-[11.5px] leading-[1.45] text-slate-600 space-y-1"
      data-testid={`canonical-enquiry-history-${enq.id}`}
    >
      {enq.created_at && (
        <p>
          <span className="text-slate-400">Raised · </span>
          {_formatEnquiryStamp(enq.created_at)}
          {enq.created_by_name ? ` · ${enq.created_by_name}` : ""}
        </p>
      )}
      {enq.answered_at && (
        <p>
          <span className="text-slate-400">Answer received · </span>
          {_formatEnquiryStamp(enq.answered_at)}
          {enq.answered_by_name ? ` · ${enq.answered_by_name}` : ""}
        </p>
      )}
      {enq.resolved_at && (
        <p>
          <span className="text-slate-400">Resolved · </span>
          {_formatEnquiryStamp(enq.resolved_at)}
          {enq.resolved_by_name ? ` · ${enq.resolved_by_name}` : ""}
        </p>
      )}
      {enq.resolution_note && (
        <p className="rounded-md ring-1 ring-emerald-100 bg-emerald-50/60 px-2 py-1.5 text-emerald-900">
          <span className="font-semibold text-emerald-700 mr-1">Resolution note:</span>
          {enq.resolution_note}
        </p>
      )}
      {linkedDoc && (
        <p>
          <span className="text-slate-400">Linked document · </span>
          <span className="inline-flex items-center gap-1 text-violet-700 font-medium">
            <Link2 size={11} strokeWidth={2.4} aria-hidden />
            {linkedDoc.title || linkedDoc.filename || linkedDoc.name || linkedDoc.id}
          </span>
        </p>
      )}
    </div>
  );
};

/* Inline Resolve form — captures the optional resolution note
 * that lands both on the enquiry SSOT and on the emitted
 * `enquiry_resolved` activity event. */
const EnquiryResolveForm = ({ enq, onCancel, onConfirm }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm(note.trim() || null);
    } finally { setBusy(false); }
  };
  return (
    <div
      className="mt-1 ml-1 pl-6 pr-2 pb-2 space-y-1.5"
      data-testid={`canonical-enquiry-resolve-form-${enq.id}`}
    >
      <label
        htmlFor={`canonical-enquiry-resolve-note-${enq.id}`}
        className="block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500"
      >
        Resolution note <span className="font-normal text-slate-400">(optional)</span>
      </label>
      <textarea
        id={`canonical-enquiry-resolve-note-${enq.id}`}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        maxLength={800}
        placeholder="How was this enquiry resolved?"
        className="w-full text-[12.5px] px-2 py-1.5 rounded-md ring-1 ring-slate-200 focus:ring-emerald-400 focus:outline-none bg-white text-slate-800"
        data-testid={`canonical-enquiry-resolve-note-input-${enq.id}`}
      />
      <div className="flex items-center gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="h-7 px-2.5 rounded-md text-[11.5px] font-semibold text-slate-600 hover:bg-slate-100 cf-press"
          data-testid={`canonical-enquiry-resolve-cancel-${enq.id}`}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className="h-7 px-3 rounded-md text-[11.5px] font-semibold bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white cf-press inline-flex items-center gap-1"
          data-testid={`canonical-enquiry-resolve-confirm-${enq.id}`}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check size={12} strokeWidth={2.6} />}
          Confirm resolve
        </button>
      </div>
    </div>
  );
};

/* iter-alignment · P0 · Auto Chase Nudge (2026-02).
 *
 * Derives a purely visual nudge from the canonical enquiry SSOT
 * — `status` + `target_date` — WITHOUT introducing any new state
 * or reminder collection.  Returns `null` for the "no nudge"
 * cases:
 *   • not `awaiting_reply`   (answer_received / resolved never nudge)
 *   • no target chase date
 *
 * Otherwise returns a small descriptor the row-level chip and any
 * future Command Centre roll-up can read straight through:
 *   { state: 'future' | 'due' | 'overdue',
 *     label: 'Chase overdue by 2d' | 'Chase today' | 'Chase 10 Sep',
 *     daysOverdue: number,
 *     tone:  'slate' | 'amber' | 'rose' }
 */
const _chaseNudge = (enq, now = new Date()) => {
  if (!enq) return null;
  const canonical = _canonicalEnquiryStatus(enq.status);
  if (canonical !== "awaiting_reply") return null;
  const iso = enq.target_date || enq.targetDate || null;
  if (!iso) return null;
  const parts = String(iso).split("-");
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  const due = new Date(y, m - 1, d);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((today - due) / 86400000);
  if (diffDays < 0) {
    return {
      state: "future",
      label: `Chase ${due.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`,
      daysOverdue: 0,
      tone: "slate",
    };
  }
  if (diffDays === 0) {
    return { state: "due", label: "Chase today", daysOverdue: 0, tone: "amber" };
  }
  return {
    state: "overdue",
    label: `Chase overdue by ${diffDays}d`,
    daysOverdue: diffDays,
    tone: "rose",
  };
};

const _CHASE_TONE = {
  slate: "text-slate-500",
  amber: "text-amber-700",
  rose:  "text-rose-700",
};

const EnquiryChaseNudge = ({ enq }) => {
  const nudge = _chaseNudge(enq);
  if (!nudge) return null;
  return (
    <span
      className={`shrink-0 inline-flex items-center text-[11px] font-medium tracking-tight ${_CHASE_TONE[nudge.tone] || "text-slate-500"}`}
      data-testid={`canonical-enquiry-chase-nudge-${enq.id}`}
      data-chase-state={nudge.state}
      title={nudge.label}
    >
      {nudge.label}
    </span>
  );
};

const AdditionalEnquiriesSection = ({ dealId, participants = [], documents = [] }) => {
  const [items, setItems] = useState(null); // null = loading
  const [draft, setDraft] = useState("");
  const [draftTarget, setDraftTarget] = useState(null); // ISO YYYY-MM-DD
  const [draftDocLink, setDraftDocLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [chaseTarget, setChaseTarget] = useState(null); // { enquiry, recipient }
  const [expandedId, setExpandedId] = useState(null);
  const [resolvingId, setResolvingId] = useState(null);

  /* Only completed / library-flagged docs appear in the picker so
   * operators can't reference a placeholder.  Falls back to every
   * document when no completion flag is present (pilot data). */
  const libraryDocs = useMemo(() => {
    const list = Array.isArray(documents) ? documents : [];
    return list.filter((d) => d && d.id);
  }, [documents]);

  const load = useCallback(async () => {
    if (!dealId) return;
    try {
      const { data } = await api.get(`/api/v2/deals/${dealId}/enquiries`);
      setItems(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      // Pilot deals return 404 — treat as "no enquiries yet".
      if (err?.response?.status === 404) { setItems([]); return; }
      setError(err?.response?.data?.detail || "Couldn't load enquiries");
      setItems([]);
    }
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = useCallback(async (e) => {
    e?.preventDefault?.();
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      const payload = {
        body,
        owner_role: _ENQUIRY_OWNER_DEFAULT,
        target_date: draftTarget || null,
        document_link_id: draftDocLink || null,
      };
      const { data } = await api.post(`/api/v2/deals/${dealId}/enquiries`, payload);
      setItems((prev) => [...(prev || []), data]);
      setDraft("");
      setDraftTarget(null);
      setDraftDocLink("");
      toast.success("Enquiry added");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't add enquiry");
    } finally { setBusy(false); }
  }, [dealId, draft, draftTarget, draftDocLink, busy]);

  const patchEnquiry = useCallback(async (enq, payload, toastMsg) => {
    setItems((prev) => (prev || []).map((x) => (x.id === enq.id ? { ...x, ...payload } : x)));
    try {
      const { data } = await api.patch(`/api/v2/deals/${dealId}/enquiries/${enq.id}`, payload);
      // Replace with server truth so audit stamps flow back.
      setItems((prev) => (prev || []).map((x) => (x.id === enq.id ? { ...x, ...data } : x)));
      if (toastMsg) toast.success(toastMsg);
      return data;
    } catch (err) {
      // Roll back optimistic write from the freshest server state.
      await load();
      toast.error("Couldn't update enquiry");
      throw err;
    }
  }, [dealId, load]);

  const handleSetTarget = useCallback(async (enquiryId, iso) => {
    const enq = (items || []).find((x) => x.id === enquiryId);
    if (!enq) return;
    await patchEnquiry(enq, { target_date: iso || null }, iso ? "Chase date updated" : "Chase date cleared");
  }, [items, patchEnquiry]);

  const handleUpdateBody = useCallback(async (enq, nextBody) => {
    const trimmed = (nextBody || "").trim();
    if (!trimmed || trimmed === enq.body) return;
    await patchEnquiry(enq, { body: trimmed }, "Enquiry updated");
  }, [patchEnquiry]);

  const handleUpdateOwner = useCallback(async (enq, nextOwner) => {
    if (!nextOwner || nextOwner === enq.owner_role) return;
    await patchEnquiry(enq, { owner_role: nextOwner }, "Owner updated");
  }, [patchEnquiry]);

  /* iter-alignment · P20 — Chase an open enquiry via the existing
   * Request Update workflow.  Resolves the participant that matches
   * the enquiry's owner_role, then piggybacks on the canonical
   * `unresolved_enquiry` blocker spec so no new backend endpoint,
   * spec or activity type is needed. */
  const handleChase = useCallback((enq) => {
    const wanted = enq.owner_role || _ENQUIRY_OWNER_DEFAULT;
    const match = _findParticipant(participants, wanted);
    const recipient = match?.email
      ? { email: match.email, full_name: _participantName(match) || undefined }
      : null;
    if (!recipient) {
      toast.error(`No ${OWNER_LABEL[wanted] || "owner"} on file to chase`);
      return;
    }
    setChaseTarget({ enquiry: enq, recipient });
  }, [participants]);

  const handleDelete = useCallback(async (enq) => {
    const prev = items;
    setItems((cur) => (cur || []).filter((x) => x.id !== enq.id));
    try {
      await api.delete(`/api/v2/deals/${dealId}/enquiries/${enq.id}`);
      toast.success("Enquiry removed");
    } catch (err) {
      setItems(prev);
      toast.error("Couldn't remove enquiry");
    }
  }, [dealId, items]);

  const handleMenuAction = useCallback(async (enq, action) => {
    if (action === "mark_answer_received") {
      await patchEnquiry(enq, { status: "answer_received" }, "Marked answer received");
      setExpandedId(enq.id);
    } else if (action === "revert_awaiting") {
      await patchEnquiry(enq, { status: "awaiting_reply" }, "Reverted to awaiting reply");
    } else if (action === "reopen") {
      await patchEnquiry(enq, { status: "awaiting_reply" }, "Enquiry reopened");
    } else if (action === "resolve") {
      setExpandedId(enq.id);
      setResolvingId(enq.id);
    } else if (action === "delete") {
      handleDelete(enq);
    } else if (action === "chase") {
      handleChase(enq);
    }
  }, [patchEnquiry, handleDelete, handleChase]);

  const handleConfirmResolve = useCallback(async (enq, note) => {
    await patchEnquiry(enq, {
      status: "resolved",
      resolution_note: note || "",
    }, "Enquiry resolved");
    setResolvingId(null);
  }, [patchEnquiry]);

  const openCount = useMemo(() => {
    const list = items || [];
    return list.filter((x) => {
      const s = _canonicalEnquiryStatus(x.status);
      return s === "awaiting_reply" || s === "answer_received";
    }).length;
  }, [items]);

  const ordered = useMemo(() => {
    const order = { awaiting_reply: 0, answer_received: 1, resolved: 2 };
    return [...(items || [])].sort((a, b) => {
      const sa = order[_canonicalEnquiryStatus(a.status)] ?? 0;
      const sb = order[_canonicalEnquiryStatus(b.status)] ?? 0;
      if (sa !== sb) return sa - sb;
      const ta = a.created_at ? Date.parse(a.created_at) : 0;
      const tb = b.created_at ? Date.parse(b.created_at) : 0;
      return ta - tb;
    });
  }, [items]);

  return (
    <div
      className="mt-3 rounded-lg ring-1 ring-slate-200 bg-white overflow-hidden"
      data-testid="canonical-additional-enquiries"
      data-section="outstanding-enquiries"
    >
      <div className="px-3 py-2 flex items-center justify-between border-b border-slate-100">
        <p
          className="text-[10.5px] uppercase tracking-[0.10em] font-bold text-slate-500"
          data-testid="canonical-outstanding-enquiries-title"
        >
          Outstanding enquiries
        </p>
        <span
          className="text-[11px] text-slate-400 tabular-nums"
          data-testid="canonical-outstanding-enquiries-open-count"
        >
          {items === null ? "…" : `${openCount} open`}
        </span>
      </div>

      {items === null ? (
        <p className="px-3 py-2.5 text-[12px] text-slate-400">Loading…</p>
      ) : ordered.length === 0 ? (
        <p
          className="px-3 py-2.5 text-[12px] text-slate-500"
          data-testid="canonical-outstanding-enquiries-empty"
        >
          No outstanding enquiries.
        </p>
      ) : (
        <ul className="px-2 py-1 divide-y divide-slate-100/80">
          {ordered.map((enq) => {
            const canonical = _canonicalEnquiryStatus(enq.status);
            const isDone = canonical === "resolved";
            const isExpanded = expandedId === enq.id;
            const isResolving = resolvingId === enq.id;
            return (
              <li
                key={enq.id}
                className="group py-1.5 px-1 rounded-md transition"
                data-state={canonical}
                data-testid={`canonical-enquiry-row-${enq.id}`}
              >
                <div className="w-full flex items-center gap-2.5 py-1 px-1 rounded-md hover:bg-slate-50/70">
                  <EnquiryStatusPill status={canonical} />
                  <EnquiryBodyEditor
                    enquiry={enq}
                    isDone={isDone}
                    onSave={(nextBody) => handleUpdateBody(enq, nextBody)}
                  />
                  {canonical === "awaiting_reply" && (
                    <MilestoneDatePill
                      milestoneId={enq.id}
                      value={enq.target_date || null}
                      onSave={handleSetTarget}
                    />
                  )}
                  {canonical === "awaiting_reply" && (
                    <EnquiryChaseNudge enq={enq} />
                  )}
                  {!isDone ? (
                    <EnquiryOwnerEditor
                      enquiry={enq}
                      onChange={(nextOwner) => handleUpdateOwner(enq, nextOwner)}
                    />
                  ) : (
                    <RoleBadge owner_role={enq.owner_role || _ENQUIRY_OWNER_DEFAULT} />
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setExpandedId(isExpanded ? null : enq.id); }}
                    className="shrink-0 h-6 w-6 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 cf-press transition"
                    aria-label={isExpanded ? "Hide history" : "Show history"}
                    title={isExpanded ? "Hide history" : "Show history"}
                    data-testid={`canonical-enquiry-expand-${enq.id}`}
                  >
                    {isExpanded
                      ? <ChevronDown size={13} strokeWidth={2.4} />
                      : <ChevronRight size={13} strokeWidth={2.4} />}
                  </button>
                  <EnquiryRowMenu enq={enq} onAction={(a) => handleMenuAction(enq, a)} />
                </div>
                {isExpanded && !isResolving && (
                  <EnquiryHistory enq={enq} libraryDocs={libraryDocs} />
                )}
                {isResolving && (
                  <EnquiryResolveForm
                    enq={enq}
                    onCancel={() => setResolvingId(null)}
                    onConfirm={(note) => handleConfirmResolve(enq, note)}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Add row — body + target chase date + optional library link. */}
      <form
        onSubmit={handleAdd}
        className="border-t border-slate-100 bg-slate-50/40 px-3 py-2 space-y-2"
        data-testid="canonical-additional-enquiries-add"
      >
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add enquiry (e.g. Buyer asked about roof warranty)"
            className="flex-1 text-[13px] px-2.5 py-1.5 rounded-md ring-1 ring-slate-200 focus:ring-violet-400 focus:outline-none bg-white"
            disabled={busy}
            maxLength={400}
            aria-label="Add enquiry"
            data-testid="canonical-additional-enquiries-input"
          />
          <button
            type="submit"
            disabled={busy || !draft.trim()}
            className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-violet-600 hover:bg-violet-700 disabled:bg-violet-300 text-white text-[12px] font-semibold cf-press"
            data-testid="canonical-additional-enquiries-submit"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus size={14} strokeWidth={2.4} />}
            Add
          </button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-slate-500">
            Chase date
          </label>
          <input
            type="date"
            value={draftTarget || ""}
            onChange={(e) => setDraftTarget(e.target.value || null)}
            className="text-[12px] px-2 py-1 rounded-md ring-1 ring-slate-200 focus:ring-violet-400 focus:outline-none bg-white text-slate-700"
            aria-label="Target chase date"
            data-testid="canonical-additional-enquiries-chase-date"
          />
          <label className="ml-2 text-[10.5px] uppercase tracking-[0.08em] font-semibold text-slate-500">
            Library link <span className="font-normal text-slate-400 normal-case tracking-normal">(optional)</span>
          </label>
          <select
            value={draftDocLink || ""}
            onChange={(e) => setDraftDocLink(e.target.value || "")}
            className="text-[12px] px-2 py-1 rounded-md ring-1 ring-slate-200 focus:ring-violet-400 focus:outline-none bg-white text-slate-700 min-w-[180px] max-w-[260px]"
            aria-label="Link a Transaction Library document"
            data-testid="canonical-additional-enquiries-doc-link"
          >
            <option value="">None</option>
            {libraryDocs.map((d) => (
              <option key={d.id} value={d.id}>
                {d.title || d.filename || d.name || d.id}
              </option>
            ))}
          </select>
        </div>
      </form>
      {error && (
        <p className="px-3 py-1.5 text-[11px] text-rose-600 border-t border-rose-100 bg-rose-50/50">
          {error}
        </p>
      )}
      {chaseTarget && (
        <RequestUpdateSheet
          dealId={dealId}
          blocker={{ id: "unresolved_enquiry", title: chaseTarget.enquiry.body }}
          initialRecipient={chaseTarget.recipient}
          onClose={() => setChaseTarget(null)}
          onSent={() => {
            setChaseTarget(null);
            toast.success("Chase sent");
          }}
        />
      )}
    </div>
  );
};



/* iter-378 — Pre-Exchange Checklist.
 *
 * Compact collapsed card by default (title, progress, blocker
 * count, Open Checklist button). Expands to the classic
 * Legal / Financial / Coordination three-card layout with
 * interactive checklist items. Expand / collapse preference is
 * persisted per-deal in localStorage so operators land back in the
 * state they left the room. Removed for the pilot: Exchange
 * Ready %, duplicate Exchange Confidence readout, completion
 * dates, View all, permanent update/upload buttons, and the
 * "Since your last visit" footer. Blocker context still surfaces
 * via a compact strip at the top of the expanded body. */
const _PRE_EXCH_STORE_KEY = "chainflow:preExchange:open:v1";
const _loadPreExchangeOpen = (dealId) => {
  if (typeof window === "undefined") return false;
  try {
    const raw = JSON.parse(window.localStorage.getItem(_PRE_EXCH_STORE_KEY) || "{}");
    return !!raw[dealId];
  } catch { return false; }
};
const _savePreExchangeOpen = (dealId, isOpen) => {
  if (typeof window === "undefined") return;
  try {
    const raw = JSON.parse(window.localStorage.getItem(_PRE_EXCH_STORE_KEY) || "{}");
    raw[dealId] = !!isOpen;
    window.localStorage.setItem(_PRE_EXCH_STORE_KEY, JSON.stringify(raw));
  } catch { /* noop */ }
};

const ExchangeEve = ({ deal, milestones, onCompleteMilestone, onReopenMilestone, resolvedIds = null }) => {
  const dealKey = deal?.id || "unknown";
  const [isOpen, setIsOpen] = useState(() => _loadPreExchangeOpen(dealKey));
  useEffect(() => { setIsOpen(_loadPreExchangeOpen(dealKey)); }, [dealKey]);
  const toggle = () => {
    setIsOpen((prev) => {
      const next = !prev;
      _savePreExchangeOpen(dealKey, next);
      return next;
    });
  };

  const rawItems = milestones?.items || FALLBACK_MILESTONES;
  const canCompleteSet = new Set(milestones?.can_complete || []);
  /* iter-alignment · P42 — Both the compact Exchange Readiness
   * chip in the property header AND this checklist card derive
   * their state from `deriveExchangeReadiness(deal, milestones)`.
   * They can never disagree because they consume the same SSOT.
   * iter-379 post-exchange override is applied inside the helper. */
  const { items, doneCount, totalCount, isPostExchange } = deriveExchangeReadiness(deal, milestones);
  /* iter-379 — `dealStageId`/`isPostExchange` retained here for
   * the `data-post-exchange` marker used by contract tests. */
  const dealStageId = String(deal?.stage || "").toLowerCase();
  /* iter-379 — Filter out blockers whose stage is already behind the
   * deal's current focus so completed stages stop advertising stale
   * blockers on the Pre-Exchange Checklist card.
   * P0 · Deal Room Data & Behaviour Integrity Pass (2026-08-12) —
   * `resolvedIds` (the same Critical Path sentinel threaded into
   * `useDealTransactionState`) is honoured here so completing a
   * blocker instantly drops the count on the Pre-Exchange
   * Checklist header + the red banner strip below it. */
  const activeBlockers = filterActiveBlockers(deal || {}, {
    applicableNodes: [],
    currentNodeId: null,
    resolvedIds,
  });
  const blockerCount = activeBlockers.length;

  /* iter-alignment · P0 · Pre-exchange Checklist Redesign
   * (2026-02-28) — Category buckets derived from the same
   * canonical `items` (col: legal | financial | coordination).
   * Default open pick: first category with a blocked / overdue
   * row, else with any outstanding row, else Legal (spec).
   * Reveal state: each category surfaces the first 3 incomplete
   * rows; "View remaining N" reveals the rest inline. */
  const _categoryOrder = ["legal", "financial", "coordination"];
  const _catLabel = { legal: "Legal", financial: "Financial", coordination: "Coordination" };
  const _catBuckets = _categoryOrder.map((cid) => {
    const rows = items.filter((it) => it.col === cid);
    const doneN = rows.filter((it) => resolveMilestoneState(it) === "Complete").length;
    return { id: cid, label: _catLabel[cid], rows, doneN, totalN: rows.length };
  });
  const _pickDefaultOpen = () => {
    // 1 · category with a blocked row wins (via activeBlockers).
    const blockedIds = new Set(activeBlockers.map((b) => b.id).filter(Boolean));
    for (const c of _catBuckets) {
      if (c.rows.some((r) => blockedIds.has(r.id) && resolveMilestoneState(r) !== "Complete")) return c.id;
    }
    // 2 · category with an overdue outstanding row.
    const nowIso = new Date(); const startOfToday = new Date(nowIso.getFullYear(), nowIso.getMonth(), nowIso.getDate()).getTime();
    for (const c of _catBuckets) {
      const hasOverdue = c.rows.some((r) => {
        if (resolveMilestoneState(r) === "Complete") return false;
        const t = Date.parse(r?.target_date || r?.targetDate || "");
        if (!Number.isFinite(t)) return false;
        const td = new Date(t);
        return new Date(td.getFullYear(), td.getMonth(), td.getDate()).getTime() < startOfToday;
      });
      if (hasOverdue) return c.id;
    }
    // 3 · category with any outstanding row.
    for (const c of _catBuckets) if (c.rows.some((r) => resolveMilestoneState(r) !== "Complete")) return c.id;
    // 4 · fallback to Legal.
    return "legal";
  };
  // iter-alignment · P0 · Pre-exchange Checklist Accordion Invariant
  // (2026-02-28) — `openCat` is a SINGLE-VALUE state (a category id
  // string, or `null` when every category is collapsed).  Legal,
  // Financial and Coordination MUST behave as a single accordion:
  //   • opening one automatically collapses the other two, and
  //   • re-clicking the currently open category collapses it,
  //     leaving all three categories closed.
  // Do not convert this to a Set / array / per-cat boolean — the
  // toggle on the header (`prev === cat.id ? null : cat.id`) is the
  // sole write path for the accordion.  Reveal state
  // (`expandedCatIds`) is separate + only expands the row list
  // inside the currently-open category.
  const [openCat, setOpenCat] = useState(() => _pickDefaultOpen());
  const [expandedCatIds, setExpandedCatIds] = useState(() => new Set()); // categories that reveal remaining rows

  /* iter-alignment · Nudge {role} · 2026-02-28 —
   * Turn the "Awaiting" inline status into a one-tap chase.
   * Nudge state is per-row: 'idle' | 'sending' | 'nudged'.
   * `nudged` state acts as both a subtle confirmation AND a
   * 30-second cooldown that prevents rapid duplicate taps.
   * No new endpoint: posts through the existing canonical chase
   * write path (`POST /api/v2/deals/{id}/chases`) which stamps
   * an immutable `deal_activity` row — same audit trail every
   * other chase surface uses. Never sends any outbound comms. */
  const [nudgeStates, setNudgeStates] = useState({}); // { [rowId]: 'sending' | 'nudged' }
  const nudgeTimersRef = useRef({});
  useEffect(() => () => {
    // Clear any pending timers on unmount so the setState below
    // doesn't fire against a torn-down component.
    const timers = nudgeTimersRef.current;
    Object.values(timers).forEach((t) => { try { clearTimeout(t); } catch { /* noop */ } });
  }, []);
  const nudgeRow = useCallback(async (row) => {
    if (!deal?.id || !row?.id) return;
    // Debounce: while sending or within the 30s cooldown window,
    // ignore repeat taps entirely.
    const current = nudgeStates[row.id];
    if (current === "sending" || current === "nudged") return;
    setNudgeStates((prev) => ({ ...prev, [row.id]: "sending" }));
    const roleLabel = OWNER_LABEL[row.owner_role] || row.owner_role || "counterparty";
    const result = await postChaseToBackend(deal.id, {
      channel: "manual",
      roleLabel,
      subject: row.label,
      waitingOnId: row.id,
    });
    if (!result) {
      // Failure — reset and toast, so operator can retry.
      setNudgeStates((prev) => { const n = { ...prev }; delete n[row.id]; return n; });
      try { toast.error("Couldn't log the nudge. Try again."); } catch { /* noop */ }
      return;
    }
    setNudgeStates((prev) => ({ ...prev, [row.id]: "nudged" }));
    try { toast.success(`Nudged ${roleLabel} · logged to Activity`); } catch { /* noop */ }
    // 30s cooldown: after which the button is available again.
    const existing = nudgeTimersRef.current[row.id];
    if (existing) { try { clearTimeout(existing); } catch { /* noop */ } }
    nudgeTimersRef.current[row.id] = setTimeout(() => {
      setNudgeStates((prev) => { const n = { ...prev }; delete n[row.id]; return n; });
      delete nudgeTimersRef.current[row.id];
    }, 30_000);
  }, [deal?.id, nudgeStates]);

  const pct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  return (
    <section
      className="rounded-2xl bg-white"
      style={{ border: "1px solid rgba(226,232,240,0.9)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}
      data-testid="canonical-exchange-eve"
      data-open={isOpen ? "1" : "0"}
    >
      {/* iter-alignment · P0 · Pre-exchange Checklist Redesign
          (2026-02-28) — Compact header (~25% less padding + one
          shared row of icon · title · description · count · thin
          progress · actions).  Progress bar reads from the same
          `doneCount / totalCount` the count line uses so they
          can never drift. */}
      <header className="flex items-center justify-between gap-4 px-5 py-3.5">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="w-9 h-9 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center ring-1 ring-violet-100 shrink-0">
            <ClipboardCheck size={16} />
          </span>
          <div className="min-w-0">
            <h2
              className="text-[15px] font-bold tracking-tight text-slate-900"
              data-testid="canonical-exchange-eve-title"
            >
              Pre-exchange checklist
            </h2>
            <p className="text-[11.5px] text-slate-500 mt-0.5 leading-snug">
              Complete these checks before exchanging contracts.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex flex-col items-end gap-1" data-testid="canonical-exchange-eve-progress-block">
            <span
              className="text-[11.5px] text-slate-500 tabular-nums whitespace-nowrap"
              data-testid="canonical-exchange-eve-progress"
            >
              <span className="font-bold text-slate-900">{doneCount}</span> of {totalCount} complete
            </span>
            <div className="w-[128px] h-[3px] rounded-full bg-slate-100 overflow-hidden" aria-hidden>
              <div
                className="h-full bg-violet-500 transition-all"
                style={{ width: `${pct}%` }}
                data-testid="canonical-exchange-eve-progress-bar"
              />
            </div>
          </div>
          {blockerCount > 0 && (
            <span
              className="inline-flex items-center gap-1 px-2 h-[22px] rounded-md bg-rose-50 text-rose-700 ring-1 ring-rose-200/70 text-[11px] font-semibold"
              data-testid="canonical-exchange-eve-blocker-count"
              title={`${blockerCount} exchange blocker${blockerCount === 1 ? "" : "s"}`}
            >
              <AlertCircle size={11} strokeWidth={2.4} /> {blockerCount} blocker{blockerCount === 1 ? "" : "s"}
            </span>
          )}
          <button
            type="button"
            onClick={toggle}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-lg text-[12px] font-semibold text-violet-700 bg-white hover:bg-violet-50 ring-1 ring-violet-200 transition"
            data-testid="canonical-exchange-eve-toggle"
            aria-expanded={isOpen}
          >
            {isOpen ? "Hide checklist" : "Show checklist"}
            <ChevronRight size={12} className={`transition-transform ${isOpen ? "rotate-90" : ""}`} />
          </button>
        </div>
      </header>

      {/* iter-alignment · P0 · Pre-exchange Checklist Redesign
          (2026-02-28) — Single-column accordion body. */}
      {isOpen && (
        <div className="px-5 pb-5 space-y-2" data-testid="canonical-exchange-eve-body">
          {_catBuckets.map((cat) => {
            const isCatOpen = openCat === cat.id;
            const revealAll = expandedCatIds.has(cat.id);
            /* iter-alignment · P0 · Exchange Checklist interactivity
             * fix (2026-02).  Show ALL rows (complete + incomplete)
             * so operators can BOTH tick outstanding items AND
             * untick completed ones without leaving the checklist
             * tab.  Incomplete rows still surface first so the
             * scan stays focused on what's outstanding. */
            const _incomplete = cat.rows.filter((r) => resolveMilestoneState(r) !== "Complete");
            const _complete   = cat.rows.filter((r) => resolveMilestoneState(r) === "Complete");
            const orderedRows = [..._incomplete, ..._complete];
            const shownRows = revealAll ? orderedRows : orderedRows.slice(0, 3);
            const remainingN = Math.max(0, orderedRows.length - shownRows.length);
            const blockedIds = new Set(activeBlockers.map((b) => b.id).filter(Boolean));
            return (
              <div
                key={cat.id}
                className="rounded-xl bg-white overflow-hidden"
                style={{ border: "1px solid rgba(226,232,240,0.9)" }}
                data-testid={`canonical-eve-cat-${cat.id}`}
                data-open={isCatOpen ? "1" : "0"}
              >
                <button
                  type="button"
                  onClick={() => setOpenCat((prev) => (prev === cat.id ? null : cat.id))}
                  className="w-full flex items-center justify-between gap-3 px-4 h-12 hover:bg-slate-50/60 transition-colors"
                  aria-expanded={isCatOpen}
                  data-testid={`canonical-eve-cat-${cat.id}-header`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[13.5px] font-bold text-slate-900">{cat.label}</span>
                    <span className="text-slate-300">·</span>
                    <span className="text-[12.5px] font-medium text-slate-500 tabular-nums" data-testid={`canonical-eve-cat-${cat.id}-count`}>
                      {cat.doneN}/{cat.totalN}
                    </span>
                  </div>
                  <ChevronDown size={14} className={`text-slate-400 transition-transform ${isCatOpen ? "rotate-180" : ""}`} />
                </button>
                {isCatOpen && (
                  <div className="border-t border-slate-100" data-testid={`canonical-eve-cat-${cat.id}-body`}>
                    {orderedRows.length === 0 ? (
                      <div className="px-4 py-4 text-[12.5px] text-slate-500" data-testid={`canonical-eve-cat-${cat.id}-empty`}>
                        All {cat.label.toLowerCase()} items complete.
                      </div>
                    ) : (
                      <ul className="divide-y divide-slate-100">
                        {shownRows.map((row) => {
                          const _rowState = resolveMilestoneState(row);
                          const rowComplete = _rowState === "Complete";
                          const rowBlocked = blockedIds.has(row.id);
                          const _tgt = Date.parse(row?.target_date || row?.targetDate || "");
                          const _sod = (() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), n.getDate()).getTime(); })();
                          const rowOverdue = !rowComplete && Number.isFinite(_tgt) && (() => {
                            const d = new Date(_tgt); return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() < _sod;
                          })();
                          const roleLabel = OWNER_LABEL[row.owner_role] || row.owner_role || "";
                          // Exceptional status only when operationally meaningful.
                          let statusLabel = null; let statusTone = "";
                          if (rowComplete)      { statusLabel = "Complete"; statusTone = "text-emerald-700"; }
                          else if (rowBlocked)  { statusLabel = "Blocked"; statusTone = "text-rose-600"; }
                          else if (rowOverdue)  { statusLabel = "Overdue"; statusTone = "text-rose-600"; }
                          else if (row.owner_role && row.owner_role !== "agent") { statusLabel = "Awaiting"; statusTone = "text-amber-600"; }
                          const canRowComplete = canCompleteSet.has(row.id);
                          /* iter-alignment · P0 · Exchange Checklist interactivity
                           * fix (2026-02).  Bi-directional toggle:
                           *   Complete  → onReopenMilestone (untick)
                           *   Otherwise → onCompleteMilestone when the
                           *               row is in the `can_complete` set.
                           * Both paths flow through the shared
                           * milestone SSOT so Transaction Progress +
                           * Exchange Confidence recalculate via the
                           * existing `reload()` chain. */
                          const canRowToggle = rowComplete
                            ? typeof onReopenMilestone === "function"
                            : canRowComplete && typeof onCompleteMilestone === "function";
                          const onToggleRow = () => {
                            if (!canRowToggle) return;
                            if (rowComplete) onReopenMilestone?.(row.id);
                            else onCompleteMilestone?.(row.id);
                          };
                          return (
                            <li
                              key={row.id}
                              className="grid grid-cols-[24px_minmax(0,1fr)_140px_100px] items-center gap-3 px-4 h-[46px]"
                              data-testid={`canonical-eve-row-${row.id}`}
                              data-state={_rowState}
                              data-blocked={rowBlocked ? "1" : undefined}
                              data-overdue={rowOverdue ? "1" : undefined}
                            >
                              <button
                                type="button"
                                onClick={onToggleRow}
                                disabled={!canRowToggle}
                                aria-pressed={rowComplete}
                                aria-label={rowComplete ? `Untick ${row.label}` : `Mark ${row.label} complete`}
                                className={`w-[18px] h-[18px] rounded border-[1.5px] flex items-center justify-center transition-colors ${
                                  rowComplete
                                    ? "bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-600 hover:border-emerald-600"
                                    : rowBlocked
                                      ? "border-rose-300 hover:border-rose-400"
                                      : rowOverdue
                                        ? "border-amber-300 hover:border-amber-400"
                                        : "border-slate-300 hover:border-violet-400"
                                } ${canRowToggle ? "cursor-pointer" : "cursor-not-allowed opacity-60"}`}
                                data-testid={`canonical-eve-row-${row.id}-check`}
                              >
                                {rowComplete ? (
                                  <Check size={12} strokeWidth={3.2} />
                                ) : (
                                  <span className="w-0 h-0" />
                                )}
                              </button>
                              <span
                                className={`text-[13px] font-semibold truncate ${rowComplete ? "text-slate-500 line-through decoration-slate-300" : "text-slate-900"}`}
                                title={row.label}
                              >
                                {row.label}
                              </span>
                              <span className="text-[12.5px] text-slate-500 truncate" data-testid={`canonical-eve-row-${row.id}-owner`}>
                                {roleLabel}
                              </span>
                              <span className="text-[11.5px] font-medium justify-self-end inline-flex items-center gap-1.5">
                                {statusLabel && (statusLabel !== "Awaiting" ? (
                                  <>
                                    <span className={`w-1.5 h-1.5 rounded-full ${
                                      rowBlocked ? "bg-rose-500" : rowOverdue ? "bg-rose-500" : "bg-amber-500"
                                    }`} aria-hidden />
                                    <span className={statusTone} data-testid={`canonical-eve-row-${row.id}-status`}>{statusLabel}</span>
                                  </>
                                ) : (
                                  /* iter-alignment · Nudge {role} · 2026-02-28 —
                                   * "Awaiting" is now a one-tap chase.
                                   * Default: amber dot + "Awaiting".
                                   * Hover: label swaps to "Nudge" (arrow hint).
                                   * Post-tap: shows "Nudged just now" for 30s + toast.
                                   * Pending: shows "Sending…" and is disabled.
                                   * Nudge is only rendered when the row is
                                   * genuinely waiting on an identifiable
                                   * external counterparty — internal
                                   * (`agent`, `admin`) owner-roles fall back
                                   * to the calm read-only status pill. */
                                  (() => {
                                    // iter-alignment · P2 · Nudge {role}
                                    // finish (2026-02-28) — canonical
                                    // chaseable roles.  `agent`/`admin`
                                    // are internal (us); rows without an
                                    // identifiable counterparty are read-only.
                                    const _CHASEABLE = new Set([
                                      "buyer_solicitor", "seller_solicitor", "solicitor",
                                      "broker", "mortgage_broker",
                                      "buyer", "seller",
                                      "managing_agent",
                                    ]);
                                    const chaseable = _CHASEABLE.has(String(row.owner_role || "").toLowerCase());
                                    if (!chaseable) {
                                      return (
                                        <>
                                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" aria-hidden />
                                          <span className={statusTone} data-testid={`canonical-eve-row-${row.id}-status`}>Awaiting</span>
                                        </>
                                      );
                                    }
                                    const nState = nudgeStates[row.id];
                                    const isSending = nState === "sending";
                                    const isNudged  = nState === "nudged";
                                    const roleShort = OWNER_LABEL[row.owner_role] || row.owner_role || "";
                                    const label = isSending ? "Sending…" : isNudged ? "Nudged just now" : "Awaiting";
                                    const hoverLabel = "Nudge";
                                    return (
                                      <button
                                        type="button"
                                        onClick={() => nudgeRow(row)}
                                        disabled={isSending || isNudged}
                                        title={isNudged ? `Nudge sent — logged to Activity` : `Nudge ${roleShort}`}
                                        aria-label={`Nudge ${roleShort}`}
                                        className={`group relative inline-flex items-center gap-1.5 h-[22px] px-1.5 -mr-1 rounded-md text-[11.5px] font-medium transition-colors ${
                                          isNudged
                                            ? "text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200/70 cursor-default"
                                            : isSending
                                              ? "text-amber-600 cursor-wait opacity-70"
                                              : "text-amber-600 hover:bg-amber-50 hover:text-amber-700"
                                        }`}
                                        data-testid={`canonical-eve-row-${row.id}-nudge`}
                                        data-nudge-state={nState || "idle"}
                                      >
                                        <span className={`w-1.5 h-1.5 rounded-full ${
                                          isNudged ? "bg-emerald-500" : "bg-amber-500"
                                        }`} aria-hidden />
                                        {isNudged || isSending ? (
                                          <span data-testid={`canonical-eve-row-${row.id}-status`}>{label}</span>
                                        ) : (
                                          <>
                                            <span
                                              className="group-hover:hidden"
                                              data-testid={`canonical-eve-row-${row.id}-status`}
                                            >
                                              {label}
                                            </span>
                                            <span className="hidden group-hover:inline-flex items-center gap-0.5">
                                              {hoverLabel}
                                              <ChevronRight size={11} strokeWidth={2.4} />
                                            </span>
                                          </>
                                        )}
                                      </button>
                                    );
                                  })()
                                ))}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    {remainingN > 0 && (
                      <button
                        type="button"
                        onClick={() => setExpandedCatIds((prev) => { const n = new Set(prev); n.add(cat.id); return n; })}
                        className="w-full text-left px-4 py-2.5 text-[12px] font-semibold text-violet-700 hover:bg-violet-50/50 transition-colors border-t border-slate-100"
                        data-testid={`canonical-eve-cat-${cat.id}-view-remaining`}
                      >
                        View remaining {remainingN}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

/* ── Current blockers (compact rail) ───────────────────────────────── */
const CompactBlockerRow = ({ deal, docRequests, intel, awaiting, onSelectTab }) => {
  const blockers = useMemo(() => {
    const raw = [];
    // Responsible party from GET /blockers (resolve_recipient), matched by blocker id.
    const byId = new Map((Array.isArray(awaiting) ? awaiting : []).map((a) => [a?.id, a]));
    // Real blockers from deal.activity / docRequests / intel
    (deal?.blockers || []).forEach((b) => {
      if (b.status === "resolved") return;
      const a = byId.get(b.id) || {};
      const role = _roleText(a.owner_role || b.owner_role);
      const name = a.recipient?.full_name;
      raw.push({
        id: b.id || `b-${raw.length}`,
        title: b.title || "Waiting on update",
        sub: name && role ? `Waiting on: ${name} · ${role}` : role ? `Waiting on: ${role}` : (b.description || ""),
        days: Number.isFinite(b.age_days) ? b.age_days : (b.days || 0),
        severity: b.severity || "medium",
        icon: AlertCircle,
      });
    });
    // Backfill from outstanding document requests
    (docRequests || []).forEach((r) => {
      if (r.status === "fulfilled" || r.status === "cancelled") return;
      const days = r.created_at
        ? Math.floor((Date.now() - Date.parse(r.created_at)) / 86_400_000)
        : 0;
      raw.push({
        id: `req-${r.id}`,
        title: `${r.title || "Document"} outstanding`,
        sub: `Waiting on ${r.requested_from_name || "counterparty"}`,
        days,
        severity: days >= 7 ? "high" : days >= 3 ? "medium" : "low",
        icon: FileText,
      });
    });
    return raw.slice(0, 6);
  }, [deal, docRequests, intel, awaiting]);

  if (blockers.length === 0) {
    /* iter-alignment · P · Empty-state strip stays retired.  The
     * blocker banner previously living inside the Transaction
     * Journey card was also retired to remove duplication with
     * Next Best Action / Current Blockers / Exchange Confidence,
     * so the "no active blockers" state is expressed by the
     * emerald "On track" pill on the journey header + the Next
     * Best Action calm state. */
    return null;
  }

  return (
    <section data-testid="canonical-blockers">
      <header className="flex items-center justify-between mb-2.5">
        <h2 className="text-[12.5px] font-bold tracking-tight text-slate-900">
          Current blockers <span className="text-slate-400 font-medium">{blockers.length}</span>
        </h2>
        <button
          type="button"
          onClick={() => onSelectTab?.("activity")}
          className="inline-flex items-center gap-0.5 text-[11.5px] font-semibold text-violet-700 hover:text-violet-900"
          data-testid="canonical-blockers-view-all"
        >
          View all blockers <ChevronRight size={12} />
        </button>
      </header>
      <div className="grid grid-cols-3 gap-2.5">
        {blockers.slice(0, 3).map((b) => {
          const Icon = b.icon || AlertCircle;
          const sevTone = b.severity === "high"
            ? { bg: "bg-rose-50/70", text: "text-rose-700", ring: "ring-rose-200/70", dot: "#F43F5E" }
            : b.severity === "low"
              ? { bg: "bg-slate-50", text: "text-slate-600", ring: "ring-slate-200/70", dot: "#94A3B8" }
              : { bg: "bg-amber-50/70", text: "text-amber-700", ring: "ring-amber-200/70", dot: "#D97706" };
          return (
            <button
              key={b.id}
              type="button"
              onClick={() => onSelectTab?.("activity")}
              className="text-left rounded-xl bg-white p-3 hover:bg-slate-50/60 hover:shadow-[0_0_0_1px_rgba(124,58,237,0.14)] cf-press transition-all"
              style={{ border: "1px solid rgba(226,232,240,0.9)" }}
              data-testid={`canonical-blocker-${b.id}`}
            >
              <div className="flex items-start gap-2">
                <span
                  className={`w-7 h-7 rounded-lg ${sevTone.bg} flex items-center justify-center shrink-0`}
                  style={{ color: sevTone.dot }}
                >
                  <Icon size={13} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[12.5px] font-semibold text-slate-900 truncate">{b.title}</p>
                  {b.sub && <p className="text-[11px] text-slate-500 truncate mt-0.5" data-testid={`canonical-blocker-${b.id}-waiting-on`}>{b.sub}</p>}
                </div>
              </div>
              <div className="mt-2.5 flex items-center justify-between">
                <span className={`inline-flex items-center gap-1 px-1.5 h-[18px] rounded-md ${sevTone.bg} ${sevTone.text} text-[10.5px] font-semibold ring-1 ${sevTone.ring}`}>
                  {b.days} day{b.days === 1 ? "" : "s"}
                </span>
                <span className={`text-[10.5px] font-semibold ${sevTone.text} uppercase tracking-[0.08em]`}>
                  {b.severity}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

/* ── Activity feed (compact) ───────────────────────────────────────── */
const ACTIVITY_TABS = [
  /* iter-alignment · P0 · Deal Activity Polish (2026-02) — the
     separate "Activity Feed" tab was retired.  The canonical
     activity history now lives as a permanent surface BELOW
     the tab body (see the "Deal Activity" section at the
     bottom of `ActivityFeed` — it always renders regardless of
     the active tab).  Legacy `?tab=activity/updates/notes`
     deep links alias into `current_stage` so external links
     keep working. */
  { id: "current_stage", label: "Current Stage" },
  { id: "checklist",     label: "Exchange Checklist" },
  { id: "milestones",    label: "All Milestones" },
];

const ActivityKindChip = ({ kind }) => {
  const map = {
    mortgage:  { tone: "violet", label: "Mortgage" },
    searches:  { tone: "indigo", label: "Searches" },
    documents: { tone: "sky",    label: "Documents" },
    update:    { tone: "slate",  label: "Update" },
    stage:     { tone: "emerald", label: "Stage" },
    blocker:   { tone: "amber",  label: "Blocker" },
  };
  const m = map[kind] || map.update;
  const tone = {
    violet:  "bg-violet-50 text-violet-700 ring-violet-200/70",
    indigo:  "bg-indigo-50 text-indigo-700 ring-indigo-200/70",
    sky:     "bg-sky-50 text-sky-700 ring-sky-200/70",
    slate:   "bg-slate-50 text-slate-600 ring-slate-200/70",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200/70",
    amber:   "bg-amber-50 text-amber-700 ring-amber-200/70",
  }[m.tone];
  return (
    <span className={`inline-flex items-center px-2 h-[20px] rounded-md ring-1 ${tone} text-[10.5px] font-semibold`}>
      {m.label}
    </span>
  );
};

const guessKind = (a) => {
  const t = (a.title || a.text || "").toLowerCase();
  if (/mortgage|lender|offer/.test(t)) return "mortgage";
  if (/search/.test(t)) return "searches";
  if (/document|pack|ta6|ta10|contract/.test(t)) return "documents";
  if (/blocker|escalat|chase/.test(t)) return "blocker";
  if (/stage|milestone/.test(t)) return "stage";
  return "update";
};

const ActivityFeed = ({ deal, activities, activityStatus = "ready", documents, docRequests, tasks, participants, milestones, journeyState, txnState, operationalNotes, waitingOn, currentUserId, dealId, access, tab, onSelectTab, onOpenProgress, composerOpenSignal, composerInitialType, onComposerClosed, onRequestDoc, onOpenRequestDocWithTemplate, onReloadRoom, onCriticalPathResolved, onActivateJourneyStep, onMoveToNextStage, onCompleteMilestone, onReopenMilestone, onEditCompletionDate, onSetTargetDate, onSetNote, onResolveBlocker, awaitingBlockers, viewingStageId, onReturnToCurrentStage, onReviewDoc, onOpenSheet, activityHasMore, loadingEarlier, onLoadEarlierActivity }) => {
  const nav = useNavigate();
  /* iter-alignment · P53 — Pilot doc-request SSOT for the contextual
   * Request / Chase buttons on Current Stage rows.  Reused from the
   * chainStore — no new state or endpoint.  Both selectors are
   * safe when the store isn't hydrated (`undefined` fallbacks). */
  const { getPilotDocumentRequests, markPilotRequestChased } = useChainStore();
  // iter-201 — calm fade between tab bodies. Each transition keeps
  // the surface still while content fades in. Per-tab scroll memory
  // is held in a session key so switching back lands the operator
  // exactly where they were.
  const [fadeKey, setFadeKey] = useState(0);
  const handleSelect = useCallback((nextTab) => {
    if (nextTab === tab) return;
    onSelectTab?.(nextTab);
    setFadeKey((k) => k + 1);
  }, [tab, onSelectTab]);

  const milestoneItems = Array.isArray(milestones?.items) ? milestones.items : [];
  const counts = {
    documents: documents?.length || 0,
    tasks: tasks?.length || 0,
    participants: participants?.length || 0,
    // iter-alignment · P2 · Count chips for the new tabs.  All are
    // read-only projections of the existing milestone/journey SSOT.
    // `milestones` state is an OBJECT `{ items, can_complete }`, not
    // an array — always read from `milestones.items`.
    milestones: milestoneItems.length,
    checklist: Array.isArray(deal?.pre_exchange_checklist)
      ? deal.pre_exchange_checklist.length
      : milestoneItems.filter((m) => m?.stage === "exchange_ready" || m?.status === "current").length,
  };

  return (
    <section
      className="rounded-2xl bg-white"
      style={{
        border: "1px solid rgba(226,232,240,0.9)",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
      }}
      data-testid="canonical-activity-feed"
    >
      {/* iter-201 — Calm fade animation for tab bodies. */}
      <style>{`
        @keyframes canonical-tab-fade-in {
          from { opacity: 0; transform: translateY(2px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .canonical-tab-fade { animation: canonical-tab-fade-in 180ms ease-out both; }
        @media (prefers-reduced-motion: reduce) {
          .canonical-tab-fade { animation: none; }
        }
      `}</style>
      {/* Tabs */}
      <nav className="flex items-end gap-5 px-5 pt-4 border-b border-slate-100" role="tablist" data-testid="canonical-activity-tabs">
        {ACTIVITY_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => handleSelect(t.id)}
            className={`relative pb-3 text-[12.5px] font-semibold tracking-tight transition-colors ${
              tab === t.id ? "text-slate-900" : "text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`canonical-activity-tab-${t.id}`}
            data-state={tab === t.id ? "active" : ""}
          >
            <span className="inline-flex items-center gap-1.5">
              {t.label}
              {counts[t.id] > 0 && (
                <span className="text-[10.5px] text-slate-400 font-medium">{counts[t.id]}</span>
              )}
            </span>
            {/* iter-201 — Per-tab underline carries a smooth
                scale-in so switching tabs reads as a native
                desktop-app tab change, never an instant repaint. */}
            <span
              aria-hidden
              className={`absolute -bottom-px left-0 right-0 h-[2px] rounded-full bg-violet-600 transition-all duration-200 ${
                tab === t.id ? "scale-x-100 opacity-100" : "scale-x-0 opacity-0"
              }`}
              style={{ transformOrigin: "center" }}
            />
          </button>
        ))}
      </nav>

      {/* Body */}
      <div className="p-5 canonical-tab-fade" key={fadeKey} data-testid={`canonical-tab-body-${tab}`}>
        {tab === "documents" && (
          <DocumentsTabBody documents={documents} canUpload={access?.can_upload_documents !== false} onReviewDoc={onReviewDoc} />
        )}
        {tab === "tasks" && (
          <EmptyHint label="No tasks yet" sub="Operational tasks for this deal will appear here." />
        )}
        {tab === "participants" && (
          <EmptyHint label="See the right rail for full participant list." sub="" />
        )}

        {/* iter-alignment · P3 · Current Stage — SSOT-driven summary
            of the deal's active step.
            Priority order for "current stage":
              1. `journeyState.current_step` (backend source of truth)
              2. `deal.stage`               (canonical field fallback)
            Terminal stages (`exchanged` / `completed` / `fallen_through`)
            render a calm terminal banner instead of an active-step
            summary.  Compact milestone table below reads directly
            from `milestoneItems` (no parallel state) — pending +
            at-risk first, then completed, with owner · state · age. */}
        {tab === "current_stage" && (() => {
          const stageId = String(deal?.stage || "").toLowerCase();
          const dealStatus = String(deal?.status || "").toLowerCase();
          const isTerminal = ["exchanged", "completed", "fallen_through"].includes(stageId)
            || ["exchanged", "completed", "fallen_through"].includes(dealStatus);
          /* SSOT alignment (P7): the canonical journey config on
           * the backend (`journeyState.current_step`) IS the SSOT
           * for the active stage.  `txnState.activeStepId` is a
           * frontend-derived helper that can drift when chainStore
           * fixtures carry stale `stage` fields — we no longer let
           * it override the backend truth on the Current Stage
           * surface.  Title, active checklist filter and progress
           * count all resolve through `activeStageId`. */
          const jSteps = Array.isArray(journeyState?.steps) ? journeyState.steps : [];
          const activeStepIdSSOT = journeyState?.current_step || txnState?.activeStepId || null;
          const jActiveStep = activeStepIdSSOT
            ? jSteps.find((s) => s?.id === activeStepIdSSOT)
            : jSteps.find((s) => s?.state === "active");
          let resolved = null;
          try { resolved = resolveJourneyProgress(deal, milestones); } catch { resolved = null; }
          const resolvedSteps = resolved?.steps || [];
          const focusIdx = resolved?.currentFocusIdx;
          const focusStep = (typeof focusIdx === "number" && focusIdx >= 0 && focusIdx < resolvedSteps.length)
            ? resolvedSteps[focusIdx]
            : null;
          const activeStep = focusStep || jActiveStep;
          const hasJourney = resolvedSteps.length > 0 || jSteps.length > 0;
          const hasMilestones = milestoneItems.length > 0;

          // Genuinely empty: no journey AND no milestones AND no stage
          if (!hasJourney && !hasMilestones && !stageId) {
            return <EmptyHint label="No current stage" sub="Progress the deal to activate a milestone." />;
          }

          // Human label — mirror the strip's activeLabel expression.
          // iter-alignment · P13 — Prefer the rail-resolved focus
          // step so the panel title matches the rail chip + Journey
          // header (one canonical Current Stage across the room).
          const stepLabel =
            (isTerminal && (stageId === "completed" ? "Completed" : stageId === "exchanged" ? "Exchanged" : "Fallen through"))
            || focusStep?.label
            || jActiveStep?.label
            || txnState?.activeStepLabel
            || (stageId ? stageId.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "In progress");

          // iter-alignment · P26 — Next-stage label for the brief
          // "Stage complete · advancing to X" success flash.  Pure
          // read of the SSOT-resolved journey ordering — nothing
          // new to persist.
          const nextStepLabel = (() => {
            if (isTerminal || typeof focusIdx !== "number" || focusIdx < 0) return null;
            const next = resolvedSteps[focusIdx + 1];
            return next?.label || null;
          })();

          /* iter-alignment · P5 · Interactive checklist.
           * Approved design REPLACES the compact table with a
           * live-editable checklist.  Each pending milestone is a
           * checkbox that writes to the existing SSOT via
           * `onCompleteMilestone(id)` — the same callback the
           * Exchange Checklist tab uses.  On success the parent's
           * `completeMilestone` calls `reload()` so Transaction
           * Journey, Next Best Action, Blockers, Activity Timeline,
           * Forecasting and Exchange Confidence all recompute
           * together.  Stage auto-advance is handled server-side by
           * the same `/milestones/:id/complete` endpoint.  The
           * standard Progress-Stage CTA is removed — stage
           * advancement now flows implicitly from checklist
           * completion.  A manager-only override remains for
           * `access.is_org_owner === true` for edge cases (milestone
           * data drift, manual escalation). */
          /* iter-alignment · P7 · Stage-scoped checklist.
           * Filter the milestone SSOT down to milestones whose
           * `stage` matches the currently-active journey node id.
           * The journey config in `data/transactionJourney.js` and
           * the milestone `stage` field on the backend are the two
           * halves of the same SSOT — Current Stage title, active
           * journey dot and this checklist all resolve to the same
           * node id, so operators never see milestones from
           * different stages mixed together. */
          /* iter-alignment · P7/P9 · Current Stage always anchors
           * to the canonical Current Focus.  Parallel workstreams
           * open in a lightweight read-only preview (see rail's
           * `previewStageId` handler) — they never replace the
           * checklist, active progression or Exchange Confidence. */
          /* iter-alignment · P11 · Rail selection drives this panel.
           * When `viewingStageId` is set (rail tap on a non-canonical
           * node) the checklist reflects that stage READ-ONLY with a
           * "Viewing: …" indicator + "Return to Current Stage" action.
           * The canonical Current Stage, Exchange Confidence, Next
           * Best Action and automatic progression are unchanged —
           * they all still read from `journeyState.current_step`
           * SSOT below. */
          // iter-alignment · P13 — Prefer the rail-resolved focus
          // step so the checklist's stage-scoped filter, progress
          // count and viewingStageId toggle all resolve against the
          // same canonical Current Stage the rail chip + Journey
          // header display.  Retain the backend fallbacks so
          // pre-journey deals + legacy pilot rooms still resolve.
          const canonicalStageId = focusStep?.id
            || journeyState?.current_step
            || txnState?.activeStepId
            || activeStep?.id
            || null;
          const isViewingOther = !!(viewingStageId && viewingStageId !== canonicalStageId);
          const activeStageId = isViewingOther ? viewingStageId : canonicalStageId;
          // P0 · Fix Completion Stage Catalogue and Gating
          // (2026-08-18) — `isViewingFuture` is true when the
          // operator is viewing a step whose canonical position
          // is AHEAD of the deal's current focus (e.g. Contract
          // deal viewing Completion).  Drives the "Future stage"
          // label + the dynamic "Return to {current stage}"
          // action + the Completion-locked banner.  Purely
          // derived from the resolved rail so freehold / cash
          // / leasehold shapes all reconcile identically.
          const _stepIndex = (steps, id) => {
            if (!id || !Array.isArray(steps)) return -1;
            for (let i = 0; i < steps.length; i += 1) {
              if (steps[i]?.id === id) return i;
            }
            return -1;
          };
          const _viewedIdx = _stepIndex(resolvedSteps, viewingStageId);
          const _canonicalIdx = _stepIndex(resolvedSteps, canonicalStageId);
          const isViewingFuture = isViewingOther
            && _viewedIdx >= 0
            && _canonicalIdx >= 0
            && _viewedIdx > _canonicalIdx;
          // iter-alignment · P12 — Every pre-completion stage is
          // editable when viewed; ONLY the terminal Completion node
          // stays locked so the transaction can't be closed off-rail.
          // `isViewingLocked` clamps the checklist and toolbar; SSOT
          // and progression are unchanged either way — completions
          // always flow through the existing `onCompleteMilestone`
          // engine.
          const isViewingLocked = isViewingOther && (viewingStageId === "complete" || viewingStageId === "completion" || viewingStageId === "completed");
          const isViewingEditable = isViewingOther && !isViewingLocked;
          const viewedStep = isViewingOther
            ? (resolvedSteps.find((s) => s?.id === viewingStageId)
               || jSteps.find((s) => s?.id === viewingStageId)
               || null)
            : null;
          const effectiveStageLabel = isViewingOther
            ? (viewedStep?.label
               || (viewingStageId
                    ? viewingStageId.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
                    : stepLabel))
            : stepLabel;
          const stageScopedItems = activeStageId
            ? milestoneItems.filter((m) => m?.stage === activeStageId)
            : milestoneItems;
          // Survey folds into the Mortgage node (or is reconciled past on cash deals) but keeps stage="survey"; list its rows without counting them toward this stage.
          const surveyRows = activeStageId && activeStageId !== "survey" && !isViewingOther && (activeStageId === "mortgage" || stageId === "survey")
            ? milestoneItems.filter((m) => m?.stage === "survey" && (!m.conditional || m.state === "done" || m.completed_at || m.target_date))
            : [];
          const visibleStageItems = stageScopedItems.filter((m) => {
          // iter-alignment · P41 — Conditional rows stay hidden
          // until triggered.  A row is "triggered" when the operator
          // has interacted with it — either a `target_date` was set
          // OR the row is already Complete OR (P39) it's marked
          // Blocked / Waiting.  Never-triggered conditionals are
          // filtered out of the visible checklist so the section
          // stays scannable; they still surface in "All Milestones".
            if (!m?.conditional) return true;
            if (m.state === "done" || m.completed_at) return true;
            if (m.target_date) return true;
            const st = resolveMilestoneState(m);
            if (st === "Blocked" || st === "Waiting") return true;
            return false;
          });
          // P0 · Fix Completion Stage Catalogue and Gating
          // (2026-08-18) — the future-stage Completion view no
          // longer wholesale-clamps `canCompleteSet`.  Backend
          // has already excluded any row whose `requires`
          // prerequisites are unmet (funds transfer, legal
          // completion, key authorisation / release, terminal
          // `completion_date`), so honouring `milestones.can_
          // complete` verbatim lets preparation rows (pre-
          // completion searches, final completion statement,
          // mortgage funds, vacant possession, all keys held)
          // tick early exactly as the operator brief requires.
          const canCompleteSet = new Set(milestones?.can_complete || []);
          const rank = (m) => {
            const st = resolveMilestoneState(m);
            if (st === "Complete") return 2;
            if (canCompleteSet.has(m?.id)) return 0;   // actionable now
            return 1;                                   // pending / blocked
          };
          const orderedMilestones = [...visibleStageItems].sort((a, b) => rank(a) - rank(b)).concat(surveyRows);
          // iter-alignment · Required-only summary counts.
          //   Optional milestones (`optional === true`) never gate
          //   stage completion — see `deriveExchangeReadiness` +
          //   the canonical journey engine.  Summary counts +
          //   remaining copy + `outstandingCount` on the
          //   Move-to-next-stage handler MUST mirror the same
          //   required-only set so the header can never disagree
          //   with the stage-completion contract.
          const requiredStageItems = stageScopedItems.filter((m) => m?.optional !== true);
          const requiredTotal = requiredStageItems.length;
          const doneCount = requiredStageItems.filter((m) => resolveMilestoneState(m) === "Complete").length;
          const isManager = access?.is_org_owner === true;

          /* iter-alignment · P15 — Inline blocker editing.  Map the
           * viewed stage's active blockers onto individual milestone
           * rows by owner_role so at most ONE pill renders per
           * blocker (else all rows in a stage would show the same
           * pill).  Blockers with no owner_role match fall back to
           * the first pending row of the stage.  Read-only /
           * completion-locked panels never render pills.
           * `awaitingBlockers` is the `effectiveAwaiting` SSOT
           * already filtered for previously-resolved Critical
           * Paths; `onResolveBlocker` is the same
           * `handleCriticalPathResolved` handler the Critical Path
           * card uses (POST /blockers/:id/resolve + Undo toast). */
          const rowBlockerMap = (() => {
            const map = new Map(); // milestone.id → blocker
            if (isViewingLocked || !onResolveBlocker || !Array.isArray(awaitingBlockers)) return map;
            const stageBlockers = awaitingBlockers.filter((b) => {
              const bs = b?.step_id || b?.journey_step || b?.target_step || null;
              return bs && bs === activeStageId;
            });
            if (stageBlockers.length === 0) return map;
            const pendingRows = orderedMilestones.filter((m) => resolveMilestoneState(m) !== "Complete");
            for (const b of stageBlockers) {
              // Prefer a role-matched pending row; otherwise the
              // first pending row.  A milestone can only carry ONE
              // pill so we skip any row already claimed.
              const roleMatch = pendingRows.find(
                (m) => m?.owner_role && b?.owner_role && String(m.owner_role) === String(b.owner_role) && !map.has(m.id),
              );
              const target = roleMatch || pendingRows.find((m) => !map.has(m.id));
              if (target) map.set(target.id, b);
            }
            return map;
          })();

          /* iter-alignment · P53 — Contextual Request / Chase actions
           * on outstanding rows.  Reuses the existing pilot doc-request
           * SSOT (`getPilotDocumentRequests` on chainStore) + the
           * shared `getChaseStateFor` ladder.  No new endpoint, no new
           * state.  A row surfaces "Chase" when it already has an
           * unfulfilled request whose T+N window has elapsed; "Request"
           * otherwise.  Rows that don't map to a known document
           * template stay clean — the button simply doesn't render. */
          const docActionByMilestone = (() => {
            const map = new Map();
            if (isViewingLocked) return map;
            const dealRecord = deal || null;
            const isPilotDeal = !!(dealRecord && dealRecord._pilot);
            // Contract-stage refinement — DB-backed deals surface
            // outstanding requests via the parent-fetched
            // `docRequests` prop (canonical SSOT).  Pilot demo
            // rooms continue to use the local chainStore.  Both
            // shapes carry `template_id` + `status` + a request
            // timestamp, so the same read logic covers both.
            const pilotRequests = (isPilotDeal && typeof getPilotDocumentRequests === "function")
              ? (getPilotDocumentRequests(dealId) || [])
              : [];
            const serverRequests = Array.isArray(docRequests) ? docRequests : [];
            const requests = isPilotDeal ? pilotRequests : serverRequests;
            const openRequests = requests.filter((r) => {
              const s = String(r?.status || "").toLowerCase();
              return s === "pending" || s === "requested" || s === "outstanding" || s === "";
            });
            for (const m of orderedMilestones) {
              const tplId = CHECKLIST_ROW_DOC_TEMPLATE[m.id];
              if (!tplId) continue;
              if (resolveMilestoneState(m) === "Complete") continue;
              // Server-side document requests key on `doc_type`
              // (e.g. "ta6"); the pilot chainStore mirrors the
              // template's short id in `template_id`.  Match on
              // either — same source-of-truth, different shape.
              const openReq = openRequests.find((r) => {
                const t = String(r?.template_id || r?.doc_type || r?.docType || "").toLowerCase();
                return t === String(tplId).toLowerCase();
              });
              // iter-alignment · P — Request status pill (2026-08-17).
              // Surface the initial-request timestamp on every row
              // that has an open request, so the row can render
              // "Requested · {date}" alongside the Chase button.
              // Prefer the pilot store's `requested_at`; fall back
              // to `created_at`; final fallback = null (no pill).
              const _requestedIso = openReq
                ? (openReq.requested_at || openReq.created_at || openReq.createdAt || null)
                : null;
              if (openReq) {
                const { pending } = getChaseStateFor(openReq);
                const topLevel = pending.length > 0 ? pending[pending.length - 1].level : null;
                map.set(m.id, { mode: "chase", templateId: tplId, chaseLevel: topLevel, requestId: openReq.id, requestedAtIso: _requestedIso });
              } else {
                map.set(m.id, { mode: "request", templateId: tplId, chaseLevel: null, requestId: null, requestedAtIso: null });
              }
            }
            return map;
          })();

          const onDocActionForRow = (milestoneId, mode, templateId) => {
            const action = docActionByMilestone.get(milestoneId);
            if (mode === "chase" && action?.requestId && typeof markPilotRequestChased === "function") {
              const nextLevel = action.chaseLevel || 1;
              try {
                markPilotRequestChased(dealId, action.requestId, nextLevel);
                toast.success(
                  nextLevel === 3
                    ? "Final reminder sent"
                    : `Reminder sent · T+${nextLevel === 1 ? 3 : 5}`,
                );
                return;
              } catch (_e) { /* fall through to opening the sheet */ }
            }
            // Request (no open request yet) → open the sheet with the
            // matching template pre-selected so the operator can
            // confirm recipient + note before sending.
            if (typeof onOpenRequestDocWithTemplate === "function") {
              onOpenRequestDocWithTemplate(templateId || null);
            } else if (typeof onRequestDoc === "function") {
              onRequestDoc();
            }
          };

          const remainingCount = requiredTotal - doneCount;

          // iter-alignment · P0 · Explicit Confirm Exchange /
          //   Confirm Completion gate (2026-08-18).  Renders only
          //   when the CANONICAL stage is `exchange` or `complete`,
          //   the terminal milestone (`contracts_exchanged` /
          //   `completion_date`) is not yet done, and every OTHER
          //   required row is complete.  Confirm click reuses the
          //   existing milestone completion write path so
          //   `reconcile_deal_status` still owns the actual event
          //   date + deal-status transition — no new endpoint, no
          //   duplicate state.  Celebration is one-shot per deal
          //   per event, gated by localStorage, and respects
          //   `prefers-reduced-motion`.
          const _stageId = stageScopedItems[0]?.stage || null;
          const _terminalId = _stageId === "exchange"
            ? "contracts_exchanged"
            : _stageId === "complete" ? "completion_date" : null;
          const _terminalRow = _terminalId
            ? requiredStageItems.find((m) => m.id === _terminalId)
            : null;
          const _otherRemaining = _terminalId
            ? requiredStageItems.filter(
                (m) => m.id !== _terminalId && resolveMilestoneState(m) !== "Complete",
              ).length
            : 0;
          // A ticked terminal row still needs the lifecycle confirmation (legacy unconfirmed ticks).
          const _gateStage = String(deal?.stage || "").toLowerCase();
          const _gateStatus = String(deal?.status || "").toLowerCase();
          const _lifecycleConfirmed = _stageId === "exchange"
            ? ["exchanged", "completed"].includes(_gateStage) || ["exchanged", "completed"].includes(_gateStatus)
            : _gateStage === "completed" || _gateStatus === "completed";
          const _terminalDone = _terminalRow
            && resolveMilestoneState(_terminalRow) === "Complete" && _lifecycleConfirmed;
          const showConfirmGate = !!_terminalId
            && _otherRemaining === 0
            && !_terminalDone
            && !isViewingOther
            && !isViewingLocked;
          const confirmEvent = _stageId === "exchange" ? "exchange" : "completion";

          // iter-alignment · P0 · Celebration triggers.  The
          //   Current Stage focus SHIFTS the moment the terminal
          //   milestone completes (exchange_ready → exchanged →
          //   rail advances the focus to Completion), so gating
          //   the celebration on `stageScopedItems[0].stage`
          //   would race the reload and never fire.  Instead we
          //   watch the two terminal rows directly on the raw
          //   milestones envelope — the celebration mounts
          //   unconditionally and the one-shot localStorage
          //   guard inside <ConfirmationCelebration/> ensures a
          //   refresh never replays either burst.
          const _rawItems = Array.isArray(milestones?.items) ? milestones.items : [];
          const _isRowDone = (m) => m && (m.state === "done" || m.state === "complete" || !!m.completed_at);
          const _lifeStage = String(deal?.stage || "").toLowerCase();
          const _exchangeConfirmed = (_lifeStage === "exchanged" || _lifeStage === "completed")
            && _rawItems.some((m) => m?.id === "contracts_exchanged" && _isRowDone(m));
          const _completionConfirmed = _lifeStage === "completed"
            && _rawItems.some((m) => m?.id === "completion_date" && _isRowDone(m));

          return (
            <div className="p-3.5 space-y-3" data-testid="canonical-tab-current-stage">
              {/* Stage header — standard Progress button removed. */}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10.5px] uppercase tracking-[0.10em] font-bold text-slate-500">
                    {isViewingFuture ? "Future stage" : isViewingOther ? "Viewing stage" : "Current stage"}
                  </p>
                  <h3 className="mt-0.5 text-[15px] font-bold text-slate-900" data-testid="canonical-current-stage-title">
                    {effectiveStageLabel}
                  </h3>
                  {isTerminal ? (
                    <p className="mt-0.5 text-[12px] text-slate-500">
                      {(stageId === "exchanged" && dealStatus !== "completed")
                        ? "Contracts exchanged — complete the remaining completion tasks below."
                        : "The transaction has reached a terminal state — no further stage progression required."}
                    </p>
                  ) : (
                    <p
                      className="mt-0.5 text-[11.5px] text-slate-500 tabular-nums"
                      data-testid="canonical-current-stage-progress"
                    >
                      {remainingCount === 0 ? (
                        <>
                          <span className="text-emerald-600 font-semibold">All required stage milestones complete</span>
                          <span className="text-slate-400"> · {doneCount} of {requiredTotal} required</span>
                        </>
                      ) : (
                        <>
                          <span className="text-slate-700 font-semibold">{remainingCount} remaining</span>
                          <span className="text-slate-400"> · {doneCount} of {requiredTotal} required complete</span>
                        </>
                      )}
                      {(isViewingOther ? viewedStep?.target_date : activeStep?.target_date) && (
                        <span className="text-slate-400"> · target {isViewingOther ? viewedStep?.target_date : activeStep?.target_date}</span>
                      )}
                    </p>
                  )}
                </div>
                {/* iter-alignment · P11/P12 · Return action + Force-Progress
                 * override.  Force-Progress is intentionally hidden while
                 * viewing another stage — it targets the canonical
                 * Current Stage and stage advancement remains the
                 * canonical progression engine's job, not the viewer's. */}
                {isViewingOther && (
                  <button
                    type="button"
                    onClick={onReturnToCurrentStage}
                    className="shrink-0 inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-violet-50 ring-1 ring-violet-200 hover:ring-violet-400 text-violet-700 text-[11.5px] font-semibold cf-press"
                    data-testid="canonical-current-stage-return"
                    title={`Return to ${stepLabel || "Current Stage"}`}
                  >
                    Return to {stepLabel || "Current Stage"}
                  </button>
                )}
                {/* iter-alignment · P — Move to next stage (2026-08-17).
                 *   Renames the Manager-only "Force progress"
                 *   override to a canonical, always-available
                 *   "Move to next stage" (or "Mark transaction
                 *   complete" at the Completion node) action.
                 *   On click:
                 *     • Compute the next applicable INCOMPLETE
                 *       journey step via `resolveJourneyProgress`
                 *       (same SSOT the panel already uses — no
                 *       duplicate wiring).
                 *     • Read the outstanding count from
                 *       `remainingCount` already computed above.
                 *     • If outstanding > 0, surface a confirm
                 *       modal with the exact "{N} items will
                 *       remain outstanding…" copy from the
                 *       brief.  Otherwise advance immediately.
                 *     • Call `chainStore.advanceStage(...,
                 *       { manual: true, outstandingCount })` —
                 *       the audit trail is emitted server-side
                 *       via the `manual_stage_advance` activity
                 *       row.
                 *   The button never auto-completes any milestone
                 *   — the checklist stays exactly as it was so
                 *   the operator can still tick or unbind items
                 *   after the stage cursor has moved.
                 *   Hidden while viewing another stage — the
                 *   Return button remains the canonical action
                 *   there. */}
                {!isViewingOther && !isTerminal && (activeStepIdSSOT || activeStep?.id) && (() => {
                  // Resolve the next applicable step from the SAME
                  // SSOT the panel uses (`resolved.steps` +
                  // `currentFocusIdx`).  Fall back to journeyState
                  // when the resolver hasn't hydrated yet.
                  const _steps = Array.isArray(resolved?.steps) && resolved.steps.length
                    ? resolved.steps
                    : jSteps;
                  const _focusIdx = typeof resolved?.currentFocusIdx === "number"
                    ? resolved.currentFocusIdx
                    : _steps.findIndex((s) => s?.id === (activeStepIdSSOT || activeStep?.id));
                  const _isAtCompletion = _focusIdx >= 0 && _steps[_focusIdx]?.id === "complete";
                  const _nextStep = _isAtCompletion
                    ? null
                    : (_focusIdx >= 0 ? _steps[_focusIdx + 1] : null);
                  // Next deal-stage id.  At Completion node the
                  // canonical DEAL_STAGES terminal value is
                  // "completed".  Otherwise pick the first
                  // DEAL_STAGES id the next journey node maps to.
                  const _nextDealStage = _isAtCompletion
                    ? "completed"
                    : (Array.isArray(_nextStep?.stages) && _nextStep.stages.length
                        ? _nextStep.stages[0]
                        : null);
                  if (!_isAtCompletion && !_nextDealStage) return null;
                  const _label = _isAtCompletion ? "Mark transaction complete" : "Move to next stage";
                  const _testid = _isAtCompletion
                    ? "canonical-current-stage-mark-complete"
                    : "canonical-current-stage-move-next";
                  const _handleAdvance = () => {
                    if (typeof onMoveToNextStage !== "function") return;
                    onMoveToNextStage({
                      isCompletion: _isAtCompletion,
                      nextDealStage: _nextDealStage,
                      nextLabel: _isAtCompletion ? "Completion" : (_nextStep?.longLabel || _nextStep?.label || "Next stage"),
                      outstandingCount: remainingCount,
                    });
                  };
                  return (
                    <button
                      type="button"
                      onClick={_handleAdvance}
                      className="shrink-0 inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-white ring-1 ring-slate-300 hover:ring-violet-400 text-slate-700 hover:text-violet-700 text-[11.5px] font-semibold cf-press"
                      data-testid={_testid}
                      data-outstanding-count={remainingCount}
                      title={
                        _isAtCompletion
                          ? "Mark this transaction complete"
                          : `Advance to the next journey stage${remainingCount > 0 ? ` (${remainingCount} outstanding)` : ""}`
                      }
                    >
                      {_label}
                    </button>
                  );
                })()}
              </div>

              {/* P0 · Fix Completion Stage Catalogue and Gating
                * (2026-08-18) — Future-stage viewing banner.
                * • Locked branch (viewing Completion pre-Exchange):
                *   two-line amber banner "Completion locked /
                *   Complete and confirm Exchange to unlock this
                *   stage."  The two lines stack vertically so the
                *   layout never crams on narrow rails.
                * • Editable branch: violet accent, dynamic
                *   Current Stage name inline (no "canonical
                *   journey" language).
                */}
              {isViewingOther && (
                <div
                  className={`rounded-lg px-3 py-2.5 ring-1 ${
                    isViewingLocked
                      ? "bg-amber-50/70 ring-amber-200/60 flex flex-col gap-1.5"
                      : "bg-violet-50/70 ring-violet-200/60 flex items-center gap-2"
                  }`}
                  data-testid="canonical-current-stage-viewing-banner"
                  data-viewing-mode={isViewingLocked ? "locked" : "editable"}
                  data-viewing-future={isViewingFuture ? "1" : "0"}
                >
                  {isViewingLocked ? (
                    <>
                      <div className="flex items-center gap-2">
                        <span
                          className="text-[10.5px] uppercase tracking-[0.10em] font-bold text-amber-800"
                          data-testid="canonical-current-stage-viewing-tag"
                        >
                          Locked
                        </span>
                        <span
                          className="text-[13px] font-bold text-slate-900"
                          data-testid="canonical-current-stage-viewing-label"
                        >
                          {effectiveStageLabel}
                        </span>
                      </div>
                      <p
                        className="text-[11.5px] text-slate-700 leading-snug"
                        data-testid="canonical-current-stage-viewing-copy"
                      >
                        {(() => {
                          const _done = stageId === "completed" || dealStatus === "completed";
                          const _exAt = deal?.exchanged_at || deal?.exchangedAt;
                          if (!_done && !_exAt && stageId !== "exchanged" && dealStatus !== "exchanged") {
                            return "Complete and confirm Exchange to unlock this stage.";
                          }
                          return `${_terminalBannerCopy(_done ? "Completed" : "Exchanged", _done ? (deal?.completed_at || deal?.completedAt) : _exAt)}.`;
                        })()}
                      </p>
                    </>
                  ) : (
                    <>
                      <span
                        className="text-[10.5px] uppercase tracking-[0.10em] font-bold text-violet-800"
                        data-testid="canonical-current-stage-viewing-tag"
                      >
                        Editing
                      </span>
                      <span
                        className="text-[12.5px] font-semibold text-slate-900"
                        data-testid="canonical-current-stage-viewing-label"
                      >
                        {effectiveStageLabel}
                      </span>
                      <span
                        className="ml-auto text-[11px] text-slate-500"
                        data-testid="canonical-current-stage-viewing-copy"
                      >
                        Changes update the shared record · current stage is{" "}
                        <span className="font-semibold text-violet-700">{stepLabel}</span>
                      </span>
                    </>
                  )}
                </div>
              )}

              {/* iter-alignment · P0 · Confirm Exchange /
               *   Confirm Completion gate.  Sits above the
               *   checklist ONLY when the canonical stage is
               *   Exchange or Completion, every OTHER required
               *   row is complete, and the terminal milestone
               *   (`contracts_exchanged` / `completion_date`)
               *   has not yet been confirmed.  Uses the existing
               *   `completeMilestone` write path — no new
               *   endpoint.  Success → celebration is fired by
               *   the `<ConfirmationCelebration/>` mount below
               *   watching the same terminal-row state.
               */}
              {showConfirmGate && (
                <div
                  className="rounded-xl ring-1 ring-emerald-200 bg-emerald-50/60 px-4 py-3 flex items-center justify-between gap-3"
                  data-testid={`canonical-confirm-gate-${confirmEvent}`}
                  data-terminal-milestone={_terminalId}
                >
                  <div className="min-w-0">
                    <p className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-emerald-700">
                      {confirmEvent === "exchange" ? "Ready to Exchange" : "Ready to Complete"}
                    </p>
                    <p className="mt-0.5 text-[12px] text-emerald-900 leading-snug">
                      Every required milestone is ticked. Confirm to record the
                      actual {confirmEvent === "exchange" ? "exchange" : "completion"} event —
                      today’s date will stamp the audit trail.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (typeof onCompleteMilestone === "function" && _terminalId) {
                        onCompleteMilestone(_terminalId);
                      }
                    }}
                    className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-semibold cf-press transition"
                    data-testid={`canonical-confirm-gate-${confirmEvent}-cta`}
                  >
                    {confirmEvent === "exchange" ? "Confirm Exchange" : "Confirm Completion"}
                  </button>
                </div>
              )}
              <ConfirmationCelebration
                dealId={dealId}
                event="exchange"
                trigger={_exchangeConfirmed}
              />
              <ConfirmationCelebration
                dealId={dealId}
                event="completion"
                trigger={_completionConfirmed}
              />

              {/* Interactive checklist */}
              {(isTerminal && !(stageId === "exchanged" && dealStatus !== "completed")) ? null : orderedMilestones.length === 0 ? (
                <p className="text-[12px] text-slate-500">No milestones scoped to this stage yet.</p>
              ) : (
                <StageChecklist
                  awaitingConfirmation={showConfirmGate}
                  orderedMilestones={orderedMilestones}
                  canCompleteSet={canCompleteSet}
                  onCompleteMilestone={onCompleteMilestone}
                  onReopenMilestone={onReopenMilestone}
                  onEditCompletionDate={onEditCompletionDate}
                  onSetTargetDate={onSetTargetDate}
                  onSetNote={onSetNote}
                  rowBlockerMap={rowBlockerMap}
                  onResolveBlocker={onResolveBlocker}
                  isViewingLocked={isViewingLocked}
                  isViewingEditable={isViewingEditable}
                  stageScopedItems={stageScopedItems}
                  stepLabel={stepLabel}
                  nextStepLabel={nextStepLabel}
                  docActionByMilestone={docActionByMilestone}
                  onDocAction={onDocActionForRow}
                  expandOptional={activeStageId === "survey"}
                />
              )}

              {/* Property documents & certificates — Contract-stage
               * companion accordion.  Never affects the Contract
               * required count (all rows live in the dedicated
               * `property_certificates` stage, `optional: true`).
               * Renders only while the operator is viewing the
               * Contract stage so it stays scoped. */}
              {activeStageId === "contract" && !isViewingLocked && (
                <PropertyCertificatesAccordion
                  milestones={milestones}
                  canCompleteSet={canCompleteSet}
                  onCompleteMilestone={onCompleteMilestone}
                  onReopenMilestone={onReopenMilestone}
                  onEditCompletionDate={onEditCompletionDate}
                  onSetTargetDate={onSetTargetDate}
                  onSetNote={onSetNote}
                />
              )}

              {/* iter-alignment · P0 · Deal Room · Documents
               * Consolidation (2026-03-01) — The standalone
               * Document Requests block that used to sit under
               * Current Stage was retired.  The right-rail
               * Documents card is now the single primary
               * document control point (Upload · Request · View
               * all).  Outstanding / Overdue / Completed status
               * remains visible via the Documents tab (opened by
               * "View all") which already surfaces the same
               * `docRequests` SSOT the retired workspace
               * consumed.  No new state, no new endpoint. */}

              {/* iter-alignment · P17 — Additional Enquiries.
               * Only rendered when the ACTIVE stage is Enquiries so
               * it stays a stage-scoped surface — no room-wide
               * clutter.  Fetches, creates, edits and removes via
               * the dedicated `deal_enquiries` endpoints (see
               * `routes_deal_enquiries.py`).  These are ad-hoc
               * conveyancing questions solicitors raise — they do
               * NOT affect canonical stage progression, Exchange
               * Confidence or Next Best Action.  On pilot deals the
               * canonical POST 404s and the section stays gracefully
               * empty. */}
              {activeStageId === "enquiries" && !isViewingLocked && (
                <AdditionalEnquiriesSection
                  dealId={dealId}
                  participants={participants}
                  documents={documents}
                />
              )}
            </div>
          );
        })()}

        {/* iter-alignment · P0 · Exchange Checklist interactivity
            fix (2026-02).  The tab body now threads the same
            `completeMilestone` + `reopenMilestone` callbacks the
            standalone render already relies on so every checkbox
            in Legal / Financial / Coordination is bi-directional
            (tick + untick) and every write feeds the shared
            milestone SSOT → Transaction Progress → Exchange
            Confidence chain via the existing `reload()` path. */}
        {tab === "checklist" && (
          <div className="p-5" data-testid="canonical-tab-checklist">
            <ExchangeEve
              deal={deal}
              milestones={milestones}
              onCompleteMilestone={onCompleteMilestone}
              onReopenMilestone={onReopenMilestone}
            />
          </div>
        )}

        {/* iter-alignment · P2 · All Milestones — read-only list of
            every milestone from the existing SSOT.  Zero new state.
            `milestones` is `{ items, can_complete }` — iterate over
            `milestoneItems` (already normalised above).
            iter-alignment · P36 · All Milestones Audit — the row
            marker now reads state through the CANONICAL
            `resolveMilestoneState()` helper (same one Current Stage
            + Exchange Eve consume) so a milestone marked complete
            in one surface ALWAYS renders with the emerald tick
            here.  Prior code compared `m?.status === "done"`
            against a field that the API never sets (`state` is the
            real field on the response), which meant the tick
            never lit up regardless of completion.  No new dates,
            no new status labels — the visual language is the same
            three-state grammar every other milestone surface uses:
            Complete (emerald tick), In Progress (violet dot),
            Outstanding (empty slate ring). */}
        {tab === "milestones" && (
          <div className="p-5 space-y-2" data-testid="canonical-tab-milestones">
            {milestoneItems.length === 0 && (
              <EmptyHint label="No milestones yet" sub="Milestones will appear here as the journey progresses." />
            )}
            {milestoneItems.map((m, i) => {
              const canonState = resolveMilestoneState(m);
              const isComplete   = canonState === "Complete";
              const isInProgress = canonState === "Waiting" || canonState === "Blocked";
              return (
                <div
                  key={m?.id || i}
                  className="rounded-lg ring-1 ring-slate-200 bg-white px-3 py-2 flex items-center gap-3"
                  data-testid={`canonical-milestone-row-${i}`}
                  data-milestone-state={canonState}
                >
                  {isComplete ? (
                    <span
                      className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-emerald-500 text-white shrink-0"
                      data-testid={`canonical-milestone-tick-${i}`}
                      aria-label="Complete"
                    >
                      <Check size={11} strokeWidth={3} />
                    </span>
                  ) : isInProgress ? (
                    <span
                      className="inline-flex items-center justify-center h-4 w-4 rounded-full ring-2 ring-violet-400 bg-violet-100 shrink-0"
                      aria-label="In progress"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
                    </span>
                  ) : (
                    <span
                      className="inline-flex h-4 w-4 rounded-full ring-1 ring-slate-300 bg-white shrink-0"
                      aria-label="Outstanding"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`text-[13px] font-semibold truncate ${isComplete ? "text-slate-500 line-through decoration-slate-300" : "text-slate-800"}`}>
                      {m?.title || m?.label || "Milestone"}
                    </p>
                    {m?.description && <p className="text-[11.5px] text-slate-500 truncate">{m.description}</p>}
                  </div>
                  {m?.target_date && (
                    <span className="text-[11px] text-slate-500 tabular-nums shrink-0">{m.target_date}</span>
                  )}
                </div>
              );
            })}
            {/* iter-alignment · P0 · Historic BSA — no longer applicable
                (2026-02-28)
                -----------------------------------------------------
                Surfaces once the operator flips `Property.bsa_applies`
                back to No AFTER completing any BSA work.  Reads the
                same canonical `activities` stream — no duplicate
                store — and renders completed `milestone_completed`
                rows whose `meta.milestone_id` starts with `bsa_`,
                plus the accompanying `bsa_flag_changed` audit row
                (reason + who + when).  Hidden entirely on active
                BSA deals + non-BSA transactions. */}
            {deal?.applicableBSA === false && (() => {
              const acts = Array.isArray(activities) ? activities : [];
              const historicBsa = acts.filter((a) => {
                const at = String(a?.activity_type || a?.type || "");
                const mid = String(a?.meta?.milestone_id || "");
                return at === "milestone_completed" && mid.startsWith("bsa_");
              });
              const flagChange = acts
                .filter((a) => String(a?.activity_type || a?.type || "") === "bsa_flag_changed")
                .sort((a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0))[0];
              if (historicBsa.length === 0 && !flagChange) return null;
              return (
                <div
                  className="mt-6 rounded-xl bg-slate-50 ring-1 ring-slate-200 px-4 py-3 space-y-2"
                  data-testid="canonical-historic-bsa"
                >
                  <div className="flex items-center gap-2 text-[11.5px] uppercase tracking-[0.10em] font-semibold text-slate-500">
                    <ShieldCheck size={12} strokeWidth={2.4} className="text-slate-400" />
                    Historic BSA — no longer applicable
                  </div>
                  {flagChange && (
                    <div className="text-[12px] text-slate-600" data-testid="canonical-historic-bsa-audit">
                      {flagChange?.meta?.reason ? `"${flagChange.meta.reason}"` : "No reason recorded"}
                      {" "}·{" "}
                      {flagChange?.meta?.actor_name || flagChange?.created_by_name || "Unknown user"}
                      {flagChange?.created_at ? ` · ${new Date(flagChange.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : ""}
                    </div>
                  )}
                  {historicBsa.length > 0 && (
                    <ul className="space-y-1" data-testid="canonical-historic-bsa-list">
                      {historicBsa.map((a, i) => (
                        <li key={a?.id || i} className="flex items-center gap-2 text-[12.5px] text-slate-500">
                          <Check size={11} strokeWidth={2.6} className="text-emerald-500 shrink-0" />
                          <span className="line-through decoration-slate-300 flex-1 truncate">
                            {a?.meta?.milestone_label || a?.summary || a?.meta?.milestone_id}
                          </span>
                          {a?.created_at && (
                            <span className="text-[11px] text-slate-400 tabular-nums shrink-0">
                              {new Date(a.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" })}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      <div className="px-5 pb-4 -mt-1 text-center">
        {/* iter-240 — Activity footer no longer renders a duplicate
            "Progress deal →" CTA. The canonical Update Milestones
            button lives beneath the active milestone in the journey
            rail (see canonical-progress-transaction-btn); duplicating
            it here only fragmented the operator's mental model and
            invited the question "which button is the real one?". */}
      </div>

      {/* iter-alignment · P0 · Deal Activity Polish (2026-02) —
          The canonical Activity history now lives as an
          always-visible surface at the base of the tabbed panel.
          Retired the standalone "Activity Feed" tab so the timeline
          is one continuous scan target, not gated behind a click.
          Every ActivityPanel prop is threaded through verbatim so
          the aggregator, filters, composer + SSOT wiring stay
          identical. */}
      <div className="border-t border-slate-100 px-5 pt-4 pb-5" data-testid="canonical-deal-activity-section">
        {(() => {
          const upNext = resolveUpNext(deal, milestones);
          /* View stage / Key Dates rows open the existing Current Stage milestone view for that stage. */
          const openStageDetails = (stepId) => {
            if (!stepId) return;
            handleSelect("current_stage");
            onActivateJourneyStep?.(stepId, { asSelect: true });
            window.requestAnimationFrame(() => {
              document.querySelector('[data-testid="canonical-activity-feed"]')
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
            });
          };
          const keyDatesNode = (
            <KeyDatesSummary deal={deal} milestones={milestones} docRequests={docRequests} onOpenStage={openStageDetails} />
          );
          return (
        <ActivityPanel
          dealId={dealId}
          deal={deal}
          upNextLabel={upNext ? upNext.label : null}
          upNextStepId={upNext ? upNext.id : null}
          onViewStage={openStageDetails}
          keyDates={keyDatesNode}
          activityStatus={activityStatus}
          onRetryActivity={onReloadRoom}
          participants={participants}
          documents={documents}
          milestones={milestones}
          operationalNotes={operationalNotes}
          waitingOn={waitingOn}
          currentUserId={currentUserId}
          activities={activities}
          onActivityLogged={onReloadRoom}
          composerOpenSignal={composerOpenSignal}
          composerInitialType={composerInitialType}
          onComposerClosed={onComposerClosed}
          onRequestDoc={onRequestDoc}
          onCriticalPathResolved={onCriticalPathResolved}
          onOpenSheet={onOpenSheet}
          onReviewDoc={onReviewDoc}
          onOpenMilestones={() => {
            handleSelect("milestones");
            window.requestAnimationFrame(() => {
              document.querySelector('[data-testid="canonical-activity-feed"]')
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
            });
          }}
          activityHasMore={activityHasMore}
          loadingEarlier={loadingEarlier}
          onLoadEarlier={onLoadEarlierActivity}
        />
          );
        })()}
      </div>
    </section>
  );
};

const ActivityList = ({ activities, participants }) => {
  const items = (activities || []).slice(0, 12);
  if (items.length === 0) {
    return <EmptyHint label="No recent activity yet" sub="As the transaction progresses, every event will appear here." />;
  }
  return (
    <ul className="relative" data-testid="canonical-activity-list">
      <span aria-hidden className="absolute left-[64px] top-1 bottom-1 w-px bg-slate-100" />
      {items.map((a, i) => {
        const ts = a.at || a.created_at || a.timestamp;
        const time = ts ? new Date(ts).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "—";
        const title = a.title || a.text || "Operational update";
        const description = a.description || a.subtitle || "";
        const kind = a.kind || guessKind(a);
        const actorId = a.who || a.actor_id || a.created_by;
        const actor = (participants || []).find((p) => p.user_id === actorId || p.id === actorId);
        const actorName = actor?.full_name || actor?.email?.split("@")[0] || a.actor_name || null;
        // iter-242 — Reduce repeated metadata. When the previous
        // event was by the same actor with the same kind, soften
        // the kind chip + actor name (still rendered for testid +
        // a11y, but visually de-emphasised).
        const prev = i > 0 ? items[i - 1] : null;
        const prevActorId = prev ? (prev.who || prev.actor_id || prev.created_by) : null;
        const prevKind = prev ? (prev.kind || guessKind(prev)) : null;
        const isRepeatActorKind = !!prev && actorId === prevActorId && kind === prevKind;
        return (
          <li
            key={a.id || i}
            className="flex items-start gap-3 py-2"
            data-testid={`canonical-activity-${i}`}
          >
            <span className="w-12 shrink-0 text-[11px] font-semibold text-slate-500 tabular-nums pt-1">
              {time}
            </span>
            <span className="relative w-3 shrink-0 flex justify-center pt-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-slate-900 leading-tight">{title}</p>
              {(description || actorName) && (
                <p className={`text-[11.5px] mt-0.5 leading-snug ${isRepeatActorKind ? "text-slate-400" : "text-slate-500"}`}>
                  {actorName && (() => {
                    /* iter-alignment · P56 — Deep-link the activity
                     * actor name to their Contact Profile so the
                     * activity feed becomes a jump-off point into
                     * the wider contact context.  Only produces a
                     * link when the resolved actor carries a
                     * `contact_id` — a bare participant id would
                     * 404 to "Contact not found". */
                    const actorContactId = actor?.contact_id || null;
                    if (actorContactId) {
                      return (
                        <Link
                          to={`/contacts/${actorContactId}`}
                          onClick={(e) => e.stopPropagation()}
                          className={`font-semibold hover:text-violet-700 hover:underline underline-offset-2 rounded ${isRepeatActorKind ? "text-slate-400" : "text-slate-600"}`}
                          data-testid={`canonical-activity-actor-name-${i}`}
                          aria-label={`Open ${actorName}'s profile`}
                        >
                          {actorName}
                        </Link>
                      );
                    }
                    return (
                      <span
                        className={`font-semibold ${isRepeatActorKind ? "text-slate-400" : "text-slate-600"}`}
                        data-testid={`canonical-activity-actor-name-${i}`}
                      >
                        {actorName}
                      </span>
                    );
                  })()}
                  {actorName && description && <span className="text-slate-300 mx-1">·</span>}
                  {description}
                </p>
              )}
            </div>
            <div className={isRepeatActorKind ? "opacity-50" : ""}>
              <ActivityKindChip kind={kind} />
            </div>
            {actor ? (
              <ParticipantAvatar p={actor} size={28} anchor={true} testid={`canonical-activity-actor-${i}`} />
            ) : (
              <span
                className="w-7 h-7 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold flex items-center justify-center shrink-0"
                data-testid={`canonical-activity-actor-${i}`}
                title={a.actor_name || "Unattributed"}
              >
                {initialsOf(a.actor_name || "—")}
              </span>
            )}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  className="text-slate-400 hover:text-slate-700 shrink-0"
                  aria-label="Event actions"
                  data-testid={`canonical-activity-actions-${i}`}
                >
                  <MoreHorizontal size={14} />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-44 p-1.5" data-testid={`canonical-activity-actions-menu-${i}`}>
                <button
                  type="button"
                  onClick={() => toast.success("Pinned to top of feed", { duration: 1500 })}
                  className="w-full text-left text-[12.5px] px-2.5 py-1.5 rounded-md hover:bg-slate-50 text-slate-700 inline-flex items-center gap-2"
                  data-testid={`canonical-activity-pin-${i}`}
                >
                  <Pin size={12} className="text-slate-400" /> Pin event
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // Reply = focus the bottom composer; the operator can
                    // quote-reply by tapping @mention in the next pass.
                    const input = document.querySelector('[data-testid="canonical-composer-input"]');
                    input?.focus?.();
                  }}
                  className="w-full text-left text-[12.5px] px-2.5 py-1.5 rounded-md hover:bg-slate-50 text-slate-700 inline-flex items-center gap-2"
                  data-testid={`canonical-activity-reply-${i}`}
                >
                  <Reply size={12} className="text-slate-400" /> Reply
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const url = `${window.location.origin}${window.location.pathname}#activity-${a.id || i}`;
                    try {
                      await navigator.clipboard.writeText(url);
                      toast.success("Event link copied", { duration: 1500 });
                    } catch { toast.error("Could not copy link"); }
                  }}
                  className="w-full text-left text-[12.5px] px-2.5 py-1.5 rounded-md hover:bg-slate-50 text-slate-700 inline-flex items-center gap-2"
                  data-testid={`canonical-activity-copy-link-${i}`}
                >
                  <Link2 size={12} className="text-slate-400" /> Copy event link
                </button>
              </PopoverContent>
            </Popover>
          </li>
        );
      })}
    </ul>
  );
};

const DocumentsTabBody = ({ documents, canUpload, onReviewDoc }) => {
  if (!documents || documents.length === 0) {
    return <EmptyHint label="No documents yet" sub={canUpload ? "Upload from the right rail." : "Documents will appear here as they are shared."} />;
  }
  return (
    <ul className="space-y-2" data-testid="canonical-tab-documents">
      {documents.slice(0, 8).map((d) => {
        const rs = String(d.review_status || "").toLowerCase();
        const label =
          rs === "reviewed" ? "Reviewed" :
          rs === "replacement_requested" ? "Replacement" :
          "Needs Review";
        const pill =
          rs === "reviewed"
            ? "bg-emerald-50 text-emerald-700 ring-emerald-200/70"
            : rs === "replacement_requested"
              ? "bg-rose-50 text-rose-700 ring-rose-200/70"
              : "bg-amber-50 text-amber-700 ring-amber-200/70";
        const clickable = typeof onReviewDoc === "function";
        return (
          <li key={d.id}>
            <button
              type="button"
              onClick={clickable ? () => onReviewDoc(d) : undefined}
              disabled={!clickable}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-50/60 text-left transition-colors ${clickable ? "hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-200" : ""}`}
              data-testid={`canonical-tab-doc-${d.id}`}
              data-doc-review-status={rs || "needs_review"}
            >
              <FileText size={14} className="text-slate-500 shrink-0" />
              <p className="text-[12.5px] text-slate-700 truncate flex-1">{d.name || "Document"}</p>
              <span className={`inline-flex items-center px-2 h-[20px] rounded-md ring-1 ${pill} text-[10.5px] font-semibold whitespace-nowrap`}>{label}</span>
              <span className="text-[10.5px] text-slate-400">{fmtRel(d.created_at)}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};

const EmptyHint = ({ label, sub }) => (
  <div className="px-2 py-6 text-center">
    <p className="text-[13px] font-semibold text-slate-700">{label}</p>
    {sub && <p className="text-[11.5px] text-slate-500 mt-1">{sub}</p>}
  </div>
);

/* ── Right operational rail ────────────────────────────────────────── */
const VISIBILITY_TONE = {
  internal:         { ring: "ring-indigo-200/80", bg: "bg-indigo-50/70", text: "text-indigo-700", icon: ShieldCheck, label: "Internal",         dotBg: "#6366F1" },
  full_access:      { ring: "ring-slate-200/70", bg: "bg-slate-50", text: "text-slate-600", icon: ShieldCheck, label: "Full access",     dotBg: "#64748B" },
  solicitor_access: { ring: "ring-violet-200/80", bg: "bg-violet-50/70", text: "text-violet-700", icon: ShieldCheck, label: "Solicitor access", dotBg: "#7C3AED" },
  buyer_access:     { ring: "ring-sky-200/80", bg: "bg-sky-50/70", text: "text-sky-700", icon: ShieldCheck, label: "Buyer access",    dotBg: "#0EA5E9" },
  seller_access:    { ring: "ring-emerald-200/80", bg: "bg-emerald-50/70", text: "text-emerald-700", icon: ShieldCheck, label: "Seller access",   dotBg: "#10B981" },
  read_only:        { ring: "ring-slate-200/70", bg: "bg-slate-50", text: "text-slate-500", icon: Eye, label: "Read only",       dotBg: "#94A3B8" },
  restricted:       { ring: "ring-amber-200/70", bg: "bg-amber-50/70", text: "text-amber-700", icon: Lock, label: "Restricted",      dotBg: "#D97706" },
  observer:         { ring: "ring-slate-200/70", bg: "bg-slate-50", text: "text-slate-500", icon: Eye, label: "Observer",         dotBg: "#94A3B8" },
};

/* iter-198 — Live status palette for the multi-party operating-room
 * participant rail. Each tone maps the responsiveness signal from
 * `participants-enriched` (active | responsive | slow | silent) into
 * a calm operational presence dot + a short human caption. The Deal
 * Room avatar tiles reuse this so the eye reads "who's live, who's
 * gone quiet" in one calm scan — never noisy, never social-media. */
const RESPONSIVENESS_TONE = {
  active:     { dot: "#10B981", ringHex: "rgba(16,185,129,0.45)", label: "Active now",       text: "text-emerald-600" },
  responsive: { dot: "#10B981", ringHex: "rgba(16,185,129,0.30)", label: "Responsive",       text: "text-emerald-600" },
  slow:       { dot: "#F59E0B", ringHex: "rgba(245,158,11,0.35)", label: "Slow to respond",  text: "text-amber-600" },
  silent:     { dot: "#94A3B8", ringHex: "rgba(148,163,184,0.30)", label: "Silent",          text: "text-slate-500" },
};
const responsivenessTone = (key) => RESPONSIVENESS_TONE[key] || RESPONSIVENESS_TONE.silent;

const presenceCaption = (p) => {
  if (!p) return "Silent";
  if (p.responsiveness === "active") return "Active now";
  const iso = p.last_seen_at || p.last_message_at;
  if (iso) return `Active ${fmtRel(iso)}`;
  if (p.responsiveness === "responsive") return "Responsive";
  if (p.responsiveness === "slow") return "Slow to respond";
  return "Silent";
};

/* iter-198 — Canonical avatar tile used across the Deal Room. Carries
 * the participant's visibility ring + a small live-status dot in the
 * bottom-right. Avatar-only — name + role live in the row body. */
const ParticipantAvatar = ({ p, size = 36, anchor = true, testid }) => {
  if (!p) {
    return (
      <span
        aria-hidden
        className="rounded-full bg-slate-100 text-slate-400 flex items-center justify-center shrink-0"
        style={{ width: size, height: size, fontSize: Math.floor(size * 0.34) }}
      >
        ?
      </span>
    );
  }
  const vis = VISIBILITY_TONE[p.visibility] || VISIBILITY_TONE.full_access;
  const presence = responsivenessTone(p.responsiveness);
  const initials = initialsOf(p.full_name || p.email);
  const dotSize = Math.max(8, Math.round(size * 0.26));
  const title = `${p.full_name || p.email || "Participant"}${p.role_label ? " · " + p.role_label : ""}`;
  /* iter-alignment · P56 — Deep-link to the canonical Contact
   * Profile at `/contacts/{contact_id}` whenever the participant
   * carries one.  Falls back to the legacy in-page anchor
   * (`#canonical-rail-participant-{id}`) when no contact_id is
   * known — participants without a Contact row still scroll
   * into the rail correctly.  We deliberately do NOT fall back
   * to `p.id` because that is the deal_participants row id, not
   * a contact id (would produce a "Contact not found" page). */
  const contactId = p.contact_id || null;
  const deepLink  = anchor && contactId ? `/contacts/${contactId}` : null;
  const legacyAnchor = anchor && !deepLink ? `#canonical-rail-participant-${p.user_id || p.id}` : null;
  const Wrapper = anchor ? (deepLink ? Link : "a") : "span";
  const wrapperProps = anchor
    ? (deepLink
        ? { to: deepLink, title, "aria-label": title, onClick: (e) => e.stopPropagation() }
        : { href: legacyAnchor, title, "aria-label": title })
    : { title, "aria-label": title };
  return (
    <Wrapper
      {...wrapperProps}
      className="relative inline-flex shrink-0 group"
      data-testid={testid || `canonical-participant-avatar-${p.user_id || p.id}`}
    >
      <span
        className={`rounded-full bg-gradient-to-br from-indigo-400 to-blue-500 text-white font-bold flex items-center justify-center ring-2 ${vis.ring}`}
        style={{ width: size, height: size, fontSize: Math.floor(size * 0.32) }}
      >
        {initials}
      </span>
      <span
        aria-hidden
        className="absolute bottom-0 right-0 rounded-full ring-2 ring-white"
        style={{
          width: dotSize,
          height: dotSize,
          background: presence.dot,
          boxShadow: `0 0 0 3px ${presence.ringHex}`,
        }}
        data-testid={`canonical-participant-status-${p.user_id || p.id}`}
      />
    </Wrapper>
  );
};

/* iter-198 — Calm monochrome comms icons for the Participant Panel.
 * Email always works (we have `email`). Phone + WhatsApp render a
 * disabled-but-present affordance when we don't have phone data —
 * the operating-room hierarchy stays consistent across all
 * participants.
 *
 * P0 · Participants contact-icon visibility (2026-08-18) — the
 * enabled icon stroke darkens to `#475569` (slate-600) with a
 * locked `strokeWidth={2}` so the phone / message / email glyphs
 * read crisply on the participant panel.  Button footprint,
 * circular tile radius, ring colour + row gap all preserved.
 * Change applied on the shared comms component so every
 * participant row stays visually consistent with the right-rail
 * `<QuickAction>` render.
 */
const CommsIcons = ({ p }) => {
  const id = p.user_id || p.id;
  const email = p.email;
  const phone = p.phone || p.phone_e164 || p.mobile;
  const wa = phone ? `https://wa.me/${String(phone).replace(/[^\d]/g, "")}` : null;
  const baseBtn = "cf-inline-icon inline-flex items-center justify-center w-7 h-7 rounded-md text-[#475569] hover:text-slate-900 hover:bg-slate-50 transition-colors";
  const disabledBtn = "cf-inline-icon inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-300 cursor-not-allowed";
  return (
    <span className="inline-flex items-center gap-0.5 shrink-0" data-testid={`canonical-participant-comms-${id}`} data-icon-stroke="#475569">
      {phone ? (
        <a
          href={`tel:${phone}`}
          className={baseBtn}
          title={`Call ${p.full_name || p.email || "participant"}`}
          data-testid={`canonical-participant-call-${id}`}
        >
          <Phone size={13} strokeWidth={2} />
        </a>
      ) : (
        <button
          type="button"
          disabled
          className={disabledBtn}
          title="Phone TBC"
          data-testid={`canonical-participant-call-${id}`}
        >
          <Phone size={13} strokeWidth={2} />
        </button>
      )}
      {email ? (
        <a
          href={`mailto:${email}`}
          className={baseBtn}
          title={`Email ${p.full_name || email}`}
          data-testid={`canonical-participant-email-${id}`}
        >
          <Mail size={13} strokeWidth={2} />
        </a>
      ) : (
        <button
          type="button"
          disabled
          className={disabledBtn}
          title="Email TBC"
          data-testid={`canonical-participant-email-${id}`}
        >
          <Mail size={13} strokeWidth={2} />
        </button>
      )}
      {wa ? (
        <a
          href={wa}
          target="_blank"
          rel="noopener noreferrer"
          className={baseBtn}
          title={`WhatsApp ${p.full_name || p.email || "participant"}`}
          data-testid={`canonical-participant-whatsapp-${id}`}
        >
          <MessageCircle size={13} strokeWidth={2} />
        </a>
      ) : (
        <button
          type="button"
          disabled
          className={disabledBtn}
          title="WhatsApp TBC"
          data-testid={`canonical-participant-whatsapp-${id}`}
        >
          <MessageCircle size={13} strokeWidth={2} />
        </button>
      )}
    </span>
  );
};

/* ──────────────────────────────────────────────────────────────────
 * RightRailParticipants — iter-234
 *
 * Canonical right-rail Participants panel. Replaces the hero's
 * participant slots so the operator's eye doesn't bounce between
 * the hero block and the rail when coordinating a call/email.
 *
 * Surfaces six canonical operational roles in a fixed reading
 * order (Lead agent first because that's the operator's own
 * principal voice; everyone else flows top-to-bottom in the
 * canonical chain order):
 *
 *   1. Lead Agent           (negotiator)
 *   2. Seller
 *   3. Buyer
 *   4. Seller Solicitor
 *   5. Buyer Solicitor
 *   6. Mortgage Broker
 *
 * Quick actions per row:
 *   • Call      — tel:{phone}
 *   • WhatsApp  — https://wa.me/{e164(phone)}
 *   • Email     — mailto:{email}
 *
 * Disabled (calm slate, no hover) when the underlying field is
 * missing — never broken `mailto:undefined` links.
 *
 * Monochrome icon system — no WhatsApp green, no Phone blue. The
 * panel reads as quiet operational infrastructure, never a CRM
 * contact card.
 */
const RAIL_PARTICIPANT_ROLES = [
  { key: "negotiator",       label: "Lead Agent" },
  // iter-alignment · Deal Room · Final Right Rail (2026-03-02) —
  // SELLERS group (calm inline heading; multi-seller supported).
  { key: "seller",           label: "Seller",           group: "sellers" },
  { key: "seller_solicitor", label: "Seller Solicitor", group: "sellers" },
  // BUYERS group (multi-buyer supported; broker only when
  // funding basis is Mortgage / Gift + Mortgage — enforced by
  // the existing `displayRoles` filter).
  { key: "buyer",            label: "Buyer",            group: "buyers"  },
  { key: "buyer_solicitor",  label: "Buyer Solicitor",  group: "buyers"  },
  { key: "mortgage_broker",  label: "Mortgage Broker",  group: "buyers"  },
  { key: "managing_agent",   label: "Managing Agent" },
];

const _e164Digits = (raw) => String(raw || "").replace(/[^\d]/g, "");

const RailParticipantRow = ({ role, label, participant, dealId, canCreateContact = false }) => {
  const name = _participantName(participant);
  const phone = participant?.phone || null;
  const email = participant?.email || null;
  const initials = name ? initialsOf(name) : "—";
  const present = !!participant && !!name;
  const navigate = useNavigate();
  const location = useLocation();

  /* iter-344 — Optional last-interaction signal. Only surfaces
   * when a real Updates timeline entry references this
   * participant; never fabricated. Reads via the shared
   * `getLatestUpdateForParticipant` helper so the string matches
   * the Updates tab timeline character-for-character. */
  const latestUpdate = present && dealId
    ? getLatestUpdateForParticipant(dealId, participant.id || participant.contact_id)
    : null;
  const summary = summariseUpdate(latestUpdate);

  // Quick action targets. `null` → disabled state.
  const telHref = present && phone ? `tel:${_e164Digits(phone)}` : null;
  const waHref  = present && phone ? `https://wa.me/${_e164Digits(phone)}` : null;
  const mailHref = present && email ? `mailto:${email}` : null;

  /* iter-295 — Dynamic, name-aware tooltips. The brief calls for
   * "Call Sarah" / "Email buyer solicitor" so operators know
   * exactly who the action routes to. Names take precedence; the
   * participant's role label is the fallback. */
  const _firstName = name ? String(name).trim().split(/\s+/)[0] : null;
  const _addressee = _firstName || label || role;
  const _callTip    = phone ? `Call ${_addressee}`     : `Call · phone unavailable`;
  const _messageTip = phone ? `Message ${_addressee}`  : `Message · phone unavailable`;
  const _emailTip   = email ? `Email ${_addressee}`    : `Email · address unavailable`;

  /* P1 · Deal Room → Contact Profile deep linking (2026-02-28)
   *
   * The entire participant row is now clickable and navigates to
   * the canonical `/contacts/{contact_id}` profile.  We reuse the
   * existing linked-Contact FK (`participant.contact_id`) that
   * P56 already wired into the participant name link — no new
   * lookup, no new endpoint, no new state.  Quick action icons
   * carry `stopPropagation` (both onClick + onKeyDown) so they
   * remain independent affordances that never trigger row nav.
   * Unlinked participants gracefully disable row nav while
   * leaving every quick action functional. */
  const contactId = present ? (participant?.contact_id || null) : null;
  const rowNavigable = !!contactId;
  const handleRowNav = () => {
    if (!rowNavigable) return;
    saveDealReturnState(dealId);
    navigate(`/contacts/${contactId}`, {
      state: { fromDeal: { path: `${location.pathname}${location.search}${location.hash}` } },
    });
  };
  const handleRowKey = (e) => {
    if (!rowNavigable) return;
    // Let interactive children handle their own key events.
    if (e.target?.closest?.('a, button, [data-no-row-nav]')) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleRowNav();
    }
  };
  const handleRowClick = (e) => {
    if (!rowNavigable) return;
    if (e.target?.closest?.('a, button, [data-no-row-nav]')) return;
    handleRowNav();
  };

  /* Calm icon button. Disabled state renders as a muted slate
   * tile that does not respond to hover — communicates "no data"
   * without ever firing a broken link.
   *
   * P0 · Participants contact-icon visibility (2026-08-18) — the
   * enabled icon stroke is darkened to `#475569` (slate-600) with
   * a locked `strokeWidth={2}` so the phone / message / email
   * glyphs read crisply against the white button surface.  Size,
   * circular background (`bg-white`), border colour
   * (`ring-slate-200`) and horizontal spacing (`gap-1.5`) are
   * deliberately preserved so the Participants row layout is
   * byte-identical to the pre-fix baseline.  Hover contrast
   * (existing `hover:text-slate-900`) is retained without a new
   * treatment.  Change applied on the shared component so every
   * participant row and every icon type stays consistent. */
  const QuickAction = ({ href, label: actionLabel, tooltip, testid, ariaLabel, children }) => {
    const enabled = !!href;
    const base = "inline-flex items-center justify-center w-7 h-7 rounded-lg ring-1 transition-colors";
    // Stop propagation so the row-level nav never intercepts an
    // action-icon click / keyboard activation.
    const stopRow = (e) => e.stopPropagation();
    if (!enabled) {
      return (
        <span
          aria-disabled
          title={tooltip}
          className={`${base} bg-slate-50 ring-slate-200/70 text-slate-300 cursor-not-allowed`}
          data-testid={testid}
          data-disabled="true"
          data-no-row-nav="true"
        >
          {children}
        </span>
      );
    }
    return (
      <a
        href={href}
        target={actionLabel === "Message" ? "_blank" : undefined}
        rel={actionLabel === "Message" ? "noreferrer noopener" : undefined}
        title={tooltip}
        aria-label={ariaLabel}
        className={`${base} bg-white ring-slate-200 text-[#475569] hover:text-slate-900 hover:ring-slate-300`}
        data-testid={testid}
        data-enabled="true"
        data-no-row-nav="true"
        data-icon-stroke="#475569"
        onClick={stopRow}
        onKeyDown={stopRow}
      >
        {children}
      </a>
    );
  };

  return (
    <li
      className={`group flex items-center gap-3 -mx-1.5 px-1.5 py-1 rounded-lg transition-colors min-h-[3.25rem] ${
        rowNavigable
          ? "cursor-pointer touch-manipulation select-none hover:bg-slate-50/80 active:bg-slate-100 focus-visible:bg-slate-50/80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-violet-300"
          : ""
      }`}
      data-testid={`canonical-rail-participant-${role}`}
      data-present={present ? "true" : "false"}
      data-navigable={rowNavigable ? "true" : "false"}
      role={rowNavigable ? "button" : undefined}
      tabIndex={rowNavigable ? 0 : undefined}
      aria-label={rowNavigable ? `Open ${name}'s contact profile` : undefined}
      onClick={handleRowClick}
      onKeyDown={handleRowKey}
    >
      <span
        className={`shrink-0 rounded-full w-9 h-9 flex items-center justify-center text-[12px] font-bold ${
          present
            ? "bg-gradient-to-br from-indigo-400 to-blue-500 text-white"
            : "bg-slate-100 text-slate-400 ring-1 ring-slate-200/70"
        } ${rowNavigable ? "ring-offset-1 transition-shadow group-hover:ring-2 group-hover:ring-violet-300 group-active:ring-2 group-active:ring-violet-400" : ""}`}
        aria-hidden
        data-testid={`canonical-rail-participant-avatar-${role}`}
      >
        {present ? initials : "?"}
      </span>
      <div className="min-w-0 flex-1">
        {/* P1 · Row-level nav — the participant name renders as
             calm static text now that the whole row navigates to
             the linked Contact Profile.  The `canonical-rail-
             participant-name-{role}` testid + hover-underline
             affordance are preserved so existing contract tests
             + telemetry never drift. */}
        <p
          className={`text-[12.5px] font-semibold truncate leading-tight ${
            present
              ? rowNavigable
                ? "text-slate-900 underline decoration-slate-300 decoration-dotted underline-offset-[3px] group-hover:text-violet-700 group-hover:decoration-violet-400 group-hover:decoration-solid group-focus-visible:text-violet-700 transition-colors"
                : "text-slate-900"
              : "text-slate-400"
          }`}
          data-testid={`canonical-rail-participant-name-${role}`}
          title={rowNavigable ? `Open ${name}'s profile` : undefined}
        >
          {name || "Not assigned"}
        </p>
        <p className="text-[10.5px] text-slate-500 mt-0.5 leading-tight tracking-[-0.005em]">
          {label}
          {present && !contactId && dealId && canCreateContact && !participant?.is_internal && (
            <>
              {" · "}
              <button
                type="button"
                className="font-semibold text-violet-700 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-violet-300 rounded"
                onClick={(e) => { e.stopPropagation(); createContactForParticipant(dealId, participant); }}
                data-testid={`canonical-rail-participant-create-contact-${role}`}
              >
                Create contact
              </button>
            </>
          )}
        </p>
        {summary && (
          /* iter-344 — Operational context sub-line. Displays the
             most recent communication logged against this
             participant so the operator sees, at a glance, when the
             last touchpoint happened and via which channel. */
          <p
            className="text-[10px] text-slate-400 mt-0.5 leading-tight tracking-[-0.005em] truncate"
            data-testid={`canonical-rail-participant-last-${role}`}
          >
            <span className="font-semibold text-slate-500">Last:</span> {summary.rel} · {summary.sourceLabel}
          </p>
        )}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <QuickAction
          href={telHref}
          label="Call"
          tooltip={_callTip}
          ariaLabel={_callTip}
          testid={`canonical-rail-participant-call-${role}`}
        >
          <Phone size={13} strokeWidth={2} />
        </QuickAction>
        <QuickAction
          href={waHref}
          label="Message"
          tooltip={_messageTip}
          ariaLabel={_messageTip}
          testid={`canonical-rail-participant-whatsapp-${role}`}
        >
          <MessageCircle size={13} strokeWidth={2} />
        </QuickAction>
        <QuickAction
          href={mailHref}
          label="Email"
          tooltip={_emailTip}
          ariaLabel={_emailTip}
          testid={`canonical-rail-participant-email-${role}`}
        >
          <Mail size={13} strokeWidth={2} />
        </QuickAction>
        {/* P1 · Deep-link chevron.  Surfaces only on hover /
             focus of a navigable row so the affordance reads
             cleanly without adding persistent visual noise.
             Purely decorative — actual navigation is handled by
             the row-level click / Enter / Space handlers. */}
        {rowNavigable && (
          <ChevronRight
            size={12}
            strokeWidth={2.2}
            className="text-slate-300 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-visible:opacity-100 transition-opacity"
            aria-hidden
            data-testid={`canonical-rail-participant-chevron-${role}`}
          />
        )}
        {/* iter-alignment · P0 · Participants · Align Contact
            Actions (2026-02) — Reserve chevron slot even when
            the row isn't navigable so Call / Message / Email
            icons sit in the same fixed column across every row.
            Width matches the ChevronRight glyph (`size={12}`)
            plus the flex `gap-1.5` rhythm — no visual shift when
            the affordance appears / disappears between rows. */}
        {!rowNavigable && (
          <span
            aria-hidden
            className="inline-block w-3 h-3 shrink-0"
            data-testid={`canonical-rail-participant-chevron-spacer-${role}`}
          />
        )}
      </div>
    </li>
  );
};

const RightRailParticipants = ({ participants, onManageParticipants, dealId, deal, onUpdateFundingBasis }) => {
  /* iter-alignment · P3 · Multiple Participants per Role (2026-02-28)
   * ----------------------------------------------------------------
   * Buyer + Seller now legitimately hold >1 participant.  We
   * resolve every role by collecting ALL matches; single-owner
   * roles will always return an array of length 0 or 1 (the backend
   * refuses a second row) so the rendering path is uniform. */
  const resolved = RAIL_PARTICIPANT_ROLES.map((r) => {
    const list = _findParticipants(participants, r.key);
    return {
      ...r,
      participants: list,
      // Preserve `participant` (single) for the empty-state /
      // banner logic that still asks "is this slot filled".
      participant: list[0] || null,
    };
  });

  /* P3 · Broker Auto-Prompt (2026-02-28)
   *
   * When the deal's funding basis is `mortgage` or
   * `gift_mortgage` AND no `mortgage_broker` participant is
   * currently assigned, surface a compact "Mortgage Broker
   * Required" prompt above the participants list.  The banner
   * carries a single "Assign Broker" CTA that opens the SAME
   * ParticipantsModal with the role pre-selected — reusing the
   * existing broker assignment flow verbatim.  The banner
   * auto-hides the moment a mortgage_broker participant lands
   * in the resolved list (no manual dismissal, no lingering
   * state).  Cash-funded deals never see the prompt. */
  const fundingBasis = String(deal?.funding_basis || deal?.fundingBasis || "").toLowerCase();
  const requiresBroker = fundingBasis === "mortgage" || fundingBasis === "gift_mortgage";
  const brokerAssigned = !!resolved.find((r) => r.key === "mortgage_broker")?.participant;
  const showBrokerPrompt = requiresBroker && !brokerAssigned;

  /* iter-397a · Hide non-applicable roles.  On the pilot corpus
   * every genuine Deal Room has ≥ 5 populated participant rows,
   * so an absent Mortgage Broker means the deal is cash-only and
   * an absent Managing Agent means the property is freehold — in
   * both cases we drop the empty "Not assigned" slot.  Legacy
   * partly-seeded rooms still fall back to the full list so we
   * never silently hide a role that the operator was expecting.
   *
   * P3 · Broker Auto-Prompt — when the funding basis flags the
   * broker as required, always show the mortgage_broker slot
   * regardless of the populated-count heuristic so the operator
   * can see + assign it without opening the participants modal
   * first. */
  const populatedCount = resolved.filter((r) => !!r.participant).length;
  const displayRoles = populatedCount >= 5
    ? resolved.filter((r) => !!r.participant || (r.key === "mortgage_broker" && requiresBroker))
    : resolved;
  /* Pilot Readiness UX Sweep — calm empty state.
   * When zero participants are assigned, seven "Not assigned" rows
   * with disabled call/email/message icons dominate the rail with
   * no operational signal. Collapse them into a single actionable
   * empty state so the operator sees exactly what to do next. */
  const isFullyEmpty = populatedCount === 0;
  return (
    <section
      className="rounded-2xl bg-white py-4 px-5 cf-card-hover"
      style={{ border: "1px solid rgba(226,232,240,0.9)" }}
      data-testid="canonical-rail-participants"
      data-empty={isFullyEmpty ? "true" : "false"}
    >
      <header className="flex items-center justify-between mb-3.5">
        <h3 className="text-[13px] font-bold tracking-tight text-slate-900">Participants</h3>
        {onManageParticipants && (
          <button
            type="button"
            onClick={() => onManageParticipants()}
            className="text-[11px] font-semibold text-violet-700 hover:text-violet-800 transition-colors"
            data-testid="canonical-rail-participants-manage"
          >
            Manage
          </button>
        )}
      </header>
      {/* iter-alignment · Deal Room · Funding Basis Placement
          (2026-03-02) — Funding Basis pill retired from this card
          on 2026-03-02.  It now edits inside the Buyer Position
          card (single canonical location) using the exact same
          `onUpdateFundingBasis` handler / PATCH / SSOT.  Broker
          gate below still reads `funding_basis` off the deal so
          the "Mortgage Broker Required" prompt renders here
          verbatim (Participants remains the single place where
          the broker is assigned). */}
      {showBrokerPrompt && (
        <div
          className="mb-3 rounded-lg bg-amber-50/80 ring-1 ring-amber-200/70 px-3 py-2 flex items-center gap-2.5"
          data-testid="canonical-rail-broker-required"
          data-funding-basis={fundingBasis}
          role="status"
        >
          <AlertTriangle size={13} className="text-amber-600 shrink-0" strokeWidth={2.2} />
          <div className="min-w-0 flex-1">
            <p className="text-[11.5px] font-bold text-amber-800 leading-tight">
              Mortgage Broker Required
            </p>
            <p className="text-[10.5px] text-amber-700/90 leading-tight mt-0.5 truncate">
              Funding basis is {fundingBasis === "gift_mortgage" ? "Gift + Mortgage" : "Mortgage"} — assign a broker to complete the participants set.
            </p>
          </div>
          {onManageParticipants && (
            <button
              type="button"
              onClick={() => onManageParticipants("mortgage_broker")}
              className="shrink-0 inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[11px] font-semibold bg-amber-600 text-white hover:bg-amber-700 transition-colors cf-press"
              data-testid="canonical-rail-broker-required-cta"
            >
              Assign Broker
            </button>
          )}
        </div>
      )}
      <ul className="space-y-3" data-testid="canonical-rail-participants-list">
        {isFullyEmpty ? (
          <li
            className="rounded-xl bg-slate-50/70 ring-1 ring-slate-200/70 px-3.5 py-3.5"
            data-testid="canonical-rail-participants-empty"
          >
            <div className="flex items-start gap-3">
              <span
                className="shrink-0 rounded-full w-9 h-9 flex items-center justify-center bg-white ring-1 ring-slate-200"
                aria-hidden
              >
                <Users size={15} className="text-slate-400" strokeWidth={1.8} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[12.5px] font-semibold text-slate-800 leading-tight">
                  No participants assigned yet
                </p>
                <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                  Add participants to unlock chase actions — call, message and email straight from this rail.
                </p>
                {onManageParticipants && (
                  <button
                    type="button"
                    onClick={() => onManageParticipants()}
                    className="mt-2.5 inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[11px] font-semibold bg-violet-600 text-white hover:bg-violet-700 transition-colors cf-press"
                    data-testid="canonical-rail-participants-empty-cta"
                  >
                    <Plus size={12} strokeWidth={2.4} />
                    Add participants
                  </button>
                )}
              </div>
            </div>
          </li>
        ) : (
          /* iter-alignment · Deal Room · Final Right Rail
             (2026-03-02) — Lead Agent renders standalone; SELLERS
             / BUYERS calm group headings are injected before the
             first role of each group so 1-party and multi-party
             deals stay scannable.  Uses a `.reduce` so the
             heading + row nodes stay siblings inside the same
             `<ul>` (multi-participant expansion logic below is
             untouched). */
          displayRoles.reduce((acc, role, idx) => {
            const prev = idx > 0 ? displayRoles[idx - 1] : null;
            if (role.group && role.group !== prev?.group) {
              acc.push(
                <li
                  key={`hdr-${role.group}`}
                  className={`${idx === 0 ? "pt-0" : "pt-1.5"} pb-0.5 list-none`}
                  data-testid={`canonical-rail-participants-group-header-${role.group}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-400">
                      {role.group === "sellers" ? "SELLERS" : "BUYERS"}
                    </p>
                    {onManageParticipants && (
                      <button
                        type="button"
                        onClick={() => onManageParticipants(role.group === "sellers" ? "seller" : "buyer")}
                        className="inline-flex items-center gap-0.5 min-h-[28px] px-1 text-[10.5px] font-semibold text-violet-700 hover:text-violet-900 touch-manipulation"
                        data-testid={`canonical-rail-participants-add-another-${role.group === "sellers" ? "seller" : "buyer"}`}
                      >
                        <Plus size={11} strokeWidth={2.4} />
                        {role.group === "sellers" ? "Add another seller" : "Add another buyer"}
                      </button>
                    )}
                  </div>
                </li>
              );
            }
            const { key, label, participant, participants: rolePeople } = role;
            /* Flat list: every buyer / seller renders as its own full-width row —
             * no collapsible "Buyer (2)" group, no count label, no left border. */
            if (Array.isArray(rolePeople) && rolePeople.length > 1) {
              rolePeople.forEach((p) => acc.push(
                <RailParticipantRow
                  key={p.id || `${key}-${p.contact_id || p.email || p.full_name}`}
                  role={key}
                  label={label}
                  participant={p}
                  dealId={dealId}
                  canCreateContact={!!onManageParticipants}
                />
              ));
            } else {
              acc.push(
                <RailParticipantRow
                  key={key}
                  role={key}
                  label={label}
                  participant={participant}
                  dealId={dealId}
                  canCreateContact={!!onManageParticipants}
                />
              );
            }
            return acc;
          }, [])
        )}
      </ul>
    </section>
  );
};



/* ── Buyer Position (right rail) ───────────────────────────────
 *
 * iter-alignment · Deal Room · Final Right Rail · Buyer Position
 * (2026-03-02).  Single canonical editor for the deal-level buyer
 * position facts:
 *
 *   • Buyer type          — FTB / Home mover / Second-home /
 *                           Investor
 *   • Property to sell    — None / Not on market / On market /
 *                           Under offer / Exchanged
 *   • Funding basis       — Cash / Mortgage / Gift + Mortgage
 *                           (moved from Participants; reuses the
 *                           existing `onUpdateFundingBasis` handler
 *                           verbatim — same PATCH, SSOT and journey
 *                           reconciliation)
 *   • Mortgage position   — No AIP / AIP obtained / Application
 *                           submitted / Offer issued  (hidden when
 *                           funding basis === "cash")
 *   • Deposit             — £ amount and/or % (independently
 *                           nullable; either / both are legal)
 *   • Proof of funds      — Not requested / Requested / Received /
 *                           Verified
 *
 * All writes flow through `onUpdateBuyerPosition` → the canonical
 * deal PATCH.  Empty state renders `Buyer position not completed ·
 * + Add details` — never fabricates a value.
 */
const BUYER_TYPE_OPTS = [
  { value: "ftb",               label: "First-time buyer" },
  { value: "home_mover",        label: "Home mover" },
  { value: "second_home_buyer", label: "Second-home buyer" },
  { value: "investor",          label: "Investor" },
];
const PROPERTY_TO_SELL_OPTS = [
  { value: "none",          label: "None" },
  { value: "not_on_market", label: "Not on market" },
  { value: "on_market",     label: "On market" },
  { value: "under_offer",   label: "Under offer" },
  { value: "exchanged",     label: "Exchanged" },
];
const MORTGAGE_POSITION_OPTS = [
  { value: "no_aip",                label: "No AIP" },
  { value: "aip_obtained",          label: "AIP obtained" },
  { value: "application_submitted", label: "Application submitted" },
  { value: "offer_issued",          label: "Offer issued" },
];
const PROOF_OF_FUNDS_OPTS = [
  { value: "not_requested", label: "Not requested" },
  { value: "requested",     label: "Requested" },
  { value: "received",      label: "Received" },
  { value: "verified",      label: "Verified" },
];
const FUNDING_BASIS_OPTS = [
  { value: "cash",          label: "Cash" },
  { value: "mortgage",      label: "Mortgage" },
  { value: "gift_mortgage", label: "Gift + Mortgage" },
];

const _bpLabel = (opts, value) => {
  const hit = opts.find((o) => o.value === value);
  return hit ? hit.label : null;
};
// Purchase deposit ⇄ agreed price reconciliation (whole £, % to 2dp).
const _bpPrice = (deal) => {
  const n = Number(String(deal?.agreedPrice ?? deal?.agreed_price ?? "").replace(/[^\d.]/g, ""));
  return n > 0 ? n : null;
};
const _bpNum = (v) => (v === "" || v == null || !Number.isFinite(Number(v)) ? null : Number(v));
const _bpPctStr = (n) => String(Math.round(n * 100) / 100);
const _bpGBP = (n) => `£${Math.round(n).toLocaleString("en-GB")}`;
const _MORTGAGE_RANK = ["no_aip", "aip_obtained", "application_submitted", "offer_issued"];
// Canonical mortgage progress (milestones) can run ahead of the manually saved position.
const _bpMortgageFromMilestones = (milestones) => {
  const done = new Set((milestones?.items || []).filter((m) => m.state === "done").map((m) => m.id));
  if (done.has("mortgage_offer")) return "offer_issued";
  if (done.has("mortgage_application_submitted")) return "application_submitted";
  return null;
};

const RailBuyerPositionCard = ({ deal, milestones, canEdit, onUpdateFundingBasis, onUpdateBuyerPosition }) => {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const fb = String(deal?.funding_basis || deal?.fundingBasis || "").toLowerCase();
  const isCash = fb === "cash";
  const [draft, setDraft] = useState(() => ({
    buyer_type:          deal?.buyerType         || deal?.buyer_type         || "",
    property_to_sell:    deal?.propertyToSell    || deal?.property_to_sell   || "",
    mortgage_position:   deal?.mortgagePosition  || deal?.mortgage_position  || "",
    proof_of_funds:      deal?.proofOfFunds      || deal?.proof_of_funds     || "",
    deposit_amount:      deal?.depositAmount ?? deal?.deposit_amount ?? "",
    deposit_percentage:  deal?.depositPercentage ?? deal?.deposit_percentage ?? "",
  }));

  useEffect(() => {
    if (editing) return;
    setDraft({
      buyer_type:          deal?.buyerType         || deal?.buyer_type         || "",
      property_to_sell:    deal?.propertyToSell    || deal?.property_to_sell   || "",
      mortgage_position:   deal?.mortgagePosition  || deal?.mortgage_position  || "",
      proof_of_funds:      deal?.proofOfFunds      || deal?.proof_of_funds     || "",
      deposit_amount:      deal?.depositAmount ?? deal?.deposit_amount ?? "",
      deposit_percentage:  deal?.depositPercentage ?? deal?.deposit_percentage ?? "",
    });
  }, [deal, editing]);

  const RowRead = ({ label, valueNode, testid }) => (
    <li className="grid grid-cols-[minmax(120px,42%)_1fr] items-center gap-3 text-[13px] py-2" data-testid={testid}>
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-900 font-medium truncate">{valueNode ?? <span className="text-slate-400">—</span>}</span>
    </li>
  );

  /* Proof of funds — semantic chip tone per pilot spec:
     Verified = green, Received = neutral, Requested = amber,
     Not requested = grey.  Uses the shared calm chip chrome. */
  const _pofChipClass = (v) => (
    v === "verified"      ? "bg-emerald-50 text-emerald-700" :
    v === "received"      ? "bg-slate-100 text-slate-700"    :
    v === "requested"     ? "bg-amber-50 text-amber-700"     :
    v === "not_requested" ? "bg-slate-50 text-slate-500"     :
                            ""
  );

  const savedFB = fb;
  const savedBuyerType    = deal?.buyerType         || deal?.buyer_type         || null;
  const savedPropToSell   = deal?.propertyToSell    || deal?.property_to_sell   || null;
  const savedMortgagePos  = deal?.mortgagePosition  || deal?.mortgage_position  || null;
  const savedProofOfFunds = deal?.proofOfFunds      || deal?.proof_of_funds     || null;
  const savedDepositAmt   = _bpNum(deal?.depositAmount ?? deal?.deposit_amount);
  const savedDepositPct   = _bpNum(deal?.depositPercentage ?? deal?.deposit_percentage);
  const price = _bpPrice(deal);
  // Flag (never rewrite) a deal marked chain-free while the buyer still has a property to sell.
  const chainMismatch = deal?.chain_free === true && ["not_on_market", "on_market", "under_offer", "exchanged"].includes(savedPropToSell);
  // Canonical detector says a PoF document is on file but the card status hasn't caught up.
  const pofAwaiting = (deal?.blockers || []).some((b) => b.id === "missing_proof_of_funds" && b.severity === "info");
  const shownPof = !["received", "verified"].includes(savedProofOfFunds) && pofAwaiting ? "received" : savedProofOfFunds;
  const canonicalMortgage = _bpMortgageFromMilestones(milestones);
  const shownMortgagePos = _MORTGAGE_RANK.indexOf(canonicalMortgage) > _MORTGAGE_RANK.indexOf(savedMortgagePos)
    ? canonicalMortgage : savedMortgagePos;

  // Amount is the source of truth; % is re-derived from the agreed price when both are known.
  const depositDisplay = (() => {
    if (savedDepositAmt != null) {
      const pct = price ? (savedDepositAmt / price) * 100 : savedDepositPct;
      const text = pct != null ? `${_bpGBP(savedDepositAmt)} (${_bpPctStr(pct)}%)` : _bpGBP(savedDepositAmt);
      if (savedDepositAmt > 0 && savedDepositAmt < 1000 && price && price >= 10000) {
        return (
          <span className="inline-flex items-center gap-1.5">
            {text}
            <span
              className="rounded-full px-1.5 py-0.5 text-[10.5px] font-semibold bg-amber-50 text-amber-700"
              title="This looks like an amount in thousands — re-enter the full £ amount"
              data-testid="canonical-rail-buyer-position-deposit-check"
            >
              Check amount
            </span>
          </span>
        );
      }
      return text;
    }
    if (savedDepositPct != null) {
      return price ? `${_bpGBP((price * savedDepositPct) / 100)} (${_bpPctStr(savedDepositPct)}%)` : `${_bpPctStr(savedDepositPct)}%`;
    }
    return null;
  })();

  const onDepositAmount = (raw) => {
    const digits = String(raw).replace(/[^\d]/g, "");
    setDraft((d) => ({
      ...d,
      deposit_amount: digits,
      deposit_percentage: digits !== "" && price ? _bpPctStr((Number(digits) / price) * 100) : (digits === "" ? "" : d.deposit_percentage),
    }));
  };
  const onDepositPct = (raw) => {
    const v = String(raw).replace(/[^\d.]/g, "");
    setDraft((d) => ({
      ...d,
      deposit_percentage: v,
      deposit_amount: v !== "" && price ? String(Math.round((price * Number(v)) / 100)) : (v === "" ? "" : d.deposit_amount),
    }));
  };

  // iter-alignment · Deal Room · Buyer Position pilot polish
  // (2026-03-02) — hide Property to sell in the saved view when
  // the buyer is FTB with no property (either `none` or unset).
  // Data is preserved either way; presentation only.

  const anyValue = !!(
    draft.buyer_type || draft.property_to_sell ||
    draft.mortgage_position || draft.proof_of_funds ||
    (draft.deposit_amount !== "" && draft.deposit_amount != null) ||
    (draft.deposit_percentage !== "" && draft.deposit_percentage != null) ||
    fb
  );

  const handleSave = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const patch = {
        buyerType:          draft.buyer_type          || null,
        propertyToSell:     draft.property_to_sell    || null,
        // If cash, force mortgage_position → null.
        mortgagePosition:   isCash ? null : (draft.mortgage_position || null),
        proofOfFunds:       draft.proof_of_funds      || null,
        depositAmount:      draft.deposit_amount === "" ? null : Number(draft.deposit_amount),
        depositPercentage:  draft.deposit_percentage === "" ? null : Number(draft.deposit_percentage),
      };
      await onUpdateBuyerPosition?.(patch);
      toast.success("Buyer position updated");
      setEditing(false);
    } catch (err) {
      toast.error("Couldn't save buyer position", { description: apiError(err) });
    } finally {
      setBusy(false);
    }
  };

  const RowRead_STALE = ({ label, value, muted, testid }) => (
    <li className="flex items-center justify-between text-[12px] py-1" data-testid={testid}>
      <span className="text-slate-500">{label}</span>
      <span className={`font-semibold ${muted ? "text-slate-400" : "text-slate-800"}`}>
        {value || "—"}
      </span>
    </li>
  );

  // Empty state — no funding basis + no other fields filled.
  if (!editing && !anyValue) {
    return (
      <section
        className="rounded-2xl bg-white py-4 px-5 cf-card-hover"
        style={{ border: "1px solid rgba(226,232,240,0.9)" }}
        data-testid="canonical-rail-buyer-position"
      >
        <header className="mb-2 flex items-center justify-between">
          <h3 className="text-[13px] font-bold tracking-tight text-slate-900">Buyer Position</h3>
          {canEdit && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-[10.5px] font-semibold text-violet-700 hover:text-violet-800 hover:underline underline-offset-2"
              data-testid="canonical-rail-buyer-position-edit"
              title="Add buyer position details"
            >
              Edit
            </button>
          )}
        </header>
        <p className="text-[12px] text-slate-500" data-testid="canonical-rail-buyer-position-empty">
          Buyer position not completed ·{" "}
          <button
            type="button"
            onClick={() => canEdit && setEditing(true)}
            disabled={!canEdit}
            className="font-semibold text-violet-700 hover:text-violet-800 hover:underline underline-offset-2"
            data-testid="canonical-rail-buyer-position-add"
          >
            + Add details
          </button>
        </p>
      </section>
    );
  }

  return (
    <section
      className="rounded-2xl bg-white py-4 px-5 cf-card-hover"
      style={{ border: "1px solid rgba(226,232,240,0.9)" }}
      data-testid="canonical-rail-buyer-position"
    >
      <header className="mb-2 flex items-center justify-between">
        <h3 className="text-[13px] font-bold tracking-tight text-slate-900">Buyer Position</h3>
        {canEdit && (
          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            className="text-[10.5px] font-semibold text-violet-700 hover:text-violet-800 hover:underline underline-offset-2"
            data-testid="canonical-rail-buyer-position-edit"
          >
            {editing ? "Cancel" : "Edit"}
          </button>
        )}
      </header>

      {editing ? (
        <div className="space-y-2.5" data-testid="canonical-rail-buyer-position-form">
          {/* Buyer type */}
          <label className="flex items-center justify-between gap-3 text-[12px]">
            <span className="text-slate-500">Buyer type</span>
            <select
              value={draft.buyer_type}
              onChange={(e) => setDraft((d) => ({ ...d, buyer_type: e.target.value }))}
              className="h-8 max-w-[62%] text-[12px] px-2 rounded-md border border-slate-200 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-violet-200"
              data-testid="canonical-rail-buyer-position-input-buyer-type"
            >
              <option value="">—</option>
              {BUYER_TYPE_OPTS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </select>
          </label>
          {/* Property to sell */}
          <label className="flex items-center justify-between gap-3 text-[12px]">
            <span className="text-slate-500">Property to sell</span>
            <select
              value={draft.property_to_sell}
              onChange={(e) => setDraft((d) => ({ ...d, property_to_sell: e.target.value }))}
              className="h-8 max-w-[62%] text-[12px] px-2 rounded-md border border-slate-200 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-violet-200"
              data-testid="canonical-rail-buyer-position-input-property-to-sell"
            >
              <option value="">—</option>
              {PROPERTY_TO_SELL_OPTS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </select>
          </label>
          {/* Funding basis — reuse existing handler verbatim */}
          <div
            className="flex items-center justify-between gap-3 text-[12px]"
            data-testid="canonical-funding-basis-row"
          >
            <span className="text-slate-500">Funding basis</span>
            <div
              className="inline-flex rounded-lg ring-1 ring-slate-200 bg-slate-50 p-0.5"
              role="radiogroup"
              aria-label="Funding basis"
              data-testid="canonical-funding-basis-toggle"
            >
              {(() => {
                // iter-alignment · Deal Room · Final Right Rail —
                // Pill seeded from the canonical `funding_basis`
                // field (SSOT).  This literal is asserted by
                // `test_pill_seeded_from_canonical_funding_basis_field`.
                const current = String(deal?.funding_basis || deal?.fundingBasis || "").toLowerCase();
                return FUNDING_BASIS_OPTS.map((opt) => {
                  const active = current === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => onUpdateFundingBasis?.(opt.value)}
                      className={`h-7 px-2.5 rounded-md text-[11px] font-semibold transition-colors ${
                        active
                          ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200"
                          : "text-slate-500 hover:text-slate-700"
                      }`}
                      data-testid={`canonical-funding-basis-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  );
                });
              })()}
            </div>
          </div>
          {/* Mortgage position — hidden for Cash */}
          {!isCash && (
            <label
              className="flex items-center justify-between gap-3 text-[12px]"
              data-testid="canonical-rail-buyer-position-row-mortgage-position"
            >
              <span className="text-slate-500">Mortgage position</span>
              <select
                value={draft.mortgage_position}
                onChange={(e) => setDraft((d) => ({ ...d, mortgage_position: e.target.value }))}
                className="h-8 max-w-[62%] text-[12px] px-2 rounded-md border border-slate-200 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-violet-200"
                data-testid="canonical-rail-buyer-position-input-mortgage-position"
              >
                <option value="">—</option>
                {MORTGAGE_POSITION_OPTS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
              </select>
            </label>
          )}
          {/* Purchase deposit £ ⇄ % (reconciled against the agreed price) */}
          <div
            className="flex items-center justify-between gap-3 text-[12px]"
            data-testid="canonical-rail-buyer-position-row-deposit"
          >
            <span className="text-slate-500">Purchase deposit</span>
            <div className="flex items-center gap-1.5">
              <span className="text-slate-400 text-[11px]">£</span>
              <input
                type="text"
                inputMode="numeric"
                value={draft.deposit_amount === "" || draft.deposit_amount == null ? "" : Number(draft.deposit_amount).toLocaleString("en-GB")}
                onChange={(e) => onDepositAmount(e.target.value)}
                placeholder="75,000"
                className="h-8 w-[92px] text-[12px] px-1.5 rounded-md border border-slate-200 bg-white text-slate-800 text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-violet-200"
                data-testid="canonical-rail-buyer-position-input-deposit-amount"
              />
              <input
                type="text"
                inputMode="decimal"
                value={draft.deposit_percentage}
                onChange={(e) => onDepositPct(e.target.value)}
                placeholder="%"
                className="h-8 w-[54px] text-[12px] px-1.5 rounded-md border border-slate-200 bg-white text-slate-800 text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-violet-200"
                data-testid="canonical-rail-buyer-position-input-deposit-percentage"
              />
              <span className="text-slate-400 text-[11px]">%</span>
            </div>
          </div>
          {/* Proof of funds */}
          <label className="flex items-center justify-between gap-3 text-[12px]">
            <span className="text-slate-500">Proof of funds</span>
            <select
              value={draft.proof_of_funds}
              onChange={(e) => setDraft((d) => ({ ...d, proof_of_funds: e.target.value }))}
              className="h-8 max-w-[62%] text-[12px] px-2 rounded-md border border-slate-200 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-violet-200"
              data-testid="canonical-rail-buyer-position-input-proof-of-funds"
            >
              <option value="">—</option>
              {PROOF_OF_FUNDS_OPTS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </select>
          </label>
          <div className="pt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={busy}
              className="h-8 px-3 rounded-md text-[12px] font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-60"
              data-testid="canonical-rail-buyer-position-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={busy}
              className="h-8 px-3.5 rounded-md bg-violet-600 text-white text-[12px] font-semibold hover:bg-violet-700 disabled:opacity-60 inline-flex items-center gap-1.5"
              data-testid="canonical-rail-buyer-position-save"
            >
              {busy && <Loader2 size={12} className="animate-spin" />}
              Save
            </button>
          </div>
        </div>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="canonical-rail-buyer-position-list">
          <RowRead
            label="Buyer type"
            valueNode={_bpLabel(BUYER_TYPE_OPTS, savedBuyerType)}
            testid="canonical-rail-buyer-position-row-buyer-type"
          />
          <RowRead
            label="Property to sell"
            valueNode={savedPropToSell ? (
              <span className="inline-flex items-center gap-1.5">
                {_bpLabel(PROPERTY_TO_SELL_OPTS, savedPropToSell)}
                {chainMismatch && (
                  <span
                    className="rounded-full px-1.5 py-0.5 text-[10.5px] font-semibold bg-amber-50 text-amber-700"
                    title="This deal is marked chain-free, but the buyer still has a property to sell — please review"
                    data-testid="canonical-rail-buyer-position-chain-mismatch"
                  >
                    Check chain
                  </span>
                )}
              </span>
            ) : null}
            testid="canonical-rail-buyer-position-row-property-to-sell"
          />
          <RowRead
            label="Funding basis"
            valueNode={
              savedFB ? (
                <span className="inline-block rounded-full px-2 py-0.5 text-[12px] font-medium bg-violet-50 text-violet-700">
                  {_bpLabel(FUNDING_BASIS_OPTS, savedFB)}
                </span>
              ) : null
            }
            testid="canonical-rail-buyer-position-row-funding-basis"
          />
          {!isCash && (
            <RowRead
              label="Mortgage position"
              valueNode={_bpLabel(MORTGAGE_POSITION_OPTS, shownMortgagePos)}
              testid="canonical-rail-buyer-position-row-mortgage-position"
            />
          )}
          <RowRead
            label="Purchase deposit"
            valueNode={depositDisplay}
            testid="canonical-rail-buyer-position-row-deposit"
          />
          <RowRead
            label="Proof of funds"
            valueNode={
              shownPof ? (
                <span
                  className={`inline-block rounded-full px-2 py-0.5 text-[12px] font-medium ${_pofChipClass(shownPof)}`}
                  data-testid="canonical-rail-buyer-position-proof-chip"
                  title={shownPof !== savedProofOfFunds ? "Proof of funds document on file — awaiting verification" : undefined}
                >
                  {_bpLabel(PROOF_OF_FUNDS_OPTS, shownPof)}
                </span>
              ) : null
            }
            testid="canonical-rail-buyer-position-row-proof-of-funds"
          />
        </ul>
      )}
    </section>
  );
};

/* ── Documents hub (right rail) ────────────────────────────────────
 *
 * Doctrine: "Waiting for it → Document Requests. Already have it
 * → Documents." Right-rail Documents is a compact view of files
 * we ALREADY hold on this deal.  Uploads stream through
 * /api/deals/{id}/documents (appends `document_upload` activity
 * server-side).  Requesting a doc from another party is owned
 * exclusively by the Document Requests workspace tab, which
 * carries its own Outstanding / Overdue / Completed tracking.
 *
 * Acceptance pins (data-testid stable for contract tests):
 *   • canonical-rail-documents-upload-btn      — compact + Upload
 *   • canonical-rail-documents-input           — hidden file input
 *   • canonical-rail-documents-view-all        — jumps to tab
 *   • canonical-rail-documents-uploading       — progress chip
 *   • canonical-rail-documents-empty           — one-line calm empty state
 *   • canonical-rail-doc-{id}                  — list row (existing)
 *
 * Premium / calm constraints:
 *   • no marketing illustrations, no bright fills, no large icons.
 *   • header row keeps its visual weight balanced with `+ Upload`
 *     next to `View all`. */
const ALLOWED_FILE_TYPES = ".pdf,.docx,.xlsx,.png,.jpg,.jpeg";
const ALLOWED_MIME_PREFIXES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/msword",
  "application/vnd.ms-excel",
  "image/png",
  "image/jpeg",
];
const isAllowedFile = (f) => {
  if (!f) return false;
  const name = (f.name || "").toLowerCase();
  if (/\.(pdf|docx|xlsx|png|jpe?g)$/i.test(name)) return true;
  return ALLOWED_MIME_PREFIXES.some((m) => (f.type || "").startsWith(m));
};

const RailDocumentsHub = ({
  documents, dealId, canUpload,
  onViewAll, onUploadFiles, onReviewDoc, onRequestDoc,
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const hasDocs = Array.isArray(documents) && documents.length > 0;

  const handleFiles = useCallback(async (fileList) => {
    if (!canUpload) {
      toast.error("Read-only access — uploads disabled on this deal.");
      return;
    }
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0) return;
    const accepted = files.filter(isAllowedFile);
    const rejected = files.length - accepted.length;
    if (rejected > 0) {
      toast.error(`${rejected} file${rejected === 1 ? "" : "s"} skipped — only PDF, DOCX, XLSX, PNG, JPG accepted.`);
    }
    if (accepted.length === 0) return;
    setUploading(true);
    try {
      await onUploadFiles?.(accepted);
    } finally {
      setUploading(false);
    }
  }, [canUpload, onUploadFiles]);

  const onPickClick = useCallback(() => {
    if (!canUpload) {
      toast.error("Read-only access — uploads disabled on this deal.");
      return;
    }
    inputRef.current?.click();
  }, [canUpload]);
  const onPickChange = useCallback((e) => {
    handleFiles(e.target?.files);
    if (e.target) e.target.value = "";
  }, [handleFiles]);
  // Empty-state dropzone: drops run the same handleFiles path as "+ Upload".
  const [dragOver, setDragOver] = useState(false);
  const onDragOver = useCallback((e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; setDragOver(true); }, []);
  const onDragLeave = useCallback((e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }, []);
  const onDrop = useCallback((e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer?.files); }, [handleFiles]);

  /* iter-alignment · Deal Room Documents UX Cleanup (2026-02).
   *
   * Doctrine
   * --------
   * "Waiting for it → Document Requests. Already have it →
   * Documents." The right-rail Documents card is now a compact
   * view of files we ALREADY hold on this deal.  Requesting a
   * doc from another party lives exclusively inside the
   * Document Requests workspace tab where the Outstanding /
   * Overdue / Completed tracking already runs.
   *
   * Removals (with pytest locks refreshed to match):
   *   • Large dashed dropzone + drag-drop handlers
   *   • Full-width gradient "Request document" CTA
   *
   * Kept intact (SSOT — Transaction Library):
   *   • Section wrapper + testid `canonical-rail-documents`
   *   • `canonical-rail-documents-view-all` link
   *   • Row list + statuses + review-drawer wiring
   *   • Hidden `<input type=file>` + existing `onUploadFiles`
   *
   * New surface:
   *   • Compact `+ Upload` button in the header (opens the same
   *     file picker) — `canonical-rail-documents-upload-btn`.
   *   • Calm "No documents yet" empty state — `canonical-rail-
   *     documents-empty` — no request CTA, no dropzone. */
  return (
    <section
      className="rounded-2xl bg-white py-4 px-5 cf-card-hover"
      style={{ border: "1px solid rgba(226,232,240,0.9)" }}
      data-testid="canonical-rail-documents"
    >
      <header className="flex items-center justify-between mb-3 gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-[13px] font-bold tracking-tight text-slate-900">Documents</h3>
          {uploading && (
            <span
              className="inline-flex items-center gap-1 px-1.5 h-[18px] rounded-md bg-white ring-1 ring-violet-200 text-[10.5px] font-semibold text-violet-700"
              data-testid="canonical-rail-documents-uploading"
            >
              <Loader2 size={10} className="animate-spin" />
              Uploading…
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onPickClick}
            disabled={!canUpload}
            className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[11px] font-semibold text-violet-700 ring-1 ring-violet-200 bg-violet-50/50 hover:bg-violet-50 hover:text-violet-800 cf-press transition disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Upload document"
            title={canUpload ? "Upload a document you already have" : "Read-only access — uploads disabled"}
            data-testid="canonical-rail-documents-upload-btn"
          >
            <Plus size={11} strokeWidth={2.6} />
            Upload
          </button>
          {/* iter-alignment · P0 · Deal Room · Documents
           * Consolidation (2026-03-01) — Request lives on the
           * right-rail card so Upload · Request · View all form
           * the single primary document control point.  Reuses
           * the existing canonical `onRequestDoc` → RequestDocSheet
           * drawer that the checklist row + retired Document
           * Requests workspace already ran through — no new SSOT,
           * no new endpoint, no new state. */}
          <button
            type="button"
            onClick={() => onRequestDoc?.()}
            disabled={!canUpload || typeof onRequestDoc !== "function"}
            className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[11px] font-semibold text-slate-700 ring-1 ring-slate-200 bg-white hover:bg-slate-50 hover:text-slate-900 cf-press transition disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Request document"
            title={canUpload ? "Request a document from another party" : "Read-only access — requests disabled"}
            data-testid="canonical-rail-documents-request-btn"
          >
            <ClipboardList size={11} strokeWidth={2.6} />
            Request
          </button>
          <button
            type="button"
            onClick={() => onViewAll?.()}
            className="inline-flex items-center gap-0.5 text-[11.5px] font-semibold text-violet-700 hover:text-violet-900"
            data-testid="canonical-rail-documents-view-all"
            aria-label="View all documents for this deal"
            title="Open the Documents workspace scoped to this deal"
          >
            View all <ChevronRight size={11} strokeWidth={2.6} />
          </button>
        </div>
      </header>

      {/* Hidden file input — click-to-browse is the sole upload
       * path.  Drag-and-drop was retired because Document
       * Requests already owns the "waiting for it" workflow, and
       * files landing in the rail can drop straight through this
       * compact picker. */}
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ALLOWED_FILE_TYPES}
        onChange={onPickChange}
        className="hidden"
        data-testid="canonical-rail-documents-input"
        aria-hidden
      />

      {/* Document list — compact rows, review drawer wired. */}
      {hasDocs ? (
        <ul className="space-y-2.5" data-testid="canonical-rail-documents-list">
          {documents.slice(0, 4).map((d) => {
            /* iter-alignment · P0 Phase 2 — Prefer the canonical
             * `review_status` field written by the /review endpoint.
             * Falls back to the legacy `d.status` vocabulary so
             * pre-migration rows keep rendering the right pill.
             */
            const rs = String(d.review_status || "").toLowerCase();
            let docStatus;
            if (rs === "reviewed") docStatus = "Reviewed";
            else if (rs === "replacement_requested") docStatus = "Replacement";
            else if (rs === "needs_review") docStatus = "Needs Review";
            else {
              const raw = String(d.status || "").toLowerCase();
              docStatus =
                /approv|signed|complete/.test(raw)                ? "Approved"
                : /reviewed|acknowledg/.test(raw)                 ? "Reviewed"
                : /request|awaiting.*upload|pending.*upload/.test(raw) ? "Requested"
                : /expir/.test(raw)                                ? "Expired"
                : /miss|overdue/.test(raw)                         ? "Missing"
                : /review|upload|received/.test(raw)               ? "Needs Review"
                : "Needs Review";
            }
            const tone =
              docStatus === "Approved"     ? "emerald" :
              docStatus === "Reviewed"     ? "emerald" :
              docStatus === "Needs Review" ? "amber"   :
              docStatus === "Requested"    ? "violet"  :
              docStatus === "Replacement"  ? "rose"    :
              docStatus === "Missing"      ? "rose"    :
              docStatus === "Expired"      ? "slate"   : "sky";
            const palette = {
              emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200/70",
              amber:   "bg-amber-50 text-amber-700 ring-amber-200/70",
              violet:  "bg-violet-50 text-violet-700 ring-violet-200/70",
              rose:    "bg-rose-50 text-rose-700 ring-rose-200/70",
              slate:   "bg-slate-100 text-slate-600 ring-slate-200/70",
              sky:     "bg-sky-50 text-sky-700 ring-sky-200/70",
            }[tone];
            const uploader = d.uploaded_by_name || d.uploader_name || d.created_by_name || null;
            /* iter-362 — Distinct file-type icon. Uses the Lucide
             * vocabulary already in the app so PDFs read as
             * `FileText`, spreadsheets as `FileSpreadsheet`,
             * images as `FileImage`, archives as `FileArchive`,
             * everything else as a plain `File`. */
            const FileTypeIcon = _iconForFile(d.name || d.title, d.mime_type || d.mimeType);
            const clickable = typeof onReviewDoc === "function";
            const RowTag = clickable ? "button" : "li";
            const rowProps = clickable
              ? {
                  type: "button",
                  onClick: () => onReviewDoc(d),
                  className:
                    "w-full text-left flex items-center gap-3 rounded-lg -mx-1 px-1 py-1 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-200 transition-colors",
                }
              : { className: "flex items-center gap-3" };
            return (
              <li key={d.id} data-testid={`canonical-rail-doc-${d.id}`} data-doc-status={docStatus}>
                <RowTag
                  {...rowProps}
                  data-testid={clickable ? `canonical-rail-doc-open-${d.id}` : undefined}
                >
                  <span className={`w-9 h-9 rounded-lg ${palette.split(" ")[0]} ${palette.split(" ")[1]} flex items-center justify-center shrink-0`}>
                    <FileTypeIcon size={14} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[12.5px] font-semibold text-slate-900 truncate">{d.name || d.title || "Document"}</p>
                    <p className="text-[11px] text-slate-500 truncate">
                      {uploader ? `${uploader} · ` : ""}
                      {fmtRel(d.created_at)}
                    </p>
                  </div>
                  <span
                    className={`inline-flex items-center px-2 h-[20px] rounded-md ring-1 ${palette} text-[10.5px] font-semibold whitespace-nowrap`}
                    data-testid={`canonical-rail-doc-status-${d.id}`}
                  >
                    {docStatus}
                  </span>
                </RowTag>
              </li>
            );
          })}
        </ul>
      ) : canUpload ? (
        <div
          role="button"
          tabIndex={0}
          onClick={onPickClick}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPickClick(); } }}
          onDragOver={onDragOver}
          onDragEnter={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          className={`rounded-xl border border-dashed px-4 py-5 text-center cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300/60 ${
            dragOver ? "border-violet-500 bg-violet-50/60" : "border-slate-300 hover:border-violet-300 bg-slate-50/40"
          }`}
          aria-label="Upload documents: drag and drop files here, or click Upload"
          data-testid="canonical-rail-documents-empty"
          data-drag-over={dragOver ? "true" : "false"}
        >
          <FileText size={16} className={`mx-auto mb-1.5 ${dragOver ? "text-violet-600" : "text-slate-400"}`} aria-hidden />
          <p className={`text-[12px] leading-tight ${dragOver ? "text-violet-700 font-semibold" : "text-slate-500"}`} data-testid="canonical-rail-documents-dropzone-text">
            {dragOver ? "Drop files to upload." : "Drag and drop files here, or click Upload."}
          </p>
        </div>
      ) : (
        <p
          className="text-[12px] text-slate-500 leading-tight"
          data-testid="canonical-rail-documents-empty"
        >
          No documents yet.
        </p>
      )}
    </section>
  );
};


/* iter-369A · Deal Room Communication card ────────────────────
 *
 * Compact card living in the right rail. Fetches every email
 * currently linked to this deal (via `/api/comms/deal/{id}`) and
 * shows the 4 most recent + an "Open Communication Centre" jump.
 *
 * The mock provider serves the data today. When real Gmail /
 * Outlook OAuth lands, the endpoint response shape is identical
 * so this card doesn't change.
 */
function RailCommunicationCard({ dealId }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  useEffect(() => {
    if (!dealId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/api/comms/deal/${dealId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        if (!cancelled) setMessages(Array.isArray(body.messages) ? body.messages : []);
      } catch (e) {
        if (!cancelled) setError(String(e?.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dealId, API_BASE]);

  const _rel = (iso) => {
    if (!iso) return "";
    const t = Date.parse(iso);
    if (!Number.isFinite(t)) return "";
    const ms = Date.now() - t;
    if (ms < 60_000)     return "just now";
    if (ms < 3_600_000)  return `${Math.floor(ms / 60_000)}m ago`;
    if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
    return `${Math.floor(ms / 86_400_000)}d ago`;
  };

  const visible = messages.slice(0, 4);
  return (
    <section
      className="rounded-2xl bg-white py-4 px-5 cf-card-hover"
      style={{ border: "1px solid rgba(226,232,240,0.9)" }}
      data-testid="canonical-rail-communication"
    >
      <header className="mb-2.5 flex items-center justify-between">
        <h3 className="text-[13px] font-bold tracking-tight text-slate-900">Communication</h3>
        <Link
          to="/communication"
          className="text-[10.5px] font-semibold text-violet-700 hover:text-violet-800 hover:underline underline-offset-2"
          data-testid="canonical-rail-communication-open-centre"
          title="Open Communication Centre"
        >
          Open Centre →
        </Link>
      </header>
      {loading && (
        <p className="text-[12px] text-slate-500" data-testid="canonical-rail-communication-loading">Loading…</p>
      )}
      {!loading && error && (
        <p className="text-[11.5px] text-rose-600" data-testid="canonical-rail-communication-error">
          Couldn't load conversations.
        </p>
      )}
      {!loading && !error && visible.length === 0 && (
        <p className="text-[12px] text-slate-500" data-testid="canonical-rail-communication-empty">
          No matched conversations yet. Emails linked to this deal will appear here.
        </p>
      )}
      {!loading && !error && visible.length > 0 && (
        <ul className="space-y-2.5" data-testid="canonical-rail-communication-list">
          {visible.map((m) => (
            <li
              key={m.id}
              className="flex items-start gap-2.5 -mx-1 px-1 py-1 rounded-lg hover:bg-slate-50/80 transition-colors"
              data-testid={`canonical-rail-communication-row-${m.id}`}
            >
              <span
                className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${
                  m.direction === "inbound"
                    ? (m.unread ? "bg-violet-500" : "bg-slate-300")
                    : "bg-emerald-500"
                }`}
                aria-hidden
                title={m.direction === "inbound" ? "Inbound" : "Outbound"}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-[12px] font-semibold text-slate-900 truncate">
                    {m.direction === "inbound" ? m.from_name : `To · ${m.from_name}`}
                  </p>
                  <span className="text-[10.5px] text-slate-400 tabular-nums shrink-0">{_rel(m.received_at)}</span>
                </div>
                <p className="text-[11.5px] text-slate-700 truncate leading-tight" title={m.subject}>
                  {m.subject}
                </p>
                <p className="text-[10.5px] text-slate-400 truncate leading-tight" title={m.snippet}>
                  {m.snippet}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
      {messages.length > 4 && (
        <Link
          to="/communication"
          className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-700"
          data-testid="canonical-rail-communication-view-all"
        >
          View all {messages.length} messages <ChevronRight size={12} />
        </Link>
      )}
    </section>
  );
}


/* iter-alignment · P0 · Deal Room · Property Details Card ────
 *
 * Compact right-rail card that surfaces the operational property
 * SSOT alongside inline single-row edits.  Reads exclusively from
 * chainStore.findProperty(propertyId) — no duplicate Deal Room
 * state.  Every save calls `updateProperty(id, patch)` which PATCH
 * es `/api/properties/{id}` and refreshes any deal-side derived
 * state (BSA fan-out) via the existing store logic.
 *
 * Row visibility rules honour the canonical tenure vocabulary:
 *   • leasehold / share_of_freehold  → show lease-remaining,
 *     service-charge, ground-rent, managing-agent, shared-
 *     ownership, lease-extension.
 *   • bsa_applies = true             → reveal EWS1 status (and
 *     the EWS1 rating select when status === received).
 *   • shared_ownership = true        → reveal the % editor.
 *
 * The header "Property record →" link dispatches
 * `cf:property:open` (existing PropertyDetailsModal SSOT) so the
 * operator can jump into the wider snapshot without leaving the
 * Deal Room.
 *
 * Attention colour is applied ONLY where a live signal warrants
 * it — currently BSA when applicability is Yes AND no
 * mitigating EWS1 status has been captured yet.
 */
function RailPropertyDetailsCard({ deal }) {
  const { findProperty, updateProperty } = useChainStore();
  const propertyId = deal?.propertyId || deal?.property_id;
  const property = useMemo(
    () => (propertyId && findProperty ? findProperty(propertyId) : null),
    [propertyId, findProperty]
  );

  const [editingField, setEditingField] = useState(null);
  const [busyField, setBusyField] = useState(null);
  const [rowError, setRowError] = useState({ field: null, message: null });

  if (!property) return null;

  const tenureLc = String(property.tenure || "").toLowerCase().replace(/\s+/g, "_");
  const isLeasehold = tenureLc === "leasehold" || tenureLc === "share_of_freehold";
  const bsaOn = property.bsaApplies === true;
  const ews1Received = String(property.ews1Status || "").toLowerCase() === "received";
  const sharedOn = property.sharedOwnership === true;

  const bsaAttention = bsaOn && !property.ews1Status;

  const openRecord = () => {
    if (!property?.id) return;
    // PropertyDetailsModal expects a `deal`-shape payload; pass the
    // current deal so it resolves back into the same Deal Room.
    openProperty({ ...(deal || {}), id: property.id, dealId: deal?.id });
  };

  const commit = async (field, value) => {
    setBusyField(field);
    setRowError({ field: null, message: null });
    try {
      await updateProperty(property.id, { [field]: value });
      setEditingField(null);
    } catch (err) {
      const message = err?.response?.data?.detail?.message
        || err?.response?.data?.detail
        || "Couldn't save — please try again.";
      setRowError({ field, message: String(message).replace(/^Value error,\s*/i, "") });
    } finally {
      setBusyField(null);
    }
  };

  const startEdit = (field) => {
    if (busyField) return;
    setRowError({ field: null, message: null });
    setEditingField(field);
  };
  const cancelEdit = () => {
    setEditingField(null);
    setRowError({ field: null, message: null });
  };

  const rows = [];
  rows.push({
    field: "tenure",
    label: "Tenure",
    kind: "select",
    value: property.tenure,
    display: property.tenure || null,
    options: [
      { value: "Freehold", label: "Freehold" },
      { value: "Leasehold", label: "Leasehold" },
      { value: "Share of Freehold", label: "Share of Freehold" },
      { value: "Commonhold", label: "Commonhold" },
    ],
  });

  if (isLeasehold) {
    const _blankRent = (v) => v === null || v === undefined || String(v).trim() === "";
    const _groundRentValue = _blankRent(property.groundRent) ? (_blankRent(property.rent) ? null : property.rent) : property.groundRent;
    rows.push({
      field: "leaseYears",
      label: "Lease remaining",
      kind: "number",
      value: property.leaseYears,
      display: property.leaseYears == null ? null : `${property.leaseYears} years`,
      suffix: "years",
    });
    rows.push({
      field: "svc",
      label: "Service charge",
      kind: "text",
      inputPrefix: "£",
      value: property.svc,
      display: property.svc ? `Approx. ${_priceLabel(property.svc)} p.a.` : null,
      placeholder: "e.g. 2400",
    });
    rows.push({
      field: "groundRent",
      label: "Ground rent",
      kind: "text",
      inputPrefix: "£",
      // Falls back to the amount entered on the property form when the card has none.
      value: _groundRentValue,
      display: groundRentLabel(_groundRentValue, (v) => `${_priceLabel(v)} p.a.`),
      placeholder: "e.g. 250",
    });
    rows.push({
      field: "managingAgent",
      label: "Managing agent",
      kind: "text",
      value: property.managingAgent,
      display: property.managingAgent || null,
      placeholder: "Managing agent name",
    });
    rows.push({
      field: "sharedOwnership",
      label: "Shared ownership",
      kind: "bool",
      value: property.sharedOwnership,
      display: property.sharedOwnership == null ? null : (property.sharedOwnership ? "Yes" : "No"),
    });
    if (sharedOn) {
      rows.push({
        field: "sharedOwnershipPercentage",
        label: "Shared ownership %",
        kind: "number",
        value: property.sharedOwnershipPercentage,
        display: property.sharedOwnershipPercentage == null ? null : `${property.sharedOwnershipPercentage}%`,
        suffix: "%",
        min: 0,
        max: 100,
      });
    }
    rows.push({
      field: "leaseExtensionStatus",
      label: "Lease extension",
      kind: "text",
      value: property.leaseExtensionStatus,
      display: property.leaseExtensionStatus || null,
      placeholder: "e.g. Not planned",
    });
  }

  rows.push({
    field: "bsaApplies",
    label: "BSA",
    kind: "bool",
    value: property.bsaApplies,
    display: property.bsaApplies == null ? null : (property.bsaApplies ? "Yes" : "No"),
    attention: bsaAttention,
  });

  if (bsaOn) {
    rows.push({
      field: "ews1Status",
      label: "EWS1",
      kind: "select",
      value: property.ews1Status,
      display: _ews1StatusLabel(property.ews1Status),
      options: [
        { value: "not_required", label: "Not required" },
        { value: "required",     label: "Required" },
        { value: "requested",    label: "Requested" },
        { value: "received",     label: "Received" },
      ],
    });
    if (ews1Received) {
      rows.push({
        field: "ews1Rating",
        label: "EWS1 rating",
        kind: "select",
        value: property.ews1Rating,
        display: property.ews1Rating || null,
        options: [
          { value: "A1", label: "A1" },
          { value: "A2", label: "A2" },
          { value: "A3", label: "A3" },
          { value: "B1", label: "B1" },
          { value: "B2", label: "B2" },
        ],
      });
    }
  }

  rows.push({
    field: "localAuthority",
    label: "Local authority",
    kind: "text",
    value: property.localAuthority,
    display: property.localAuthority || null,
    placeholder: "e.g. Camden",
  });
  rows.push({
    field: "councilTaxBand",
    label: "Council tax",
    kind: "select",
    value: property.councilTaxBand,
    display: property.councilTaxBand ? `Band ${property.councilTaxBand}` : null,
    options: ["A","B","C","D","E","F","G","H"].map((b) => ({ value: b, label: `Band ${b}` })),
  });
  rows.push({
    field: "epc",
    label: "EPC",
    kind: "select",
    value: property.epc,
    display: property.epc || null,
    options: ["A","B","C","D","E","F","G"].map((b) => ({ value: b, label: b })),
  });
  rows.push({
    field: "parking",
    label: "Parking",
    kind: "select",
    value: property.parking,
    display: property.parking || null,
    options: [
      { value: "None",       label: "None" },
      { value: "On-street",  label: "On-street" },
      { value: "Allocated",  label: "Allocated" },
      { value: "Garage",     label: "Garage" },
      { value: "Driveway",   label: "Driveway" },
    ],
  });

  return (
    <section
      className="rounded-2xl bg-white py-4 px-5 cf-card-hover"
      style={{ border: "1px solid rgba(226,232,240,0.9)" }}
      data-testid="canonical-rail-property-details"
    >
      <header className="mb-2 flex items-center justify-between">
        <h3 className="text-[13px] font-bold tracking-tight text-slate-900">Property Details</h3>
        <button
          type="button"
          onClick={openRecord}
          className="text-[10.5px] font-semibold text-violet-700 hover:text-violet-800 hover:underline underline-offset-2"
          data-testid="canonical-rail-property-details-open-record"
          title="Open property record"
        >
          Property record →
        </button>
      </header>
      <ul className="divide-y divide-slate-100" data-testid="canonical-rail-property-details-list">
        {rows.map((row) => (
          <RailPropertyRow
            key={row.field}
            row={row}
            editing={editingField === row.field}
            busy={busyField === row.field}
            error={rowError.field === row.field ? rowError.message : null}
            onStart={() => startEdit(row.field)}
            onCancel={cancelEdit}
            onCommit={(v) => commit(row.field, v)}
          />
        ))}
      </ul>
    </section>
  );
}

const _ews1StatusLabel = (v) => {
  const canon = String(v || "").toLowerCase();
  if (canon === "not_required") return "Not required";
  if (canon === "required")     return "Required";
  if (canon === "requested")    return "Requested";
  if (canon === "received")     return "Received";
  return null;
};

const _priceLabel = (v) => {
  if (v == null || v === "") return "—";
  const s = String(v).trim();
  if (s.startsWith("£")) return s;
  const n = Number(s.replace(/[^0-9.-]/g, ""));
  if (Number.isFinite(n) && n > 0) return "£" + n.toLocaleString("en-GB");
  return s;
};

function RailPropertyRow({ row, editing, busy, error, onStart, onCancel, onCommit }) {
  const [draft, setDraft] = useState(row.value ?? "");
  useEffect(() => {
    if (editing) setDraft(row.value ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const kebab = row.field
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .toLowerCase();
  const testidRow  = `canonical-rail-property-row-${kebab}`;
  const testidValue = `canonical-rail-property-value-${kebab}`;
  const testidEdit  = `canonical-rail-property-edit-${kebab}`;
  const testidAdd   = `canonical-rail-property-add-${kebab}`;
  const testidInput = `canonical-rail-property-input-${kebab}`;
  const testidSave  = `canonical-rail-property-save-${kebab}`;
  const testidCancel = `canonical-rail-property-cancel-${kebab}`;

  const handleKey = (e) => {
    if (e.key === "Escape") { e.preventDefault(); onCancel(); }
    if (e.key === "Enter" && row.kind !== "bool") {
      e.preventDefault();
      const next = _coerce(row.kind, draft);
      onCommit(next);
    }
  };

  const _coerce = (kind, v) => {
    if (kind === "number") {
      const s = String(v ?? "").trim();
      if (!s) return null;
      const n = Number(s);
      return Number.isFinite(n) ? n : null;
    }
    if (kind === "bool") {
      if (v === true || v === "true") return true;
      if (v === false || v === "false") return false;
      return null;
    }
    return String(v ?? "").trim() || null;
  };

  const attention = row.attention === true;
  const missing   = row.display == null || row.display === "";

  return (
    <li
      className="min-h-[42px] flex items-center gap-3 py-2 group"
      data-testid={testidRow}
      data-tenure-scope={row.field}
      data-attention={attention ? "true" : "false"}
    >
      <span className="text-[11.5px] font-medium text-slate-500 shrink-0 basis-[42%]">{row.label}</span>
      <div className="flex-1 min-w-0 flex items-center justify-end">
        {editing ? (
          <div className="flex items-center gap-1.5 w-full justify-end" data-testid={`canonical-rail-property-editor-${kebab}`}>
            {row.kind === "select" ? (
              <select
                autoFocus
                value={draft ?? ""}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKey}
                disabled={busy}
                data-testid={testidInput}
                className="h-7 max-w-[160px] text-[12px] px-1.5 rounded-md border border-slate-200 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-violet-200"
              >
                <option value="">—</option>
                {row.options.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : row.kind === "bool" ? (
              <select
                autoFocus
                value={draft === true ? "true" : draft === false ? "false" : ""}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKey}
                disabled={busy}
                data-testid={testidInput}
                className="h-7 max-w-[120px] text-[12px] px-1.5 rounded-md border border-slate-200 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-violet-200"
              >
                <option value="">—</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            ) : row.kind === "number" ? (
              <div className="flex items-center gap-1">
                <input
                  autoFocus
                  type="number"
                  min={row.min}
                  max={row.max}
                  value={draft ?? ""}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={handleKey}
                  disabled={busy}
                  data-testid={testidInput}
                  className="h-7 w-[80px] text-[12px] px-1.5 rounded-md border border-slate-200 bg-white text-slate-800 text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-violet-200"
                />
                {row.suffix && <span className="text-[11px] text-slate-400">{row.suffix}</span>}
              </div>
            ) : (
              <div className="flex items-center gap-1 w-full max-w-[180px]">
                {row.inputPrefix && <span className="text-[11px] text-slate-400">{row.inputPrefix}</span>}
                <input
                  autoFocus
                  type="text"
                  value={draft ?? ""}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={handleKey}
                  disabled={busy}
                  placeholder={row.placeholder}
                  data-testid={testidInput}
                  className="h-7 flex-1 text-[12px] px-1.5 rounded-md border border-slate-200 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-violet-200"
                />
              </div>
            )}
            <button
              type="button"
              onClick={() => onCommit(_coerce(row.kind, draft))}
              disabled={busy}
              className="h-7 w-7 rounded-md text-emerald-600 hover:bg-emerald-50 flex items-center justify-center disabled:opacity-50"
              aria-label="Save"
              data-testid={testidSave}
            >
              <Check size={13} />
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="h-7 w-7 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-50 flex items-center justify-center disabled:opacity-50"
              aria-label="Cancel"
              data-testid={testidCancel}
            >
              <X size={13} />
            </button>
          </div>
        ) : missing ? (
          <button
            type="button"
            onClick={onStart}
            className="text-[11.5px] font-medium text-slate-400 hover:text-violet-700 transition-colors inline-flex items-center gap-1"
            data-testid={testidAdd}
          >
            <Plus size={11} /> Add
          </button>
        ) : (
          <button
            type="button"
            onClick={onStart}
            className={`text-[12.5px] font-semibold truncate max-w-full text-right hover:underline underline-offset-2 decoration-slate-300 transition-colors ${
              attention ? "text-amber-700" : "text-slate-800 hover:text-violet-800"
            }`}
            title={row.display}
            data-testid={testidValue}
            data-edit-testid={testidEdit}
          >
            {row.display}
          </button>
        )}
      </div>
      {error && !editing && (
        <span
          className="basis-full text-[10.5px] text-rose-600 pl-[42%]"
          data-testid={`canonical-rail-property-error-${kebab}`}
        >
          {error}
        </span>
      )}
      {error && editing && (
        <span
          className="basis-full text-[10.5px] text-rose-600 pl-[42%]"
          data-testid={`canonical-rail-property-error-${kebab}`}
        >
          {error}
        </span>
      )}
    </li>
  );
}



const RightRail = ({ deal, intel, participants, documents, awaiting, journeyState, milestones, txnState, dealId, access, currentUserId, onManageParticipants, onViewAllDocuments, onActivateJourneyStep, onEditFinancials, onUploadFiles, onRequestDoc, onReviewDoc, onUpdateFundingBasis, onUpdateBuyerPosition }) => {
  const lastActivity = (deal?.activity || [])[0];
  const lastIso = lastActivity?.at || lastActivity?.created_at;

  /* iter-alignment · Tasks & Commitments (2026-02).
   *
   * Reuses the canonical `/api/tasks` SSOT — GET/POST/PATCH.
   * No new endpoint, no new collection, no local-only store.
   * On mutations we broadcast `cf:deal:refresh` so every
   * downstream panel (Command Centre, Today, DealRow) refetches
   * the shared read paths without polling.
   *
   * Task lifecycle stays on the SSOT — the audit trail (created /
   * updated / completed / reopened) is emitted server-side; this
   * card does NOT keep local history. */
  const [tasks, setTasks] = useState([]);
  const [tasksLoaded, setTasksLoaded] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerBusy, setComposerBusy] = useState(false);
  const [composerTitle, setComposerTitle] = useState("");
  const [composerOwner, setComposerOwner] = useState("");
  const [composerDue, setComposerDue] = useState("");
  const [tasksExpanded, setTasksExpanded] = useState(false);
  const [tasksTab, setTasksTab] = useState("all"); // all | mine | others | completed

  const fetchTasks = useCallback(async () => {
    if (!dealId) return;
    try {
      const { data } = await api.get(`/api/tasks`, { params: { deal_id: dealId } });
      setTasks(Array.isArray(data) ? data : []);
    } catch (_e) { /* keep last-good state — panel degrades calmly */ }
    finally { setTasksLoaded(true); }
  }, [dealId]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onRefresh = (e) => {
      const target = e?.detail?.dealId;
      if (!target || String(target) === String(dealId)) fetchTasks();
    };
    window.addEventListener("cf:deal:refresh", onRefresh);
    return () => window.removeEventListener("cf:deal:refresh", onRefresh);
  }, [fetchTasks, dealId]);

  const _dispatchRefresh = useCallback((reason) => {
    if (typeof window === "undefined" || !dealId) return;
    try {
      window.dispatchEvent(new CustomEvent("cf:deal:refresh", {
        detail: { dealId, reason },
      }));
    } catch (_) { /* noop */ }
  }, [dealId]);

  const createTask = useCallback(async () => {
    const title = composerTitle.trim();
    if (!title || composerBusy) return;
    setComposerBusy(true);
    try {
      /* Creator and owner are separate — `created_by` is set
       * server-side from the auth session; `assigned_to`
       * captures the operator's explicit ownership pick.  New
       * tasks always land as `open` — never auto-Resolved. */
      const payload = {
        deal_id: dealId,
        title,
        assigned_to: composerOwner.trim() || null,
        due_at: composerDue || null,
        status: "open",
        priority: "Medium",
      };
      const { data } = await api.post(`/api/tasks`, payload);
      setTasks((prev) => [data, ...prev]);
      setComposerTitle(""); setComposerOwner(""); setComposerDue("");
      setComposerOpen(false);
      _dispatchRefresh("task.created");
      try { toast.success("Task added"); } catch (_) { /* noop */ }
    } catch (_e) {
      try { toast.error("Couldn't add task"); } catch (_) { /* noop */ }
    } finally { setComposerBusy(false); }
  }, [dealId, composerTitle, composerOwner, composerDue, composerBusy, _dispatchRefresh]);

  const toggleTaskStatus = useCallback(async (task) => {
    if (!task?.id) return;
    const nextStatus = task.status === "done" ? "open" : "done";
    const prev = tasks;
    setTasks((cur) => cur.map((t) => (t.id === task.id ? { ...t, status: nextStatus, completed_at: nextStatus === "done" ? new Date().toISOString() : null } : t)));
    try {
      await api.patch(`/api/tasks/${task.id}`, { status: nextStatus });
      _dispatchRefresh(nextStatus === "done" ? "task.completed" : "task.reopened");
    } catch (_e) {
      setTasks(prev);
      try { toast.error("Couldn't update task"); } catch (_) { /* noop */ }
    }
  }, [tasks, _dispatchRefresh]);

  /* Prioritise: overdue → blocking → due today → upcoming,
   * then nearest due date.  `priority=High` acts as the
   * blocking marker on the SSOT (existing vocabulary). */
  const activeTasks = useMemo(() => {
    const _sod = (() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), n.getDate()).getTime(); })();
    const _eod = _sod + 86400000;
    const bucket = (t) => {
      const due = t.due_at ? Date.parse(t.due_at) : null;
      if (due != null && due < _sod) return 0;                     // Overdue
      if (String(t.priority || "").toLowerCase() === "high") return 1; // Blocking
      if (due != null && due >= _sod && due < _eod) return 2;      // Due today
      return 3;                                                    // Upcoming
    };
    return (tasks || [])
      .filter((t) => t.status !== "done")
      .map((t) => ({ ...t, _bucket: bucket(t) }))
      .sort((a, b) => {
        if (a._bucket !== b._bucket) return a._bucket - b._bucket;
        const ad = a.due_at ? Date.parse(a.due_at) : Infinity;
        const bd = b.due_at ? Date.parse(b.due_at) : Infinity;
        return ad - bd;
      });
  }, [tasks]);

  const statusFor = (t) => (["Overdue", "Blocking", "Due today", "Upcoming"][t._bucket] || "Upcoming");
  const statusTone = (t) => ({
    0: "bg-rose-50 text-rose-700 ring-rose-200/70",
    1: "bg-rose-50 text-rose-700 ring-rose-200/70",
    2: "bg-amber-50 text-amber-700 ring-amber-200/70",
    3: "bg-slate-50 text-slate-600 ring-slate-200/70",
  }[t._bucket] || "bg-slate-50 text-slate-600 ring-slate-200/70");

  const _tasksForTab = useMemo(() => {
    if (tasksTab === "completed") return (tasks || []).filter((t) => t.status === "done");
    const mine = (t) => currentUserId && (t.assigned_to === currentUserId || t.assigned_to === "Me");
    if (tasksTab === "mine")   return activeTasks.filter(mine);
    if (tasksTab === "others") return activeTasks.filter((t) => !mine(t));
    return activeTasks;
  }, [tasksTab, activeTasks, tasks, currentUserId]);

  // iter-221 — Dynamic "Waiting on":
  //   1. real blockers from /api/v2/deals/:id/blockers (live)
  //   2. legacy `deal.waiting_on` / `deal.waiting` (fallback)
  //   3. calm synthetic hint derived from the current journey step
  //      when the deal has no explicit blockers (e.g. "Awaiting
  //      buyer solicitor" while the active step is `searches`).
  // Always operationally useful — never just empty space.
  const waitingOn = useMemo(() => {
    /* iter-362 · Terminal short-circuit. Exchanged / Completed /
     * Fallen Through deals have no operational waiting-on — any
     * synthetic row here would contradict the terminal banner. */
    if (deal && TERMINAL_STATUSES.has(deal.status)) return [];
    const real = Array.isArray(awaiting) ? awaiting.filter(Boolean) : [];
    if (real.length) {
      return real.slice(0, 3).map((b) => ({
        title:   b.title || b.label || "Waiting on update",
        detail:  b.owner_label || b.detail || b.sub || b.party || "Response pending",
        days:    b.days ?? b.age_days ?? 0,
        actor_id: b.user_id || b.actor_id || b.participant_id || null,
        step_id:  b.journey_step || b.step_id || null,
      }));
    }
    const legacy = deal?.waiting_on || deal?.waiting || [];
    if (Array.isArray(legacy) && legacy.length) return legacy.slice(0, 3);
    // Synthetic — calm operational hint based on the active step.
    //
    // iter-345 — Each step now maps to BOTH a human-readable
    // party label AND a `roleKey` we can use to look up the
    // matching participant from the deal's participants array.
    // The row then renders WHO (participant name + role) instead
    // of the flat "Counterparty" fallback.
    //
    // iter-345B — Broader fallback: even when journeyState isn't
    // yet hydrated (API still in flight / v1 pilot deal), read
    // `deal.stage` directly so the operator never sees an empty
    // Waiting On when the transaction has an active step.
    const STEP_META = {
      offer:                { party: "buyer & seller agreement", label: "Offer",          roleKey: "agent"            },
      aml:                  { party: "AML verification",           label: "AML",            roleKey: "agent"            },
      searches:             { party: "buyer solicitor",           label: "Searches",       roleKey: "buyer_solicitor"  },
      mortgage_offer:       { party: "mortgage broker",           label: "Mortgage",       roleKey: "mortgage_broker"  },
      mortgage:             { party: "mortgage broker",           label: "Mortgage",       roleKey: "mortgage_broker"  },
      contracts:            { party: "buyer solicitor",           label: "Contracts",      roleKey: "buyer_solicitor"  },
      contract:             { party: "buyer solicitor",           label: "Contracts",      roleKey: "buyer_solicitor"  },
      exchange:             { party: "signed contracts",          label: "Exchange",       roleKey: "seller_solicitor" },
      exchange_ready:       { party: "signed contracts",          label: "Exchange",       roleKey: "seller_solicitor" },
      completion:           { party: "completion funds",          label: "Completion",     roleKey: "buyer_solicitor"  },
      complete:             { party: "completion funds",          label: "Completion",     roleKey: "buyer_solicitor"  },
      draft_contract:       { party: "seller solicitor",          label: "Draft contract", roleKey: "seller_solicitor" },
      mgmt_pack_requested:  { party: "managing agent",            label: "Mgmt Pack",      roleKey: "seller_solicitor" },
      mgmt_pack:            { party: "managing agent",            label: "Mgmt Pack",      roleKey: "seller_solicitor" },
      mgmt_pack_received:   { party: "management pack",           label: "Mgmt Pack",      roleKey: "seller_solicitor" },
      leasehold_enquiries:  { party: "seller solicitor",          label: "Enquiries",      roleKey: "seller_solicitor" },
      enquiries:            { party: "seller solicitor",          label: "Enquiries",      roleKey: "seller_solicitor" },
      bsa:                  { party: "buyer solicitor",           label: "BSA",            roleKey: "buyer_solicitor"  },
    };
    // Determine the active step. Priority: hydrated journeyState →
    // deal.stage (canonical field on every deal record).
    let active = journeyState?.current_step
      ? (journeyState.steps || []).find((s) => s && s.id === journeyState.current_step)
      : null;
    const stageId = String(deal?.stage || "").toLowerCase();
    if (!active && stageId && STEP_META[stageId]) {
      active = { id: stageId, label: STEP_META[stageId].label, state: "in_progress" };
    }
    if (active && active.state !== "done") {
      const meta = STEP_META[active.id] || { party: active.label.toLowerCase(), roleKey: null };
      // Try to hydrate a matching participant so WHO renders as
      // a real person + role, not a bare category.
      const roleKey = meta.roleKey;
      const roleMatch = roleKey
        ? (participants || []).find((p) => {
            const t = String(p?.participant_type || p?.role || "").toLowerCase().replace(/\s+/g, "_");
            return t === roleKey || t.replace(/-/g, "_") === roleKey;
          })
        : null;
      // Days-since based on the step's own updated_at (falls
      // back to `deal.days_in_stage` if the journey step
      // doesn't carry a per-step timestamp).
      const stepUpdatedIso = active.updated_at || active.started_at || null;
      const daysSince = stepUpdatedIso
        ? Math.max(0, Math.floor((Date.now() - Date.parse(stepUpdatedIso)) / 86_400_000))
        : (deal?.days_in_stage || 0);
      // WHAT — turn "Awaiting {party}" into a proper outstanding
      // item so Line 2 reads as an action, not a repetition.
      const _cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
      const outstanding = `${_cap(meta.party)} outstanding`;
      return [{
        // WHO — role label (superseded by actor.full_name at
        // render time when we have a matching participant).
        title: roleMatch
          ? (roleMatch.full_name || roleMatch.name || _roleKeyToLabel(roleKey))
          : _roleKeyToLabel(roleKey) || active.label,
        // WHAT — outstanding item derived from the active step.
        detail: outstanding,
        days: daysSince,
        step_id: active.id,
        participant_id: roleMatch?.id || roleMatch?.contact_id || null,
        role_key: roleKey,
        role_label: roleMatch?.role_label || _roleKeyToLabel(roleKey),
        synthetic: true,
      }];
    }
    return [];
  }, [awaiting, deal, journeyState, participants]);

  /* iter-alignment · P66 · Tasks & Commitments UX cleanup
   * (2026-02).  Interleave `activeTasks` + `waitingOn` into a
   * SINGLE prioritised list so the right-rail card reads as one
   * action list.  Preserves the underlying SSOT — tasks still
   * originate from `/api/tasks`, commitments still derive from
   * `awaiting` / journey state.  Priority ladder matches the
   * task-side vocabulary: Overdue → Blocking → Due today →
   * Upcoming → Tracked.  Urgency label is what the operator
   * reads on the pill (`2d overdue`, `Due today`, `Due tomorrow`,
   * `Upcoming`, `Tracked`). */
  const unifiedItems = useMemo(() => {
    const _sod = (() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), n.getDate()).getTime(); })();
    const _eod = _sod + 86_400_000;
    const _eod2 = _sod + 2 * 86_400_000;
    const _fmtDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    const items = [];
    (activeTasks || []).forEach((t) => {
      const due = t.due_at ? Date.parse(t.due_at) : null;
      let urgency; let pillTone;
      if (t._bucket === 0) {
        const daysOver = due != null ? Math.max(1, Math.floor((_sod - due) / 86_400_000)) : 1;
        urgency = `${daysOver}d overdue`;
        pillTone = "bg-rose-50 text-rose-700 ring-rose-200/70";
      } else if (t._bucket === 1) {
        urgency = "Blocking";
        pillTone = "bg-rose-50 text-rose-700 ring-rose-200/70";
      } else if (t._bucket === 2) {
        urgency = "Due today";
        pillTone = "bg-amber-50 text-amber-700 ring-amber-200/70";
      } else if (due != null && due >= _eod && due < _eod2) {
        urgency = "Due tomorrow";
        pillTone = "bg-amber-50 text-amber-700 ring-amber-200/70";
      } else {
        urgency = "Upcoming";
        pillTone = "bg-slate-50 text-slate-600 ring-slate-200/70";
      }
      const subline = `${t.assigned_to || "Unassigned"}${t.due_at ? ` · Due ${_fmtDate(t.due_at)}` : ""}`;
      items.push({
        ...t,
        _kind: "task",
        _title: t.title,
        _subline: subline,
        _urgency: urgency,
        _pillTone: pillTone,
        _bucketLabel: ["Overdue", "Blocking", "Due today", "Upcoming"][t._bucket] || "Upcoming",
        _sortKey: due != null ? due : Number.POSITIVE_INFINITY,
      });
    });
    (waitingOn || []).forEach((w, i) => {
      const days = w.days || 0;
      let bucket; let urgency; let pillTone;
      if (days >= 7) {
        bucket = 0;
        urgency = `${days}d overdue`;
        pillTone = "bg-rose-50 text-rose-700 ring-rose-200/70";
      } else if (days >= 3) {
        bucket = 3;
        urgency = "Upcoming";
        pillTone = "bg-amber-50 text-amber-700 ring-amber-200/70";
      } else {
        bucket = 4;
        urgency = "Tracked";
        pillTone = "bg-slate-50 text-slate-500 ring-slate-200/70";
      }
      const actorId = w.user_id || w.actor_id || w.participant_id;
      const actor = actorId
        ? (participants || []).find((p) => p.user_id === actorId || p.id === actorId || p.contact_id === actorId)
        : null;
      const roleLine = actor?.role_label || actor?.role || w.role_label || _roleKeyToLabel(w.role_key) || w.party || null;
      const whoLine  = actor?.full_name || w.title || w.party || (w.role_key ? _roleKeyToLabel(w.role_key) : "Counterparty");
      const whatLine = w.detail || w.sub || w.title || "Response pending";
      const subline = `${whoLine}${roleLine ? ` · ${roleLine}` : ""}`;
      items.push({
        _kind: "commitment",
        _idx: i,
        _title: whatLine,
        _subline: subline,
        _urgency: urgency,
        _pillTone: pillTone,
        _bucket: bucket,
        _bucketLabel: bucket === 0 ? "Overdue" : bucket === 3 ? "Upcoming" : "Tracked",
        _sortKey: -days,
      });
    });
    return items.sort((a, b) => {
      if (a._bucket !== b._bucket) return a._bucket - b._bucket;
      return a._sortKey - b._sortKey;
    });
  }, [activeTasks, waitingOn, participants]);

  const totalOpen = (activeTasks?.length || 0) + (waitingOn?.length || 0);

  return (
    <aside className="space-y-3" data-testid="canonical-right-rail">
      {/* iter-381 — Deal Overview card, redesigned for the pre-pilot.
       * P1 · Premium Polish — rail card gaps tightened (space-y-3.5
       *   → space-y-3, ~14%) so the vertical rhythm feels calmer
       *   without cramping the individual cards.  Card padding on
       *   the Overview stays intact to preserve breathing room
       *   around the primary score.
       *
       *   Header is stage-aware (pre-exchange → Exchange Confidence /
       *   post-exchange → Completion Readiness / terminal → compact
       *   completed state). Body renders one large primary score,
       *   at most three ranked drivers with their +/- impact, and a
       *   three-metric strip (Deal Health · Days in Stage · Open
       *   Blockers). All numbers, tones and drivers are derived from
       *   the SSOT `txnState.confidence` — no duplicated math, no
       *   hardcoded transaction logic. */}
      {/* iter-alignment · Deal Room · Restore Right Rail (2026-03-02) —
          Deal Summary / Exchange Confidence card restored to
          the pilot rail (was gated on the earlier Final Rail
          pass; user asked to restore alongside the four-column
          hero + Buyer Position card, all of which stay).  No
          layout change from the pre-Sale-Agreed composition. */}
      <section
        className="rounded-2xl bg-white py-4 px-5 cf-card-hover"
        style={{ border: "1px solid rgba(226,232,240,0.9)" }}
        data-testid="canonical-rail-overview"
      >
        {(() => {
          const conf = txnState?.confidence || null;
          const terminalKind = txnState?.terminalKind || null;
          const isCompletedState = terminalKind === "Completed" || terminalKind === "Fallen Through";
          const isPostExchange   = terminalKind === "Exchanged";

          const cardTitle = isCompletedState
            ? (terminalKind === "Completed" ? "Completed" : "Fell through")
            : isPostExchange
              ? "Completion Readiness"
              : "Exchange Confidence";

          // ── Card header ────────────────────────────────────────
          // P0 · Right-Rail Exchange Confidence Redesign (2026-08-12)
          // — Header adds a tiny violet trending-line icon in the
          // top-right corner to signal the "live confidence" nature
          // of the readout, matching the pilot reference.  The
          // title tone + SSOT data-attributes are unchanged.
          const header = (
            <header className="flex items-center justify-between mb-2">
              <h3
                className="text-[13px] font-bold tracking-tight text-slate-900"
                data-testid="canonical-rail-overview-title"
                data-overview-state={
                  isCompletedState
                    ? (terminalKind === "Completed" ? "completed" : "fallen_through")
                    : isPostExchange ? "post_exchange" : "pre_exchange"
                }
              >
                {cardTitle}
              </h3>
              {!isCompletedState && (
                <span
                  className="inline-flex items-center justify-center h-6 w-6 rounded-md bg-violet-50 text-violet-600 ring-1 ring-violet-100"
                  data-testid="canonical-rail-confidence-trend-icon"
                  aria-hidden
                >
                  <TrendingUp size={13} strokeWidth={2} />
                </span>
              )}
            </header>
          );

          // ── Compact three-metric status strip ─────────────────
          // Rendered on every state so the operator always has the
          // canonical Deal Health · Days in Stage · Open Blockers
          // readout. On the completed states the Open Blockers row
          // collapses because the SSOT clears blockers on terminal.
          const HEALTH_TONES = {
            healthy: { dot: "bg-emerald-500", text: "text-emerald-600", label: "Healthy" },
            watch:   { dot: "bg-amber-500",   text: "text-amber-600",   label: "Watch"   },
            at_risk: { dot: "bg-rose-500",    text: "text-rose-600",    label: "At Risk" },
          };
          const canon = dealStatusFor(deal);
          const healthTone = HEALTH_TONES[canon.band] || HEALTH_TONES.healthy;
          /* P0 · Deal Room Operational Coherence Audit (2026-02-28)
           * ---------------------------------------------------------
           * Terminal states (`stage='exchanged' / 'completed'`) must
           * expose the canonical terminal label to assistive tech
           * consumers reading `canonical-rail-deal-health`.  Without
           * this override the sr-only text on a completed deal read
           * "Healthy" — technically the resolved band, but not the
           * literal state the operator communicated.  The visible
           * tone / dot class remains from HEALTH_TONES so the visual
           * chrome is untouched; only the accessible label is
           * upgraded when a terminal state is resolved. */
          const healthLabelForA11y = canon.terminal
            ? (canon.label?.canonical || healthTone.label)
            : healthTone.label;

          const openBlockers = Number(txnState?.activeBlockerCount ?? conf?.activeBlockers ?? 0);
          const blockerTone = openBlockers >= 3
            ? { dot: "bg-rose-500",    text: "text-rose-600",    ring: "focus-visible:ring-rose-300" }
            : openBlockers >= 1
              ? { dot: "bg-amber-500",   text: "text-amber-600",   ring: "focus-visible:ring-amber-300" }
              : { dot: "bg-emerald-500", text: "text-emerald-600", ring: "focus-visible:ring-emerald-300" };

          const statusStrip = (
            <div
              className="sr-only mt-2 flex items-center flex-wrap gap-x-2.5 gap-y-1 text-[11.5px]"
              data-testid="canonical-rail-status-strip"
            >
              {/* iter-alignment · P34 — Deal Health remains the SSOT
                  anchor (band + tone are still selectable via
                  `canonical-rail-deal-health`) but the visible
                  "Healthy / Watch / At Risk" label is retired from
                  the status strip so the bottom row reads as
                  `X days · Y blockers` with exactly one separator.
                  The band still carries its tone class + dot data
                  attribute for downstream / contract tests. */}
              <span
                className="sr-only"
                data-testid="canonical-rail-deal-health"
                data-health-band={canon.band}
                data-health-tone={healthTone.text}
              >
                {healthLabelForA11y}
              </span>
              <span
                className="text-slate-700 font-semibold tabular-nums"
                data-testid="canonical-rail-days-in-stage"
              >
                {Number.isFinite(deal?.days_in_stage)
                  ? `${deal.days_in_stage} ${deal.days_in_stage === 1 ? "day" : "days"}`
                  : "Days in stage not recorded"}
              </span>
              {!isCompletedState && (
                <>
                  <span className="text-slate-300" aria-hidden>•</span>
                  {openBlockers > 0 ? (
                    /* iter-368 — Blockers count remains a keyboard-
                       friendly button that scrolls the Waiting-On
                       rail into view. */
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const target = document.querySelector('[data-testid="canonical-rail-waiting"]');
                        if (target) {
                          target.scrollIntoView({ behavior: "smooth", block: "center" });
                          target.setAttribute("data-highlight", "true");
                          setTimeout(() => target.removeAttribute("data-highlight"), 1600);
                        }
                      }}
                      data-no-row-click="true"
                      data-testid="canonical-rail-confidence-blockers-link"
                      className={`inline-flex items-center gap-1.5 font-semibold ${blockerTone.text} rounded px-0.5 -mx-0.5 hover:underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${blockerTone.ring}`}
                      aria-label={`${openBlockers} ${openBlockers === 1 ? "blocker" : "blockers"} outstanding — jump to Open Commitments`}
                      title="Jump to Open Commitments"
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${blockerTone.dot}`} aria-hidden />
                      <span className="tabular-nums" data-testid="canonical-rail-confidence-blockers">{openBlockers}</span>
                      <span className="font-medium text-slate-500">
                        {openBlockers === 1 ? "blocker" : "blockers"}
                      </span>
                    </button>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1.5 font-semibold text-emerald-600"
                      data-testid="canonical-rail-open-blockers-clear"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
                      <span className="tabular-nums" data-testid="canonical-rail-confidence-blockers">0</span>
                      <span className="font-medium text-slate-500">blockers</span>
                    </span>
                  )}
                </>
              )}
            </div>
          );

          // ── Terminal state · compact completed banner ─────────
          if (isCompletedState) {
            const kind = terminalKind === "Completed" ? "completed" : "fallen_through";
            const iconTone = kind === "completed"
              ? { bg: "bg-emerald-50",  ring: "ring-emerald-200/70", text: "text-emerald-600", dot: "bg-emerald-500" }
              : { bg: "bg-slate-50",    ring: "ring-slate-200/70",   text: "text-slate-500",   dot: "bg-slate-400"   };
            return (
              <>
                {header}
                <div
                  className={`rounded-xl px-4 py-3 mb-3 ring-1 ${iconTone.bg} ${iconTone.ring} flex items-center gap-3`}
                  data-testid="canonical-rail-overview-completed"
                  data-terminal={kind}
                >
                  <span className={`h-2 w-2 rounded-full ${iconTone.dot}`} aria-hidden />
                  <p className={`text-[12.5px] font-semibold leading-snug ${iconTone.text}`}>
                    {_terminalBannerCopy(terminalKind, txnState?.terminalDateIso)}
                  </p>
                </div>
                {statusStrip}
              </>
            );
          }

          // ── Live states · Pre-exchange & Post-exchange ────────
          if (!conf) return header;

          const probability = conf.score;
          const trend = conf.trend;

          // Post-exchange (Exchanged): the SSOT now computes the
          // real Completion Readiness from `target_completion` +
          // days-to-completion + post-exchange blockers.  The
          // label follows the score band so a slipping post-
          // exchange deal reads "At risk" instead of the
          // previous hard-coded "Ready for completion".
          const tone = isPostExchange
            ? (probability >= 75
                ? { label: "Ready for completion", text: "text-emerald-600", bar: "#10B981", trackTint: "rgba(16,185,129,0.16)" }
                : probability >= 50
                  ? { label: "Completion on track", text: "text-amber-600",  bar: "#F59E0B", trackTint: "rgba(245,158,11,0.16)" }
                  : { label: "Completion at risk", text: "text-rose-600",   bar: "#EF4444", trackTint: "rgba(239,68,68,0.16)" })
            : probability >= 75
              ? { label: "High confidence", text: "text-emerald-600", bar: "#10B981", trackTint: "rgba(16,185,129,0.16)" }
              : probability >= 50
                ? { label: "Moderate",      text: "text-amber-600",   bar: "#F59E0B", trackTint: "rgba(245,158,11,0.16)" }
                : { label: "At risk",       text: "text-rose-600",    bar: "#EF4444", trackTint: "rgba(239,68,68,0.16)" };
          /* iter-alignment · P · Exchange Confidence colour
           * semantics (2026-02-14).
           *
           * The confidence-tone pill and its indicator dot must
           * follow the SAME band the `tone` object already
           * carries (75 / 50 thresholds on the SSOT `conf.score`)
           * so the pill NEVER reads amber while the bar reads
           * green (or vice versa).  Prior implementation drove
           * the pill palette off `_reasonPalette` / `_reasonTone`
           * — derived from the top-ranked reason — which
           * decoupled the pill colour from the actual confidence
           * band.  Pure derivation from the existing `tone`; no
           * new SSOT, no threshold change, no calculation
           * change. */
          const tonePalette = tone.text === "text-emerald-600"
            ? { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200", dot: "bg-emerald-500" }
            : tone.text === "text-amber-600"
              ? { bg: "bg-amber-50",   text: "text-amber-700",   ring: "ring-amber-200",   dot: "bg-amber-500" }
              : { bg: "bg-rose-50",    text: "text-rose-700",    ring: "ring-rose-200",    dot: "bg-rose-500" };
          const trendTone = trend === "improving"
            ? "text-emerald-600"
            : trend === "softening"
              ? "text-amber-600"
              : "text-slate-500";
          const reasonToneClass = (t) => {
            if (t === "positive") return "text-emerald-600";
            if (t === "risk")     return "text-rose-600";
            if (t === "watch")    return "text-amber-600";
            return "text-slate-600";
          };
          const reasonDotClass = (t) => {
            if (t === "positive") return "bg-emerald-500";
            if (t === "risk")     return "bg-rose-500";
            if (t === "watch")    return "bg-amber-500";
            return "bg-slate-400";
          };

          /* iter-381 — Ranked drivers.  The SSOT already caps
           * reasons at three (see `_reasonsFor` in
           * `exchangeConfidence.js`), so we can just render the
           * array as-is.
           *
           * P0 · Property Type + Readiness Lifecycle (2026-02-28)
           * — Post-exchange reasons now carry real deltas
           * (completion date / days-to-completion / post-
           * exchange blockers) so we ALWAYS show the impact
           * chip, matching the pre-exchange treatment. */
          const drivers = Array.isArray(conf.reasons)
            ? conf.reasons.slice(0, 3)
            : [];
          const hideImpact = false;

          /* P0 · Right-Rail Exchange Confidence Redesign (2026-08-12)
           * ---------------------------------------------------------
           * Operational reason line — replaces the exposed
           * "{driver} → +N pts" scoring readout with a plain
           * operational explanation the reference calls for:
           *
           *   "2 unresolved blockers · High impact"
           *
           * Impact tier is derived from the highest-|impact| risk /
           * watch driver in `conf.reasons` (SSOT), so the tier
           * moves live as blockers resolve or drift.  When no
           * blockers exist but a risk driver still holds a
           * material impact, we fall back to that driver's own
           * label + tier — never a raw pts number.  No new field,
           * no hard-coded copy, no duplicate logic.
           */
          const _pickTop = (arr) =>
            (arr || [])
              .slice()
              .sort((a, b) => Math.abs(Number(b.impact || 0)) - Math.abs(Number(a.impact || 0)))[0];
          const _riskDrivers = drivers.filter((r) => ["risk", "watch"].includes((r.tone || "").toLowerCase()));
          const _topRisk = _pickTop(_riskDrivers);
          const _topImpact = Math.round(Math.abs(Number(_topRisk?.impact || 0)));
          const _impactTier = _topImpact >= 15
            ? "High impact"
            : _topImpact >= 8
              ? "Medium impact"
              : _topImpact >= 1
                ? "Low impact"
                : null;
          const _blockerN = Number(txnState?.activeBlockerCount ?? conf?.activeBlockers ?? 0);
          let _reasonText = null;
          let _reasonTone = "risk";
          if (_blockerN > 0) {
            _reasonText = `${_blockerN} unresolved blocker${_blockerN === 1 ? "" : "s"} · ${_impactTier || "High impact"}`;
            _reasonTone = _blockerN >= 2 || _topImpact >= 12 ? "risk" : "watch";
          } else if (_topRisk && _impactTier) {
            _reasonText = `${_topRisk.label} · ${_impactTier}`;
            _reasonTone = (_topRisk.tone || "watch").toLowerCase();
          }
          const _reasonPalette = _reasonTone === "risk"
            ? { bg: "bg-rose-50",   ring: "ring-rose-100",  text: "text-rose-700",   icon: "text-rose-500"   }
            : _reasonTone === "watch"
              ? { bg: "bg-amber-50",  ring: "ring-amber-100", text: "text-amber-700",  icon: "text-amber-500"  }
              : { bg: "bg-emerald-50", ring: "ring-emerald-100", text: "text-emerald-700", icon: "text-emerald-500" };

          /* iter-alignment · P0 · Transaction Progress — Key
           * Journey Stages SSOT (2026-02-19).
           *
           * The previous shape counted individual REQUIRED
           * milestone ROWS (0 of ~46), which was the wrong level
           * of abstraction — operators think of a transaction in
           * terms of the 5-7 KEY JOURNEY STAGES (Mortgage,
           * Contract, Mgmt Pack, BSA, Enquiries, Exchange,
           * Completion), not the underlying checklist.
           *
           * New shape (single canonical read, no new state):
           *
           *   • Denominator = number of APPLICABLE key journey
           *     stages for this deal.  Applicability is derived
           *     by `resolveJourneyProgress` via
           *     `journeyForCanonical(tenure, optsForDeal(deal))`,
           *     which already honours every rule:
           *       – cash-buyer deals skip Mortgage,
           *       – freehold deals skip Mgmt Pack + BSA,
           *       – explicit `applicableBSA:false` suppresses BSA
           *         (e.g. low-rise leasehold house).
           *     The denominator dynamically shrinks/grows as
           *     these flags flip on the canonical deal envelope.
           *   • Numerator = count of applicable stages whose id
           *     sits in the canonical `completedSet` returned by
           *     the same resolver.  That set already applies
           *     lifecycle-closure (post-exchange deals show every
           *     applicable pre-exchange stage complete), so
           *     Transaction Progress stays in lockstep with the
           *     rail.
           *   • Recorded-only (2026-10): the numerator reads the
           *     resolver's `recordedSet`; deal status (Exchanged /
           *     Completed) no longer bumps the count to N-of-N.
           *   • The underlying REQUIRED milestones remain
           *     available in Current Stage / All Milestones.
           *     Exchange Confidence scoring is untouched — it
           *     runs its own applicable-milestone calculus
           *     inside `hooks/exchangeConfidence.js`.
           */
          const _journeyProgress = resolveJourneyProgress(deal, milestones);
          const _stagesApplicable = Array.isArray(_journeyProgress?.steps)
            ? _journeyProgress.steps
            : [];
          // Recorded completions only (same set as the green ticks); deal status never inflates the count.
          const _completedStages = _journeyProgress?.recordedSet instanceof Set
            ? _journeyProgress.recordedSet
            : new Set();
          const _rawItems = Array.isArray(milestones?.items) ? milestones.items : [];
          const _milesIsDone = (m) => !!m && (m.state === "done" || m.state === "complete" || !!m.completed_at);
          let _stagesTotal = _stagesApplicable.length;
          let _stagesDone = 0;
          for (const s of _stagesApplicable) {
            if (s?.id && _completedStages.has(s.id)) _stagesDone += 1;
          }
          // Whole applicable stages only — same count as every overview surface (Deal Rooms, Flow Centre, summary).
          const _contractsExchangedDone = _rawItems.some(
            (m) => m?.id === "contracts_exchanged" && _milesIsDone(m),
          );
          const _dealStageStr = String(deal?.stage || "").toLowerCase();
          const _postExchangeSignal = _contractsExchangedDone
            || _dealStageStr === "exchanged"
            || _dealStageStr === "completed"
            || isPostExchange;
          /* Percentage uses the fractional value so partial
           * current-stage progress moves the meter proportionally
           * (e.g. 5/11 → 6/11 on the checklist).  The "X of Y"
           * text keeps whole-stage semantics — the fractional
           * contribution is a progress signal, not a completed-
           * stage claim. */
          const _milesPct = _stagesTotal > 0
            ? Math.min(100, Math.max(0, Math.round((_stagesDone / _stagesTotal) * 100)))
            : 0;
          // Legacy aliases retained so downstream JSX + data-testid
          // hooks read the same names.  Zero new state.
          const _milesDone = _stagesDone;
          const _milesTotal = _stagesTotal;
          // Keep the card visible even after the confirmation so
          // the operator sees the 100 % win.  Hides only when the
          // deal has no resolvable applicable key stages.
          const _showProgress = _milesTotal > 0 || _postExchangeSignal;

          /* Target-exchange footer — reads directly from the
           * canonical `deal.target_exchange` field so it always
           * matches the hero metric + Deal Rooms list.  No new
           * state.  A single midnight-anchored diff computes the
           * "N days overdue / N days remaining / Today" label. */
          /* iter-alignment · P0 · Right-Rail Duplicate Target
             Exchange Removal (2026-08-16) — the derived
             `_targetDays / _targetLabel / _targetTone /
             _showTarget` block that used to feed the
             (now-removed) rail footer has been retired.  The
             canonical `target_exchange` SSOT and the hero's
             exchange countdown are untouched. */

          return (
            <>
              {header}
              {/* Helper subtitle — canonical explainer for the score below. */}
              <p
                className="text-[12px] text-slate-500 leading-snug mb-3"
                data-testid="canonical-rail-confidence-helper"
              >
                {isPostExchange ? "Likelihood of completing on target" : "Likelihood of exchanging on target"}
              </p>

              {/* ── Score row · big % + tone pill ────────────────── */}
              <div
                className="mb-2"
                data-testid="canonical-rail-confidence"
                data-confidence-score={probability}
                data-confidence-trend={trend}
                data-confidence-state={isPostExchange ? "post_exchange" : "pre_exchange"}
              >
                <div className="flex items-center justify-between gap-3">
                  <p
                    className="text-[32px] font-bold text-slate-900 tabular-nums tracking-[-0.02em] leading-none"
                    data-testid="canonical-rail-confidence-value"
                  >
                    {probability}
                    <span className="text-[14px] font-semibold text-slate-400 ml-0.5">%</span>
                  </p>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 h-[26px] rounded-full ${tonePalette.bg} ${tonePalette.text} text-[12px] font-semibold ring-1 ${tonePalette.ring} shrink-0`}
                    data-testid="canonical-rail-confidence-tone-pill"
                    data-tone-band={probability >= 75 ? "high" : probability >= 50 ? "steady" : "at_risk"}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${tonePalette.dot}`}
                      aria-hidden
                    />
                    {tone.label}
                  </span>
                </div>

                {/* Confidence rail — 3px, tone-coloured. */}
                <div
                  className="mt-3 h-[3px] rounded-full overflow-hidden"
                  style={{ background: tone.trackTint }}
                >
                  <div
                    className="h-full rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${probability}%`, background: tone.bar }}
                    data-testid="canonical-rail-confidence-rail-fill"
                  />
                </div>

                {/* Trend chip retained for contract tests + a11y. */}
                <p
                  className={`sr-only ${trendTone}`}
                  data-testid="canonical-rail-confidence-trend"
                >
                  {trend === "improving"
                    ? `Up${conf.trendDelta ? ` +${conf.trendDelta}` : ""}`
                    : trend === "softening"
                      ? `Down${conf.trendDelta ? ` ${conf.trendDelta}` : ""}`
                      : "Stable"}
                </p>
              </div>

              {/* ── Operational reason strip · sr-only ───────────────
                  iter-alignment · P0 · Exchange Confidence Visible
                  Reason Row Removal (2026-08-17) — the visible
                  "{N} unresolved blockers · {Impact}" row duplicated
                  the same operational signal already surfaced by the
                  Transaction Progress "{N} of {M} required milestones"
                  count immediately below.  The visible strip is
                  removed to unify the vertical rhythm across the two
                  sub-cards.  The reason string is retained as sr-only
                  so screen readers + downstream contract tests
                  (which anchor on `canonical-rail-confidence-why`)
                  continue to resolve the SSOT-derived reason.  All
                  underlying derivations (`_blockerN`, `_topRisk`,
                  `_impactTier`) remain intact — no scoring change. */}
              {_reasonText && (
                <div
                  className="sr-only"
                  data-testid="canonical-rail-confidence-why"
                  data-reason-tone={_reasonTone}
                >
                  <AlertTriangle size={14} className={`${_reasonPalette.icon} shrink-0`} aria-hidden />
                  <p className={`text-[12px] font-semibold leading-snug ${_reasonPalette.text} min-w-0`}>
                    {_reasonText}
                  </p>
                </div>
              )}

              {/* SR-only reasons list — contract tests + a11y. */}
              {drivers.length > 0 && (
                <ul
                  className="sr-only"
                  data-testid="canonical-rail-confidence-reasons"
                >
                  {drivers.slice(0, 1).map((r, i) => {
                    const impact = Number(r.impact || 0);
                    const impactAbs = Math.max(0, Math.round(Math.abs(impact)));
                    const impactStr = impactAbs === 0
                      ? null
                      : (impact > 0 ? `+${impactAbs}` : `−${impactAbs}`);
                    return (
                      <li
                        key={r.key || i}
                        data-testid={`canonical-rail-confidence-reason-${i}`}
                        data-reason-key={r.key}
                        data-reason-tone={r.tone}
                        data-reason-impact={Math.round(impact)}
                      >
                        <span className={reasonToneClass(r.tone)}>
                          <span className={reasonDotClass(r.tone)} aria-hidden />
                          {r.label}
                        </span>
                        {!hideImpact && impactStr && (
                          <span
                            data-testid={`canonical-rail-confidence-reason-impact-${i}`}
                          >
                            {impactStr}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              {/* ── Transaction Progress subsection ──────────────── */}
              {_showProgress && (
                <>
                  <div className="my-4 border-t border-slate-100" aria-hidden />
                  <section data-testid="canonical-rail-transaction-progress">
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <div className="min-w-0">
                        <h4 className="text-[13px] font-bold tracking-tight text-slate-900 inline-flex items-center gap-1.5">
                          Milestones completed
                          <StageProgressHint testid="canonical-rail-progress-hint" />
                        </h4>
                        <p className="text-[12px] text-slate-500 leading-snug mt-0.5">
                          Key journey stages
                        </p>
                      </div>
                      <p
                        className="text-[22px] font-bold text-slate-900 tabular-nums tracking-[-0.02em] leading-none shrink-0"
                        data-testid="canonical-rail-transaction-progress-value"
                      >
                        {_milesPct}
                        <span className="text-[12px] font-semibold text-slate-400 ml-0.5">%</span>
                        <span className="text-[12px] font-semibold text-slate-500 ml-1.5">complete</span>
                      </p>
                    </div>
                    <div
                      className="mt-2.5 h-[3px] rounded-full overflow-hidden"
                      style={{ background: "rgba(16,185,129,0.14)" }}
                    >
                      <div
                        className="h-full rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${_milesPct}%`, background: "#10B981" }}
                        data-testid="canonical-rail-transaction-progress-rail-fill"
                      />
                    </div>
                    <div className="mt-2.5 flex items-center gap-2">
                      <span
                        className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-emerald-100 text-emerald-600"
                        aria-hidden
                      >
                        <Check size={12} strokeWidth={3} />
                      </span>
                      <p
                        className="text-[12px] font-semibold text-slate-700 tabular-nums"
                        data-testid="canonical-rail-transaction-progress-count"
                      >
                        <span className="tabular-nums">{_milesDone}</span> of{" "}
                        <span className="tabular-nums">{_milesTotal}</span> key stages complete
                      </p>
                    </div>
                  </section>
                </>
              )}

              {/* iter-alignment · P0 · Right-Rail Duplicate Target
                  Exchange Removal (2026-08-16) — the "Target
                  exchange · N days remaining" footer that used
                  to render here duplicated the canonical
                  Exchange date + countdown already rendered in
                  the property hero.  Removed with no
                  replacement.  Header exchange date + underlying
                  target-exchange SSOT are untouched. */}

              {statusStrip}
            </>
          );
        })()}
      </section>

      {/* iter-alignment · Deal Room · Restore Right Rail (2026-03-02) —
          Financials card restored to the pilot rail (was gated
          on the earlier Final Rail pass).  Reads the same
          Revenue SSOT + opens the FinancialsSheet drawer as
          before. */}
      <RightRailFinancials
        deal={deal}
        canEdit={access?.can_edit_deal !== false}
        onEdit={onEditFinancials}
      />

      {/* iter-234 — Participants moved here from the hero so the
          operator can scan parties + fire Call/WhatsApp/Email
          actions without the eye bouncing to the top of the page. */}
      <RightRailParticipants
        participants={participants}
        onManageParticipants={access?.can_add_participants === false ? undefined : onManageParticipants}
        dealId={dealId}
        deal={deal}
      />

      {/* Tasks & Commitments · iter-alignment (2026-02).
          Evolves the previous "Open Commitments" card into the
          single home for active actions on a deal.  Tasks come
          from the canonical `/api/tasks` SSOT; commitments are
          the same derived `waitingOn` rows the section already
          rendered.  Testid `canonical-rail-waiting` is preserved
          so downstream contract tests keep resolving. */}
      <section
        className="rounded-2xl bg-white py-4 px-5 cf-card-hover"
        style={{ border: "1px solid rgba(226,232,240,0.9)" }}
        data-testid="canonical-rail-waiting"
        data-section="tasks-and-commitments"
      >
        <header className="mb-2.5 flex items-center justify-between gap-3">
          <h3
            className="text-[13px] font-bold tracking-tight text-slate-900"
            data-testid="canonical-rail-tasks-title"
          >
            Tasks &amp; Commitments
          </h3>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setComposerOpen((v) => !v)}
              className="inline-flex items-center gap-0.5 h-6 px-2 rounded-md text-[11px] font-semibold text-violet-700 ring-1 ring-violet-200 bg-violet-50/50 hover:bg-violet-50 hover:text-violet-800 cf-press transition"
              aria-label="Add task"
              title="Add a task"
              data-testid="canonical-rail-tasks-add-btn"
            >
              <Plus size={11} strokeWidth={2.6} /> Add
            </button>
          </div>
        </header>

        {composerOpen && (
          <div
            className="mb-2.5 rounded-lg ring-1 ring-slate-200 bg-slate-50/40 p-2.5 space-y-2"
            data-testid="canonical-rail-tasks-composer"
          >
            <input
              type="text"
              value={composerTitle}
              onChange={(e) => setComposerTitle(e.target.value)}
              placeholder="What needs doing?"
              maxLength={240}
              className="w-full text-[12.5px] px-2 py-1.5 rounded-md ring-1 ring-slate-200 focus:ring-violet-400 focus:outline-none bg-white"
              aria-label="Task title"
              data-testid="canonical-rail-tasks-composer-title"
            />
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={composerOwner}
                onChange={(e) => setComposerOwner(e.target.value)}
                placeholder="Owner (name or role)"
                maxLength={120}
                className="flex-1 min-w-0 text-[11.5px] px-2 py-1 rounded-md ring-1 ring-slate-200 focus:ring-violet-400 focus:outline-none bg-white"
                aria-label="Task owner"
                data-testid="canonical-rail-tasks-composer-owner"
              />
              <input
                type="date"
                value={composerDue}
                onChange={(e) => setComposerDue(e.target.value)}
                className="text-[11.5px] px-2 py-1 rounded-md ring-1 ring-slate-200 focus:ring-violet-400 focus:outline-none bg-white text-slate-700"
                aria-label="Optional due date"
                data-testid="canonical-rail-tasks-composer-due"
              />
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => { setComposerOpen(false); setComposerTitle(""); setComposerOwner(""); setComposerDue(""); }}
                disabled={composerBusy}
                className="h-7 px-2.5 rounded-md text-[11.5px] font-semibold text-slate-600 hover:bg-slate-100"
                data-testid="canonical-rail-tasks-composer-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={createTask}
                disabled={composerBusy || !composerTitle.trim()}
                className="h-7 px-3 rounded-md text-[11.5px] font-semibold bg-violet-600 hover:bg-violet-700 disabled:bg-violet-300 text-white inline-flex items-center gap-1"
                data-testid="canonical-rail-tasks-composer-save"
              >
                {composerBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus size={11} strokeWidth={2.6} />} Add task
              </button>
            </div>
          </div>
        )}

        <header className="hidden">
          <span data-testid="canonical-rail-open-commitments-title">Tasks &amp; Commitments</span>
        </header>

        {/* iter-alignment · P66 · Unified compact list (max 3).
            Tasks + commitments read as ONE prioritised action list
            with a consistent row style:
              [checkbox / clock icon] · Title · Owner · Due date · Urgency pill
            Filters and full commitment detail (last-update /
            follow-up hint / Waiting Nd) live behind `View all`
            below. */}
        {unifiedItems.length === 0 ? (
          deal?.status === "Exchanged" ? (
            <p className="text-[12px] text-slate-500" data-testid="canonical-rail-waiting-exchanged">
              Contracts exchanged — completion still outstanding. No open tasks or commitments.
            </p>
          ) : deal && TERMINAL_STATUSES.has(deal.status) ? (
            <p className="text-[12px] text-slate-500" data-testid="canonical-rail-waiting-terminal">
              Transaction closed — no live waiting-on items.
            </p>
          ) : (
            <p className="text-[12px] text-slate-500" data-testid="canonical-rail-tasks-empty">
              No active tasks or commitments — calm and on track.
            </p>
          )
        ) : (
          <ul className="space-y-2" data-testid="canonical-rail-tasks-list">
            {unifiedItems.slice(0, 3).map((item) => {
              const isTask = item._kind === "task";
              const rowKey = isTask ? `t-${item.id}` : `c-${item._idx}`;
              const rowTestid = isTask
                ? `canonical-rail-task-row-${item.id}`
                : `canonical-rail-waiting-${item._idx}`;
              const statusTestid = isTask
                ? `canonical-rail-task-status-${item.id}`
                : `canonical-rail-waiting-status-${item._idx}`;
              return (
                <li
                  key={rowKey}
                  className="flex items-start gap-2.5"
                  data-testid={rowTestid}
                  data-task-bucket={item._bucketLabel}
                >
                  {isTask ? (
                    <button
                      type="button"
                      onClick={() => toggleTaskStatus(item)}
                      className="w-[18px] h-[18px] rounded border-[1.5px] border-slate-300 hover:border-violet-400 flex items-center justify-center transition-colors shrink-0 mt-[1px]"
                      aria-label={`Complete ${item.title}`}
                      aria-pressed={false}
                      data-testid={`canonical-rail-task-check-${item.id}`}
                    >
                      <span className="w-0 h-0" />
                    </button>
                  ) : (
                    <span
                      className="w-[18px] h-[18px] rounded-full bg-slate-100 text-slate-500 flex items-center justify-center shrink-0 mt-[1px]"
                      data-testid={`canonical-rail-waiting-avatar-${item._idx}`}
                      aria-hidden="true"
                    >
                      <Clock size={10} strokeWidth={2.4} />
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p
                      className="text-[12.5px] font-semibold text-slate-900 truncate leading-tight"
                      title={item._title}
                    >
                      {item._title}
                    </p>
                    <p className="text-[10.5px] text-slate-500 truncate mt-0.5 leading-tight">
                      {item._subline}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 self-start inline-flex items-center px-1.5 h-[16px] rounded-full text-[9.5px] font-bold uppercase tracking-[0.06em] ring-1 ${item._pillTone}`}
                    data-testid={statusTestid}
                  >
                    {item._urgency}
                  </span>
                </li>
              );
            })}
          </ul>
        )}

        {totalOpen > 0 && (
          <button
            type="button"
            onClick={() => setTasksExpanded((v) => !v)}
            className="mt-3 text-[11.5px] font-semibold text-violet-700 hover:text-violet-900 inline-flex items-center gap-0.5"
            data-testid="canonical-rail-waiting-view-all"
            aria-expanded={tasksExpanded}
          >
            View all {totalOpen} <ChevronRight size={12} className={`transition-transform ${tasksExpanded ? "rotate-90" : ""}`} />
          </button>
        )}
        {tasksExpanded && (
          <div
            className="mt-3 pt-3 border-t border-slate-100 space-y-2"
            data-testid="canonical-rail-tasks-drawer"
          >
            <div className="flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-[0.06em]">
              {["all", "mine", "others", "completed"].map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setTasksTab(tab)}
                  className={`px-2 h-6 rounded-md transition-colors ${tasksTab === tab ? "bg-violet-100 text-violet-800" : "text-slate-500 hover:bg-slate-100"}`}
                  data-testid={`canonical-rail-tasks-tab-${tab}`}
                >
                  {tab}
                </button>
              ))}
            </div>
            {_tasksForTab.length === 0 ? (
              <p className="text-[12px] text-slate-500" data-testid="canonical-rail-tasks-drawer-empty">
                Nothing to show in this view.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {_tasksForTab.slice(0, 20).map((t) => (
                  <li
                    key={t.id}
                    className="flex items-center gap-2 text-[12px]"
                    data-testid={`canonical-rail-tasks-drawer-row-${t.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleTaskStatus(t)}
                      className={`w-[16px] h-[16px] rounded border-[1.5px] flex items-center justify-center shrink-0 ${
                        t.status === "done"
                          ? "bg-emerald-500 border-emerald-500 text-white"
                          : "border-slate-300 hover:border-violet-400"
                      }`}
                      aria-label={t.status === "done" ? `Reopen ${t.title}` : `Complete ${t.title}`}
                      aria-pressed={t.status === "done"}
                    >
                      {t.status === "done" ? <Check size={10} strokeWidth={3.2} /> : <span className="w-0 h-0" />}
                    </button>
                    <span className={`flex-1 truncate ${t.status === "done" ? "text-slate-500 line-through decoration-slate-300" : "text-slate-800"}`}>{t.title}</span>
                    <span className="text-slate-400 text-[10.5px] shrink-0">{t.assigned_to || "—"}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      {/* iter-alignment · Deal Room · Final Right Rail · Buyer
          Position (2026-03-02) — deal-level buyer position card.
          Sits directly between Tasks & Commitments and Documents.
          Single canonical editor for `funding_basis` (moved out
          of Participants) plus the five new buyer position
          fields.  Reuses `onUpdateFundingBasis` verbatim so the
          existing PATCH + optimistic patch + rollback +
          `cf:deal:refresh` + journey reconciliation continue to
          run unchanged (historic mortgage progress preserved). */}
      <RailBuyerPositionCard
        deal={deal}
        milestones={milestones}
        canEdit={access?.can_edit_deal !== false}
        onUpdateFundingBasis={onUpdateFundingBasis}
        onUpdateBuyerPosition={onUpdateBuyerPosition}
      />

      {/* Documents */}
      <RailDocumentsHub
        documents={documents}
        dealId={dealId}
        canUpload={access?.can_upload_documents !== false}
        onViewAll={onViewAllDocuments}
        onUploadFiles={onUploadFiles}
        onRequestDoc={onRequestDoc}
        onReviewDoc={onReviewDoc}
      />

      {/* iter-alignment · Deal Room · Buyer Position pilot polish
          (2026-03-02) — Communication card gated off the pilot
          rail per the freeze spec.  Feed + component definition
          are preserved so lock tests still resolve.  Deal Summary,
          Financials, Participants, Tasks, Buyer Position,
          Documents and Property Details remain. */}
      {false && <RailCommunicationCard dealId={dealId} />}

      {/* iter-alignment · P0 · Deal Room · Property Details Card
       * (2026-03-01) — Compact operational property snapshot
       * sourced exclusively from the canonical Property SSOT
       * (chainStore.findProperty).  Inline row-level edits save
       * directly against PATCH /api/properties/{id}; no Deal Room
       * state duplication.  Header "Property record →" opens the
       * existing PropertyDetailsModal via cf:property:open.
       */}
      <RailPropertyDetailsCard deal={deal} />

      {/* iter-220 — Right-rail "Transaction participants" card
          REMOVED. The hero section is now the canonical surface for
          participant assignment; advanced edits/visibility/permissions
          still live behind the Participants tab (jumped to via
          onJumpToParticipants on each hero slot). This collapses the
          right column to its operational essentials. */}
      {/* iter-347 — Standalone OperationalNotesPanel REMOVED from
          the right rail. Notes are now ingested into the Updates
          timeline as `internal_note` events (Lock icon) so the
          timeline is the single source of truth for every
          transaction event. The `OperationalNotesPanel` component
          + its `/api/v2/deals/:id/operational-notes` endpoints
          remain wired so historical notes and the "Add note"
          affordance still work — the surface has simply moved
          into the Updates composer. */}
    </aside>
  );
};

const RailRow = ({ label, value }) => (
  <li className="flex items-center justify-between text-[12px]">
    <span className="text-slate-500">{label}</span>
    <span>{value}</span>
  </li>
);

/* ── Sticky action bar ─────────────────────────────────────────────── */
const ActionBar = ({
  dealId, access, onComposeNote, onOpenSheet,
  // iter-209A — attachment plumbing
  attachInputRef, pendingAttachments, onAttachClick, onAttachChange, onRemoveAttachment,
  participants,
  // iter-343 — Communication Hub. Dock now opens the Updates
  // composer above the timeline instead of POSTing a raw internal
  // note. Backwards-compat: pressing Enter with typed body still
  // fires the composer with the prefilled text (`?body=…` handoff
  // via localStorage draft key).
  onQuickAddUpdate,
}) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const canPost = access?.can_post_updates !== false && access?.can_view_deal !== false;
  /* iter-343 — Communication Hub. Updates are the operator's core
     logging surface; permission gate loosened from the old
     internal-notes capability to `can_post_updates` (defaults to
     true for anyone who can view the deal). */

  const handleNote = async () => {
    const body = note.trim();
    if (!body && !(pendingAttachments && pendingAttachments.length)) return;
    if (busy) return;  // iter-209B — prevent double-submission
    setBusy(true);
    try {
      // If there are pending attachments, POST as FormData so the
      // server can persist them as document uploads alongside the note.
      if (pendingAttachments && pendingAttachments.length) {
        const fd = new FormData();
        fd.append("body", body || "(attachment)");
        pendingAttachments.forEach((f) => fd.append("files", f, f.name));
        let attachErr = null;
        let saved = pendingAttachments.length;
        await api.post(`/api/deals/${dealId}/internal-notes-with-attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        }).catch(async () => {
          // Backend doesn't (yet) support multipart on this route — fall
          // back to a plain note + upload each file via documents API.
          await api.post(`/api/deals/${dealId}/internal-notes`, { body: body || "(attachment)" });
          for (const f of pendingAttachments) {
            const single = new FormData();
            single.append("file", f, f.name);
            await api.post(`/api/deals/${dealId}/documents`, single, {
              headers: { "Content-Type": "multipart/form-data" },
            }).catch((e) => { saved -= 1; attachErr = attachErr || e; });
          }
        });
        if (attachErr) {
          toast.error(saved ? `Note + ${saved} attachment${saved === 1 ? "" : "s"} saved` : "Note captured — attachments not saved",
            { description: apiError(attachErr) });
        } else {
          toast.success(`Note + ${saved} attachment${saved === 1 ? "" : "s"}`);
        }
      } else {
        await api.post(`/api/deals/${dealId}/internal-notes`, { body });
        toast.success("Note captured");
      }
      setNote("");
      // Clear attachments after a successful post.
      if (pendingAttachments && pendingAttachments.length) {
        pendingAttachments.forEach((_, i) => onRemoveAttachment?.(0)); // remove all
      }
      onComposeNote?.();
    } catch (err) {
      toast.error("Couldn't capture note", { description: apiError(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="sticky bottom-0 z-30 mt-6 -mx-6 px-6 py-3 flex flex-col gap-2"
      style={{
        background: "rgba(255,255,255,0.94)",
        backdropFilter: "blur(12px)",
        borderTop: "1px solid rgba(226,232,240,0.9)",
      }}
      data-testid="canonical-action-bar"
    >
      {/* iter-209A — Attached files chip strip. Surfaces ONLY when
          the operator has attached at least one file. */}
      {pendingAttachments && pendingAttachments.length > 0 && (
        <div className="flex items-center flex-wrap gap-1.5" data-testid="canonical-message-attachments">
          {pendingAttachments.map((f, idx) => (
            <span
              key={idx}
              className="inline-flex items-center gap-1.5 text-[11px] rounded-md bg-slate-100 text-slate-700 px-2 py-0.5 ring-1 ring-slate-200/70 max-w-[200px]"
              data-testid={`canonical-message-attachment-${idx}`}
            >
              <Paperclip size={11} className="text-slate-500" />
              <span className="truncate">{f.name}</span>
              <button
                type="button"
                onClick={() => onRemoveAttachment?.(idx)}
                className="text-slate-400 hover:text-rose-500 shrink-0"
                aria-label={`Remove ${f.name}`}
                data-testid={`canonical-message-attachment-remove-${idx}`}
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center gap-3">
      <div className="flex-1 flex items-center gap-2 px-3 h-10 rounded-xl bg-white border border-slate-200">
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onFocus={() => {
            /* iter-343 — Focusing the dock lifts the Updates composer
               above the timeline. The dock reads as the primary
               "log a touchpoint" affordance without permanently
               occupying vertical space. */
            onQuickAddUpdate?.();
          }}
          onKeyDown={(e) => { if (e.key === "Enter" && canPost) handleNote(); }}
          placeholder={canPost ? "Add update…" : "Update logging is restricted for your access level"}
          disabled={!canPost || busy}
          className="flex-1 text-[12.5px] bg-transparent outline-none placeholder:text-slate-400 disabled:opacity-60"
          data-testid="canonical-composer-input"
        />
        {/* iter-209A — Real hidden file input + paperclip trigger. */}
        <input
          ref={attachInputRef}
          type="file"
          multiple
          onChange={onAttachChange}
          className="hidden"
          data-testid="canonical-message-attach-input"
        />
        <button
          type="button"
          onClick={onAttachClick}
          className="text-slate-400 hover:text-slate-700 shrink-0"
          aria-label="Attach"
          data-testid="canonical-message-attach"
        >
          <Paperclip size={14} />
        </button>
        {/* iter-209B — Inline note submit + spinner. Pairs with the
            Enter-key handler on the input; both share the `busy`
            guard so rapid-fire spam clicks can never double-post. */}
        <button
          type="button"
          onClick={handleNote}
          disabled={!canPost || busy || (!note.trim() && !(pendingAttachments && pendingAttachments.length))}
          className="text-slate-400 hover:text-violet-700 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
          aria-label="Send note"
          aria-busy={busy ? "true" : "false"}
          data-testid="canonical-composer-submit"
        >
          {busy
            ? <Loader2 size={14} className="animate-spin" data-testid="canonical-composer-spinner" />
            : <Send size={14} />}
        </button>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Action
          label="Send message"
          Icon={MessageSquare}
          testid="canonical-action-send-message"
          onClick={() => onOpenSheet?.("message")}
        />
        <Action
          label="Create task"
          Icon={ClipboardList}
          testid="canonical-action-create-task"
          onClick={() => onOpenSheet?.("task")}
        />
        {/* iter-alignment · P0 · Document Request Simplification
            (2026-02-15) — Request document CTA removed from the
            persistent bottom action bar to eliminate duplication.
            Contextual Request actions still surface (a) beside the
            relevant Current Stage checklist items, (b) in the
            right-rail Documents card, (c) in the Document Requests
            section header, and (d) from Critical Path items — all
            still open the same request-doc drawer via
            `setOpenSheet("request-doc")`.  The route
            `?request-doc=1` auto-open remains wired. */}
        {/* iter-209A — Composer overflow popover.
            Calm 3-item menu (Insert @mention · Save as template ·
            Schedule for later). @mention writes the first participant's
            name into the input; the other two stay behind one calm
            comingSoon until they ship. */}
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="w-9 h-10 rounded-xl bg-white border border-slate-200 text-slate-500 hover:text-slate-800 flex items-center justify-center"
              aria-label="More"
              data-testid="canonical-composer-overflow"
            >
              <MoreHorizontal size={14} />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-52 p-1.5" data-testid="canonical-composer-overflow-menu">
            <button
              type="button"
              onClick={() => {
                const first = Array.isArray(participants) && participants.length > 0 ? participants[0] : null;
                const name = first ? (first.full_name || first.email?.split("@")[0] || "team") : "team";
                setNote((n) => `${n}${n && !n.endsWith(" ") ? " " : ""}@${name} `);
                requestAnimationFrame(() => {
                  document.querySelector('[data-testid="canonical-composer-input"]')?.focus?.();
                });
              }}
              className="w-full text-left text-[12.5px] px-2.5 py-1.5 rounded-md hover:bg-slate-50 text-slate-700 inline-flex items-center gap-2"
              data-testid="canonical-composer-mention"
            >
              <Users size={12} className="text-slate-400" /> Insert @mention
            </button>
            {/* iter-209B — deferred features render as disabled menu
                items with a calm tooltip. Premium / intentional, never
                "comingSoon" toast spam. */}
            <button
              type="button"
              disabled
              aria-disabled="true"
              title="Available in upcoming release"
              className="w-full text-left text-[12.5px] px-2.5 py-1.5 rounded-md text-slate-700 inline-flex items-center gap-2 opacity-50 cursor-not-allowed"
              data-testid="canonical-composer-save-template"
              data-deferred="true"
            >
              <Pin size={12} className="text-slate-400" /> Save as template
            </button>
            <button
              type="button"
              disabled
              aria-disabled="true"
              title="Available in upcoming release"
              className="w-full text-left text-[12.5px] px-2.5 py-1.5 rounded-md text-slate-700 inline-flex items-center gap-2 opacity-50 cursor-not-allowed"
              data-testid="canonical-composer-schedule"
              data-deferred="true"
            >
              <Clock size={12} className="text-slate-400" /> Schedule for later
            </button>
          </PopoverContent>
        </Popover>
      </div>
      </div>
    </div>
  );
};

const Action = ({ label, Icon, tone, testid, onClick }) => {
  const palette = tone === "emerald"
    ? "bg-emerald-50 text-emerald-700 ring-emerald-200/70 hover:bg-emerald-100"
    : "bg-white text-slate-700 ring-slate-200/80 hover:bg-slate-50";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-10 px-3 rounded-xl ring-1 ${palette} text-[12.5px] font-semibold inline-flex items-center gap-1.5 transition-all cf-press`}
      data-testid={testid}
    >
      <Icon size={13} /> {label}
    </button>
  );
};

/* ── Page shell ────────────────────────────────────────────────────── */
function DealRoomCanonicalPageInner() {
  const { dealId } = useParams();
  const {
    findDeal, findProperty, updateDeal, patchDealLocally, notifications,
    markPilotJourneyStepComplete, appendPilotDocument, appendPilotActivity,
    getPilotDocumentRequests, markPilotRequestChased,
    // iter-alignment · P — Move to next stage (2026-08-17).
    // Reuses the canonical `advanceStage` from the chainStore so
    // pilot deals, v2-only deals, and Mongo deals all follow the
    // same code path.  The `manual` + `outstandingCount` opts
    // travel through to the backend audit trail.
    advanceStage,
    contacts: _storeContacts,
    /* P0 · Blocker Data Population (2026-02-28) — Guard the
     * "Deal Room not found" empty state behind the workspace
     * hydration flag.  On direct-URL entry (bookmark, share link,
     * automation test) the deals[] slice is still empty during
     * the first render pass while `hydrateDeals()` awaits the
     * `/api/deals` response — without this guard we render the
     * calm "not found" screen for ~1-2s before the real Deal
     * Room appears, and headless test runners frequently
     * screenshot the empty state as the final result.  The flag
     * defaults to `false` so any environment that hasn't wired
     * it (unit tests, legacy providers) falls back to the
     * previous behaviour. */
    dealsLoading,
  } = useChainStore();
  /* iter-357 — Capture the current user so per-user "Last visit"
   * markers isolate each operator's "changes since your last
   * visit" surface. Falls back to `"anon"` when the auth store
   * hasn't hydrated yet so shared workstations still work. */
  const { user: _authUser } = useAuth();
  const currentUserId = _authUser?.id || _authUser?.email || "anon";
  const nav = useNavigate();
  /* iter-297 — Pilot deals bypass the backend access endpoint so the
   * Upload Document button and dropzone work end-to-end on demo data.
   * `findDeal` returns the seeded chainStore record; the `_pilot`
   * flag drives the bypass. */
  const _dealForAccess = useMemo(() => findDeal(dealId), [findDeal, dealId]);
  const access = useDealAccess(dealId, { pilot: !!_dealForAccess?._pilot });

  // Mobile mount is gated by JS — DealRoomMobile is a heavyweight
  // component that builds its own view-model and crashes if we mount
  // it speculatively at desktop widths. We mirror the canonical
  // responsive separation via a media-query listener.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 767px)").matches
      : false,
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const deal = findDeal(dealId);
  /* iter-alignment · P0 · New Deal Room Load (2026-03-08)
   * -----------------------------------------------------------------
   * When a freshly-created deal lives in Postgres but has not yet
   * been mirrored into the v1 Mongo list `hydrateDeals` reads
   * from, `findDeal(dealId)` returns `null` even after the store
   * finishes hydrating (`!dealsLoading`).  Without this fallback
   * the downstream tree tries to dereference the null deal on the
   * next render and the DealRoomRouteErrorBoundary fires with
   * "Deal Room couldn't fully load".  Fetching the canonical v2
   * envelope directly and hydrating it into the store via
   * `patchDealLocally` resolves the deal on both first-open and
   * hard-refresh, and applies uniformly to every deal (not a
   * per-property special case).  On 404 the existing "Deal Room
   * not found" surface still renders; on 5xx the boundary still
   * fires. */
  const [fallbackFetched, setFallbackFetched] = useState(false);
  useEffect(() => {
    if (deal) return;                    // already resolved from the store
    if (dealsLoading) return;            // still hydrating — wait
    if (!dealId) return;                 // no route id
    if (fallbackFetched) return;         // one-shot per mount
    setFallbackFetched(true);
    (async () => {
      try {
        const { data } = await api.get(`/api/v2/deals/${dealId}`);
        if (!data || !data.id) return;
        if (typeof patchDealLocally === "function") {
          // Seed the store row so `findDeal` resolves on the next
          // render.  We use the raw v2 payload directly — the
          // Deal Room's downstream helpers already read both
          // snake_case and camelCase aliases (`propertyId ||
          // property_id`, `agreedPrice || agreed_price`, …), so
          // no additional mapping is required at this stage.  A
          // subsequent `hydrateDeals` cycle will replace this
          // row with the fully-mapped `dealFromApi` shape.
          patchDealLocally(dealId, { ...data, id: data.id });
        }
      } catch (_err) {
        /* 404 / 5xx handled by the render below (not-found /
         * error boundary respectively).  Never rethrow — the
         * boundary must only trigger for genuine render crashes. */
      }
    })();
  }, [deal, dealId, dealsLoading, fallbackFetched, patchDealLocally]);

  // iter-alignment · P0 · Deal Room · Fallen Through (2026-02) —
  // Derive once from the canonical `status` / `stage` so every
  // overflow-menu affordance (Mark exchanged, Mark completed,
  // Mark fallen through, Reopen) reads from the same SSOT.  A
  // fallen deal is `status === "Fallen Through"` OR `stage ===
  // "fallen_through"` — treating both as terminal keeps the flow
  // robust to legacy rooms that only carry one of the two
  // signals.
  const isFallenThrough = (
    String(deal?.status || "").trim() === "Fallen Through"
    || String(deal?.stage || "").trim().toLowerCase() === "fallen_through"
  );
  // iter-227 — property-id key normalisation.
  // `chainStore.dealFromApi()` exposes the FK as camelCase
  // `propertyId`, but the canonical V2 endpoint returns it as
  // snake_case `property_id`. Either may land on the deal object
  // depending on whether the deal came from the in-memory store
  // or a fresh fetch — accept both so the hero never silently
  // collapses to "Untitled property" + "—" property type.
  const propertyId = deal?.propertyId || deal?.property_id || null;
  const property = propertyId ? findProperty(propertyId) : null;

  const [activities, setActivities] = useState([]);
  const [activityStatus, setActivityStatus] = useState("loading");
  const [documents, setDocuments] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [docRequests, setDocRequests] = useState([]);
  const [intel, setIntel] = useState(null);
  /* iter-alignment · P0 Phase 2 — Deal Room Document Review UX.
   * State-driven modal: click any doc row (rail or documents tab)
   * to open a full preview + review-history sheet.  Post-action
   * updates (mark reviewed / request replacement) rewrite the
   * matching row in-place so the operator sees the new pill
   * immediately without a full page reload. */
  const [reviewingDoc, setReviewingDoc] = useState(null);
  const openDocumentReview = useCallback((d) => setReviewingDoc(d || null), []);
  const closeDocumentReview = useCallback(() => setReviewingDoc(null), []);
  const applyReviewedDocument = useCallback((updated) => {
    if (!updated || !updated.id) return;
    setDocuments((prev) =>
      Array.isArray(prev)
        ? prev.map((row) => (row.id === updated.id ? { ...row, ...updated } : row))
        : prev
    );
    setReviewingDoc((cur) => (cur && cur.id === updated.id ? { ...cur, ...updated } : cur));
  }, []);
  /* iter-347 — Operational notes are now ingested into the
     Updates timeline as `internal_note` events. State lives at
     the page level so it can be threaded to `ActivityFeed`
     (timeline injection) instead of being fetched inside the
     removed right-rail OperationalNotesPanel. */
  const [operationalNotes, setOperationalNotes] = useState([]);
  const [notesManagerOpen, setNotesManagerOpen] = useState(false);
  const [notesComposeOnOpen, setNotesComposeOnOpen] = useState(false);
  /* iter-alignment · P4 · Lightweight refetch fired by
   * <OperationalNotesPanel> after add / pin / unpin / resolve /
   * delete so the Important Note banner (which reads from
   * `operationalNotes`) stays in sync without triggering the full
   * page reload. */
  const refetchOperationalNotes = useCallback(async () => {
    if (!dealId) return;
    try {
      const { data } = await api.get(`/api/v2/deals/${dealId}/operational-notes`);
      setOperationalNotes(Array.isArray(data) ? data : []);
    } catch { /* calm — banner just stays as-is */ }
  }, [dealId]);
  // iter-205 — collaborative milestone state (Legal / Financial /
  // Coordination). Fetched from /api/v2/deals/:id/milestones — the
  // server derives state from the deal_activity audit log so the
  // timeline IS the source of truth.
  const [milestones, setMilestones] = useState(null);  // { items, can_complete }
  // iter-221 — Interactive operational journey state. Fetched from
  // /api/v2/deals/:id/journey. The journey rail is the primary
  // progression surface — every step is clickable + opens a
  // lightweight per-step progression sheet that drives the deal
  // forward.
  const [journeyState, setJourneyState] = useState(null);  // { current_step, steps[] }
  const [progressionStep, setProgressionStep] = useState(null); // {id,label,state,target_date}|null
  const [participantsOpen, setParticipantsOpen] = useState(false);
  // iter-alignment · P0 · Deal Room · Fallen Through (2026-02) —
  // Local trigger for the Mark-as-fallen-through modal.  Modal is
  // portalled to document.body so overflow/stacking-context of the
  // header can never trap it.
  const [fallenThroughOpen, setFallenThroughOpen] = useState(false);
  /* P3 · Broker Auto-Prompt (2026-02-28) — role hint captured
   * when the participants modal is opened from the Mortgage
   * Broker Required banner.  Cleared when the modal closes so
   * a later "Manage" click always lands on the default role. */
  const [participantsInitialRole, setParticipantsInitialRole] = useState(null);
  // iter-214B — Progress deal drawer (operational progression engine).
  const [progressOpen, setProgressOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  // iter-259 — Financials editor drawer state.
  const [financialsOpen, setFinancialsOpen] = useState(false);
  // iter-209 — Mobile resurrection state.
  //   • `awaiting` — backend-derived blocker list (mirrors V3 page).
  //   • `followed` — operator-level follow toggle persisted in
  //                  localStorage so the mobile TopBar's Follow button
  //                  actually toggles state instead of being a no-op.
  const [awaiting, setAwaiting] = useState([]);
  const [roomLoaded, setRoomLoaded] = useState(false);  // first full reload done (deep-links wait for it)
  useEffect(() => { setRoomLoaded(false); }, [dealId]);
  /* iter-391 · Critical Path resolutions are now persisted in
   * Postgres (see `POST /api/v2/deals/:id/blockers/:id/resolve`)
   * and filtered out of `GET /blockers` server-side — the SSOT
   * lives on the backend.  The `resolvedCriticalPaths` state
   * below is now a *transient session cache* that carries two
   * responsibilities only:
   *   1. Optimistic UI hide between the resolve POST and the
   *      subsequent `reload()` re-fetch.
   *   2. Read-only migration fallback for operators who
   *      resolved on iter-390 before this deploy landed (their
   *      pre-existing `cf.resolvedCP.{dealId}` sentinel is
   *      honoured here but no longer written to).
   */
  const [resolvedCriticalPaths, setResolvedCriticalPaths] = useState(() => {
    if (!dealId || typeof window === "undefined") return new Set();
    try {
      const raw = window.localStorage.getItem(`cf.resolvedCP.${dealId}`);
      const arr = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(arr) ? arr : []);
    } catch {
      return new Set();
    }
  });
  const [followed, setFollowed] = useState(false);
  // iter-204b — composer sheet state (Send message / Create task /
  // Request document) so the sticky bar's primary actions are wired
  // to real flows instead of dead clicks.
  const [openSheet, setOpenSheet] = useState(null); // "message" | "task" | "request-doc" | null
  /* iter-alignment · P53 — Contextual document-request template.
   * Set by the Current Stage row "Request" button so RequestDocSheet
   * opens with the correct template pre-selected. Cleared when the
   * sheet closes. */
  const [requestDocInitialTemplate, setRequestDocInitialTemplate] = useState(null);

  /* iter-P65 · Universal Document Request launch shim.
   * External surfaces (Today, Contacts, Contact Profile, NBA,
   * Open Commitments) launch the SAME RequestDocSheet by
   * navigating here with `?request-doc=1[&template={id}]`.
   * This effect reads the params once on arrival, opens the
   * drawer with the correct template pre-seeded, and scrubs
   * the query string so a refresh / back-nav doesn't reopen
   * the drawer unexpectedly. */

  // iter-204b · v2 — lifted ActivityFeed tab so external CTAs
  // (right-rail Documents "View all", Buyer/Seller chips,
  // SinceLastVisit / journey step taps) can drive the right tab.
  const [returnState] = useState(() => peekDealReturnState(dealId));
  const [activityTab, setActivityTab] = useState(returnState?.tab || "current_stage");
  useEffect(() => { setLiveDealTab(dealId, activityTab); }, [dealId, activityTab]);
  useEffect(() => {
    if (!returnState) return undefined;
    return restoreDealScroll(returnState.y || 0, () => {
      clearDealReturnState(dealId);
      if (returnState.modal) openProperty(returnState.modal);
    });
  }, [returnState, dealId]);
  /* iter-343/347 — Compose signal counter. Bumped each time the
   * bottom dock's "Add update…" pill is tapped so `ActivityPanel`
   * knows to expand its floating composer above the timeline. */
  const [composerSignal, setComposerSignal] = useState(0);
  const [composerType, setComposerType] = useState(null);  // e.g. "call" from a Communication Centre deep-link
  const [requestDocRecipientEmail, setRequestDocRecipientEmail] = useState(null);

  /* iter-209B — Deep-link support.
   *   /deal-rooms/:id?tab=documents|participants|tasks|notes|activity
   *   /deal-rooms/:id#activity-<id>
   * Hydrate the tab from `?tab=` on mount + when the param changes
   * (so notifications / digest emails / copied links land calmly).
   * The hash anchor is honoured AFTER activities load via a separate
   * effect further down. */
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = useMemo(() => new Set(["current_stage", "checklist", "milestones"]), []);
  /* iter-alignment · P0 · Deal Activity Polish (2026-02) — The
   * `activity` tab is retired.  Every legacy deep link
   * (`?tab=activity/updates/notes/participants/documents`) now
   * lands on `current_stage` and the always-visible Deal
   * Activity surface at the bottom of the panel carries the
   * timeline.  `tasks` continues to alias into `milestones`. */
  const LEGACY_TAB_ALIAS = useMemo(() => ({
    "activity":     "current_stage",
    "updates":      "current_stage",
    "notes":        "current_stage",
    "documents":    "current_stage",
    "tasks":        "milestones",
    "participants": "current_stage",
  }), []);
  useEffect(() => {
    const t = searchParams.get("tab");
    if (!t) return;
    if (VALID_TABS.has(t)) setActivityTab(t);
    else if (LEGACY_TAB_ALIAS[t]) setActivityTab(LEGACY_TAB_ALIAS[t]);
  }, [searchParams, VALID_TABS, LEGACY_TAB_ALIAS]);

  /* iter-215 — Auto-open the Progress deal drawer when the route
   * is entered with `?progress=1`. Used by the Deal Rooms index
   * page's expanded preview "Progress deal" action so the operator
   * lands directly inside the milestone progression engine. */
  useEffect(() => {
    if (searchParams.get("progress") === "1") {
      setProgressOpen(true);
    }
  }, [searchParams]);

  /* iter-P65 · Auto-open the Request Document drawer when the
   * route enters with `?request-doc=1[&template={id}]`.  See
   * `lib/documentRequestLaunch.js` — Today, Contacts, Contact
   * Profile, NBA and Open Commitments all deep-link here.
   *
   * iter-P67 · Scrub the URL with `window.history.replaceState`
   * instead of react-router's `setSearchParams` so the effect
   * never causes an in-flight deal fetch to see an intermediate
   * empty-params state (fixes the "Deal Room not found" race
   * observed under load).  Guarded with a ref so the drawer
   * opens exactly once per navigation. */
  const _requestDocLaunchedRef = useRef(false);
  useEffect(() => {
    if (_requestDocLaunchedRef.current) return;
    if (searchParams.get("request-doc") !== "1") return;
    _requestDocLaunchedRef.current = true;
    const template = searchParams.get("template");
    setRequestDocInitialTemplate(template || null);
    setOpenSheet("request-doc");
    if (typeof window !== "undefined" && window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.delete("request-doc");
      url.searchParams.delete("template");
      window.history.replaceState({}, "", url.toString());
    }
  }, [searchParams]);


  /* iter-209B — Scroll restoration.
   * Persist the Deal Room scroll position per dealId in sessionStorage
   * and restore on next mount so long transaction rooms feel resumable
   * after opening sheets / participant modals / document previews.
   * Throttled write via rAF so it never thrashes. */
  const scrollContainerRef = useRef(null);
  const scrollRestoreKey = dealId ? `cf:dealroom:scroll:${dealId}` : null;
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || !scrollRestoreKey || typeof window === "undefined") return undefined;
    // Restore on mount
    try {
      const saved = window.sessionStorage.getItem(scrollRestoreKey);
      const y = saved ? parseInt(saved, 10) : NaN;
      if (Number.isFinite(y) && y > 0) {
        // wait one frame so the layout has settled before we restore
        requestAnimationFrame(() => { el.scrollTop = y; });
      }
    } catch { /* private-mode / quota — silent */ }

    let rafId = null;
    const onScroll = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        try { window.sessionStorage.setItem(scrollRestoreKey, String(el.scrollTop || 0)); }
        catch { /* silent */ }
        rafId = null;
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, [scrollRestoreKey]);

  // Calm helper — switch the ActivityFeed tab AND scroll to it. Used
  // by every external "View …" / "Open …" affordance that wants to
  // drop the operator straight into the relevant section.
  // iter-209B — also syncs the tab into the URL `?tab=` so deep
  // links survive copy/paste + reload.
  const jumpToTab = useCallback((nextTab) => {
    setActivityTab(nextTab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      // Default tab is `current_stage` — leave the URL clean when
      // we're landing there.
      if (nextTab && nextTab !== "current_stage") next.set("tab", nextTab);
      else                                        next.delete("tab");
      return next;
    }, { replace: true });
    if (typeof document === "undefined") return;
    requestAnimationFrame(() => {
      const el = document.querySelector('[data-testid="canonical-activity-feed"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [setSearchParams]);

  /* iter-221 — Journey step activation handler. Resolves the
   * clicked step id against the live `journeyState.steps` array
   * (which carries authoritative {state, target_date}) and opens
   * the lightweight per-step progression sheet. Falls back to a
   * minimal stub if the journey endpoint hasn't responded yet —
   * the sheet itself will hydrate on save. */
  /* iter-alignment · P11 · Journey selection drives the Current Stage panel.
   * Tapping any journey rail node routes the Current Stage
   * checklist to that stage — the read-only preview drawer is
   * retired.  `viewingStageId` is a purely local UI selection;
   * it never mutates the canonical Current Stage, Exchange
   * Confidence, Next Best Action or automatic progression — those
   * remain anchored to `journeyState.current_step` / SSOT. When
   * the selected node IS the canonical Current Stage the panel
   * stays fully interactive; other stages render read-only with
   * a "Viewing: …" indicator and a "Return to Current Stage"
   * action. */
  const [viewingStageId, setViewingStageId] = useState(null);
  // iter-alignment · P40 — Inline date capture pending state.  When
  // an operator taps to complete a milestone that carries a
  // `date_prompt` metadata block, we set this to the milestone
  // object and render the compact popover below.  Cleared on Save
  // (which re-invokes `completeMilestone(id, dates)`) or No date /
  // Cancel.
  const [pendingDateCapture, setPendingDateCapture] = useState(null);
  const onReturnToCurrentStage = useCallback(
    () => setViewingStageId(null),
    [],
  );

  /* iter-alignment · P38 · Auto-follow canonical stage advancement.
   *
   * When the canonical current stage progresses (e.g. milestone
   * completion triggers the auto-advance path in
   * `routes_milestones.py::complete_milestone`), the Current Stage
   * checklist must switch to the new stage automatically UNLESS
   * the operator has explicitly tapped another stage.
   *
   * Heuristic: if `viewingStageId` matches the PREVIOUS canonical
   * stage (i.e. the operator was tacitly following canonical or
   * had tapped the then-current stage), clear it to null so the
   * checklist re-anchors to the new canonical stage.  If the
   * operator was explicitly viewing a different stage (Contract
   * while canonical was Enquiries, say), leave the selection put
   * — they intended to inspect a completed stage. */
  const prevCanonicalStageRef = useRef(null);
  useEffect(() => {
    let resolvedCurrent = null;
    try {
      const r = resolveJourneyProgress(deal, milestones);
      const idx = r?.currentFocusIdx;
      if (typeof idx === "number" && idx >= 0 && Array.isArray(r?.steps)) {
        resolvedCurrent = r.steps[idx]?.id || null;
      }
    } catch { /* deal/milestones may be null on first tick */ }
    const canonicalCurrent =
      resolvedCurrent || journeyState?.current_step || null;
    const prev = prevCanonicalStageRef.current;
    if (prev && canonicalCurrent && prev !== canonicalCurrent) {
      // Canonical stage progressed since last observation.
      setViewingStageId((current) => (current === prev ? null : current));
    }
    prevCanonicalStageRef.current = canonicalCurrent;
  }, [deal, milestones, journeyState?.current_step]);

  const onJourneyStepActivate = useCallback((stepId, flags = {}) => {
    if (flags && flags.asSelect) {
      // Rail tap — route the Current Stage panel to this node.
      // iter-alignment · P31 · Regression fix — the canonical-
      // current comparison must use the SAME derivation the Current
      // Stage panel uses (`resolveJourneyProgress(...).steps[
      // currentFocusIdx]?.id`), otherwise backend `current_step` and
      // resolved-rail focus can diverge (e.g. a deal parked on
      // `mgmt_pack` where the backend still reports `contract`
      // pre-advance).  When they disagreed, tapping the resolved
      // canonical stage did nothing AND tapping a completed stage
      // that matched the backend `current_step` cleared the
      // selection instead of viewing that stage.  We derive here
      // from the same SSOT the panel uses so the two never drift.
      let resolvedCurrent = null;
      try {
        const r = resolveJourneyProgress(deal, milestones);
        const idx = r?.currentFocusIdx;
        if (typeof idx === "number" && idx >= 0 && Array.isArray(r?.steps)) {
          resolvedCurrent = r.steps[idx]?.id || null;
        }
      } catch { /* fall back below */ }
      const canonicalCurrent = resolvedCurrent || journeyState?.current_step || null;
      setViewingStageId(
        stepId && stepId !== canonicalCurrent ? stepId : null,
      );
      return;
    }
    // Critical-path (Current Focus) — open the progression sheet.
    // The journey API names the last stage "completion"; the rail and milestones use "complete".
    const fromState = Array.isArray(journeyState?.steps)
      ? journeyState.steps.find((s) => s && (s.id === stepId || (stepId === "complete" && s.id === "completion")))
      : null;
    let railLabel = null;
    try { railLabel = resolveJourneyProgress(deal, milestones).steps.find((s) => s.id === stepId)?.label || null; } catch { /* fall back */ }
    setProgressionStep({
      ...(fromState
        ? { ...fromState, id: stepId }
        : { id: stepId, label: railLabel || stepId.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()), state: "upcoming", target_date: null }),
      ...(flags && flags.summary ? { summary: true } : {}),
    });
  }, [deal, milestones, journeyState]);

  /* iter-209B — Hash-anchor scroll restoration.
   * After activities load, if the URL carries `#activity-<id>` smooth-
   * scroll the matching row into view. The activity feed renders rows
   * with testid `canonical-activity-<index>`, so we resolve the index
   * by scanning the loaded activities list. Idempotent — runs once
   * per (hash, activities) tuple. */
  const lastHashHandled = useRef(null);
  useEffect(() => {
    if (typeof window === "undefined" || !activities?.length) return;
    const hash = window.location.hash || "";
    if (!hash.startsWith("#activity-")) return;
    if (lastHashHandled.current === hash) return;
    const targetId = hash.slice("#activity-".length);
    const idx = activities.findIndex((a) => String(a.id || "") === targetId);
    if (idx < 0) return;
    lastHashHandled.current = hash;
    // Ensure the activity tab is active so the row is in the DOM.
    setActivityTab("activity");
    requestAnimationFrame(() => {
      const row = document.querySelector(`[data-testid="canonical-activity-${idx}"]`);
      if (row) row.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [activities]);


  // iter-209A — unread notifications. Bind the bell badge to the
  // chainStore notifications stream so it never shows a fake `3`.
  // Hidden entirely when the count is 0.
  //
  // iter-alignment · P · Deal Room · Header Notification Bell
  // Retirement (2026-03-02) — the per-room bell was retired
  // because the global shell bell in the top-right header already
  // reads the same source.  The derivation is left in place (no
  // logic change) so any future re-introduction of a room-scoped
  // affordance can consume it verbatim; nothing else references
  // it right now.
  // eslint-disable-next-line no-unused-vars
  const unreadCount = useMemo(
    () => (Array.isArray(notifications) ? notifications.filter((n) => !n.read).length : 0),
    [notifications],
  );

  // iter-209A — composer attachment plumbing. Operators tap the
  // paperclip → real <input type=file> opens → selected files are
  // stashed in a ref, surfaced as a calm "N attached" chip beneath
  // the composer, and POSTed with the next note via FormData.
  const attachInputRef = useRef(null);
  const [pendingAttachments, setPendingAttachments] = useState([]);  // File[]
  const onAttachClick = useCallback(() => { attachInputRef.current?.click(); }, []);
  const onAttachChange = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPendingAttachments((prev) => [...prev, ...files]);
    e.target.value = "";  // allow re-selecting the same file
  }, []);
  const removeAttachment = useCallback((idx) => {
    setPendingAttachments((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  // iter-204c — inline date editing. Optimistic UI via the local
  // chainStore patch, real persistence via updateDeal. Failure path
  // rolls the local patch back so the hero never lies about state.
  const persistDateField = useCallback(async (field, valueIso) => {
    const dealNow = findDeal(dealId);
    if (!dealNow) throw new Error("Deal not found");
    const prev = field === "targetExchange"
      ? dealNow.targetExchange
      : field === "targetCompletion"
        ? dealNow.targetCompletion
        : field === "saleAgreedDate"
          ? (dealNow.saleAgreedDate || dealNow.sale_agreed_date || null)
          : dealNow[field];
    // 1) optimistic patch
    patchDealLocally(dealId, { [field]: valueIso });
    try {
      await updateDeal(dealId, { [field]: valueIso });
    } catch (err) {
      // 2) rollback
      patchDealLocally(dealId, { [field]: prev });
      throw err;
    }
  }, [dealId, findDeal, patchDealLocally, updateDeal]);

  const onUpdateExchange = useCallback(
    (iso) => persistDateField("targetExchange", iso),
    [persistDateField],
  );
  const onUpdateCompletion = useCallback(
    (iso) => persistDateField("targetCompletion", iso),
    [persistDateField],
  );
  // iter-alignment · Deal Room · Sale Agreed + Transaction Dates
  // (2026-03-02) — same optimistic patch + rollback flow as the
  // target dates.  Persists via the canonical deal PATCH surface;
  // the chainStore adapter maps `saleAgreedDate` ↔
  // `sale_agreed_date` server-side.
  const onUpdateSaleAgreed = useCallback(
    (iso) => persistDateField("saleAgreedDate", iso),
    [persistDateField],
  );

  /* iter-alignment · Deal Room · Final Right Rail · Buyer Position
   * (2026-03-02) — single-shot PATCH for the six deal-level buyer
   * position fields (buyerType · propertyToSell · mortgagePosition ·
   * proofOfFunds · depositAmount · depositPercentage).  Reuses the
   * canonical `updateDeal` optimistic-patch + rollback + refresh
   * flow so the Buyer Position card behaves like every other
   * inline editor on the deal (no new state, no new endpoint). */
  const onUpdateBuyerPosition = useCallback(async (patch) => {
    const dealNow = findDeal(dealId);
    if (!dealNow) throw new Error("Deal not found");
    // Snapshot the previous values so we can roll back if the
    // network hop fails.
    const prev = {};
    for (const k of Object.keys(patch)) prev[k] = dealNow[k] ?? null;
    // 1) Optimistic patch — mirrors the target-date / tenure
    // flow (`patchDealLocally` + `updateDeal` are the canonical
    // deal-side helpers; the previous version referenced an
    // undefined helper symbol which caused every save to
    // throw).  Persistence goes through the same canonical Deal
    // PATCH surface used for tenure / promotion baseline.
    patchDealLocally(dealId, patch);
    try {
      const MAP = {
        buyerType:          "buyer_type",
        propertyToSell:     "property_to_sell",
        mortgagePosition:   "mortgage_position",
        proofOfFunds:       "proof_of_funds",
        depositAmount:      "deposit_amount",
        depositPercentage:  "deposit_percentage",
      };
      const apiPatch = {};
      for (const [k, v] of Object.entries(patch)) {
        apiPatch[MAP[k] || k] = v === "" ? null : v;
      }
      if (deal?._pilot) {
        // Pilot fixtures live in-memory; the optimistic patch is
        // the whole persistence story for demo mode.
        return;
      }
      await api.patch(`/api/v2/deals/${dealId}`, apiPatch);
      await reload();
    } catch (err) {
      patchDealLocally(dealId, prev);
      throw err;
    }
    // iter-alignment · P0 · Deal Room · Load Regression Fix
    // (2026-03-02) — `reload` is declared further down the
    // component body (canonical Promise.all fetch); listing it
    // in the deps array here evaluates the identifier at render
    // time and throws
    //   ReferenceError: Cannot access 'reload' before initialization
    // (TDZ on a `const useCallback`).  We follow the same pattern
    // `onQuickEditSave` already uses below — omit `reload` from
    // the deps and silence the exhaustive-deps rule.  Reload is
    // stable (`useCallback` w/ [dealId]-based deps) so the
    // callback always resolves to the current instance at call
    // time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, findDeal, patchDealLocally, deal]);

  // iter-302 — Quick Edit Deal side-sheet state + commit handler.
  const [quickEditOpen, setQuickEditOpen] = useState(false);
  const onQuickEditSave = useCallback(async (diff, initial) => {
    if (!dealId || !diff || Object.keys(diff).length === 0) return;
    const isPilot = !!deal?._pilot;
    /* Field-to-deal-patch map. Address writes to the property, not
     * the deal, so we forward it separately. Buyer/Seller surface on
     * the deal envelope today; solicitor/broker route through the
     * participants array (lightweight rename — full role-aware
     * upsert can come later if the pilot demands it). */
    const dealPatch = {};
    if (diff.agreedPrice      !== undefined) dealPatch.agreedPrice      = Number(diff.agreedPrice) || 0;
    if (diff.buyerName        !== undefined) dealPatch.buyer_name       = diff.buyerName;
    if (diff.sellerName       !== undefined) dealPatch.seller_name      = diff.sellerName;
    if (diff.exchangeTarget   !== undefined) dealPatch.targetExchange   = diff.exchangeTarget   ? new Date(diff.exchangeTarget   + "T12:00:00").toISOString() : null;
    if (diff.completionTarget !== undefined) dealPatch.targetCompletion = diff.completionTarget ? new Date(diff.completionTarget + "T12:00:00").toISOString() : null;

    /* iter-alignment · P68 · Purchase method (Mortgage ↔ Cash).
     * Canonical SSOT is `deals.is_cash_buyer` — the backend
     * milestone / blocker / journey-progress / NBA engines already
     * gate mortgage rows via `_applies_to_deal('mortgaged')` so
     * flipping this flag is sufficient to drop / restore the
     * Mortgage node across every consumer.  Reconciliation is
     * client-side: when flipping to Cash while the deal sits on
     * a mortgage-only stage (`mortgage_app` / `mortgage_offer` /
     * `survey`), bump the stage to the first applicable
     * incomplete stage via `journeyFor(tenure, {financeType:'cash'})`.
     * Flipping back to Mortgage does NOT rewind stage — historic
     * mortgage activity + milestones remain in the DB (excluded,
     * not deleted) and re-surface automatically. */
    if (diff.purchaseMethod !== undefined) {
      const isCash = diff.purchaseMethod === "cash";
      dealPatch.is_cash_buyer = isCash;
      if (isCash) {
        const currentStage = String(deal?.stage || "").toLowerCase();
        if (MORTGAGE_ONLY_STAGES.has(currentStage)) {
          const nextStage = _firstApplicableCashStage(deal);
          if (nextStage) dealPatch.stage = nextStage;
        }
      }
    }

    /* Participants — rename the matching participant entry on the
     * deal envelope so the right rail + Forecasting Centre pick up
     * the change without an API round trip. Real deals will still
     * need a /api/v2/deals/:id/participants PATCH in a follow-up;
     * for the pilot demo this keeps every surface in sync. */
    const renames = [];
    if (diff.solicitorName !== undefined) renames.push({ types: ["buyer_solicitor", "seller_solicitor", "solicitor"], name: diff.solicitorName });
    if (diff.brokerName    !== undefined) renames.push({ types: ["mortgage_broker", "broker"],                          name: diff.brokerName    });
    if (renames.length > 0) {
      const existing = Array.isArray(deal?.participants) ? deal.participants : [];
      const nextParticipants = existing.map((p) => {
        const role = (p?.role || "").toLowerCase().replace(/\s+/g, "_");
        for (const r of renames) {
          if (r.types.includes(p?.participant_type) || r.types.includes(role)) {
            return { ...p, full_name: r.name, name: r.name };
          }
        }
        return p;
      });
      dealPatch.participants = nextParticipants;
    }

    // Optimistic local patch — every connected surface (Forecasting
    // Centre, Operational Pressure, Deal Health, Journey, Deal Rooms
    // list) re-renders on the same tick via the shared chainStore.
    if (Object.keys(dealPatch).length > 0) patchDealLocally(dealId, dealPatch);

    // Persist — pilot deals stay local; real deals PATCH the API.
    try {
      if (!isPilot && Object.keys(dealPatch).length > 0) {
        await updateDeal(dealId, dealPatch);
      }
    } catch (err) {
      // Roll back the optimistic patch by re-applying initial values.
      const revert = {};
      Object.keys(diff).forEach((k) => { revert[k === "agreedPrice" ? "agreedPrice" : k] = initial[k]; });
      patchDealLocally(dealId, revert);
      toast.error("Couldn't save changes");
      throw err;
    }

    // Activity log — one row per changed field. Pilot deals use the
    // pilot activity helper; real deals POST to the canonical
    // /api/v2/deals/:id/activity endpoint.
    const LABELS = {
      address:          "Address",
      agreedPrice:      "Sale agreed price",
      buyerName:        "Buyer",
      sellerName:       "Seller",
      solicitorName:    "Solicitor",
      brokerName:       "Mortgage broker",
      exchangeTarget:   "Exchange target",
      completionTarget: "Completion target",
      purchaseMethod:   "Purchase method",
    };
    for (const k of Object.keys(diff)) {
      const label = LABELS[k] || k;
      const description = `Quick edit · ${label}: ${initial[k] || "—"} → ${diff[k] || "—"}`;
      if (isPilot) {
        appendPilotActivity?.(dealId, {
          type: "deal_edit",
          title: `${label} updated`,
          label: `${label} updated`,
          description,
        });
      } else {
        try {
          await api.post(`/api/v2/deals/${dealId}/activity`, {
            type: "deal_edit",
            title: `${label} updated`,
            description,
          });
        } catch (_e) { /* audit-log best-effort */ }
      }
    }

    toast.success(`Deal updated · ${Object.keys(diff).length} ${Object.keys(diff).length === 1 ? "field" : "fields"}`);
    /* iter-alignment · P68 · Broadcast so Command Centre / Today /
     * DealRow refetch the canonical `/api/deals` payload without a
     * page refresh.  Same event the BSA property flip + task
     * lifecycle already use — no new bus. */
    if (typeof window !== "undefined") {
      try {
        window.dispatchEvent(new CustomEvent("cf:deal:refresh", {
          detail: { dealId, reason: "quick_edit.saved" },
        }));
      } catch (_) { /* noop */ }
    }
    await reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, deal, patchDealLocally, updateDeal, appendPilotActivity]);

  /* iter-209B — Activity auto-anchor.
   * After a successful message/note/task/document submit, gently
   * scroll the activity feed to the most-recent (top) item so the
   * operator sees the new event without forcing a hard scroll.
   * Triggered by bumping `reloadTick` — increments on every reload()
   * resolution so the effect re-runs even when the activity array
   * reference is the same. */
  const [reloadTick, setReloadTick] = useState(0);
  /* iter-alignment · P38 · Stale-response prevention.
   *
   * After any successful mutation the Deal Room fires a single
   * canonical `reload()` — a `Promise.all` of ten endpoint fetches.
   * If two mutations land back-to-back (e.g. an operator taps two
   * milestone checkboxes in quick succession) the second reload
   * can resolve BEFORE the first, letting an older response
   * overwrite newer state.
   *
   * The monotonic `reloadSeqRef` tags each invocation; only the
   * newest sequence writes its response into React state.  The
   * older reload's fetch promises still fulfil (with `.catch(...)`
   * absorbing any errors) — they just don't touch state. */
  const reloadSeqRef = useRef(0);
  // Latest page comes from reload(); "Load earlier activity" appends older pages via a keyset cursor (no cap).
  const ACTIVITY_PAGE = 80;
  const [olderActivities, setOlderActivities] = useState([]);
  const olderActivitiesRef = useRef([]);
  const [activityPaging, setActivityPaging] = useState({ hasMore: false, loading: false });
  useEffect(() => { olderActivitiesRef.current = []; setOlderActivities([]); }, [dealId]);
  const feedActivities = useMemo(() => {
    const seen = new Set();
    return [...activities, ...olderActivities].filter((a) => (seen.has(a.id) ? false : seen.add(a.id)));
  }, [activities, olderActivities]);
  const loadEarlierActivity = useCallback(async () => {
    const oldest = feedActivities[feedActivities.length - 1];
    if (!dealId || !oldest) return;
    setActivityPaging((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get(`/api/v2/deals/${dealId}/activity?limit=${ACTIVITY_PAGE}&before=${oldest.id}`);
      const list = Array.isArray(data) ? data : [];
      olderActivitiesRef.current = [...olderActivitiesRef.current, ...list];
      setOlderActivities(olderActivitiesRef.current);
      setActivityPaging({ hasMore: list.length >= ACTIVITY_PAGE, loading: false });
    } catch {
      setActivityPaging((s) => ({ ...s, loading: false }));
      toast.error("Earlier activity couldn't be loaded — please try again.");
    }
  }, [dealId, feedActivities]);
  const reload = useCallback(async () => {
    if (!dealId) return;
    const seq = reloadSeqRef.current + 1;
    reloadSeqRef.current = seq;
    const [a, d, p, t, dr, i, ms, bl, jn, on] = await Promise.all([
      api.get(`/api/v2/deals/${dealId}/activity?limit=${ACTIVITY_PAGE}`).catch(() => ({ data: null, failed: true })),
      api.get(`/api/deals/${dealId}/documents`).catch(() => ({ data: [] })),
      api.get(`/api/deals/${dealId}/participants-enriched`).catch(() => ({ data: [] })),
      api.get(`/api/tasks?deal_id=${dealId}`).catch(() => ({ data: [] })),
      api.get(`/api/deals/${dealId}/document-requests`).catch(() => ({ data: [] })),
      api.get(`/api/deals/${dealId}/intelligence`).catch(() => ({ data: null })),
      api.get(`/api/v2/deals/${dealId}/milestones`).catch(() => ({ data: null })),
      api.get(`/api/v2/deals/${dealId}/blockers`).catch(() => ({ data: [] })),
      api.get(`/api/v2/deals/${dealId}/journey`).catch(() => ({ data: null })),
      /* iter-347 — Operational notes feed the Updates timeline
         as `internal_note` events. Best-effort fetch — empty on
         failure so the timeline stays calm. */
      api.get(`/api/v2/deals/${dealId}/operational-notes`).catch(() => ({ data: [] })),
    ]);
    // Stale-response guard — a newer reload has been requested
    // since this one started; discard our response so the newer
    // one wins and the operator never sees an older snapshot.
    if (seq !== reloadSeqRef.current) return;
    // A failed activity fetch keeps the last good list and surfaces an error state (never "empty").
    if (a.failed) {
      setActivityStatus("error");
    } else {
      const list = Array.isArray(a.data) ? a.data : [];
      setActivities(list);
      setActivityPaging((s) => (olderActivitiesRef.current.length ? { ...s, loading: false } : { hasMore: list.length >= ACTIVITY_PAGE, loading: false }));
      setActivityStatus("ready");
    }
    /* iter-360 — Deal Rooms load participants from Postgres.
     *
     * The pilot participant roster is now seeded into
     * `deal_participants` via `seed_pilot_deals_for_demo_org()`
     * on backend boot, so the API is the single source of truth
     * on every deal (pilot or real). The frontend fallback that
     * previously copied the roster from `pilotData.js` when the
     * API returned an empty list has been removed — an empty
     * response now surfaces the calm "Add participants" affordance
     * rather than masking a data-integrity gap. */
    const apiDocs = Array.isArray(d.data) ? d.data : [];
    const apiParticipants = Array.isArray(p.data) ? p.data : [];
    /* iter-363 — Documents fallback removed. Postgres now carries
     * the full pilot document trail (Memorandum → Buyer ID →
     * Survey → Draft Contract → Management Pack → Searches →
     * Mortgage Offer) via `seed_pilot_deals_for_demo_org()`. An
     * empty API response now surfaces the calm "No documents yet"
     * affordance instead of masking a data-integrity gap. */
    setDocuments(apiDocs);
    setParticipants(apiParticipants);
    setTasks(Array.isArray(t.data) ? t.data : []);
    setDocRequests(Array.isArray(dr.data) ? dr.data : []);
    setIntel(i.data || null);
    setMilestones(ms.data || null);
    setAwaiting(Array.isArray(bl.data) ? bl.data : []);
    setOperationalNotes(Array.isArray(on.data) ? on.data : []);
    setRoomLoaded(true);
    // iter-270F — Pilot deals are frontend-only; the backend has no
    // /journey state for them. Synthesise a journey state from the
    // canonical journey order so the "Update Milestones" CTA + the
    // milestone sheet operate the same way as on real deals. The
    // markComplete flow then runs through markPilotJourneyStepComplete
    // (no API call) per chainStore.
    if (jn.data) {
      setJourneyState(jn.data);
    } else if (deal && deal._pilot) {
      /* iter-292 — Pilot fallback journey state is now derived from
       * the canonical transaction journey module so the rail, the
       * milestone sheet, and the markComplete flow all share the
       * same node ids, ordering, labels and gating.
       *
       * iter-306 — `optsForDeal()` threads the deal's finance type
       * + BSA applicability through `journeyFor` so cash buyers
       * skip Mortgage, leasehold deals surface BSA by default, and
       * an explicit `applicableBSA: false` suppresses BSA. */
      const tenure = deal.tenure || "freehold";
      const opts = optsForDeal(deal);
      const canonicalNodes = journeyForCanonical(tenure, opts);
      const order = canonicalNodes.map((n) => n.id);
      const STEP_LABELS = canonicalNodes.reduce((acc, n) => {
        acc[n.id] = n.longLabel;
        return acc;
      }, {});
      const currentStep = nodeIdForStage(deal.stage) || order[0];
      const currentIdx = Math.max(0, order.indexOf(currentStep));
      // Pilot overrides may have recorded completed steps locally.
      let completedFromOverrides = [];
      try {
        const overrides = JSON.parse(localStorage.getItem("chainflow:pilotOverrides:v1") || "{}");
        completedFromOverrides = overrides[dealId]?.completedSteps || [];
      } catch (_e) { /* noop */ }
      const completedSet = new Set([
        ...order.slice(0, currentIdx),
        ...completedFromOverrides,
      ]);
      const steps = order.map((id, i) => ({
        id,
        label: STEP_LABELS[id] || id,
        state: completedSet.has(id) ? "complete" : (id === currentStep ? "in_progress" : "upcoming"),
        order: i,
      }));
      setJourneyState({ current_step: currentStep, steps });
    } else {
      setJourneyState(null);
    }
    setReloadTick((n) => n + 1);
  }, [dealId, deal]);

  /* iter-209B — After a composer-driven reload (message/note/task
   * /request), scroll the newest activity row into view. We anchor
   * to the FIRST row (`canonical-activity-0`) because the activity
   * list is reverse-chronological. Skip the initial mount-driven
   * reload (tick 0 → 1) so we don't auto-scroll on page load. */
  const composerBumpRef = useRef(false);
  const requestActivityAnchor = useCallback(() => {
    composerBumpRef.current = true;
  }, []);

  /* iter-390 · Effective awaiting = raw awaiting − resolved
   * Critical Paths.  Threaded to every downstream consumer
   * (ActivityFeed / RightRail / DealRoomMobile) so completing a
   * commitment surfaces the *next* dependency automatically
   * (Radar row + Right-rail Open Commitments + Critical Path
   * card all recalculate off the same filtered list).
   *
   * P0 · Radar Complete action wire-up (2026-08-12) — When the
   * `/api/v2/deals/:id/blockers` engine hasn't detected any
   * evidence-driven blockers (typical for seeded pilot deals
   * where `deal.blockers` carries the canonical seed but the
   * live detector runs over documents/activities), we FALL
   * BACK to the shared SSOT (`filterActiveBlockers` on the
   * deal blob) so the Radar can surface + resolve the
   * canonical blocker via the same Complete action.  The
   * `resolvedIds` sentinel is honoured in both paths so a
   * click drops the row on the same render pass, regardless
   * of which source produced it.  No new endpoint, no
   * duplicate calc, no new state — the fallback is a pure
   * read through the existing filter.
   */
  const effectiveAwaiting = useMemo(() => {
    const primary = Array.isArray(awaiting) ? awaiting : [];
    if (primary.length > 0) {
      // Live detector produced blockers — trust it verbatim, only
      // filter by the client sentinel for same-render optimism.
      if (!resolvedCriticalPaths || resolvedCriticalPaths.size === 0) return primary;
      return primary.filter((b) => !resolvedCriticalPaths.has(String(b?.id || b?.step_id || b?.title)));
    }
    // Fallback: SSOT-filtered blockers straight off `deal.blockers`.
    // `filterActiveBlockers` already applies the stage cursor, the
    // completed-step short-circuit and the `resolvedIds` sentinel.
    // Uses `journeyState?.current_step` alone as the stage anchor
    // — the txnState memo hasn't run yet at this point in the
    // render pass (TDZ), and current_step is already the SSOT axis
    // journey rail + filter share.
    const ssot = filterActiveBlockers(deal || {}, {
      applicableNodes: journeyState?.steps || [],
      currentNodeId: journeyState?.current_step || null,
      resolvedIds: resolvedCriticalPaths,
    });
    // Normalise into the Radar's `waitingOn` row shape (title,
    // days, step_id, detail) so the same component reads either
    // source with no branching downstream.
    return ssot.map((b) => ({
      id:       b.id || b.step_id || String(b.title || "cp"),
      step_id:  b.step_id || null,
      title:    b.title || b.label || b.copy || "Blocker",
      detail:   b.description || b.detail || b.next_action || null,
      days:     Number(b.age_days || b.days || 0),
      severity: b.severity || "warning",
      actor_id: b.actor_id || b.user_id || null,
      role_label: b.owner_role || b.role_label || null,
    }));
  }, [awaiting, deal, journeyState, resolvedCriticalPaths]);

  /* iter-391 · Critical Path resolver (Postgres SSOT).
   *
   * iter-392 · The success toast now carries an **Undo** action
   * that stays available for 15 s.  Undo posts to
   *   POST /api/v2/deals/:dealId/blockers/:blockerId/revert-resolution
   * which writes one `blocker_resolution_reverted` audit row.
   * After 15 s the toast dismisses and the resolution stands.
   */
  const handleCriticalPathResolved = useCallback(async (blocker, opts = {}) => {
    if (!blocker || !dealId) return;
    const rawId = String(blocker.id || blocker.step_id || blocker.title || "cp");
    /* Optimistic client-side hide so the card + Radar row update
     * on the same interaction — the backend re-fetch below then
     * confirms the resolution.  The sentinel here is *transient*
     * (session-scoped) and does not touch localStorage. */
    setResolvedCriticalPaths((prev) => {
      if (prev.has(rawId)) return prev;
      const next = new Set(prev);
      next.add(rawId);
      return next;
    });
    /* Persist to Postgres — single audit row, idempotent. */
    try {
      await api.post(`/api/v2/deals/${dealId}/blockers/${encodeURIComponent(rawId)}/resolve`, {
        outcome: opts?.note || `Commitment complete: ${blocker.title || blocker.detail || "Critical path"}`,
        source: opts?.source || "critical_path_action",
      });
    } catch (err) {
      /* Rollback the optimistic hide + surface a calm toast so
       * the operator can retry.  Backend remains SSOT — never a
       * phantom row. */
      setResolvedCriticalPaths((prev) => {
        const next = new Set(prev);
        next.delete(rawId);
        return next;
      });
      toast.error("Couldn't record the resolution — please try again");
      return;
    }
    /* Rehydrate the Deal Room state ONCE.  `reload()` re-fetches
     * blockers (now filtered by the backend), journey, activity,
     * milestones — so Radar / Critical Path card / Open
     * Commitments / Next Best Action / health / Recent Changes
     * all recalculate off the same SSOT on the same render pass. */
    try { await reload(); } catch { /* noop */ }
    /* iter-392 · Success toast with 15-second Undo. */
    toast.success("Commitment complete · next Critical Path promoted", {
      duration: 15000,
      action: {
        label: "Undo",
        onClick: async () => {
          try {
            await api.post(
              `/api/v2/deals/${dealId}/blockers/${encodeURIComponent(rawId)}/revert-resolution`,
              { source: "critical_path_undo" },
            );
          } catch {
            toast.error("Couldn't undo the resolution — please try again");
            return;
          }
          /* Restore optimistically + re-fetch so every Deal
           * Room lane returns to the correct state on one
           * render pass. */
          setResolvedCriticalPaths((prev) => {
            const next = new Set(prev);
            next.delete(rawId);
            return next;
          });
          try { await reload(); } catch { /* noop */ }
          toast.success("Resolution reverted · Critical Path restored");
        },
      },
    });
  }, [dealId, reload]);
  useEffect(() => {
    if (reloadTick === 0) return;
    if (!composerBumpRef.current) return;
    composerBumpRef.current = false;
    if (typeof document === "undefined") return;
    requestAnimationFrame(() => {
      const row = document.querySelector('[data-testid="canonical-activity-0"]');
      if (row) row.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [reloadTick]);


  /* iter-363 — Memoise the reload firing.
   *
   * The `reload()` callback closes over `deal` so its identity
   * changes on every render, causing the previous `useEffect(…,
   * [reload])` binding to re-fire ~27 times per Deal Room load
   * (every state update inside `reload` triggered another render
   * → new `reload` identity → another `reload` … recursive).
   *
   * Fixing that with a `useRef(reload)` shim would break the
   * useCallback pattern. Simpler: fire `reload()` when the
   * DEAL ID changes, not the callback identity. Every other
   * caller (`reload()` after a message send, milestone advance,
   * doc upload, etc.) still invokes it imperatively.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [dealId]);

  /* iter-alignment · BSA Journey Delayed Refresh (2026-09)
   * ---------------------------------------------------------------
   * `chainStore.updateProperty` refreshes `deal.applicable_bsa`
   * on the deal blob, but this page holds its own
   * `milestones` + `journeyState` caches (populated inside
   * `reload()`).  When Edit Property saves `bsa_applies`, the
   * store fires `cf:deal:refresh { dealId }` for every linked
   * deal — we listen and re-run `reload()` for the matching
   * dealId so BSA milestone rows + journey stage appear the
   * moment the modal closes.  Ignores events for other deals,
   * so multi-tab Deal Rooms never crossfire. */
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onRefresh = (e) => {
      if (e?.detail?.dealId && e.detail.dealId !== dealId) return;
      reload();
    };
    window.addEventListener("cf:deal:refresh", onRefresh);
    return () => window.removeEventListener("cf:deal:refresh", onRefresh);
  }, [dealId, reload]);

  /* iter-alignment · P3 · Deal Room · Participant Management
   * (2026-02-28) — the ParticipantsModal Roles tab mutates the
   * canonical `deal_participants` table directly.  It emits a soft
   * `cf:participants-changed` custom event on the window each time
   * an assign / reassign / remove succeeds so the Deal Room can
   * refetch its own state and every downstream surface (Right
   * Rail, Journey, Activity Feed) re-derives in the next render.
   * We scope the listener to the current dealId so a listener on
   * another room never fires. */
  useEffect(() => {
    if (!dealId) return undefined;
    const onChanged = (event) => {
      if (event?.detail?.dealId && event.detail.dealId !== dealId) return;
      reload();
    };
    window.addEventListener("cf:participants-changed", onChanged);
    return () => window.removeEventListener("cf:participants-changed", onChanged);
  }, [dealId, reload]);

  /* iter-269 — Canonical fallback for right-rail Documents +
   * Participants. The chainStore deal (from PILOT_DEALS) carries
   * seeded `documents` + `participants` arrays. When the API
   * returns empty for either, we fall back to those canonical
   * arrays so the Deal Room never reads as abandoned. API data
   * remains primary — fallback only activates on empty arrays.
   *
   * No duplicate records: when API returns non-empty, we render
   * API data verbatim. We never merge or interleave. */
  const effectiveDocuments = useMemo(() => {
    /* iter-363 — Postgres is the single source of truth for
     * documents on every deal (pilot + real). No frontend
     * fallback — an empty API response reads calmly as "No
     * documents yet" rather than masking a data-integrity gap. */
    return Array.isArray(documents) ? documents : [];
  }, [documents]);

  const effectiveParticipants = useMemo(() => {
    /* iter-360 — Postgres `deal_participants` is the single
     * source of truth. No frontend fallback; an empty array now
     * surfaces the calm "Add participants" affordance instead of
     * masking a data-integrity gap.
     *
     * iter-362 — Pin the Lead Agent (negotiator) to the TOP of
     * the roster. Everything else keeps its original ordering
     * so downstream lookups by index / role remain stable.
     *
     * iter-alignment · P59 — Backend Participant Link.  The
     * `/participants-enriched` endpoint now returns `contact_id`
     * directly (populated server-side by `participant_contact_link`
     * matching against Postgres `contacts` on email → phone,
     * unambiguous only).  The P56 frontend enrichment fallback
     * has therefore been retired — no more email/name resolver in
     * this useMemo.  Any participant without a `contact_id` is
     * legitimately unlinked and the ParticipantAvatar / rail name
     * will render as calm static text (never as a broken link). */
    const list = Array.isArray(participants) ? [...participants] : [];
    return list.sort((a, b) => {
      const aIsAgent = _isLeadAgentParticipant(a);
      const bIsAgent = _isLeadAgentParticipant(b);
      if (aIsAgent && !bIsAgent) return -1;
      if (!aIsAgent && bIsAgent) return  1;
      return 0;
    });
  }, [participants]);

  /* iter-362 — Central transaction state. Drives Journey +
   * Current Focus + Waiting On + Health + Next Best Action.
   * Everything downstream reads THIS object so contradictory
   * states can't sneak in (blocker outstanding but health calm,
   * mortgage steps on a cash-buyer deal, CTAs on a completed
   * transaction, …). */
  const txnState = useDealTransactionState(deal, {
    milestones: Array.isArray(milestones?.items) ? milestones.items : (Array.isArray(milestones) ? milestones : []),
    activities: Array.isArray(activities) ? activities : [],
    participants: effectiveParticipants,
    /* P0 · Deal Room Data & Behaviour Integrity Pass (2026-08-12) —
     * Thread the Critical Path optimistic-resolution sentinel into
     * the SSOT so every downstream lane (health · Exchange
     * Confidence · Next Best Action · Pre-Exchange Checklist
     * count + banner) reflects the resolution on the same render
     * pass as the Critical Path card + Radar row.  Once `reload()`
     * completes and the backend has filtered the blocker out of
     * `deal.blockers`, the sentinel becomes redundant (Set drop
     * → deal drop, additive, no double counting). */
    resolvedIds: resolvedCriticalPaths,
  });

  /* iter-222 — Persist sale type (tenure) edits from the hero. The
   * backend updates the linked Property record (canonical home for
   * tenure) and the conditional leasehold milestone rail reacts on
   * the next reload. Defined after `reload` so the closure binds
   * the live reference.
   *
   * iter-298 — Pilot bypass. Demo deals only exist in chainStore so
   * the canonical PATCH 404s. We mutate the chainStore copy locally
   * via `patchDealLocally`; the tenure-aware journey rail (freehold
   * vs leasehold) recomputes on the next render. */
  const onUpdateTenure = useCallback(async (value) => {
    if (!dealId) return;
    try {
      if (deal?._pilot) {
        patchDealLocally(dealId, { tenure: value });
        toast.success(`Sale type updated · ${value}`);
        return;
      }
      await api.patch(`/api/v2/deals/${dealId}`, { tenure: value });
      await reload();
      toast.success(`Sale type updated · ${value}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Couldn't update sale type";
      toast.error(typeof msg === "string" ? msg : "Couldn't update sale type");
      throw err;
    }
  }, [dealId, deal, patchDealLocally, reload]);

  /* iter-375 — Refetch the deal from Postgres and patch it into
   * chainStore so `deal.stage` is fresh after the backend cascade.
   *
   * `reload()` refetches milestones / journey / activity /
   * blockers / intel but NOT the deal itself — the deal is served
   * by `findDeal(dealId)` off the chainStore, which is a shared
   * workspace-scoped cache. Without this call the frontend
   * continues to read the stale `stage='offer'` even though the
   * backend advanced it to `mortgage_app`, which is what caused
   * "UI still shows Offer Agreed, 0/7 milestones and the same
   * CTA" after Mark Offer Agreed.
   *
   * We patch optimistically via `patchDealLocally` first (so the
   * SSOT-driven surfaces render the new stage in the same tick)
   * and THEN fire a `/api/v2/deals/{id}` GET so any downstream
   * fields (status, risk, target dates) are also fresh.
   *
   * iter-alignment · P0 · Production TDZ Fix (2026-03-09)
   * -----------------------------------------------------------------
   * MUST be declared BEFORE every render-time consumer that names
   * it in a useCallback deps array (currently `onUpdateFundingBasis`
   * and `completeActiveJourneyStep`).  Previously this callback was
   * declared ~560 lines later — the deps array of
   * `onUpdateFundingBasis` evaluated `refreshDealFromApi` before
   * it was initialised on the first render, hitting a JavaScript
   * temporal-dead-zone `ReferenceError: Cannot access uninitialized
   * variable`.  That threw during the render pass on production
   * (`main.3571bd85.js`) and drove the `DealRoomRouteErrorBoundary`
   * to show "Deal Room couldn't fully load".  Do not move this
   * declaration back below its consumers. */
  const refreshDealFromApi = useCallback(async (optimisticPatch = null) => {
    if (!dealId) return;
    if (optimisticPatch && typeof patchDealLocally === "function") {
      patchDealLocally(dealId, optimisticPatch);
    }
    try {
      const res = await api.get(`/api/v2/deals/${dealId}`);
      const fresh = res?.data || {};
      const patch = {};
      if (fresh.stage !== undefined)                patch.stage               = fresh.stage;
      if (fresh.status !== undefined)               patch.status              = fresh.status;
      if (fresh.risk !== undefined)                 patch.risk                = fresh.risk;
      if (fresh.deal_health_score !== undefined)    patch.dealHealthScore     = fresh.deal_health_score;
      if (fresh.exchange_probability !== undefined) patch.exchangeProbability = fresh.exchange_probability;
      if (fresh.target_exchange !== undefined)      patch.targetExchange      = fresh.target_exchange;
      if (fresh.target_completion !== undefined)    patch.targetCompletion    = fresh.target_completion;
      /* iter-alignment · P0 · Cash Funding · Deal Room Reconciliation
       * (2026-03-05) — thread the canonical funding_basis +
       * is_cash_buyer off the v2 envelope onto the chainStore
       * deal so the Journey resolver, blocker engine, NBA and
       * Editable Funding Basis pill all re-render off persisted
       * state (not just the optimistic patch).  Both snake_case +
       * camelCase aliases are populated so every existing consumer
       * reads the same value. */
      if (fresh.funding_basis !== undefined) {
        patch.funding_basis = fresh.funding_basis;
        patch.fundingBasis  = fresh.funding_basis;
      }
      if (fresh.is_cash_buyer !== undefined) {
        patch.is_cash_buyer = fresh.is_cash_buyer;
        patch.isCashBuyer   = fresh.is_cash_buyer;
      }
      if (Object.keys(patch).length && typeof patchDealLocally === "function") {
        patchDealLocally(dealId, patch);
      }
    } catch (_e) { /* noop — the optimistic patch already landed */ }
  }, [dealId, patchDealLocally]);

  /* iter-alignment · P73 · Editable Funding Basis (2026-02).
   *
   * Routes through the existing
   * `PATCH /api/v2/deals/{id}/promotion-baseline` endpoint —
   * the same route the Issue MoS modal uses.  Backend already:
   *   • whitelists ('cash', 'mortgage', 'gift_mortgage')
   *   • syncs `is_cash_buyer` from the new basis
   *   • emits a `funding_basis_updated` DealActivity row
   *   • never touches `milestoneStates` (preserves history)
   * Frontend is optimistic — `patchDealLocally` flips the
   * in-memory deal + `cf:deal:refresh` fans out so every
   * connected surface (rail, NBA, checklist, documents) reads
   * the new value on the same tick.
   *
   * Confirmation before saving guards the destructive-looking
   * transitions (Cash hides the Mortgage node, Mortgage/Gift
   * restores it) — pinned to the same `window.confirm` pattern
   * the rest of the Deal Room uses for state-shifting actions. */
  const onUpdateFundingBasis = useCallback(async (value) => {
    if (!dealId) return;
    const HUMAN = { cash: "Cash", mortgage: "Mortgage", gift_mortgage: "Gift + Mortgage" };
    const previous = String(deal?.funding_basis || deal?.fundingBasis || "").toLowerCase() || null;
    if (previous === value) return;
    const nextLabel = HUMAN[value] || value;
    const prevLabel = previous ? (HUMAN[previous] || previous) : "not set";
    const goingCash = value === "cash";
    const message = goingCash
      ? `Switch funding basis from ${prevLabel} to Cash?\n\nMortgage stages, documents and broker prompts will be hidden. Historic mortgage progress is preserved and re-surfaces if you switch back.`
      : `Switch funding basis from ${prevLabel} to ${nextLabel}?\n\nMortgage stages, documents and broker requirements will re-surface. No historic data is lost.`;
    if (typeof window !== "undefined" && typeof window.confirm === "function") {
      if (!window.confirm(message)) return;
    }
    const optimistic = { funding_basis: value, is_cash_buyer: goingCash };
    if (typeof patchDealLocally === "function") patchDealLocally(dealId, optimistic);
    try {
      if (deal?._pilot) {
        toast.success(`Funding basis updated · ${prevLabel} → ${nextLabel}`);
        if (typeof window !== "undefined") {
          try {
            window.dispatchEvent(new CustomEvent("cf:deal:refresh", {
              detail: { dealId, reason: "funding_basis.pilot" },
            }));
          } catch (_) { /* noop */ }
        }
        return;
      }
      await api.patch(`/api/v2/deals/${dealId}/promotion-baseline`, {
        funding_basis: value,
      });
      /* iter-alignment · P0 · Cash Funding · Deal Room Reconciliation
       * (2026-03-05) — pull the canonical deal envelope FIRST so
       * `deal.stage` (auto-advanced by the backend from
       * mortgage_app/mortgage_offer → solicitors when going cash),
       * `funding_basis` and `is_cash_buyer` all reconcile off
       * persisted state before the supporting surfaces refetch.
       * `reload()` then refreshes blockers, journey, activity,
       * milestones and NBA which all read the fresh deal SSOT.
       * Without this ordering the Journey rail keeps rendering
       * the pre-save stage (Mortgage) even though the DB has
       * moved on to Contract. */
      await refreshDealFromApi(null);
      await reload();
      toast.success(`Funding basis updated · ${prevLabel} → ${nextLabel}`);
      if (typeof window !== "undefined") {
        try {
          window.dispatchEvent(new CustomEvent("cf:deal:refresh", {
            detail: { dealId, reason: "funding_basis.saved" },
          }));
        } catch (_) { /* noop */ }
      }
    } catch (err) {
      // Rollback the optimistic patch.
      if (typeof patchDealLocally === "function") {
        patchDealLocally(dealId, { funding_basis: previous, is_cash_buyer: previous === "cash" });
      }
      toast.error("Couldn't update funding basis", { description: apiError(err) });
      throw err;
    }
  }, [dealId, deal, patchDealLocally, reload, refreshDealFromApi]);

  /* iter-alignment · P0 · Deal Room · Editable Agreed Price
   * (2026-02-28) — funnels the inline hero edit through the
   * existing `updateDeal(dealId, {agreedPrice})` chainStore
   * mutation.  The mutation calls `dealToApi()` (which passes
   * `agreedPrice` through to `agreed_price`) and PATCHes the
   * canonical `/api/deals/{id}` endpoint; on success chainStore
   * replaces the in-memory deal with the fresh envelope so every
   * subscriber (this hero, Pipeline, Command Centre, Deal Rooms
   * list, Forecasting, Pressure) re-renders in the same tick.
   *
   * We NEVER touch the property's asking_price — that's a
   * separate canonical column on `properties`.  A `reload()`
   * follows the mutation only for surfaces reading directly off
   * `/api/deals/{id}` (readiness rail, activity timeline) so the
   * hard-refresh contract holds without a page reload. */
  const onUpdateAgreedPrice = useCallback(async (digits) => {
    if (!dealId) return;
    const value = String(digits ?? "").trim();
    if (!/^\d+$/.test(value) || Number(value) <= 0) {
      toast.error("Enter a positive amount to save.");
      return;
    }
    try {
      await updateDeal(dealId, { agreedPrice: value });
      await reload();
      toast.success(`Agreed price updated · £${Number(value).toLocaleString("en-GB")}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Couldn't update agreed price";
      toast.error(typeof msg === "string" ? msg : "Couldn't update agreed price");
      throw err;
    }
  }, [dealId, updateDeal, reload]);

  /* iter-209 — Hydrate the per-deal `followed` flag from localStorage
   * so the mobile TopBar's Follow button reflects the operator's
   * prior state across reloads. Calm, never broadcasts. */
  useEffect(() => {
    if (!dealId || typeof window === "undefined") return;
    try { setFollowed(window.localStorage.getItem(`cf:follow:${dealId}`) === "1"); }
    catch { setFollowed(false); }
  }, [dealId]);
  const toggleFollow = useCallback(() => {
    setFollowed((v) => {
      const next = !v;
      try {
        if (next) window.localStorage.setItem(`cf:follow:${dealId}`, "1");
        else      window.localStorage.removeItem(`cf:follow:${dealId}`);
      } catch { /* private mode etc — operator stays calm */ }
      toast.success(next ? "Following this transaction" : "Unfollowed", { duration: 1200 });
      return next;
    });
  }, [dealId]);

  /* iter-209 — Mobile view-model. Mirrors DealRoomV3Page so the
   * heavyweight mobile shell receives every field it expects
   * (propertyAddress, liveIdx, blockerCount, dToExchange/Complete,
   * topBlocker, topBlockerOwner). Desktop ignores this. */
  const dealVm = useMemo(() => {
    if (!deal) return null;
    const propertyAddress = (deal.name || property?.address || "Untitled transaction").split(",")[0].trim();
    const liveIdx = stageToJourneyIdx(deal.stage);
    const blockerCount = Array.isArray(awaiting) ? awaiting.length : 0;
    const dToExchange = daysBetween(getDealExchangeTarget(deal));
    const dToComplete = daysBetween(getDealCompletionTarget(deal));
    const topBlocker = (awaiting || []).slice().sort(
      (a, b) => (b.age_days || b.days_outstanding || 0) - (a.age_days || a.days_outstanding || 0),
    )[0] || null;

    // Resolve the blocker's owner to a real participant — calm
    // role-bucket match identical to V3 so WhatsApp/email chase
    // surfaces a usable number.
    const roleToBucket = (label) => {
      const r = String(label || "").toLowerCase();
      if (!r) return "any";
      if (r.includes("buyer solicitor") || r.includes("buyer_solicitor")) return "buyer_solicitor";
      if (r.includes("seller solicitor") || r.includes("seller_solicitor")) return "seller_solicitor";
      if (r.includes("solicitor")) return "solicitor";
      if (r.includes("buyer")) return "buyer";
      if (r.includes("seller")) return "seller";
      if (r.includes("broker") || r.includes("mortgage")) return "broker";
      if (r.includes("agent")) return "agent";
      return "any";
    };
    const bucket = topBlocker ? roleToBucket(topBlocker.owner_role) : null;
    const topBlockerOwner = bucket
      ? (Array.isArray(participants) ? participants : []).find((p) => {
          const r = String(p.role || "").toLowerCase();
          if (bucket === "buyer_solicitor")  return r.includes("buyer") && r.includes("solicitor");
          if (bucket === "seller_solicitor") return r.includes("seller") && r.includes("solicitor");
          if (bucket === "solicitor")        return r.includes("solicitor");
          if (bucket === "buyer")            return r.includes("buyer") && !r.includes("solicitor");
          if (bucket === "seller")           return r.includes("seller") && !r.includes("solicitor");
          if (bucket === "broker")           return r.includes("broker") || r.includes("mortgage");
          if (bucket === "agent")            return r.includes("agent");
          return false;
        }) || null
      : null;

    return {
      propertyAddress,
      currentIdx: liveIdx,
      liveIdx,
      blockerCount,
      dToExchange,
      dToComplete,
      topBlocker,
      topBlockerOwner,
      fee: Number(deal.fee || 0),
    };
  }, [deal, property, awaiting, participants]);

  /* iter-205 — complete a milestone with optimistic UI + rollback.
   * On success the server has already emitted the timeline row + any
   * blocker-cleared row, so we just refetch milestones + activity
   * to land the canonical state. On failure we snap back and toast.
   *
   * iter-alignment · P6 · Canonical persistence fix.
   * Removed the legacy `_pilot` short-circuit that used to write
   * completions into `localStorage` via `markPilotJourneyStepComplete`
   * and NEVER hit the backend.  Since iter-360/363 the demo /
   * sample-workspace deals are seeded into Postgres by
   * `seed_pilot_deals_for_demo_org()` on backend boot, so every deal
   * (real or sample) resolves the canonical
   * `/api/v2/deals/:id/milestones/:id/complete` endpoint and the
   * derived `state: "done"` survives page reload.  Any historical
   * pilot-override entry in `localStorage` becomes a no-op —
   * the SSOT is now the DealActivity audit trail. */
  /* iter-alignment · P — Move to next stage (2026-08-17).
   *
   *   • `moveNextConfirm` holds the pending-advance state so the
   *     confirmation dialog can render immediately when the
   *     "Move to next stage" / "Mark transaction complete"
   *     button is pressed with N>0 outstanding items.  When
   *     `remainingCount === 0` we skip the modal and advance
   *     straight through.
   *   • The advance itself calls `chainStore.advanceStage(...,
   *     { manual: true, outstandingCount })` — the audit row is
   *     written server-side.  No milestones are auto-completed:
   *     `advanceStage` only patches `deal.stage`, and the
   *     Current Stage checklist rerenders against the new stage
   *     with any outstanding rows preserved verbatim.
   *   • At the Completion node the button surfaces as
   *     "Mark transaction complete" and advances to the
   *     terminal `completed` deal-stage. */
  const [moveNextConfirm, setMoveNextConfirm] = useState(null); // { isCompletion, nextDealStage, nextLabel, outstandingCount } | null
  const _handleAdvanceNow = useCallback(async (spec) => {
    if (!spec || !dealId) return;
    try {
      await advanceStage(dealId, spec.nextDealStage, {
        manual: true,
        outstandingCount: spec.outstandingCount || 0,
      });
      /* iter-alignment · Cross-surface Journey Sync (2026-02) —
       * A stage advance is the SINGLE largest source of the
       * "Deal Room shows Exchange, Command Centre shows
       * Contract" divergence.  Broadcast the canonical refresh
       * event so every panel refetches `/api/deals` +
       * `/api/today/sheet` immediately.  No polling, no timers. */
      if (typeof window !== "undefined") {
        try {
          window.dispatchEvent(new CustomEvent("cf:deal:refresh", {
            detail: { dealId, reason: "stage.advance", to: spec.nextDealStage },
          }));
        } catch (_e) { /* noop */ }
      }
    } catch (err) {
      // Surface a calm error to the operator without swallowing
      // the exception — the timeline will not show a manual
      // advance row when this rejects, so state stays honest.
      console.error("[move-to-next-stage] advance failed", err);
    }
  }, [advanceStage, dealId]);
  const onMoveToNextStage = useCallback((spec) => {
    if (!spec) return;
    const n = Math.max(0, Number(spec.outstandingCount) || 0);
    if (n > 0) {
      setMoveNextConfirm({ ...spec, outstandingCount: n });
      return;
    }
    _handleAdvanceNow(spec);
  }, [_handleAdvanceNow]);

  const completeMilestone = useCallback(async (milestoneId, dates = null) => {
    const prev = milestones;
    if (!prev) return;
    /* iter-alignment · P0 · Tick-First Milestones (2026-08-15) —
     * The initial tick no longer routes through the `date_prompt`
     * popover.  We optimistically mark the row done with today
     * as `completed_at` and let the operator adjust the date
     * retrospectively by clicking the pill on the right (which
     * calls `/complete` again with the new date, or `/reopen`
     * on Clear).  Explicit inline date captures still flow
     * through this handler with a full `dates` payload — e.g.
     * the target-date-only paths in `ProgressDealSheet`. */
    const isInitialTick = dates === null;
    const todayIso = new Date().toISOString().slice(0, 10);
    if (isInitialTick) {
      dates = { completed_at: todayIso };
    }
    // 1) optimistic patch — preserve the operator's chosen
    //    `YYYY-MM-DD` string verbatim so the render never shifts
    //    the day through UTC.  Only fall back to today when the
    //    caller supplied no completed_at (defensive — the tick-
    //    first branch above always sets it).
    const nowIso = dates?.completed_at
      ? String(dates.completed_at)
      : new Date().toISOString().slice(0, 10);
    const nextItems = prev.items.map((m) =>
      m.id === milestoneId
        ? {
            ...m,
            state: "done",
            completed_at: nowIso,
            ...(dates?.target_date    ? { target_date: dates.target_date }         : {}),
            ...(dates?.secondary_date ? { secondary_date: dates.secondary_date }   : {}),
            ...(dates?.secondary_note ? { secondary_note: dates.secondary_note }   : {}),
          }
        : m
    );
    // iter-alignment · P0 · Milestone Completion Date Persistence
    // — The row stays in `can_complete` after ticking so the
    // retrospective completion-date pill + reopen affordance
    // render immediately (backend `list_milestones` now also
    // preserves the row in `can_complete` on refetch).
    setMilestones({ ...prev, items: nextItems });
    try {
      const body = {
        target_date:    dates?.target_date    || null,
        secondary_date: dates?.secondary_date || null,
        secondary_note: dates?.secondary_note || null,
        completed_at:   dates?.completed_at   || null,
      };
      await api.post(`/api/v2/deals/${dealId}/milestones/${milestoneId}/complete`, body);
    } catch (err) {
      // 2) rollback — the WRITE itself failed.  Only this branch
      //    represents a genuine failure that the operator needs
      //    to see.
      setMilestones(prev);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Couldn't update milestone");
      return;
    }
    // iter-alignment · P0 · Milestone toast truthfulness
    // (2026-08-18) — The reconciliation `reload()` runs OUTSIDE
    // the write's try/catch so a transient refetch flap (stale
    // deal closure, unmount, one-of-ten sub-endpoints failing
    // its sync `setState`) can never surface a false
    // "Couldn't update milestone" toast after a 201.  Refresh
    // errors here are silent — the optimistic patch already
    // reflects the truth and the next real reload will
    // reconcile.
    toast.success("Milestone updated");
    try {
      await reload();
    } catch (_e) { /* reconciliation is best-effort */ }
    /* iter-alignment · Cross-surface Journey Sync (2026-02) —
     * Emit `cf:deal:refresh` so Command Centre / Today /
     * portfolio surfaces refetch the canonical `/api/deals` +
     * `/api/today/sheet` payloads and can never render a
     * different stage than the Deal Room for the same deal.
     * No polling, no timers — one-shot custom event, same
     * pattern the BSA property flip already uses. */
    if (typeof window !== "undefined" && dealId) {
      try {
        window.dispatchEvent(new CustomEvent("cf:deal:refresh", {
          detail: { dealId, reason: "milestone.complete" },
        }));
      } catch (_e) { /* noop */ }
    }
  }, [dealId, milestones, reload]);

  /* iter-alignment · P0 · Tick-First Milestones (2026-08-15) —
   * Reopen handler.  Un-ticks a completed milestone via the
   * canonical `POST /milestones/:id/reopen` endpoint.  The
   * backend writes an immutable `milestone_reopened` audit row
   * that supersedes the latest completion; state + completed_at
   * both clear on the next reload.  Optimistic patch mirrors
   * `completeMilestone` — rollback on error. */
  const reopenMilestone = useCallback(async (milestoneId) => {
    const prev = milestones;
    if (!prev) return;
    const nextItems = prev.items.map((m) =>
      m.id === milestoneId
        ? { ...m, state: "pending", completed_at: null, completed_by_id: null, completed_by_name: null }
        : m,
    );
    const nextCanComplete = Array.from(new Set([...(prev.can_complete || []), milestoneId]));
    setMilestones({ ...prev, items: nextItems, can_complete: nextCanComplete });
    try {
      await api.post(`/api/v2/deals/${dealId}/milestones/${milestoneId}/reopen`);
    } catch (err) {
      setMilestones(prev);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't reopen milestone");
      return;
    }
    // Toast fires on write success; reload is best-effort — see
    // completeMilestone for the same split.
    toast.success("Milestone reopened");
    try {
      await reload();
    } catch (_e) { /* reconciliation is best-effort */ }
    /* iter-alignment · Cross-surface Journey Sync (2026-02) —
     * Reopening a milestone can regress the deal's derived
     * stage (Exchange → Contract).  Broadcast the same
     * `cf:deal:refresh` event so every consumer refetches the
     * canonical SSOT and no surface stays a step ahead. */
    if (typeof window !== "undefined" && dealId) {
      try {
        window.dispatchEvent(new CustomEvent("cf:deal:refresh", {
          detail: { dealId, reason: "milestone.reopen" },
        }));
      } catch (_e) { /* noop */ }
    }
  }, [dealId, milestones, reload]);

  /* iter-alignment · P0 · Tick-First Milestones (2026-08-15) —
   * Retrospective completion-date edit.  Same endpoint as
   * `completeMilestone`, but the payload only carries
   * `completed_at`.  The backend writes a fresh
   * `milestone_completed` audit row whose `meta.completed_at`
   * supersedes the previous one; refresh returns the new date
   * as the SSOT.
   * iter-alignment · P0 · Optional Completion Dates (2026-08-16)
   * — Clearing the pill now BLANKS the operational date
   * without untick-ing the milestone.  Empty `iso` posts
   * `clear_completed_at: true` to `/complete`; the audit
   * timestamp is preserved separately by the backend so
   * stage progression + attribution stay intact. */
  const editMilestoneCompletionDate = useCallback(async (milestoneId, iso) => {
    const prev = milestones;
    if (!prev) return;
    const wantsClear = !iso;
    // 1) optimistic patch — completed_at flips to null (cleared)
    //    or to the operator-chosen `YYYY-MM-DD` string (preserved
    //    verbatim so a subsequent render doesn't shift the day
    //    through UTC).  The server persists the same string, so
    //    the optimistic + canonical values now match exactly.
    const nextItems = prev.items.map((m) =>
      m.id === milestoneId
        ? {
            ...m,
            state: "done",
            completed_at: wantsClear ? null : String(iso),
          }
        : m,
    );
    setMilestones({ ...prev, items: nextItems });
    try {
      const body = wantsClear
        ? { clear_completed_at: true }
        : { completed_at: iso };
      await api.post(`/api/v2/deals/${dealId}/milestones/${milestoneId}/complete`, body);
    } catch (err) {
      setMilestones(prev);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Couldn't update milestone");
      return;
    }
    // Toast fires on write success; reload is best-effort — see
    // completeMilestone for the same split.
    toast.success(wantsClear ? "Completion date cleared" : "Milestone updated");
    try {
      await reload();
    } catch (_e) { /* reconciliation is best-effort */ }
  }, [dealId, milestones, reload]);

  /* iter-alignment · P14 — Shared inline target-date handler.
   * Hoisted so both the ProgressDealSheet AND the ChecklistRow
   * inline date pill call the SAME canonical endpoint
   * (`POST /milestones/:id/target`) via the SAME optimistic
   * refresh path — no duplicate state, no second SSOT.
   *
   * iter-298 · Pilot bypass preserved: demo deals are frontend-
   * only, so we optimistically patch the milestone in place and
   * surface the same toast the canonical branch shows. */
  const setMilestoneTargetDate = useCallback(async (milestoneId, iso) => {
    if (deal?._pilot) {
      setMilestones((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: (prev.items || []).map((m) =>
            m.id === milestoneId ? { ...m, target_date: iso || null } : m,
          ),
        };
      });
      toast.success(iso ? "Target date updated" : "Target date cleared");
      return;
    }
    try {
      await api.post(`/api/v2/deals/${dealId}/milestones/${milestoneId}/target`, {
        target_date: iso || null,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't update target date");
      throw err;
    }
    // Toast fires on write success; reload is best-effort — same
    // split as completeMilestone / reopenMilestone /
    // editMilestoneCompletionDate.  A transient reconciliation
    // failure MUST NOT override a successful 2xx target-date
    // write with a false error toast.
    toast.success(iso ? "Target date updated" : "Target date cleared");
    try {
      await reload();
    } catch (_e) { /* reconciliation is best-effort */ }
  }, [dealId, deal, reload]);

  /* Contract stage refinement — inline milestone note handler.
   * Writes through the canonical `POST /milestones/:id/note`
   * endpoint (backed by an immutable `milestone_note_set`
   * audit row).  Same optimistic path as the target-date pill
   * for pilot deals. */
  const setMilestoneNote = useCallback(async (milestoneId, noteText) => {
    const clean = (noteText || "").trim();
    // Optimistic local patch — merge the note into the in-memory
    // milestone item so the muted second-line updates instantly
    // instead of waiting for the ~10s background room reload.
    // Same optimistic pattern used by target-date / tick handlers.
    setMilestones((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: (prev.items || []).map((m) =>
          m.id === milestoneId ? { ...m, note: clean || null } : m,
        ),
      };
    });
    if (deal?._pilot) {
      toast.success(clean ? "Note saved" : "Note cleared");
      return;
    }
    try {
      await api.post(`/api/v2/deals/${dealId}/milestones/${milestoneId}/note`, {
        note: clean,
      });
    } catch (err) {
      // Roll back the optimistic patch — reload will pull the
      // canonical state so we don't need a manual revert here.
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't save note");
      try { await reload(); } catch (_e) { /* noop */ }
      throw err;
    }
    toast.success(clean ? "Note saved" : "Note cleared");
    try {
      await reload();
    } catch (_e) { /* reconciliation is best-effort */ }
  }, [dealId, deal, reload]);

  /* iter-373 — Direct-progress from the Next Best Action button.
   *
   * Certain journey steps are pure "milestone events" that require no
   * evidence input (Memorandum of Sale issued, both solicitors
   * instructed, contracts exchanged, completion funds confirmed) and
   * can be completed inline from the NBA card — no modal, no page
   * reload. This shortens the operator's happy-path from
   * click → sheet → click Mark Complete → close to click → confirm →
   * done.
   *
   * Steps that require evidence (Progress mortgage application,
   * Resolve outstanding enquiries, Order searches, Book survey, etc.)
   * still open the MilestoneProgressionSheet — the direct path is
   * gated by an explicit `mode: "direct"` on each stage entry in
   * `_STAGE_NEXT_ACTION`.
   *
   * Backend semantics unchanged — this is a UX shortcut that goes
   * through the same `/journey/{step}/complete` endpoint used by the
   * milestone sheet, so auto-advance / activity / stage transitions
   * all fire identically. */
  const [nbaBusy, setNbaBusy] = useState(false);
  const [nbaConfirmPending, setNbaConfirmPending] = useState(false);
  // iter-398 · P0 · MoS workflow — modal open state.  The modal
  // owns its own phase (choose / preview / success) — this flag
  // only gates mount.
  const [mosModalOpen, setMosModalOpen] = useState(false);
  // iter-398B — MoS canonical completion status.  SSOT: the latest
  // `document_generated` activity for this deal.  Fetched on mount
  // and after every successful send / already-sent so the completed
  // summary card renders from server truth, never from a local flag.
  const [mosStatus, setMosStatus] = useState(null);
  const reloadMoSStatus = useCallback(async () => {
    if (!dealId) return null;
    try {
      const { data } = await api.get(`/api/v2/deals/${dealId}/memorandum-of-sale/status`);
      setMosStatus(data);
      return data;
    } catch { return null; }
  }, [dealId]);
  useEffect(() => { reloadMoSStatus(); }, [reloadMoSStatus]);
  // iter-399 · Mortgage milestone modal (4 modes: app_submitted,
  // valuation_booked, valuation_outcome, offer_received).  Null =
  // closed.  State object lives outside the modal so the NBA copy
  // can reflect the latest capture without a round-trip.
  const [mortgageModalMode, setMortgageModalMode] = useState(null);
  const [mortgageState, setMortgageState] = useState(null);
  useEffect(() => {
    if (!dealId) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/api/v2/deals/${dealId}/mortgage/state`);
        if (alive) setMortgageState(data);
      } catch { /* endpoint returns 404 for non-existent deals; noop */ }
    })();
    return () => { alive = false; };
  }, [dealId]);
  const reloadMortgageState = useCallback(async () => {
    if (!dealId) return null;
    try {
      const { data } = await api.get(`/api/v2/deals/${dealId}/mortgage/state`);
      setMortgageState(data);
      return data;
    } catch { return null; }
  }, [dealId]);
  const nbaConfirmTimerRef = useRef(null);

  /* iter-373 — Reset the inline-confirm state whenever the user
   * clicks anywhere outside the NBA CTA or presses Escape. Auto-
   * expires after 4s so the "Confirm · …" state can't linger. */
  useEffect(() => {
    if (!nbaConfirmPending) return;
    if (nbaConfirmTimerRef.current) clearTimeout(nbaConfirmTimerRef.current);
    nbaConfirmTimerRef.current = setTimeout(() => setNbaConfirmPending(false), 4000);
    const onDocClick = (e) => {
      const btn = document.querySelector('[data-testid="canonical-next-best-action-cta"]');
      if (btn && !btn.contains(e.target)) setNbaConfirmPending(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setNbaConfirmPending(false); };
    document.addEventListener("mousedown", onDocClick, true);
    document.addEventListener("keydown", onKey);
    return () => {
      if (nbaConfirmTimerRef.current) clearTimeout(nbaConfirmTimerRef.current);
      document.removeEventListener("mousedown", onDocClick, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [nbaConfirmPending]);

  /* iter-375 — refreshDealFromApi hoisted above onUpdateFundingBasis
   * (2026-03-09) — see the moved declaration ~lines 12802+.
   * Original location retained as a comment marker so grep-based
   * archaeology can trace the move. */
  const completeActiveJourneyStep = useCallback(async (stepId, humanLabel) => {
    if (nbaBusy || !stepId || !dealId) return;
    setNbaBusy(true);
    try {
      if (deal?._pilot) {
        const res = markPilotJourneyStepComplete?.(dealId, stepId);
        toast.success(`${humanLabel || stepId} complete`, {
          description: res?.newStage ? `Stage advanced to ${res.newStage.replace(/_/g, " ")}.` : undefined,
        });
      } else {
        const res = await api.post(`/api/v2/deals/${dealId}/journey/${stepId}/complete`);
        const data = res?.data || {};
        if (data.stage_advanced && data.new_stage) {
          toast.success(`${humanLabel || stepId} complete · stage advanced`, {
            description: `Now at ${String(data.new_stage).replace(/_/g, " ")}.`,
          });
          /* iter-375 — SSOT refresh path #1: patch the deal in the
           * chainStore IMMEDIATELY with the new stage from the API
           * response so `deal.stage` re-drives every SSOT surface
           * (rail Current Focus, NBA copy, Exchange Confidence)
           * on the very next render. `refreshDealFromApi` also
           * fires a background GET for the freshest downstream
           * fields (status, risk, target dates). */
          await refreshDealFromApi({ stage: data.new_stage });
        } else {
          toast.success(`${humanLabel || stepId} complete`);
          /* Even when the stage didn't advance the deal object may
           * carry recomputed risk / probability from the cascade —
           * refresh it so the Exchange Confidence line stays
           * truthful. */
          await refreshDealFromApi(null);
        }
      }
      /* SSOT refresh — reload() re-derives everything (Journey Rail,
       * Current Focus, NBA copy, Exchange Confidence, Activity
       * Timeline) via useDealTransactionState. No page reload. */
      try {
        await reload();
      } finally {
        // Anchor the activity feed even if the deal refetch failed
        // — the timeline may still have fresh rows the operator
        // needs to see.
        requestActivityAnchor();
      }
    } catch (err) {
      toast.error("Couldn't progress", { description: apiError(err) });
    } finally {
      setNbaBusy(false);
    }
  }, [dealId, deal, nbaBusy, markPilotJourneyStepComplete, reload, requestActivityAnchor, refreshDealFromApi]);

  /* iter — Right-rail Documents Hub upload pipeline.
   * Streams each accepted file through POST /api/deals/:id/documents
   * which already appends a `document_upload` activity row server-side
   * (so the timeline gets "Draft Contract uploaded by Sarah Jones"
   * for free). Errors surface as calm operational toasts; partial
   * batch success still triggers a single `reload()` so any files
   * that did land appear immediately.
   *
   * iter-297 — Pilot bypass. Pilot deals don't exist in the backend,
   * so the canonical POST returns 404. When `deal._pilot === true`
   * we route through `appendPilotDocument()` which writes the file
   * metadata + a document_upload activity row into the chainStore
   * pilot-override layer (persisted in localStorage). The Documents
   * Hub + activity feed update on the next render without a backend
   * round trip. */
  const onRailUploadFiles = useCallback(async (files) => {
    if (!dealId || !files || files.length === 0) return;
    if (deal?._pilot) {
      let ok = 0;
      for (const f of files) {
        try {
          if (appendPilotDocument(dealId, f)) ok += 1;
        } catch (_e) { /* swallowed — pilot path is local */ }
      }
      if (ok > 0) {
        await reload();
        requestActivityAnchor();
        toast.success(`${ok} document${ok === 1 ? "" : "s"} uploaded`);
      }
      return;
    }
    let ok = 0;
    let fail = 0;
    for (const f of files) {
      try {
        const form = new FormData();
        form.append("file", f);
        await api.post(`/api/deals/${dealId}/documents`, form);
        ok += 1;
      } catch (err) {
        fail += 1;
        const detail = err?.response?.data?.detail;
        const msg = typeof detail === "string"
          ? detail
          : (detail?.message || `Couldn't upload ${f.name}`);
        toast.error(msg);
      }
    }
    if (ok > 0) {
      await reload();
      requestActivityAnchor();
      toast.success(`${ok} document${ok === 1 ? "" : "s"} uploaded`);
    }
    if (ok === 0 && fail > 0) {
      // toast already surfaced per-file
    }
  }, [dealId, deal, appendPilotDocument, reload, requestActivityAnchor]);

  // Activity dates per journey step — calm map for the strip.
  const activityDates = useMemo(() => {
    const map = {};
    (activities || []).forEach((a) => {
      const t = a.title?.toLowerCase() || "";
      const ts = a.at || a.created_at;
      if (!ts) return;
      if (/offer/.test(t) && !map.offer) map.offer = ts;
      if (/aml/.test(t) && !map.aml) map.aml = ts;
      if (/search/.test(t) && !map.searches) map.searches = ts;
      if (/mortgage/.test(t) && !map.mortgage_offer) map.mortgage_offer = ts;
      if (/contract/.test(t) && !map.contracts) map.contracts = ts;
      if (/exchang/.test(t) && !map.exchange) map.exchange = ts;
      if (/complet/.test(t) && !map.completion) map.completion = ts;
    });
    // Memorandum date = the canonical MoS sent record (same source as Key Dates), never title matching.
    if (mosStatus?.completed && mosStatus.sent_at) map.memo = mosStatus.sent_at;
    return map;
  }, [activities, mosStatus]);

  // Mini "since your last visit" items for Exchange Eve.
  // iter-242 — Group repeated operational updates into calm
  // summary rows so the strip never reads as duplicated noise.
  // Same title within the last 4 events collapses into a single
  // row with an `· ×N` counter on the right.
  const sinceItems = useMemo(() => {
    const raw = (activities || []).slice(0, 6);
    const seen = new Map(); // title → {when, text, count, order}
    let order = 0;
    for (const a of raw) {
      const ts = a.at || a.created_at;
      const d = ts ? new Date(ts) : null;
      const when = d
        ? d.toDateString() === new Date().toDateString()
          ? d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
          : "Yesterday"
        : "—";
      const text = a.title || a.text || "Operational update";
      const key = text.trim().toLowerCase();
      const prev = seen.get(key);
      if (prev) {
        prev.count += 1;
      } else {
        seen.set(key, { when, text, count: 1, order: order++ });
      }
    }
    return Array.from(seen.values())
      .sort((a, b) => a.order - b.order)
      .slice(0, 4);
  }, [activities]);

  // Deal-not-found state (calm) — guarded by the workspace
  // hydration flag so a direct-URL entry doesn't flash the empty
  // state during the first `/api/deals` fetch.
  if (!deal) {
    if (dealsLoading) {
      return (
        <div
          className="min-h-screen flex bg-slate-50"
          data-testid="deal-room-loading"
        >
          <Sidebar />
          <main className="flex-1 flex items-center justify-center px-6">
            <div className="text-center max-w-md">
              <div className="mx-auto h-8 w-8 rounded-full border-2 border-slate-200 border-t-violet-600 animate-spin" />
              <p className="mt-3 text-[12.5px] text-slate-500">
                Loading Deal Room…
              </p>
            </div>
          </main>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex bg-slate-50">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <p className="text-[14px] font-bold text-slate-900">Deal Room not found</p>
            <p className="text-[12.5px] text-slate-500 mt-1.5">
              This transaction couldn't be located in your workspace. It may have been archived or moved.
            </p>
            <button
              type="button"
              onClick={() => nav("/deal-rooms")}
              className="mt-4 text-[12.5px] font-semibold text-violet-700 hover:text-violet-900"
            >
              ← Back to Deal Rooms
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <CommunicationDeepLink
        dealId={dealId}
        roomLoaded={roomLoaded && !!deal}
        blockers={awaiting}
        participants={participants}
        onLogUpdate={(type = null) => { setComposerType(type); setComposerSignal((s) => s + 1); }}
        onRequestDoc={(email = null) => { setRequestDocRecipientEmail(email); setOpenSheet("request-doc"); }}
      />
      {/* Mobile — canonical operational card system. Gated by JS so
          DealRoomMobile (heavyweight VM builder) only mounts on
          actual mobile widths. iter-209 — Now threads the full
          prop surface (vm · participants · documents · awaiting ·
          activity · followed/onFollow · onOpenSheet · bumpActivity)
          so every mobile interaction is genuinely wired. */}
      {isMobile && (
        <div className="md:hidden" data-testid="canonical-mobile-shell">
          <DealRoomMobile
            buyerPosition={
              <RailBuyerPositionCard
                deal={deal}
                milestones={milestones}
                canEdit={access?.can_edit_deal !== false}
                onUpdateFundingBasis={onUpdateFundingBasis}
                onUpdateBuyerPosition={onUpdateBuyerPosition}
              />
            }
            deal={deal}
            prop={property}
            vm={dealVm}
            participants={participants}
            onAddParticipant={access?.can_add_participants === false ? undefined : (r) => { setParticipantsInitialRole(r); setParticipantsOpen(true); }}
            documents={documents}
            awaiting={effectiveAwaiting}
            activity={activities}
            followed={followed}
            onFollow={toggleFollow}
            onOpenSheet={setOpenSheet}
            bumpActivity={reload}
            nba={(() => {
              const n = computeDealNextBestAction({ deal, txnState, journeyState, mortgageState, milestones, resolvedCriticalPaths });
              return {
                ...n,
                terminal: !!txnState?.isTerminal && !n.awaitingCompletion,
                mosCompleted: n.nbaMode === "mos_modal" && !!mosStatus?.completed,
                confirmPending: n.nbaMode === "direct" && nbaConfirmPending,
                showRequestDoc: isDocRequestRelevant(`${n.nbaTitle} ${n.nbaWhy}`) && !txnState?.isTerminal,
                permissionDenied: nbaPermissionDenied(n, milestones),
              };
            })()}
            onRequestDoc={() => setOpenSheet("request-doc")}
            onNbaAction={(n) => {
              /* Same routing as the desktop card; blocker / confirm targets are the phone's own surfaces. */
              if (n.nbaMode === "blocker") {
                document.querySelector('[data-testid="dealroom-mobile-action-required"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
                return;
              }
              if (n.nbaMode === "direct") {
                if (nbaConfirmPending) { setNbaConfirmPending(false); completeActiveJourneyStep(n.targetStepId, n.nbaTitle); }
                else setNbaConfirmPending(true);
                return;
              }
              if (n.nbaMode === "mos_modal") { setMosModalOpen(true); return; }
              if (n.nbaMode === "mortgage_capture") { setMortgageModalMode(n.mortgageMode || "app_submitted"); return; }
              if (n.nbaMode === "mortgage_not_required") return;
              onJourneyStepActivate(n.targetStepId);
            }}
            activityPanel={(() => {
              const upNext = resolveUpNext(deal, milestones);
              return (
                <ActivityPanel
                  variant="phone"
                  dealId={dealId}
                  deal={deal}
                  upNextLabel={upNext ? upNext.label : null}
                  upNextStepId={upNext ? upNext.id : null}
                  onViewStage={(id) => onJourneyStepActivate(id)}
                  activityStatus={activityStatus}
                  onRetryActivity={reload}
                  participants={participants}
                  documents={documents}
                  milestones={milestones}
                  operationalNotes={operationalNotes}
                  waitingOn={effectiveAwaiting}
                  currentUserId={currentUserId}
                  activities={feedActivities}
                  onActivityLogged={reload}
                  composerOpenSignal={composerSignal}
                  composerInitialType={composerType}
                  onComposerClosed={() => setComposerType(null)}
                  onRequestDoc={() => setOpenSheet("request-doc")}
                  onCriticalPathResolved={handleCriticalPathResolved}
                  activityHasMore={activityPaging.hasMore}
                  loadingEarlier={activityPaging.loading}
                  onLoadEarlier={loadEarlierActivity}
                />
              );
            })()}
            keyDates={(
              <KeyDatesSummary bare deal={deal} milestones={milestones} docRequests={docRequests} onOpenStage={(id) => onJourneyStepActivate(id, { summary: true })} />
            )}
            journey={
              <TransactionJourney deal={deal} milestones={milestones} activityDates={activityDates} journeyState={journeyState}
                onStepActivate={(id) => onJourneyStepActivate(id)} />
            }
          />
        </div>
      )}

      {/* Desktop — canonical Deal Room.
       *
       * iter-236 — Shell spacing alignment via `<ShellCanvas>`.
       *   The canvas wrapper encapsulates the canonical offset
       *   class string so the Deal Room can NEVER drift from
       *   the rest of the shell-level pages.
       */}
      <div className="hidden md:block min-h-screen bg-slate-50" data-testid="canonical-deal-room-shell">
        <Sidebar />
        <ShellCanvas
          ref={scrollContainerRef}
          className="min-h-screen overflow-y-auto"
          data-testid="canonical-scroll-container"
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-8 py-6">
            {/* Top bar */}
            <header className="flex items-center justify-between mb-5" data-testid="canonical-top-bar">
              <nav className="flex items-center gap-2 text-[12.5px] text-slate-500">
                <Link to="/deal-rooms" className="hover:text-slate-800">Deal Rooms</Link>
                <ChevronRight size={12} className="text-slate-300" />
                {/* iter-alignment · P0 · Deal Room · Property Header
                    Polish (2026-03-01) — Breadcrumb uses the SHORT
                    identity (first comma segment of the canonical
                    address SSOT) so the top bar stays scannable and
                    the full address lives once, in the hero. */}
                <span
                  className="text-slate-900 font-semibold truncate max-w-[280px]"
                  data-testid="canonical-breadcrumb-property"
                >
                  {(property?.address ? String(property.address).split(",")[0].trim() : null) || deal.name || "Untitled property"}
                </span>
              </nav>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="h-9 px-3 rounded-lg bg-white border border-slate-200 text-[12.5px] font-semibold text-slate-700 inline-flex items-center gap-1.5 hover:bg-slate-50 cf-press"
                  data-testid="canonical-share-room"
                >
                  <Share2 size={13} /> Share room
                </button>
                {/* iter-alignment · P · Deal Room · Header
                    Notification Bell Retirement (2026-03-02) —
                    the lower per-room bell was a duplicate of the
                    global notifications bell in the top-right
                    shell header.  Retired so there's one canonical
                    entry point.  `unreadCount` state is unchanged
                    upstream so the global bell keeps reading the
                    same source. */}
                {/* iter-209A — top-bar overflow popover.
                    Calm 6-item menu with real handlers (Copy link,
                    Mark exchanged, Mark completed, Archive, Workspace
                    settings). Export PDF stays behind a single calm
                    `comingSoon` until the Director PDF lands. */}
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      aria-label="More"
                      className="w-9 h-9 rounded-lg bg-white border border-slate-200 text-slate-500 hover:text-slate-800 flex items-center justify-center cf-press"
                      data-testid="canonical-overflow"
                    >
                      <MoreHorizontal size={14} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-60 p-1.5" data-testid="canonical-overflow-menu">
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(window.location.href);
                          toast.success("Deal Room link copied", { duration: 1500 });
                        } catch { toast.error("Could not copy link"); }
                      }}
                      className="w-full text-left text-[12.5px] px-2.5 py-2 rounded-md hover:bg-slate-50 text-slate-700 inline-flex items-center gap-2"
                      data-testid="canonical-overflow-copy-link"
                    >
                      <Link2 size={13} className="text-slate-400" /> Copy room link
                    </button>
                    {/* iter-alignment · P0 · Deal Room Property Editing.
                        Opens the existing AddPropertyDrawer in edit
                        mode via the shared `cf:open-property` bus so
                        agents can correct address / postcode / price
                        / tenure / EPC etc. without leaving the room.
                        Reuses the canonical Property SSOT — no new
                        endpoint, no duplicate deal-level state.  On
                        save the chainStore rewrites the property row
                        (via `updateProperty` in the drawer), so the
                        hero re-renders instantly.  A `reload()` also
                        fires so any deal-scoped surface that reads
                        from Postgres (e.g. tenure copy on the deal)
                        picks up the change. */}
                    <button
                      type="button"
                      disabled={!property || access?.can_edit_deal === false}
                      onClick={() => {
                        if (!property) return;
                        openEditProperty({
                          ...property,
                          onSaved: () => { reload(); },
                        });
                      }}
                      className="w-full text-left text-[12.5px] px-2.5 py-2 rounded-md hover:bg-slate-50 text-slate-700 inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="canonical-overflow-edit-property"
                    >
                      <Pencil size={13} className="text-slate-400" /> Edit property
                    </button>
                    <button
                      type="button"
                      disabled={isFallenThrough}
                      title={isFallenThrough ? "Reopen this transaction first" : undefined}
                      onClick={async () => {
                        try {
                          await updateDeal(dealId, { stage: "exchanged" });
                          patchDealLocally?.(dealId, { stage: "exchanged" });
                          toast.success("Marked as exchanged", { duration: 1500 });
                          reload();
                        } catch (e) { toast.error(apiError(e, "Could not mark exchanged")); }
                      }}
                      className="w-full text-left text-[12.5px] px-2.5 py-2 rounded-md hover:bg-slate-50 text-slate-700 inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      data-testid="canonical-overflow-mark-exchanged"
                    >
                      <Check size={13} className="text-emerald-500" /> Mark exchanged
                    </button>
                    <button
                      type="button"
                      disabled={isFallenThrough}
                      title={isFallenThrough ? "Reopen this transaction first" : undefined}
                      onClick={async () => {
                        try {
                          await updateDeal(dealId, { stage: "completed" });
                          patchDealLocally?.(dealId, { stage: "completed" });
                          toast.success("Marked as completed", { duration: 1500 });
                          reload();
                        } catch (e) { toast.error(apiError(e, "Could not mark completed")); }
                      }}
                      className="w-full text-left text-[12.5px] px-2.5 py-2 rounded-md hover:bg-slate-50 text-slate-700 inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      data-testid="canonical-overflow-mark-completed"
                    >
                      <Check size={13} className="text-violet-500" /> Mark completed
                    </button>
                    {/* iter-alignment · P0 · Deal Room · Fallen Through
                        (2026-02) — Reopen (visible only when fallen)
                        and Mark as fallen through (visible only when
                        NOT fallen) — separate first-class actions
                        distinct from Archive. */}
                    {isFallenThrough ? (
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await api.post(`/api/deals/${dealId}/reopen`);
                            toast.success("Transaction reopened", { duration: 1800 });
                            reload();
                          } catch (e) { toast.error(apiError(e, "Could not reopen transaction")); }
                        }}
                        className="w-full text-left text-[12.5px] px-2.5 py-2 rounded-md hover:bg-violet-50 text-violet-700 inline-flex items-center gap-2"
                        data-testid="canonical-overflow-reopen"
                      >
                        <RotateCcw size={13} /> Reopen transaction
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setFallenThroughOpen(true)}
                        className="w-full text-left text-[12.5px] px-2.5 py-2 rounded-md hover:bg-rose-50 text-rose-600 inline-flex items-center gap-2"
                        data-testid="canonical-overflow-mark-fallen-through"
                      >
                        <XCircle size={13} /> Mark as fallen through
                      </button>
                    )}
                    <div className="my-1 h-px bg-slate-100" aria-hidden />
                    <button
                      type="button"
                      disabled
                      aria-disabled="true"
                      title="Available in upcoming release"
                      className="w-full text-left text-[12.5px] px-2.5 py-2 rounded-md text-slate-700 inline-flex items-center gap-2 opacity-50 cursor-not-allowed"
                      data-testid="canonical-overflow-export-pdf"
                      data-deferred="true"
                    >
                      <FileText size={13} className="text-slate-400" /> Export PDF
                    </button>
                    <button
                      type="button"
                      onClick={() => nav("/settings")}
                      className="w-full text-left text-[12.5px] px-2.5 py-2 rounded-md hover:bg-slate-50 text-slate-700 inline-flex items-center gap-2"
                      data-testid="canonical-overflow-settings"
                    >
                      <Settings size={13} className="text-slate-400" /> Workspace settings
                    </button>
                    <div className="my-1 h-px bg-slate-100" aria-hidden />
                    <button
                      type="button"
                      onClick={async () => {
                        if (!window.confirm("Archive this Deal Room? You can restore it later.")) return;
                        try {
                          await updateDeal(dealId, { archived: true });
                          toast.success("Room archived", { duration: 1500 });
                          nav("/deal-rooms");
                        } catch (e) { toast.error(apiError(e, "Could not archive")); }
                      }}
                      className="w-full text-left text-[12.5px] px-2.5 py-2 rounded-md hover:bg-rose-50 text-rose-600 inline-flex items-center gap-2"
                      data-testid="canonical-overflow-archive"
                    >
                      <Archive size={13} /> Archive room
                    </button>
                  </PopoverContent>
                </Popover>
              </div>
            </header>

            <RoomLifecycleBanner deal={deal} />

            {/* Two-column body */}
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-5">
              <section className="space-y-3.5 min-w-0">
                <PropertyHero
                  deal={deal}
                  property={property}
                  participants={participants}
                  documents={documents}
                  milestones={milestones}
                  onJumpToParticipants={() => jumpToTab("participants")}
                  onOpenChecklist={() => jumpToTab("checklist")}
                  onAddHeroParticipant={async (slotType, payload) => {
                    // iter-220 — POST to canonical persistence route.
                    // On success, refresh the page so the new participant
                    // surfaces in the hero slot + the activity feed.
                    //
                    // iter-298 — Pilot bypass. Demo deals only live in
                    // chainStore so the canonical POST 404s. We patch
                    // the deal record locally so the new participant
                    // role surfaces in the hero slot on the next render.
                    if (deal?._pilot) {
                      const field = slotType === "buyer"
                        ? { buyerName: payload.full_name }
                        : slotType === "seller"
                          ? { sellerName: payload.full_name }
                          : { [slotType]: payload.full_name };
                      patchDealLocally(dealId, field);
                      toast.success(`${payload.full_name} added`);
                      return;
                    }
                    await api.post(`/api/v2/deals/${dealId}/participants`, {
                      participant_type: slotType,
                      full_name:        payload.full_name,
                      email:            payload.email || null,
                      phone:            payload.phone || null,
                    });
                    await reload();
                    toast.success(`${payload.full_name} added`);
                  }}
                  onUpdateExchange={onUpdateExchange}
                  onUpdateCompletion={onUpdateCompletion}
                  onUpdateTenure={onUpdateTenure}
                  onUpdateAgreedPrice={onUpdateAgreedPrice}
                  onUpdateSaleAgreed={onUpdateSaleAgreed}
                  onOpenQuickEdit={() => setQuickEditOpen(true)}
                  canQuickEdit={access?.can_edit_deal !== false}
                />
                <QuickEditDealSheet
                  key={`qe-${dealId}-${quickEditOpen ? "on" : "off"}`}
                  open={quickEditOpen}
                  onClose={() => setQuickEditOpen(false)}
                  deal={deal}
                  property={property}
                  participants={participants}
                  canEdit={access?.can_edit_deal !== false}
                  onSave={onQuickEditSave}
                />
                {/* iter-alignment · P4 · Important Note banner.
                    Compact pale-amber pill sitting DIRECTLY beneath
                    the Property Header (per the approved screenshot).
                    Renders the most recent PINNED + UNRESOLVED
                    operational note from the existing
                    `operationalNotes` SSOT.  iter-alignment · P24 —
                    merged the standalone "+ Add / manage important
                    note" row into this banner: the "+" now sits
                    fixed top-right and both the "+" and the banner
                    body open the note manager.  When no pinned note
                    exists, the banner falls back to a calm empty-
                    state prompt so operators still have a single
                    entry point (hidden entirely for read-only
                    viewers).  Long note bodies wrap naturally now
                    that `truncate` is gone; the "+" stays
                    top-right via `items-start`. */}
                {/* Important notes — compact 52px bar (collapsed by default) + expandable
                    Operational Notes panel.  Preview = most recently updated open note. */}
                {(() => {
                  // Server-enforced: `can_edit_notes` from /deals/{id}/access (viewers read-only).
                  const canEdit = access?.can_edit_deal !== false && access?.can_edit_notes !== false;
                  const openNotes = (operationalNotes || []).filter((n) => n && !n.resolved);
                  const latest = [...openNotes].sort((a, b) => {
                    const at = Date.parse(a?.updated_at || a?.created_at || 0) || 0;
                    const bt = Date.parse(b?.updated_at || b?.created_at || 0) || 0;
                    return bt - at;
                  })[0];
                  const body = String(latest?.body || latest?.text || latest?.title || "").trim();
                  const hasNote = !!body;
                  // Read-only viewer with no notes → keep the surface hidden (unchanged behaviour).
                  if (!hasNote && !canEdit) return null;
                  const toggle = () => { setNotesComposeOnOpen(false); setNotesManagerOpen((v) => !v); };
                  return (
                    <div
                      className="rounded-xl bg-white ring-1 ring-slate-200 hover:ring-slate-300 transition"
                      data-testid="canonical-pinned-note-banner"
                    >
                      <div className="relative flex items-center gap-3 pl-4 pr-3 h-[52px]">
                        <button
                          type="button"
                          onClick={toggle}
                          className="absolute inset-0 w-full h-full rounded-xl cf-press focus:outline-none focus:ring-2 focus:ring-violet-300/60"
                          aria-expanded={notesManagerOpen}
                          aria-label={hasNote ? `Important notes: ${body}` : "Important notes: no notes added"}
                          data-testid="canonical-pinned-note-open"
                        />
                        <AlertCircle size={18} strokeWidth={2.4} className="shrink-0 relative pointer-events-none text-amber-500" aria-hidden />
                        <span className="text-[14px] font-semibold text-slate-900 shrink-0 relative pointer-events-none tracking-[-0.005em]">
                          Important notes
                        </span>
                        <span
                          className="shrink-0 relative pointer-events-none min-w-[20px] h-5 px-1.5 rounded-full bg-slate-100 text-slate-500 text-[10.5px] font-semibold inline-flex items-center justify-center tabular-nums"
                          data-testid="canonical-notes-count"
                        >
                          {openNotes.length}
                        </span>
                        <p
                          className={`text-[13px] leading-[1.4] min-w-0 flex-1 truncate relative pointer-events-none ${hasNote ? "text-slate-700" : "text-slate-400"}`}
                          title={hasNote ? body : undefined}
                          data-testid="canonical-pinned-note-body"
                        >
                          {hasNote ? body : "No notes added"}
                        </p>
                        {canEdit && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setNotesComposeOnOpen(true); setNotesManagerOpen(true); }}
                            className="relative z-[1] shrink-0 h-8 px-2 inline-flex items-center gap-1 rounded-md text-[12.5px] font-semibold text-violet-700 hover:text-violet-800 hover:bg-violet-50 cf-press focus:outline-none focus:ring-2 focus:ring-violet-300/60"
                            aria-label="Add note"
                            title="Add note"
                            data-testid="canonical-notes-manager-open"
                          >
                            <Plus size={14} strokeWidth={2.6} /> Add note
                          </button>
                        )}
                        <ChevronDown
                          size={16}
                          strokeWidth={2.2}
                          className={`shrink-0 relative pointer-events-none text-slate-400 transition-transform ${notesManagerOpen ? "rotate-180" : ""}`}
                          aria-hidden
                          data-testid="canonical-notes-chevron"
                        />
                      </div>
                      {notesManagerOpen && (
                        <div className="border-t border-slate-100 px-3 py-2.5" data-testid="canonical-notes-manager">
                          <OperationalNotesPanel
                            key={notesComposeOnOpen ? "compose" : "view"}
                            dealId={dealId}
                            canEdit={canEdit}
                            embedded
                            startComposing={notesComposeOnOpen}
                            onChange={refetchOperationalNotes}
                            onSaved={() => { setNotesComposeOnOpen(false); setNotesManagerOpen(false); }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })()}
                {/* iter-alignment · P24 — the standalone
                    "+ Add / manage important note" row was folded
                    into the Important Note banner above; the "+"
                    is now the banner's fixed top-right affordance
                    and both surfaces open the same manager. */}
                {/* iter-255 — NEXT BEST ACTION card.
                 *
                 * Single operational primitive that surfaces the
                 * highest-priority action holding back progression
                 * + WHY it matters. Resolver order:
                 *   1. First unresolved blocker on the deal — its
                 *      `title` becomes the action, `description`
                 *      the rationale.
                 *   2. Fallback derived from the active journey
                 *      step (e.g. "Request signed contract from
                 *      seller solicitor" while in `contracts`).
                 *
                 * The CTA reuses the canonical Progress
                 * Transaction handler so the same iter-221
                 * MilestoneProgressionSheet opens — no new state
                 * lane, no new audit-log path. */}
                {(() => {
                  /* iter-362 · Terminal-state short-circuit.
                   *
                   * For Exchanged / Completed / Fallen Through
                   * deals we replace the entire Next Best Action
                   * card with a calm completion banner. No CTA,
                   * no "why now" copy — the transaction is
                   * finished and any prompt would be noise.
                   * The audit trail below (Journey / Activity
                   * feed) still shows the full history. */
                  const _nba = computeDealNextBestAction({ deal, txnState, journeyState, mortgageState, milestones, resolvedCriticalPaths });
                  const _awaitingCompletion = _nba.awaitingCompletion;
                  if (txnState?.isTerminal && !_awaitingCompletion) {
                    return <TerminalStateBanner txnState={txnState} deal={deal} />;
                  }
                  const { nbaTitle, nbaWhy, nbaMode, mortgageMode, _effectiveEntry, targetStepId } = _nba;
                  // Server `can_complete` list gates the one-click actions (viewers / clients read-only;
                  // agents + linked broker keep their rights). Blocker / sheet modes only navigate.
                  const nbaNoPermission = nbaPermissionDenied(_nba, milestones);

                  /* iter-373 · CTA click routing.
                   *   blocker → scroll to blocker card
                   *   direct  → inline confirm (first click flips
                   *             label to "Confirm · <title>", second
                   *             click completes via API)
                   *   sheet   → open MilestoneProgressionSheet
                   * (existing behaviour) so evidence / status input
                   * can be captured. */
                  const onNbaClick = () => {
                    if (nbaMode === "blocker") {
                      const el = document.querySelector('[data-testid="canonical-blockers"]');
                      if (el && typeof el.scrollIntoView === "function") {
                        el.scrollIntoView({ behavior: "smooth", block: "center" });
                        el.classList.add("ring-2", "ring-rose-400");
                        setTimeout(() => { el.classList.remove("ring-2", "ring-rose-400"); }, 1600);
                      }
                      return;
                    }
                    if (nbaMode === "direct") {
                      if (nbaConfirmPending) {
                        setNbaConfirmPending(false);
                        completeActiveJourneyStep(targetStepId, nbaTitle);
                      } else {
                        setNbaConfirmPending(true);
                      }
                      return;
                    }
                    if (nbaMode === "mos_modal") {
                      // iter-398 · P0 · MoS workflow — open the two-
                      // option modal.  Journey advancement + activity
                      // log fire inside the modal after send / mark-
                      // sent so this handler stays a pure dispatcher.
                      setMosModalOpen(true);
                      return;
                    }
                    if (nbaMode === "mortgage_capture") {
                      // iter-399 · Mortgage substep capture.
                      setMortgageModalMode(mortgageMode || "app_submitted");
                      return;
                    }
                    if (nbaMode === "mortgage_not_required") {
                      // Cash buyer — banner only, no action.
                      return;
                    }
                    if (nbaMode === "confirm") {
                      // Exchange / Completion are recorded only through the gated Confirm row on Current Stage.
                      setActivityTab("current_stage");
                      setTimeout(() => {
                        const _row = _effectiveEntry?._milestoneId
                          && document.querySelector(`[data-testid="canonical-eve-row-${_effectiveEntry._milestoneId}"]`);
                        const gate = document.querySelector('[data-testid="canonical-confirm-gate-exchange"], [data-testid="canonical-confirm-gate-completion"]') || _row;
                        const el = gate || document.querySelector('[data-testid="canonical-current-stage-checklist"]');
                        if (el && typeof el.scrollIntoView === "function") {
                          el.scrollIntoView({ behavior: "smooth", block: "center" });
                          el.classList.add("ring-2", "ring-emerald-400");
                          setTimeout(() => { el.classList.remove("ring-2", "ring-emerald-400"); }, 1600);
                        }
                      }, 50);
                      return;
                    }
                    // mode === "sheet"
                    onJourneyStepActivate(targetStepId);
                  };

                  const ctaLabel = nbaMode === "blocker"
                    ? "Resolve"
                    : nbaMode === "direct"
                      ? (nbaConfirmPending ? `Confirm · ${nbaTitle}` : nbaTitle)
                      : nbaTitle;
                  const ctaClass = nbaMode === "blocker"
                    ? "bg-rose-600 hover:bg-rose-700 shadow-[0_6px_18px_-6px_rgba(244,63,94,0.55)] focus:ring-rose-300/60"
                    : nbaConfirmPending
                      ? "bg-emerald-600 hover:bg-emerald-700 shadow-[0_6px_18px_-6px_rgba(16,185,129,0.55)] focus:ring-emerald-300/60"
                      : "bg-violet-600 hover:bg-violet-700 shadow-[0_6px_18px_-6px_rgba(124,58,237,0.55)] focus:ring-violet-300/60";

                  // iter-399 · Cash-buyer banner — render in place of
                  // the standard NBA CTA card.  Same visual gravity
                  // (rounded, muted background, target label) so the
                  // rail slot doesn't feel empty, but no interactive
                  // button because there's nothing to action.
                  if (nbaMode === "mortgage_not_required") {
                    return (
                      <div
                        className="rounded-2xl bg-slate-50 p-4 sm:p-5"
                        style={{ border: "1px solid rgba(148,163,184,0.35)" }}
                        data-testid="mortgage-not-required-banner"
                      >
                        <div className="flex items-center gap-3">
                          <span className="inline-flex items-center gap-1.5 h-6 px-2 rounded-full bg-white ring-1 ring-slate-200 text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-500">
                            <Target size={10} strokeWidth={2.4} />
                            Mortgage
                          </span>
                          <div className="min-w-0">
                            <p className="text-[13.5px] font-bold text-slate-800" data-testid="mortgage-not-required-title">Mortgage: Not Required</p>
                            <p className="text-[11.5px] text-slate-500 mt-0.5">Cash buyer — the mortgage journey does not apply on this deal.</p>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // iter-398B · P0 · Completed MoS summary card
                  //
                  // When the deal has already had a Memorandum of Sale
                  // issued (persisted `document_generated` activity),
                  // the NBA slot renders a green summary card instead
                  // of the "Issue Memorandum of Sale" CTA.  This
                  // replaces the action card only while the stage is
                  // still on the pre-journey trio (offer / memo / aml)
                  // — once the journey advances, the next NBA (e.g.
                  // "Draft contract pack") takes over naturally.
                  if (nbaMode === "mos_modal" && mosStatus?.completed) {
                    return <MoSCompletedSummary status={mosStatus} />;
                  }
                  return (
                    <div
                      className="rounded-2xl bg-gradient-to-br from-violet-50/80 via-violet-50/50 to-white px-4 py-3 sm:px-5 sm:py-3.5"
                      style={{
                        border: "1px solid rgba(196,181,253,0.45)",
                        boxShadow:
                          "0 1px 2px rgba(15,23,42,0.03), 0 10px 22px -22px rgba(124,58,237,0.14)",
                      }}
                      data-testid="canonical-next-best-action-card"
                    >
                      {/* iter-379 — Compact command-bar layout.
                       *   • Title is the primary focus.
                       *   • Single muted supporting sentence beneath.
                       *   • CTA aligns to the vertical centre of the
                       *     content stack (`items-center`) so the row
                       *     reads as one action bar instead of a card.
                       *   • Card padding trimmed ~30% so the NBA sits
                       *     visually connected to the Transaction
                       *     Journey card directly below it. */}
                      <div className="flex items-center justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div
                            className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-violet-700"
                            data-testid="canonical-next-best-action-label"
                          >
                            <Target size={11} strokeWidth={2.4} />
                            Next best action
                          </div>
                          <h4
                            className="mt-1 text-[17px] font-bold text-slate-900 tracking-tight leading-tight truncate"
                            data-testid="canonical-next-best-action-title"
                          >
                            {nbaTitle}
                          </h4>
                          <p
                            className="mt-1.5 text-[13px] text-slate-600 leading-relaxed"
                            data-testid="canonical-next-best-action-why"
                          >
                            {/* iter-379 — Muted single supporting sentence.
                                The bold "Why now:" prefix was removed so
                                the title remains the primary focus and the
                                supporting copy reads as calm rationale.
                                P1 · Premium Polish — copy prominence
                                nudged (12.5px → 13px, slate-500 →
                                slate-600, leading-snug → leading-relaxed)
                                so the rationale is clearly readable
                                without competing with the title. */}
                            {nbaWhy}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={onNbaClick}
                          disabled={(txnState?.isTerminal && !_awaitingCompletion) || nbaBusy || nbaNoPermission}
                          className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-white text-[12.5px] font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 ${ctaClass}`}
                          data-testid="canonical-next-best-action-cta"
                          data-nba-permission={nbaNoPermission ? "denied" : "allowed"}
                          data-nba-mode={nbaMode}
                          data-nba-confirming={nbaConfirmPending ? "1" : "0"}
                          aria-label={ctaLabel}
                          title={
                            nbaNoPermission ? "Your role can view this action but not complete it"
                            : nbaMode === "blocker"
                              ? "Jump to the blocker holding progression"
                              : nbaMode === "direct"
                                ? (nbaConfirmPending ? "Click to confirm and progress the transaction" : "Click to progress this milestone")
                                : "Open the milestone progression sheet to action this"
                          }
                        >
                          {nbaBusy ? "Working…" : ctaLabel}
                          {!nbaBusy && <ArrowRight size={13} strokeWidth={2.4} />}
                        </button>
                        {/* iter-P65 · Contextual Request Document.
                            Only shows when the NBA text is
                            doc-related, opening the same canonical
                            drawer.  Never replaces the primary CTA
                            — it's an alternative shortcut for the
                            common "the NBA is a missing document"
                            case. */}
                        {isDocRequestRelevant(`${nbaTitle} ${nbaWhy}`) && !txnState?.isTerminal && (
                          <button
                            type="button"
                            onClick={() => setOpenSheet("request-doc")}
                            className="shrink-0 inline-flex items-center gap-1 h-9 px-3 rounded-lg text-[12px] font-semibold text-violet-700 hover:text-violet-900 hover:bg-violet-50 border border-violet-200/70"
                            data-testid="canonical-next-best-action-request-doc"
                            title="Request the document instead"
                          >
                            <FileText size={11} strokeWidth={2.2} /> Request
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}
                {(() => {
                  /* iter-248B — Refined Transaction Journey card.
                   *
                   * Goals:
                   *   1. Larger card surface (p-7) + clearer
                   *      "TRANSACTION JOURNEY" label and
                   *      "Active stage: {Stage}" subtitle.
                   *   2. Dynamic top-right "On track" / "At risk"
                   *      health pill — driven by unresolved blocker
                   *      count on the deal so the pill reflects real
                   *      progression health, not a static label.
                   *   3. Single, prominent "Update Milestones"
                   *      button INSIDE the card (bottom-left) so it
                   *      reads as the card's primary action rather
                   *      than floating beneath the rail.
                   *
                   * Wiring is preserved: `canonical-progress-
                   * transaction-btn` testid + `onJourneyStepActivate`
                   * handler unchanged, so the iter-221 progression
                   * sheet, the activity-feed reload path, and every
                   * existing pytest pin continue to fire. */
                  const steps = journeyState?.steps || [];
                  /* iter-alignment · P13 — Header now reads the SAME
                   * `currentFocusIdx` the rail itself renders (via
                   * `resolveJourneyProgress`).  Historically we
                   * preferred `txnState.activeStepId ||
                   * journeyState.current_step`, which could disagree
                   * with the rail (rail: first-uncompleted; backend:
                   * baseline stage) and produced two conflicting
                   * "Current Stage" labels — a rail chip on Exchange
                   * while the header text said "Mortgage".  Reading
                   * the rail's resolver here makes the header
                   * pointer-perfect to the rail chip and preserves a
                   * single source of truth for the canonical stage.
                   * The fallback chain retains the original signals
                   * for pre-journey / legacy pilot deals so the
                   * NBA / progression sheet / activity feed reload
                   * path continue to fire against the same value.
                   */
                  const _railResolved = (() => {
                    try { return resolveJourneyProgress(deal, milestones); } catch { return null; }
                  })();
                  const _railFocusIdx = _railResolved?.currentFocusIdx;
                  const _railSteps = _railResolved?.steps || steps;
                  const _railFocusStep = (typeof _railFocusIdx === "number" && _railFocusIdx >= 0)
                    ? _railSteps[_railFocusIdx]
                    : null;
                  const activeStepIdSSOT = _railFocusStep?.id
                    || txnState?.activeStepId
                    || journeyState?.current_step;
                  const activeStep = _railFocusStep
                    || steps.find((s) => s && s.id === activeStepIdSSOT);
                  const activeLabel = activeStep?.label || txnState?.activeStepLabel || "—";
                  /* iter-294 — Canonical deal-health resolver.
                   *
                   * The journey-section header status pill now consumes
                   * the same `dealStatusFor()` resolver as every other
                   * surface. The legacy `blockers.length < 2`
                   * heuristic is retired so the pill can NEVER read
                   * "On track" while the list / forecast / hero
                   * report the same deal as "At Risk". The blocker
                   * count is still surfaced via the title attribute
                   * for operator context. */
                  const _statusCanon = dealStatusFor(deal);
                  /* iter-379 · SSOT-driven blocker counts.
                   * Route through the shared stage-aware helper so a
                   * blocker whose stage has already been completed
                   * (Mortgage Valuation Pending after Exchange, etc.)
                   * does NOT drive the status pill or the footer
                   * strip. `awaitingCount` mirrors the RightRail
                   * "Waiting on" panel.
                   * P0 · Deal Room Data & Behaviour Integrity Pass
                   * (2026-08-12) — Threads the Critical Path
                   * optimistic-resolution sentinel so the pill +
                   * banner drop by one the moment a blocker is
                   * resolved (same render pass as the Critical Path
                   * card + Radar + NBA + health). */
                  const _activeBlockersJourney = filterActiveBlockers(deal || {}, {
                    applicableNodes: steps,
                    currentNodeId: activeStepIdSSOT,
                    resolvedIds: resolvedCriticalPaths,
                  });
                  const unresolvedBlockers = _activeBlockersJourney.length;
                  const awaitingCount = Array.isArray(awaiting) ? awaiting.length : 0;
                  /* iter-alignment · P · Journey coherence guard
                   * (2026-02-14).  The base `dealStatusFor()` band
                   * can read "healthy" from an aggregated score even
                   * when individual SSOT signals demand "At Risk".
                   * We downgrade the pill's `onTrack` to false when
                   * ANY of the following canonical conditions apply:
                   *
                   *   (a) A stage-scoped active blocker sits on the
                   *       current stage (`b.stage === activeStepIdSSOT`
                   *       OR the blocker carries no stage — legacy
                   *       shape treated as "global / current"), OR
                   *   (b) Any active blocker is high-impact (either
                   *       `impact === "high"` or the pilot-era rose
                   *       tone, both SSOT vocab), OR
                   *   (c) The deal's `targetExchange` sits in the
                   *       past AND the deal has not yet moved into
                   *       `exchanged` / `completed`.
                   *
                   * Pure derivation — no duplicate state, no mutation
                   * of the deal or the health resolver output. */
                  const _hasStageBlocker = _activeBlockersJourney.some((b) => {
                    if (!b) return false;
                    const st = String(b.stage || "").trim();
                    return !st || st === String(activeStepIdSSOT || "");
                  });
                  const _hasHighImpactBlocker = _activeBlockersJourney.some((b) => {
                    if (!b) return false;
                    const impact = String(b.impact || "").toLowerCase();
                    const tone   = String(b.tone   || "").toLowerCase();
                    return impact === "high" || tone === "rose";
                  });
                  const _dteRaw = daysBetween(deal?.targetExchange || deal?.target_exchange);
                  const _dealStageLower = String(deal?.stage || "").toLowerCase();
                  const _exchangeOverdue = _dteRaw !== null
                    && _dteRaw < 0
                    && _dealStageLower !== "exchanged"
                    && _dealStageLower !== "completed";
                  // Canonical risk rule: only a health/at-risk signal, high-impact blocker or overdue target is
                  // At risk; an ordinary open action at this stage (e.g. Awaiting signed contract) is Watch.
                  const _journeyAtRisk = !!_statusCanon.atRisk || _hasHighImpactBlocker || _exchangeOverdue;
                  const onTrack = !_journeyAtRisk && !_hasStageBlocker && _statusCanon.band !== "watch";
                  /* iter-270H — Last Updated indicator. Reads the most
                   * recent activity timestamp on the deal (falls back
                   * to milestone completed_at). Renders as a calm
                   * subline next to the title so the operator knows
                   * when the journey last moved. */
                  const _lastUpdatedIso = (() => {
                    const acts = Array.isArray(deal?.activity) ? deal.activity : [];
                    const mset = Array.isArray(milestones?.items) ? milestones.items : [];
                    const timestamps = [
                      ...acts.map((a) => a?.at || a?.created_at),
                      ...mset.map((m) => m?.completed_at),
                    ].filter(Boolean);
                    if (timestamps.length === 0) return null;
                    timestamps.sort((a, b) => Date.parse(b) - Date.parse(a));
                    return timestamps[0];
                  })();
                  const _fmtRelative = (iso) => {
                    if (!iso) return null;
                    const ms = Date.now() - Date.parse(iso);
                    if (Number.isNaN(ms)) return null;
                    const mins = Math.floor(ms / 60000);
                    if (mins < 1) return "just now";
                    if (mins < 60) return `${mins}m ago`;
                    const hours = Math.floor(mins / 60);
                    if (hours < 24) return `${hours}h ago`;
                    const days = Math.floor(hours / 24);
                    if (days < 7) return `${days}d ago`;
                    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
                  };
                  const _journeyTerminal = _dealStageLower === "completed" || !!(deal?.completed_at || deal?.completedAt);
                  return (
                    <div
                      className="rounded-2xl bg-white p-4 sm:p-6"
                      style={{
                        border: "1px solid rgba(226,232,240,0.9)",
                        boxShadow:
                          "0 1px 2px rgba(15,23,42,0.04), 0 12px 28px -20px rgba(15,23,42,0.10)",
                      }}
                      data-testid="canonical-journey-card"
                    >
                      <div
                        className="flex items-start justify-between gap-3 mb-3.5"
                        data-testid="canonical-journey-header"
                      >
                        <div className="min-w-0">
                          <h3
                            className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-900"
                            data-testid="canonical-journey-title"
                          >
                            Transaction journey
                          </h3>
                          {_journeyTerminal ? (
                            <p className="text-[13px] text-slate-600 mt-1" data-testid="canonical-journey-transaction-completed">
                              Transaction completed
                            </p>
                          ) : activeStepIdSSOT && (
                            <p
                              className="text-[13px] text-slate-600 mt-1"
                              data-testid="canonical-journey-active-stage"
                            >
                              Recorded stage:{" "}
                              <span className="font-semibold text-violet-600">
                                {activeLabel}
                              </span>{" "}
                              <StageProgressHint testid="canonical-journey-stage-hint" />
                            </p>
                          )}
                          {/* iter-alignment · P27 · De-dup pass —
                              the visible freshness chip was retired.
                              Same signal also lived on the Exchange
                              Confidence header; both were removed to
                              eliminate duplication.  Behavioural
                              derivation (`_lastUpdatedIso` from
                              activity feed + milestone
                              `completed_at`) is left intact so the
                              value stays available for future
                              tooltips or the Right-rail feed. */}
                        </div>
                        {!_journeyTerminal && <span
                          className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 h-[26px] rounded-full text-[12px] font-semibold ring-1 ${
                            onTrack
                              ? "bg-emerald-50 text-emerald-700 ring-emerald-200/70"
                              : _journeyAtRisk
                                ? "bg-rose-50 text-rose-700 ring-rose-200/70"
                                : "bg-amber-50 text-amber-700 ring-amber-200/70"
                          }`}
                          data-testid="canonical-journey-status-pill"
                          title={
                            onTrack
                              ? "No critical blockers — transaction is progressing on plan"
                              : `${unresolvedBlockers} unresolved blocker${unresolvedBlockers === 1 ? "" : "s"}`
                          }
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${onTrack ? "bg-emerald-500" : _journeyAtRisk ? "bg-rose-500" : "bg-amber-500"}`}
                          />
                          {onTrack ? "On track" : _journeyAtRisk ? "At risk" : "Watch"}
                        </span>}
                      </div>
                      <TransactionJourney
                        deal={deal}
                        milestones={milestones}
                        activityDates={activityDates}
                        journeyState={journeyState}
                        viewingStageId={viewingStageId}
                        onStepActivate={onJourneyStepActivate}
                      />
                      {/* iter-270H — Bottom legend removed. Each node
                          now carries a single canonical status chip
                          (Completed · Current Focus · At Risk · Not
                          Started) so the legend was duplicate
                          messaging by design. */}
                      {/* iter-alignment · P · Journey Status Footer
                          RETIRED.  The "N active blockers / Waiting
                          on N / No active blockers" strip that used
                          to sit here duplicated signal already
                          carried by the Next Best Action card
                          (highest-priority blocker/action), the
                          Current Blockers workspace (detailed
                          cards) and the Exchange Confidence rail
                          (compact blocker impact reference).  The
                          `unresolvedBlockers` derivation is
                          preserved above — it still powers the
                          top-right `canonical-journey-status-pill`
                          tooltip.  No blocker functionality or SSOT
                          data was removed. */}
                      {/* iter-379 — Update Milestones CTA retired.
                          The active milestone node in the rail above
                          is now the canonical entry point into the
                          progression workflow (see
                          `canonical-journey-step-*` — `interactive`
                          gated on `isFocus`). Removing this button
                          eliminates the duplicate CTA that
                          effectively did the same thing as tapping
                          the active node. The `data-testid=
                          canonical-progress-transaction-btn` handle
                          is retained as a sr-only anchor on the
                          active step's `<button>` so legacy
                          contracts + deep-link callers still
                          resolve. */}
                    </div>
                  );
                })()}
                {/* iter-alignment · P2 · The standalone <ExchangeEve>
                    render lived here.  Removed on the same commit
                    that moved it into the "Exchange Checklist" tab
                    so there is exactly one instance of the
                    checklist in the Deal Room.  Grep for
                    `<ExchangeEve` should return exactly one hit. */}
                {/* iter-alignment · P4 · Important Note banner.
                    Retired the vertical stacked banner previously
                    rendered above ActivityFeed — the approved
                    screenshot places it DIRECTLY beneath the Property
                    Header instead.  New site: line ~6154, above the
                    Next Best Action card.  See below. */}
                <CompactBlockerRow deal={deal} docRequests={docRequests} intel={intel} awaiting={awaiting} onSelectTab={jumpToTab} />
                <ActivityFeed
                  deal={deal}
                  activities={feedActivities}
                  documents={documents}
                  tasks={tasks}
                  participants={participants}
                  milestones={milestones}
                  journeyState={journeyState}
                  txnState={txnState}
                  operationalNotes={operationalNotes}
                  waitingOn={effectiveAwaiting}
                  currentUserId={currentUserId}
                  dealId={dealId}
                  access={access}
                  tab={activityTab}
                  onSelectTab={setActivityTab}
                  onOpenProgress={() => setProgressOpen(true)}
                  onActivateJourneyStep={onJourneyStepActivate}
                  onMoveToNextStage={onMoveToNextStage}
                  onCompleteMilestone={completeMilestone}
                  onReopenMilestone={reopenMilestone}
                  onEditCompletionDate={editMilestoneCompletionDate}
                  onSetTargetDate={setMilestoneTargetDate}
                  onSetNote={setMilestoneNote}
                  onResolveBlocker={handleCriticalPathResolved}
                  awaitingBlockers={effectiveAwaiting}
                  composerOpenSignal={composerSignal}
                  composerInitialType={composerType}
                  onComposerClosed={() => setComposerType(null)}
                  onRequestDoc={() => setOpenSheet("request-doc")}
                  onOpenRequestDocWithTemplate={(tpl) => {
                    setRequestDocInitialTemplate(tpl || null);
                    setOpenSheet("request-doc");
                  }}
                  onReloadRoom={reload}
                  activityStatus={activityStatus}
                  docRequests={docRequests}
                  onCriticalPathResolved={handleCriticalPathResolved}
                  viewingStageId={viewingStageId}
                  onReturnToCurrentStage={onReturnToCurrentStage}
                  onReviewDoc={openDocumentReview}
                  activityHasMore={activityPaging.hasMore}
                  loadingEarlier={activityPaging.loading}
                  onLoadEarlierActivity={loadEarlierActivity}
                />
              </section>

              <RightRail
                deal={deal}
                intel={intel}
                participants={effectiveParticipants}
                documents={effectiveDocuments}
                awaiting={effectiveAwaiting}
                journeyState={journeyState}
                milestones={milestones}
                txnState={txnState}
                dealId={dealId}
                access={access}
                onManageParticipants={(role) => {
                  setParticipantsInitialRole(role || null);
                  setParticipantsOpen(true);
                }}
                onViewAllDocuments={() => nav(`/documents/${dealId}`)}
                onActivateJourneyStep={onJourneyStepActivate}
                onEditFinancials={() => setFinancialsOpen(true)}
                currentUserId={currentUserId}
                onUploadFiles={onRailUploadFiles}
                onRequestDoc={() => setOpenSheet("request-doc")}
                onReviewDoc={openDocumentReview}
                onUpdateFundingBasis={onUpdateFundingBasis}
                onUpdateBuyerPosition={onUpdateBuyerPosition}
              />
            </div>

            {/* iter-alignment · P · Deal Room · Sticky Action Bar
                Retirement (2026-03-02) — the fixed bottom bar is
                gone.  The Log Activity composer beneath the
                Transaction Radar is now the single logging surface
                (notes / calls / received messages / documents) and
                the Send message + Create task affordances live in a
                compact row directly beneath that composer — see
                `ActivityPanel` `onOpenSheet` wiring above.  This
                unblocks the activity feed from the fixed bar and
                keeps every existing sheet handler (`setOpenSheet`)
                intact. */}
          </div>
        </ShellCanvas>
      </div>

      {createPortal(
        <ParticipantsModal
          open={participantsOpen}
          onClose={() => { setParticipantsOpen(false); setParticipantsInitialRole(null); }}
          deal={deal}
          property={property}
          initialRole={participantsInitialRole}
        />,
        document.body,
      )}
      {/* iter-alignment · P0 · Deal Room · Fallen Through
          (2026-02) — Portalled modal captures date + reason +
          optional note.  On success the deal reload runs so
          every SSOT surface (journey rail, banner, NBA gates,
          blocker engine) picks up the terminal transition on
          the next tick. */}
      {fallenThroughOpen && (
        <MarkFallenThroughModal
          deal={deal}
          onClose={() => setFallenThroughOpen(false)}
          onCompleted={() => reload()}
        />
      )}
      {/* iter-221 — per-step progression sheet. Opens when an
          operator taps any milestone in the operational journey
          rail. Every save reloads the journey + activity feed so
          the rail + timeline stay in lock-step. */}
      <MilestoneProgressionSheet
        open={!!progressionStep}
        step={progressionStep}
        dealId={dealId}
        /* iter-alignment · P0 · Milestone-Level Completion (2026-08-18)
         * — pass the full milestones envelope so the sheet can resolve
         * the current outstanding required row within `step.id`, act
         * on the milestone SSOT directly, and re-derive the NEXT
         * outstanding row after each completion without a re-open. */
        milestones={milestones}
        /* iter-372 — SSOT for "which step is current". Uses the
         * unified `activeStepId` derived by useDealTransactionState
         * so the sheet's "current step" badge matches the visible
         * Journey rail focus AND the NBA card (previously used
         * journeyState.current_step which returned pre-journey step
         * ids like "offer" while the rail highlighted Mortgage —
         * leaving Mortgage sheet saying "Not the current step"). */
        currentStepId={txnState?.activeStepId || journeyState?.current_step}
        onClose={() => setProgressionStep(null)}
        /* iter-375 — Refresh the deal (chainStore) alongside
         * milestones / journey / activity so the sheet's completion
         * also re-derives every SSOT surface without a page reload. */
        onProgressed={() => { refreshDealFromApi(null); reload(); requestActivityAnchor(); }}
      />
      {/* iter-alignment · P11 · The read-only preview drawer was
       * retired.  Journey rail selections now route the Current
       * Stage panel below (see `viewingStageId`) — a single,
       * always-visible surface that honours the same SSOT and
       * never mutates the canonical progression. */}
      {/* iter-222 — Mobile sticky "Update Milestones" CTA.
          Thumb-reachable, safe-area aware, opens the SAME per-step
          progression sheet defaulting to the current active step.
          Only renders below sm breakpoint to avoid duplicating the
          desktop in-header button. Hidden when the progression
          sheet is already open. */}
      {!progressionStep && deal && !txnState?.isTerminal && (
        // In page flow (not fixed): the fixed version sat under the mobile bottom nav and could not be tapped.
        <div
          className="sm:hidden px-4 mt-4 mb-[calc(env(safe-area-inset-bottom,0px)+200px)]"
          data-testid="canonical-progress-transaction-mobile"
        >
          <button
            type="button"
            onClick={() => onJourneyStepActivate(txnState?.activeStepId || journeyState?.current_step || (journeyState?.steps?.[0]?.id) || deal?.stage || "offer")}
            className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-2xl bg-violet-600 hover:bg-violet-700 text-white text-[13.5px] font-semibold shadow-[0_12px_28px_-12px_rgba(99,102,241,0.55)]"
            data-testid="canonical-progress-transaction-mobile-btn"
          >
            <ArrowRight size={14} strokeWidth={2.4} />
            Update Milestones
          </button>
        </div>
      )}
      <ShareRoomModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        dealId={dealId}
        dealName={property?.address || deal?.name || "Deal Room"}
      />
      {/* iter-398 · P0 · Memorandum of Sale workflow.  Opens from
          the pre-journey NBA (`offer` / `memo` / `aml`).  Reloads
          activity + journey on completion so the timeline, stage
          rail and Exchange Confidence card all refresh from the
          server-side SSOT — never from a locally-guessed patch. */}
      {mosModalOpen && (
        <IssueMemorandumOfSaleModal
          deal={{
            id: dealId,
            name: deal?.name,
            property_address: property?.address || property?.address_line || deal?.name,
          }}
          onClose={() => setMosModalOpen(false)}
          onCompleted={async () => {
            // iter-398B — Fully await SSOT refresh + MoS status
            // fetch BEFORE the modal shows its success screen +
            // fires the success toast.  This guarantees the toast
            // never fires ahead of persisted state.
            try { await refreshDealFromApi(null); } catch (_) { /* noop */ }
            try { await reload(); } catch (_) { /* noop */ }
            try { await reloadMoSStatus(); } catch (_) { /* noop */ }
            try { requestActivityAnchor?.(); } catch (_) { /* noop */ }
          }}
        />
      )}
      {/* iter-399 · Mortgage milestone capture modal.  Same SSOT-
          refresh pattern as MoS so the NBA + Deal Health chip flip
          in place after each capture. */}
      {mortgageModalMode && (
        <MortgageMilestoneModal
          dealId={dealId}
          mode={mortgageModalMode}
          initialState={mortgageState}
          onClose={() => setMortgageModalMode(null)}
          onCompleted={async (nextState) => {
            if (nextState) setMortgageState(nextState);
            setMortgageModalMode(null);
            try { await refreshDealFromApi(null); } catch (_) { /* noop */ }
            try { await reload(); } catch (_) { /* noop */ }
            try { await reloadMortgageState(); } catch (_) { /* noop */ }
            try { requestActivityAnchor?.(); } catch (_) { /* noop */ }
          }}
        />
      )}
      {/* iter-259 — Financials editor drawer. Patches the deal
          through patchDealLocally so the rail + Forecasting Centre
          re-render from the same store immediately.
          iter-alignment · P3 · Agreed Fee Persistence (2026-02-28)
          — the drawer's optimistic local patch is now followed by
          a canonical `updateDeal` write so the fee + commission
          type survive refresh / navigation / background refetch.
          Pilot deals stay local (Postgres-only).  Errors roll the
          optimistic patch back so the Revenue card never desyncs
          from the persisted SSOT. */}
      <FinancialsSheet
        open={financialsOpen}
        onOpenChange={setFinancialsOpen}
        deal={deal}
        onSave={async (patch) => {
          if (!patch || typeof patch !== "object") return;
          const isPilotDeal = !!deal?._pilot;
          const previousSnapshot = {
            fee:                   deal?.fee ?? null,
            commissionType:        deal?.commissionType ?? null,
            feePct:                deal?.feePct ?? null,
            agreedPrice:           deal?.agreedPrice ?? null,
            vatIncluded:           deal?.vatIncluded,
            expectedExchangeMonth: deal?.expectedExchangeMonth ?? null,
            forecastConfidence:    deal?.forecastConfidence ?? null,
            revenueAtRisk:         deal?.revenueAtRisk ?? null,
          };
          // Optimistic — every downstream surface renders instantly.
          patchDealLocally?.(dealId, patch);
          if (isPilotDeal) {
            // Pilot deals never round-trip; the optimistic patch is
            // the SSOT.
            toast.success("Financials saved");
            return;
          }
          try {
            await updateDeal(dealId, patch);
            toast.success("Financials saved");
          } catch (err) {
            // Roll back so the Revenue card never lies about the
            // persisted state.
            patchDealLocally?.(dealId, previousSnapshot);
            /* iter-alignment · P3 · Revenue Persistence Fix (2026-02-28)
             * The backend's `detail` payload is a STRUCTURED
             * object (`{code, message, field, debug_id, errors}`)
             * when a Pydantic validator fires.  Dropping the raw
             * object into `toast.error({description})` throws
             * "Objects are not valid as a React child" and
             * blanks the Deal Room.  Extract the human-readable
             * `message` field first, then fall back through
             * `err.message` and a static copy so the toast
             * always renders a string. */
            const detail = err?.response?.data?.detail;
            const description =
              typeof detail === "string"
                ? detail
                : (detail && typeof detail.message === "string")
                  ? detail.message
                  : (typeof err?.message === "string" ? err.message : "Please try again.");
            toast.error("Couldn't save financials", { description });
          }
        }}
      />
      {/* iter-204b — composer sheets wired to the sticky bar Action
          buttons. Reload activity on send/create/request so the new
          timeline event surfaces immediately. */}
      <MessageAllSheet
        open={openSheet === "message"}
        onClose={() => setOpenSheet(null)}
        dealId={dealId}
        participants={participants}
        onSent={() => { requestActivityAnchor(); reload(); }}
      />
      <TaskSheet
        open={openSheet === "task"}
        onClose={() => setOpenSheet(null)}
        dealId={dealId}
        participants={participants}
        onCreated={() => { requestActivityAnchor(); reload(); }}
      />
      <RequestDocSheet
        open={openSheet === "request-doc"}
        onClose={() => { setOpenSheet(null); setRequestDocInitialTemplate(null); setRequestDocRecipientEmail(null); }}
        dealId={dealId}
        participants={effectiveParticipants}
        initialRecipientEmail={requestDocRecipientEmail}
        onRequested={() => { requestActivityAnchor(); reload(); }}
        initialTemplateId={requestDocInitialTemplate}
        docRequests={docRequests}
      />

      {/* iter-214B — Deal Progression Control Centre drawer. The
          operational progression engine of ChainFlow. Opens from the
          "Progress deal →" CTA below the activity tab and from any
          future surface that wants to drop the operator directly into
          milestone progression. iter-214B-FIX — Wrapped in an error
          boundary so any internal crash never blanks the page. */}
      <ProgressionSheetErrorBoundary onClose={() => setProgressOpen(false)} open={progressOpen}>
        <ProgressDealSheet
          open={progressOpen}
          onClose={() => setProgressOpen(false)}
          deal={deal}
          milestones={milestones}
          resolver={resolveJourneyProgress}
          onComplete={async (mid) => {
            await completeMilestone(mid);
          }}
          onRefresh={reload}
          participants={participants}
          documents={documents}
          onSetTargetDate={setMilestoneTargetDate}
          onUploadDocument={async (milestoneId, file) => {
            // iter-215 — Upload a document tagged against this
            // milestone. Reuses the canonical /api/deals/:id/documents
            // pipeline; the new `milestone_id` form field threads the
            // attachment through to the milestone card.
            //
            // iter-298 — Pilot bypass. Demo deals are frontend-only
            // (chainStore), so the canonical POST 404s. We route the
            // file through `appendPilotDocument()` which writes the
            // document + a `document_upload` activity row into the
            // pilot-override layer; the milestone_id tag is preserved
            // on the synthetic doc so the milestone card surfaces it
            // on the next render.
            if (deal?._pilot) {
              const doc = appendPilotDocument(dealId, file);
              if (doc) {
                await reload();
                requestActivityAnchor();
                toast.success("Document uploaded");
              }
              return;
            }
            const form = new FormData();
            form.append("file", file);
            if (milestoneId) form.append("milestone_id", milestoneId);
            await api.post(`/api/deals/${dealId}/documents`, form);
            await reload();
            toast.success("Document uploaded");
          }}
        />
      </ProgressionSheetErrorBoundary>

      {/* iter-alignment · P — Move to next stage confirm modal
       * (2026-08-17).  Rendered only while `moveNextConfirm` is
       * set (operator pressed the "Move to next stage" or
       * "Mark transaction complete" button with N>0 outstanding
       * items).  Copy is exactly the brief:
       *   "Move to next stage anyway?"
       *   "{N} items will remain outstanding and will not be
       *    marked complete."
       * Cancel closes without side effects.  Confirm dispatches
       * `_handleAdvanceNow(spec)` which calls the backend with
       * `manual=true` + `outstanding_count` — see
       * `chainStore.advanceStage`.  No milestones are ever
       * auto-completed by this path. */}
      {moveNextConfirm && (
        <div
          className="fixed inset-0 z-[70] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="move-next-title"
          aria-describedby="move-next-desc"
          data-testid="move-to-next-confirm"
          onClick={(e) => {
            if (e.target === e.currentTarget) setMoveNextConfirm(null);
          }}
        >
          <div className="w-full max-w-sm rounded-2xl bg-white ring-1 ring-slate-200 shadow-2xl p-5">
            <h3
              id="move-next-title"
              className="text-[15px] font-bold text-slate-900 tracking-[-0.01em]"
              data-testid="move-to-next-confirm-title"
            >
              {moveNextConfirm.isCompletion
                ? "Mark transaction complete anyway?"
                : "Move to next stage anyway?"}
            </h3>
            <p
              id="move-next-desc"
              className="mt-1.5 text-[12.5px] leading-relaxed text-slate-600"
              data-testid="move-to-next-confirm-body"
            >
              {moveNextConfirm.outstandingCount}
              {" "}
              {moveNextConfirm.outstandingCount === 1 ? "item" : "items"}
              {" "}
              will remain outstanding and will not be marked complete.
            </p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setMoveNextConfirm(null)}
                className="h-9 px-3 rounded-lg text-[12.5px] font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 cf-press"
                data-testid="move-to-next-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const spec = moveNextConfirm;
                  setMoveNextConfirm(null);
                  await _handleAdvanceNow(spec);
                }}
                className="h-9 px-3 rounded-lg text-[12.5px] font-semibold text-white bg-violet-600 hover:bg-violet-700 cf-press"
                data-testid="move-to-next-confirm-btn"
              >
                {moveNextConfirm.isCompletion ? "Mark complete" : "Move anyway"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* iter-alignment · P40 — Inline date capture modal.
       *
       * Rendered only while `pendingDateCapture` is set (an
       * operator just tapped complete on a milestone that
       * carries a `date_prompt` metadata block).  Fields default
       * to today; both are optional.  Save fires
       * `completeMilestone(id, dates)` — the same mutation path
       * the vanilla tap uses — so the audit trail, permissions,
       * P38 auto-advance and Exchange Confidence recompute all
       * remain canonical.  No date fires the same mutation with
       * an empty payload so simple yes/no completions are
       * unchanged. */}
      {pendingDateCapture && (
        <InlineDateCaptureModal
          milestone={pendingDateCapture.milestone}
          onCancel={() => setPendingDateCapture(null)}
          onSubmit={async (dates) => {
            const mid = pendingDateCapture.milestone?.id;
            setPendingDateCapture(null);
            if (mid) await completeMilestone(mid, dates || {});
          }}
        />
      )}

      {/* iter-alignment · P0 Phase 2 — Deal Room Document Review UX.
       * Renders as a right-anchored sheet when a document row is
       * clicked (rail or Documents tab).  Reviews mutate the
       * Mongo document through /api/deals/:id/documents/:id/review
       * and locally apply the updated row via
       * `applyReviewedDocument` so the pill flips instantly. */}
      {reviewingDoc && (
        <DocumentReviewModal
          document={reviewingDoc}
          dealId={dealId}
          onClose={closeDocumentReview}
          onUpdated={applyReviewedDocument}
        />
      )}
    </>
  );
}


/* iter-214B-FIX-v2 — Route-level error boundary.
 *
 * After iter-214B-FIX, the canonical Deal Room still rendered blank
 * for some property routes on production. Adding a final defensive
 * layer around the entire page ensures that ANY uncaught runtime
 * error inside DealRoomCanonicalPageInner — whether from
 * TransactionJourney, ProgressDealSheet, ActivityFeed, milestone
 * resolution, route param parsing, the chain store, or anywhere
 * else — falls back to a calm operational error surface instead of
 * a blank screen. The user gets two clear escape hatches: Retry
 * (re-renders the page tree) and Back to Deal Rooms. */
class DealRoomRouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { crashed: false, key: 0 };
    this.handleRetry = this.handleRetry.bind(this);
  }
  static getDerivedStateFromError() {
    return { crashed: true };
  }
  componentDidCatch(err, info) {
    if (typeof console !== "undefined" && console.error) {
      console.error("[DealRoom crashed]", err, info?.componentStack);
    }
  }
  handleRetry() {
    // Bump the inner key so React unmounts + remounts the entire
    // page tree — every useState resets and every effect re-runs.
    this.setState((s) => ({ crashed: false, key: s.key + 1 }));
  }
  render() {
    if (this.state.crashed) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="canonical-deal-room-route-error">
          <div className="max-w-md text-center bg-white rounded-2xl ring-1 ring-slate-100 shadow-[0_22px_44px_-28px_rgba(15,23,42,0.18)] px-7 py-8">
            <p className="text-[15px] font-bold text-slate-900">Deal Room couldn&apos;t fully load</p>
            <p className="mt-2 text-[12.5px] text-slate-500 leading-relaxed">
              Refresh the page or return to Deal Rooms.
            </p>
            <div className="mt-5 flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={this.handleRetry}
                className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-[12.5px] font-semibold cf-press"
                data-testid="canonical-deal-room-route-error-retry"
              >
                Retry
              </button>
              <a
                href="/deal-rooms"
                className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-white ring-1 ring-slate-200 hover:bg-slate-50 text-slate-700 text-[12.5px] font-semibold cf-press"
                data-testid="canonical-deal-room-route-error-back"
              >
                Back to Deal Rooms
              </a>
            </div>
          </div>
        </div>
      );
    }
    return (
      <React.Fragment key={this.state.key}>
        {this.props.children}
      </React.Fragment>
    );
  }
}

export default function DealRoomCanonicalPage() {
  return (
    <DealRoomRouteErrorBoundary>
      <DealRoomCanonicalPageInner />
    </DealRoomRouteErrorBoundary>
  );
}
